// src/extensions/media-uploader/uploadMediaToServer.ts
async function uploadMediaToServer(editor, file) {
  try {
    const { endpoint, id } = await getMediaUploadEndpoint(editor, file);
    await uploadMediaToEndpoint(endpoint, file);
    const src = await getUploadedMediaSrc(editor, id);
    return src;
  } catch (error) {
    return Promise.reject(error);
  }
}
async function getMediaUploadEndpoint(editor, media) {
  const { workId, assessmentId, tenant, mediaAPIBase, userRole, userId } = await getMediaUploaderOpts(editor);
  const body = buildData({
    filename: media.name,
    type: media.type,
    workId,
    assessmentId,
    userId
  });
  const endpoint = `${mediaAPIBase}/upload_url`;
  const res = await request(
    body,
    endpoint,
    "post",
    cadmusHeaders(tenant, userRole, userId)
  );
  const data = await res.json();
  if (data?.endpoint && data?.id) {
    return data;
  }
  return Promise.reject(new Error("failed uploading media"));
}
async function uploadMediaToEndpoint(endpoint, file) {
  const res = await fetch(endpoint, {
    method: "put",
    body: file,
    mode: "cors"
  });
  return statusOk(res);
}
async function getUploadedMediaSrc(editor, mediaId) {
  const { tenant, mediaAPIBase, userRole, userId } = await getMediaUploaderOpts(editor);
  const body = buildData({ mediaId });
  const endpoint = `${mediaAPIBase}/upload`;
  const response = await request(
    body,
    endpoint,
    "post",
    cadmusHeaders(tenant, userRole, userId)
  );
  const data = await response.json();
  if (data?.src) {
    return data.src;
  }
  return Promise.reject(new Error("failed uploading media"));
}
async function getMediaUploaderOpts(editor) {
  const MediaUploaderExt = editor.extensionManager.extensions.find(
    (ext) => ext.name === "mediaUploader"
  );
  if (!MediaUploaderExt) {
    return Promise.reject(new Error("Media API is not set"));
  }
  return MediaUploaderExt.options;
}
var cadmusHeaders = (tenant, userRole, userId) => ({
  "x-cadmus-tenant": tenant ?? "",
  "x-cadmus-role": userRole ?? "",
  "x-cadmus-user": userId ?? ""
});
async function statusOk(response) {
  if (response.ok) {
    return Promise.resolve(response);
  }
  return Promise.reject(new Error("failed uploading media"));
}
async function request(body, endpoint, method, headers = {}) {
  const res = await fetch(endpoint, {
    method,
    body,
    headers: {
      Accept: "application/json",
      ...headers
    },
    credentials: "include"
  });
  return statusOk(res);
}
function buildData(args) {
  const body = new FormData();
  Object.entries(args).forEach(
    ([key, value]) => value && body.append(key, value)
  );
  return body;
}

// src/extensions/media-uploader/media-uploader.ts
import { Extension } from "@tiptap/core";

// src/extensions/media-uploader/media-uploader-plugin.ts
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import crel from "crelt";

// src/extensions/media-uploader/media-error.png
var media_error_default = "./media-error-R3T6GV3A.png";

// src/extensions/media-uploader/media-uploader-plugin.ts
var MediaUploaderPluginKey = new PluginKey(
  "mediaUploader"
);
var MediaUploaderPlugin = () => new Plugin({
  key: MediaUploaderPluginKey,
  state: {
    init() {
      return DecorationSet.empty;
    },
    apply(tr, decorations) {
      decorations = decorations.map(tr.mapping, tr.doc);
      const actions = tr.getMeta(
        MediaUploaderPluginKey
      );
      actions?.forEach((action) => {
        switch (action.type) {
          case "addMedia": {
            const deco = AddMediaWidget(action);
            decorations = decorations.add(tr.doc, [deco]);
            break;
          }
          case "addError": {
            const deco = ErrorWidget(action);
            decorations = decorations.add(tr.doc, [deco]);
            break;
          }
          case "removePlaceholder": {
            const { id } = action;
            const decos = decorations.find(
              void 0,
              void 0,
              (spec) => spec.id === id
            );
            decorations = decorations.remove(decos);
            break;
          }
        }
      });
      return decorations;
    }
  },
  props: {
    decorations(editorState) {
      return this.getState(editorState);
    }
  }
});
var ErrorWidget = (payload) => {
  const { id, pos, message, side } = payload;
  return Decoration.widget(
    pos,
    (view) => {
      const onCancel = () => {
        const meta = [
          { type: "removePlaceholder", id }
        ];
        const tr = view.state.tr.setMeta(MediaUploaderPluginKey, meta);
        view.dispatch(tr);
      };
      const widget = crel(
        "figure",
        {
          class: "media-uploader-error",
          contenteditable: "false"
        },
        [
          crel("img", {
            // TODO: verify if we use img to show error for all media type or handle individually
            src: media_error_default,
            draggable: "false",
            contenteditable: "false"
          }),
          crel(
            "p",
            {
              contenteditable: "false"
            },
            message ? [message] : [
              `There was an error while uploading this media.`,
              crel("br"),
              `Please check your internet connection and try again.`
            ]
          ),
          crel("button", { class: "cui-LinkButton", onclick: onCancel }, [
            crel("span", ["Cancel"])
          ])
        ]
      );
      return widget;
    },
    {
      id,
      side
    }
  );
};
function getAddMediaElement(payload) {
  switch (payload.mediaType) {
    case "image":
      return crel("img", {
        src: payload.src,
        draggable: "false",
        style: payload.width ? `max-width: ${payload.width}px;` : void 0
      });
    case "audio":
      return crel("audio", {
        src: payload.src,
        controls: "true",
        draggable: "false",
        style: "pointer-events: none; opacity: 0.75; width: 100%;",
        controlsList: "nodownload noplaybackrate"
      });
    case "video":
      return crel("video", {
        src: payload.src,
        controls: "true",
        draggable: "false",
        style: "pointer-events: none; opacity: 0.75; width: 100%;",
        controlsList: "nodownload noplaybackrate"
      });
    default:
      throw new Error(
        `Unsupported media type: ${payload.mediaType}`
      );
  }
}
var AddMediaWidget = (payload) => {
  const { id, pos, src, width, side, mediaType } = payload;
  const elem = getAddMediaElement(payload);
  const loader2 = crel("span", {
    "data-loading": "true"
  });
  const figure = crel(
    "figure",
    {
      "data-loading": "true",
      draggable: "false",
      contenteditable: "false"
    },
    [elem, loader2]
  );
  return Decoration.widget(pos, () => figure, {
    id,
    src,
    width,
    side,
    mediaType
  });
};
function addErrorPlaceholder(attrs) {
  return { type: "addError", ...attrs };
}
function addMediaPlaceholder(attrs) {
  return { type: "addMedia", ...attrs };
}
function removePlaceholder(id) {
  return { type: "removePlaceholder", id };
}
function setMediaUploaderTransactionMeta(editor, meta) {
  editor.commands.command(({ tr }) => {
    tr.setMeta(MediaUploaderPluginKey, meta);
    return true;
  });
}
function findUploadPlaceholder(state, id) {
  const decos = MediaUploaderPluginKey.getState(state);
  if (!decos) return null;
  const found = decos.find(void 0, void 0, (spec) => spec.id === id);
  return found.length ? found[0].from : null;
}
function findPlaceholdersAtPos(state, pos) {
  const decos = MediaUploaderPluginKey.getState(state);
  if (!decos) return null;
  return decos.find(void 0, void 0, () => true).filter((deco) => deco.from === pos);
}

// src/extensions/media-uploader/media-uploader.ts
var MediaUploader = Extension.create({
  name: "mediaUploader",
  addProseMirrorPlugins() {
    return [MediaUploaderPlugin()];
  },
  // add keyboard shortcut to remove media widget around the selection
  // when backspace/delete key is pressed
  addKeyboardShortcuts() {
    const handleBackspace = () => this.editor.commands.command(({ tr, state }) => {
      const { selection } = state;
      const { from, to } = selection;
      const decos = MediaUploaderPluginKey.getState(state);
      if (!decos) return false;
      const actions = [];
      decos.find(void 0, void 0, () => true).forEach((deco) => {
        if (!deco.spec.src && deco.from >= from - 1 && deco.from <= to) {
          actions.push(removePlaceholder(deco.spec.id));
        }
      });
      if (actions.length) {
        tr.setMeta(MediaUploaderPluginKey, actions);
      }
      return false;
    });
    const handleDelete = () => this.editor.commands.command(({ tr, state }) => {
      const { selection } = state;
      const { from, to } = selection;
      const decos = MediaUploaderPluginKey.getState(state);
      if (!decos) return false;
      const actions = [];
      decos.find(void 0, void 0, () => true).forEach((deco) => {
        if (!deco.spec.src && deco.from >= from && deco.from <= to + 1) {
          actions.push(removePlaceholder(deco.spec.id));
        }
      });
      if (actions.length) {
        tr.setMeta(MediaUploaderPluginKey, actions);
      }
      return false;
    });
    return {
      Backspace: () => handleBackspace(),
      "Mod-Backspace": () => handleBackspace(),
      Delete: () => handleDelete(),
      "Mod-Delete": () => handleDelete()
    };
  }
});

// src/extensions/media-uploader/file-utils.ts
import ImageCompressor from "compressorjs";
import isSvg from "is-svg";
import { filetypeinfo } from "magic-bytes.js";
var ALLOWED_MIME_TYPES = {
  image: ["image/jpeg", "image/png", "image/svg+xml", "image/tiff"],
  audio: ["audio/mpeg", "audio/wav", "audio/x-wav"],
  video: ["video/mp4", "video/quicktime"]
};
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    new ImageCompressor(file, {
      maxWidth: 2048,
      success: (blobOrFile) => {
        if (isBlob(blobOrFile)) {
          return resolve(blobToFile(blobOrFile));
        }
        return resolve(blobOrFile);
      },
      error: reject
    });
  });
}
function isBlob(file) {
  return file.name === void 0 && file.lastModified === void 0;
}
function blobToFile(blob, options) {
  return new File([blob], "someMedia", options);
}
async function readMediaFile(file) {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
function isAllowedMimeType(mime, mediaType) {
  return ALLOWED_MIME_TYPES[mediaType].includes(mime);
}
async function mediaMimes(file) {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      if (reader.result) {
        const buffer = new Uint8Array(reader.result);
        const filetypes = filetypeinfo(buffer);
        const svg = isSvg(new TextDecoder("utf-8").decode(buffer));
        if (svg) {
          resolve(["image/svg+xml"]);
        } else if (filetypes.length > 0) {
          resolve(filetypes.map(({ mime }) => mime ?? ""));
        } else {
          resolve([]);
        }
      } else {
        resolve([]);
      }
    };
    reader.onerror = () => {
      reject(new Error("Error determining media mime type"));
    };
    reader.readAsArrayBuffer(file);
  });
}
var MAX_SIZE = 800;
var SIZE_STEP = 100;
async function getImageWidth(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let roundedWidth = Math.round(img.naturalWidth / SIZE_STEP) * SIZE_STEP;
      roundedWidth = roundedWidth > 0 ? roundedWidth : SIZE_STEP;
      const width = roundedWidth > MAX_SIZE ? MAX_SIZE : roundedWidth;
      resolve(width);
    };
    img.src = url;
  });
}

// src/extensions/media-uploader/insertMedia.ts
async function insertMedia(editor, files, mediaType, insertAt) {
  const { tr, doc } = editor.view.state;
  const $pos = insertAt ? doc.resolve(insertAt) : tr.selection.$from;
  const node = $pos.node($pos.depth);
  const isEmpty = node?.content.size === 0;
  let pos = $pos.pos;
  if (isEmpty) {
    const from = $pos.before($pos.depth);
    const to = from + node.nodeSize;
    tr.delete(from, to);
    pos = from;
  } else {
    pos = $pos.after($pos.depth + 1);
  }
  const length = files.length;
  const placeholders = findPlaceholdersAtPos(editor.view.state, pos);
  const start = lowestDecoSide(placeholders) - length;
  await Promise.all(
    files.map(
      (file, index) => uploadMedia(editor, file, mediaType, pos, start + index)
    )
  );
}
function lowestDecoSide(decos) {
  let lowest = 0;
  if (decos) {
    decos.forEach((deco) => {
      if (deco.spec.side < lowest) {
        lowest = deco.spec.side;
      }
    });
  }
  return lowest;
}
async function uploadMedia(editor, file, mediaType, pos, side) {
  const id = {};
  const mimes = await mediaMimes(file[0]);
  const isAllowed = !!mimes.find((mime) => isAllowedMimeType(mime, mediaType));
  const view = editor.view;
  if (!isAllowed) {
    setMediaUploaderTransactionMeta(editor, [
      {
        type: "addError",
        id,
        pos,
        message: "Media file type is not supported"
      }
    ]);
    return;
  }
  try {
    const compressedFile = mediaType === "image" ? await compressImage(file[0]) : file[0];
    const src = await readMediaFile(compressedFile);
    const width = mediaType === "image" ? await getImageWidth(compressedFile) : void 0;
    setMediaUploaderTransactionMeta(editor, [
      addMediaPlaceholder({
        id,
        pos,
        src,
        width,
        side,
        mediaType
      })
    ]);
    const mediaSrc = await uploadMediaToServer(editor, compressedFile);
    const placeholderPos = findUploadPlaceholder(view.state, id);
    if (placeholderPos === null) return;
    const placeholderText = file[1] ?? "";
    if (mediaType === "image") {
      editor.commands.setImage({
        src: mediaSrc,
        width: width && width >= 800 ? "fitToText" /* FitToText */ : "original" /* Original */,
        position: placeholderPos,
        hasCaption: !!placeholderText,
        caption: placeholderText
      });
    } else if (mediaType === "audio") {
      editor.commands.setAudio({
        src: mediaSrc,
        position: placeholderPos,
        hasCaption: !!placeholderText,
        caption: placeholderText
      });
    } else if (mediaType === "video") {
      editor.commands.setVideo({
        src: mediaSrc,
        position: placeholderPos,
        hasCaption: !!placeholderText,
        caption: placeholderText
      });
    }
    const placeholderMetas = repositionedPlaceholders(
      editor,
      placeholderPos,
      placeholderText.length,
      id
    );
    setMediaUploaderTransactionMeta(editor, placeholderMetas);
  } catch (error) {
    console.error(error);
    const placeholderPos = findUploadPlaceholder(view.state, id);
    if (placeholderPos !== null) {
      setMediaUploaderTransactionMeta(editor, [
        removePlaceholder(id),
        addErrorPlaceholder({ id, pos: placeholderPos })
      ]);
    } else {
      setMediaUploaderTransactionMeta(editor, [
        addErrorPlaceholder({ id, pos })
      ]);
    }
  }
}
function repositionedPlaceholders(editor, placeholderPos, captionLength, deletedId) {
  const placeholders = findPlaceholdersAtPos(editor.view.state, placeholderPos);
  const uploadedPlaceholder = placeholders?.find(
    (deco) => deco.spec.id === deletedId
  );
  const uploadedSide = uploadedPlaceholder?.spec.side;
  const movedPlaceholders = placeholders?.filter(
    (deco) => deco.spec.side > uploadedSide
  );
  const placeholderMetas = [
    removePlaceholder(deletedId)
  ];
  if (movedPlaceholders) {
    placeholderMetas.push(
      ...movedPlaceholders.map((deco) => removePlaceholder(deco.spec.id))
    );
    movedPlaceholders.forEach((deco) => {
      const { id, width, src, mediaType } = deco.spec;
      if (id && src && mediaType) {
        placeholderMetas.push(
          // Move other media placeholders position to inserted media's caption's
          // length + 2 to make sure that the rest of the media inserted on the
          // right position
          addMediaPlaceholder({
            id,
            width,
            src,
            mediaType,
            pos: placeholderPos + captionLength + 2
          })
        );
      }
    });
  }
  return placeholderMetas;
}

// src/extensions/media-uploader/patchMedia.ts
import { findChildren } from "@tiptap/core";
function patchMedia(editor, tr) {
  const { mediaAPIBase } = editor.extensionManager.extensions.find(
    (ext) => ext.name === "mediaUploader"
  )?.options ?? {};
  const isInvalidMedia = (node) => {
    if (node.type.name === "image") {
      return new URL(mediaAPIBase).hostname !== new URL(node.attrs.src).hostname;
    }
    return false;
  };
  let invalidMedia;
  const queuedMedia = [];
  while ((invalidMedia = findChildren(tr.doc, isInvalidMedia)) && invalidMedia.length !== 0) {
    const firstImage = invalidMedia[0];
    const { node, pos } = firstImage;
    const { src } = node.attrs;
    const { textContent: textContent2 } = node;
    if (src && !/^file/.test(src)) {
      queuedMedia.push([src, pos, textContent2]);
    }
    tr.delete(pos, pos + node.nodeSize);
  }
  if (queuedMedia.length) {
    setTimeout(() => {
      queuedMedia.forEach(([src, pos, textContent2]) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const image = new Image();
        image.crossOrigin = "Anonymous";
        image.onload = () => {
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          context?.drawImage(image, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              const file = blobToFile(blob, {
                type: "image/png"
              });
              insertMedia(editor, [[file, textContent2]], "image", pos);
            } else {
              setMediaUploaderTransactionMeta(editor, [
                addErrorPlaceholder({ id: {}, pos })
              ]);
            }
          });
        };
        image.onerror = () => {
          setMediaUploaderTransactionMeta(editor, [
            addErrorPlaceholder({ id: {}, pos })
          ]);
        };
        image.src = src;
      });
    }, 0);
  }
  return tr;
}

// src/core/text/word-count.ts
import { Node as PMNode } from "@tiptap/pm/model";
import { Parser } from "htmlparser2";

// src/core/text/regex.ts
var WORD_REGEX = /[\p{P}\p{L}\p{N}\p{M}-]+|[a-zA-Z0-9_\-–—'’\u0392-\u03c9\u0400-\u04FF\u0027\u00C0-\u017F]+|[\u4E00-\u9FFF\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\uac00-\ud7af\u0400-\u04FF]+|[\u00E4\u00C4\u00E5\u00C5\u00F6\u00D6]+|[\u0531-\u0556\u0561-\u0586\u0559\u055A\u055B]+|w+/gu;
function buildRegex(baseRegex, prefix = "", postfix = "", flags) {
  return new RegExp(
    `${prefix}${baseRegex.source}${postfix}`,
    flags ?? baseRegex.flags
  );
}
function combineRegex(firstRegex, secondRegex, flags) {
  return new RegExp(`((${firstRegex.source})|(${secondRegex.source}))`, flags);
}
var BASE_URL_REGEX = new RegExp(
  // protocol identifier (optional)
  "(?:(?:(?:https?):)?\\/\\/)?(?:\\S+(?::\\S*)?@)?(?:(?!(?:10|127)(?:\\.\\d{1,3}){3})(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z0-9\\u00a1-\\uffff][a-z0-9\\u00a1-\\uffff_-]{0,62})?[a-z0-9\\u00a1-\\uffff]\\.)+(?:[a-z\\u00a1-\\uffff]{2,}\\.?))(?::\\d{2,5})?(?:[/?#]\\S*)?",
  "i"
);
var URL_REGEX = buildRegex(BASE_URL_REGEX, "^", "$", "gui");
var URL_REGEX_EXACT = buildRegex(BASE_URL_REGEX, "^", "$", "ui");
var URL_OR_WORD_REGEX = combineRegex(BASE_URL_REGEX, WORD_REGEX, "gu");
var MARKDOWN_CODEBLOCK_REGEX = new RegExp(
  /```([a-z]*)\n([\s\S]*?)\n```/,
  "ui"
);
var CODEBLOCK_MARKDOWN_INPUT_REGEX = /^```([a-z]+)?[\s\n]$/;
var BLOCK_MATH_REGEX = new RegExp(
  /(?:^|\s)(?:\$\$)(((?!\$).)*)(?:\$\$)/,
  "ui"
);
var INLINE_MATH_REGEX = new RegExp(
  /(?:^|\s)(?!\$\$(?:((?!\$).)+)\$\$)(?:\$(((?!\$).)+)((?!\$\$)\$))/,
  "ui"
);

// src/core/text/url.ts
function isValidURL(url) {
  return URL_REGEX_EXACT.test(url);
}

// src/core/text/tokeniser.ts
function tokeniseUniqueWords(doc, from, to) {
  const words = /* @__PURE__ */ new Set();
  let wordCount = 0;
  const text = textBetween(doc, " ", from, to);
  const matches = wordRegexMatchAll(text);
  for (const match of matches) {
    words.add(match[0]);
    wordCount++;
  }
  return [words, wordCount];
}
function processTextBetween(topNode, from, to, onProcessChunk, ignoreNode = isOneWordNode) {
  let textFrom = from;
  let text = "";
  let startConcat = false;
  topNode.nodesBetween(from, to, (node, pos) => {
    if (node.isText) {
      if (startConcat) textFrom = pos;
      const isLeftover = ignoreNode(node);
      if (isLeftover) {
        onProcessChunk(text, textFrom, node.text ?? null);
        startConcat = true;
        text = "";
      } else {
        text += node.text;
        startConcat = false;
      }
    } else if (node.isBlock) {
      if (ignoreNode(node)) return false;
      onProcessChunk(text, textFrom, null);
      startConcat = true;
      text = "";
    } else if (node.type.name === "hardBreak") {
      text += "\n";
      startConcat = false;
    } else if (node.isInline) {
      const isLeftover = ignoreNode(node);
      if (isLeftover) {
        onProcessChunk(text, textFrom, null);
        startConcat = true;
        text = "";
        return false;
      } else {
        text += node.text;
        startConcat = false;
      }
    }
  });
  if (text !== "") {
    onProcessChunk(text, textFrom, null);
  }
}
function isOneWordNode(node) {
  const isLink = node.marks.some((mark) => mark.type.name === "link");
  if (isLink) {
    return isValidURL(node.text ?? "");
  }
  return false;
}
function wordRegexMatch(text) {
  return text.match(URL_OR_WORD_REGEX);
}
function wordRegexMatchAll(text) {
  return text.matchAll(URL_OR_WORD_REGEX);
}

// src/core/text/word-count.ts
function nodeWordCount(topNode) {
  return textWordCount(textBetween(topNode, " "));
}
function textBetween(node, blockSeparator, from, to) {
  const nodeSize = node instanceof PMNode ? node.content.size : node.size;
  const topBoundary = to ?? nodeSize;
  const bottomBoundary = from ?? 0;
  let text = "", separated = true;
  const leafText = " ";
  node.nodesBetween(
    bottomBoundary,
    topBoundary,
    (node2, pos) => {
      if (node2.attrs.hideText) return false;
      if (node2.isText) {
        text += node2?.text?.slice(
          Math.max(bottomBoundary, pos) - pos,
          topBoundary - pos
        ) ?? "";
        separated = !blockSeparator;
      } else if (node2.isLeaf && leafText) {
        text += leafText;
        separated = !blockSeparator;
      } else if (!separated && node2.isBlock) {
        text += blockSeparator;
        separated = true;
      }
      return true;
    },
    0
  );
  return text;
}
function selectionWordCount(state) {
  const { doc, selection } = state;
  if (selection) {
    const { from, to } = selection;
    const text = textBetween(doc, " ", from, to);
    return textWordCount(text);
  }
  return 0;
}
function textWordCount(text) {
  const match = wordRegexMatch(text.trim());
  return match ? match.length : 0;
}
function getHTMLWordCount(html) {
  let text = "";
  const parser = new Parser(
    {
      ontext(data) {
        text += data + " ";
      }
    },
    { decodeEntities: true }
  );
  parser.write(html);
  parser.end();
  return textWordCount(text.trim());
}

// src/core/text/plain-text.ts
import { getSchema } from "@tiptap/core";
import { Node as Node2 } from "@tiptap/pm/model";
function getText(doc, extensions) {
  const schema = getSchema(extensions);
  const contentNode = Node2.fromJSON(schema, doc);
  return nodeText(contentNode);
}
function nodeText(node) {
  return textBetween(node, "\n");
}

// src/extensions/paste/paste-plugin.ts
import { Fragment as Fragment2, Slice } from "@tiptap/pm/model";
import { Plugin as Plugin2, PluginKey as PluginKey2 } from "@tiptap/pm/state";
import { __serializeForClipboard } from "@tiptap/pm/view";

// src/extensions/paste/global-clipboard.ts
var __CADMUS_EDITOR_CLIPBOARD = null;
function getLastClipboard() {
  return __CADMUS_EDITOR_CLIPBOARD;
}
function saveToClipboard(clipboard) {
  __CADMUS_EDITOR_CLIPBOARD = clipboard;
}

// src/extensions/paste/paste-plugin.ts
var PastePluginKey = new PluginKey2("paste");
function PastePlugin(editor, props) {
  const { editorId } = props;
  const handleCopyEvents = copyEventHandler(editorId);
  return new Plugin2({
    key: PastePluginKey,
    appendTransaction(transactions, _oldState, newState) {
      const pasteTransaction = transactions.find(
        (tr) => tr.getMeta(PastePluginKey)
      );
      if (pasteTransaction) {
        const pasteMeta = pasteTransaction.getMeta(PastePluginKey);
        let newTr;
        if (!pasteMeta.internal) {
          newTr = newTr ?? newState.tr;
          newTr = patchMedia(editor, newTr);
        }
        if (newTr) return newTr;
      }
      return void 0;
    },
    props: {
      handleDrop: (_view, event, slice, moved) => {
        const html = event.dataTransfer?.getData("text/html");
        const text = event.dataTransfer?.getData("text/plain");
        editor.chain().command(({ commands }) => {
          if (!moved) {
            commands.setDropSelection(event);
          }
          return true;
        }).handlePaste(event, moved, slice, html, text).command(({ tr, dispatch }) => {
          dispatch?.(tr.setMeta("uiEvent", "drop"));
          return true;
        }).run();
        if (moved) {
          return false;
        }
        return true;
      },
      // Handle direct paste events
      handlePaste(_view, event, slice) {
        const html = event.clipboardData?.getData("text/html");
        let text = event.clipboardData?.getData("text/plain");
        let pasteSlice = slice;
        const uriList = event.clipboardData?.getData("text/uri-list");
        if (uriList && !text && !html) {
          text = uriList;
          pasteSlice = new Slice(Fragment2.from(editor.schema.text(text)), 1, 1);
        }
        editor.chain().handlePaste(event, false, pasteSlice, html, text).command(({ tr, dispatch }) => {
          dispatch?.(tr.setMeta("uiEvent", "paste"));
          return true;
        }).run();
        return true;
      },
      // Copy events
      handleDOMEvents: {
        dragstart: handleCopyEvents,
        copy: handleCopyEvents,
        cut: handleCopyEvents
      }
    }
  });
}
function copyEventHandler(editorId) {
  return (view, _event) => {
    const { selection } = view.state;
    if (selection.empty) return false;
    const slice = selection.content();
    const { dom, text } = __serializeForClipboard(view, slice);
    if (dom?.innerHTML && saveToClipboard) {
      saveToClipboard({
        origin: editorId,
        content: dom.innerHTML
      });
    } else if (text && saveToClipboard) {
      saveToClipboard({
        origin: editorId,
        content: text
      });
    }
    return false;
  };
}

// src/extensions/blank/blank.ts
import { findChildren as findChildren2, mergeAttributes, Node as Node3 } from "@tiptap/core";
import { Plugin as Plugin5 } from "@tiptap/pm/state";
import { v4 as uuid } from "uuid";

// src/core/helpers/rangeFromTransaction.ts
function rangeFromTransaction(tr) {
  let from, to;
  for (let i = 0; i < tr.steps.length; i++) {
    const step = tr.steps[i];
    const stepMapping = step.getMap();
    const stepFrom = stepMapping.map(step.from || step.pos, -1);
    const stepTo = stepMapping.map(step.to || step.pos, 1);
    if (from) {
      from = Math.min(stepMapping.map(from, -1), stepFrom);
    } else {
      from = stepFrom;
    }
    if (to) {
      to = Math.max(stepMapping.map(to, 1), stepTo);
    } else {
      to = stepTo;
    }
  }
  return {
    from,
    to
  };
}

// src/extensions/paste/paste.ts
import { Extension as Extension2 } from "@tiptap/core";
import { Fragment as Fragment3, Slice as Slice2 } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";

// src/core/text/word-count-changes.ts
import { ChangeSet } from "@tiptap/pm/changeset";
function wordCountChanges(transaction) {
  const { before: oldDoc, doc } = transaction;
  let changeset = ChangeSet.create(oldDoc);
  let deletedWords = 0;
  let addedWords = 0;
  changeset = changeset.addSteps(doc, transaction.mapping.maps, void 0);
  changeset.changes.forEach((change) => {
    const { deleted, fromA, toA, fromB, toB, inserted } = change;
    if (deleted) {
      deletedWords += wordRegexMatch(oldDoc.textBetween(fromA, toA, "\n"))?.length ?? 0;
    }
    if (inserted) {
      addedWords += wordRegexMatch(doc.textBetween(fromB, toB, "\n"))?.length ?? 0;
    }
  });
  return {
    addedWords,
    deletedWords
  };
}

// src/extensions/code-block/plugins/code-block-delete-indicator-plugin.ts
import { Plugin as Plugin3, PluginKey as PluginKey3 } from "@tiptap/pm/state";
import { Decoration as Decoration2, DecorationSet as DecorationSet2 } from "@tiptap/pm/view";

// src/extensions/code-block/utils.ts
import { findParentNode } from "@tiptap/core";

// src/extensions/code-block/types.ts
var LANGUAGE_CONFIG = {
  ["c" /* C */]: { label: "C", mode: "text/x-csrc", markdown: ["c"] },
  ["c++" /* CPP */]: {
    label: "C++",
    mode: "text/x-c++src",
    markdown: ["c++", "cpp", "cplusplus"]
  },
  ["c#" /* CSharp */]: {
    label: "C#",
    mode: "text/x-csharp",
    markdown: ["cs", "csharp"]
  },
  ["java" /* Java */]: {
    label: "Java",
    mode: "text/x-java",
    markdown: ["java"]
  },
  ["scala" /* Scala */]: {
    label: "Scala",
    mode: "text/x-scala",
    markdown: ["scala"]
  },
  ["kotlin" /* Kotlin */]: {
    label: "Kotlin",
    mode: "text/x-kotlin",
    markdown: ["kotlin", "kt"]
  },
  ["objective-c" /* ObjectiveC */]: {
    label: "Objective C",
    mode: "text/x-objectivec",
    markdown: ["objc", "objectivec"]
  },
  ["javascript" /* Javascript */]: {
    label: "JavaScript",
    mode: "javascript",
    markdown: ["coffeescript", "ecmascript", "javascript", "js", "node"]
  },
  ["typescript" /* Typescript */]: {
    label: "TypeScript",
    mode: "text/typescript",
    markdown: ["ts", "typescript"]
  },
  ["json" /* Json */]: {
    label: "JSON",
    mode: "application/json",
    markdown: ["json"]
  },
  ["html" /* HTML */]: {
    label: "HTML",
    mode: "text/html",
    markdown: ["html", "xhtml", "xml"]
  },
  ["css" /* CSS */]: {
    label: "CSS",
    mode: "text/css",
    markdown: ["css", "less", "sass", "scss", "styl", "stylus"]
  },
  ["r" /* R */]: { label: "R", mode: "r", markdown: ["r"] },
  ["python" /* Python */]: {
    label: "Python",
    mode: "python",
    markdown: ["py", "python"]
  },
  ["yaml" /* Yaml */]: {
    label: "YAML",
    mode: "yaml",
    markdown: ["yaml", "yml"]
  },
  ["markdown" /* Markdown */]: {
    label: "Markdown",
    mode: "markdown",
    markdown: ["markdown"]
  },
  ["go" /* Go */]: { label: "Go", mode: "go", markdown: ["go"] },
  ["shell" /* Shell */]: {
    label: "Shell",
    mode: "text/x-sh",
    markdown: ["bash", "sh", "shell", "zsh"]
  },
  ["matlab" /* Matlab */]: {
    label: "Matlab",
    mode: "text/x-octave",
    markdown: ["matlab", "Matlab"]
  },
  ["sql" /* SQL */]: {
    label: "SQL",
    mode: "text/x-sql",
    markdown: ["sql", "SQL"]
  },
  ["elixir" /* Elixir */]: {
    label: "Elixir",
    mode: "elixir",
    markdown: ["elixir", "ex", "exs"]
  },
  ["haskell" /* Haskell */]: {
    label: "Haskell",
    mode: "haskell",
    markdown: ["haskell", "hs"]
  },
  ["plain" /* PlainText */]: {
    label: "Plain Text",
    mode: "plain",
    markdown: [""]
  }
};

// src/extensions/code-block/utils.ts
var getLanguageDisplay = (language) => {
  return LANGUAGE_CONFIG[language]["label"];
};
var getLanguageMode = (language) => {
  return LANGUAGE_CONFIG[language]["mode"];
};
var findCodeBlockSelection = (selection) => findParentNode((node) => node.type.name == "codeBlock")(selection);
var parseMarkdownLanguage = (lang) => {
  for (const [key, value] of Object.entries(LANGUAGE_CONFIG)) {
    if (value.markdown.includes(lang)) return key;
  }
  return "plain" /* PlainText */;
};

// src/extensions/code-block/plugins/code-block-delete-indicator-plugin.ts
var CodeBlockDeleteIndicatorPluginKey = new PluginKey3("CodeBlockDeleteIndicator");
var CodeBlockDeleteIndicatorPlugin = () => new Plugin3({
  key: CodeBlockDeleteIndicatorPluginKey,
  state: {
    init() {
      return {
        highlight: false
      };
    },
    apply(tr, currentState) {
      const payload = tr.getMeta(CodeBlockDeleteIndicatorPluginKey);
      if (payload) return payload;
      return currentState;
    }
  },
  props: {
    decorations(editorState) {
      const deleteIndicatorState = this.getState(editorState);
      if (deleteIndicatorState?.highlight) {
        const codeBlock = findCodeBlockSelection(editorState.selection);
        const decorations = [];
        if (codeBlock) {
          decorations.push(
            Decoration2.node(
              codeBlock.pos,
              codeBlock.pos + codeBlock.node.nodeSize,
              {
                class: "code-block-pre-delete"
              }
            )
          );
        }
        return DecorationSet2.create(editorState.doc, decorations);
      }
      return DecorationSet2.empty;
    }
  }
});

// src/extensions/code-block/plugins/code-block-paste-plugin.ts
import { Plugin as Plugin4, PluginKey as PluginKey4 } from "@tiptap/pm/state";
var CodeBlockPastePluginKey = new PluginKey4("codeBlockPastePlugin");
var CodeBlockPastePlugin = () => {
  return new Plugin4({
    key: CodeBlockPastePluginKey
  });
};

// src/extensions/table/constants.ts
import { colors } from "@vericus/cadmus-ui";
import { opacify, transparentize } from "polished";
var defaultTableColor = transparentize(0.94, colors.blue500);
var tableColors = [
  {
    button: opacify(0.19, colors.grey50),
    actual: colors.grey50
  },
  {
    button: transparentize(0.75, colors.blue500),
    actual: defaultTableColor
  },
  {
    button: transparentize(0.75, colors.green500),
    actual: transparentize(0.94, colors.green500)
  },
  {
    button: transparentize(0.75, colors.yellow500),
    actual: transparentize(0.94, colors.yellow500)
  },
  {
    button: transparentize(0.75, colors.red500),
    actual: transparentize(0.94, colors.red500)
  },
  {
    button: transparentize(0.75, colors.indigo500),
    actual: transparentize(0.94, colors.indigo500)
  }
];
var paddingForTableControl = 44;
var maxWidth = 800;
var cellMinWidth = 15;
var cellDefaultWidth = 50;
var borderSides = ["left", "right", "top", "bottom"];
var borderSideOptions = [
  ...borderSides,
  "insideHorizontal",
  "insideVertical",
  "inside",
  "outside",
  "all"
];

// src/extensions/paste/cleaner/clipboard-cleaner.ts
var ClipboardCleaner = class {
  outHTML;
  doc;
  dom;
  constructor(doc) {
    this.doc = doc;
  }
  getOutput() {
    this.dom = this.doc.getElementsByTagName("body")[0];
    this.iterateNode(this.dom);
    this._patchDocument();
    this.cleanDOM();
    this.outHTML = this.dom.innerHTML;
    return this.outHTML;
  }
  // generic patch document logic
  // to lift/convert element so that it match the expected
  // element to be parsed using ParseDOM
  _patchDocument() {
    this.patchDocument();
  }
  // extra patching that can be implemented based on the cleaner type
  patchDocument() {
    const images = this.dom?.querySelectorAll("img");
    images?.forEach((img) => {
      const parent = img.parentElement;
      if (parent && parent.tagName.toLowerCase() !== "figure") {
        const figure = document.createElement("figure");
        figure.appendChild(img);
        if (parent.nextSibling && parent.parentNode) {
          parent.parentNode.insertBefore(figure, parent.nextSibling);
        } else {
          this.dom?.appendChild(figure);
        }
      }
    });
    const tables = this.dom?.querySelectorAll("table");
    tables?.forEach((table) => {
      const trs = table.querySelectorAll("tr");
      trs?.forEach((tr) => {
        const cols = tr.querySelectorAll("td,th");
        if (cols) {
          const colNum = Array.from(cols).reduce((acc, col) => {
            const colSpan = col.getAttribute("colSpan") ?? "1";
            return acc + parseInt(colSpan, 10);
          }, 0) ?? 1;
          cols.forEach((col) => {
            const colSpan = col.getAttribute("colSpan") ?? "1";
            const colWidth = maxWidth / colNum * parseInt(colSpan, 10);
            col.setAttribute("colwidth", `${colWidth}`);
          });
        }
      });
    });
  }
  iterateNode(node) {
    if (node.tagName === "P" && !node.firstChild) {
      node.parentNode?.removeChild(node);
      return;
    } else if (node.nodeType === Node.COMMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      let childNode = node.firstChild;
      while (childNode) {
        const nextChildNode = childNode.nextSibling;
        this.iterateNode(childNode);
        childNode = nextChildNode;
      }
      node = this.convertNode(node);
    }
  }
  // Remove unused content
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  cleanDOM() {
  }
  // Convert an existing node to a different node, if needed.
  convertNode(node) {
    if (node.tagName === "THREAD") {
      const newNode = document.createElement("span");
      newNode.innerHTML = node.innerHTML;
      node.parentNode?.replaceChild(newNode, node);
      return newNode;
    }
    return node;
  }
};

// src/extensions/paste/cleaner/google-docs-clipboard-cleaner.ts
var GoogleDocsClipboardCleaner = class extends ClipboardCleaner {
  // Convert an existing node to a different node, if needed.
  convertNode(node) {
    super.convertNode(node);
    const element = node;
    if (element.tagName === "B" && element.style.fontWeight === "normal") {
      return this.neutralizeInlineNode(node);
    }
    return node;
  }
  // Replace any type of inline node with a span node.
  neutralizeInlineNode(node) {
    const newNode = document.createElement("span");
    while (node.firstChild) {
      newNode.appendChild(node.firstChild);
    }
    node.parentNode?.replaceChild(newNode, node);
    return newNode;
  }
};

// src/extensions/paste/cleaner/microsoft-word-clipboard-cleaner.ts
var MicrosoftWordClipboardCleaner = class extends ClipboardCleaner {
  // Remove unused content
  cleanDOM() {
    const removableElements = this.dom?.querySelectorAll(
      'div[style*="mso-element:footnote-list"]'
    );
    removableElements?.forEach((el) => el?.parentNode?.removeChild(el));
  }
  // Iterate over pasted nodes and their children
  iterateNode(node) {
    const element = node;
    if (element.tagName === "P" && !element.firstChild) {
      node.parentNode?.removeChild(node);
      return true;
    } else if (node.nodeType === Node.COMMENT_NODE) {
      if (node.textContent === "EndFragment") {
        while (node) {
          const nextSibling = node.nextSibling;
          node.parentNode?.removeChild(node);
          node = nextSibling;
        }
        return false;
      } else {
        node.parentNode?.removeChild(node);
        return true;
      }
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      let childNode = node.firstChild;
      while (childNode) {
        const nextChildNode = childNode.nextSibling;
        if (this.iterateNode(childNode)) {
          childNode = nextChildNode;
        } else {
          childNode = false;
        }
      }
      node = this.convertNode(node);
    }
    return true;
  }
};

// src/extensions/paste/cleaner/parseHTMLString.ts
function parseHTMLString(html) {
  const parser = new DOMParser();
  return parser.parseFromString(html, "text/html");
}

// src/extensions/paste/cleaner/paste-origin.ts
function htmlPasteOrigin(doc) {
  const body = doc.getElementsByTagName("body")[0];
  const html = doc.getElementsByTagName("html")[0];
  const firstB = body.querySelector("b");
  const firstElement = body.firstElementChild;
  if (html.hasAttribute("xmlns:w")) {
    return "Microsoft Word" /* WORD */;
  } else if (firstB?.id?.startsWith("docs-internal-guid")) {
    return "Google Docs" /* DOCS */;
  } else if (firstElement?.hasAttribute("data-pm-slice")) {
    return "prosemirror" /* PROSEMIRROR */;
  } else {
    return "Unknown" /* UNKNOWN */;
  }
}

// src/extensions/paste/cleaner/cleanPastedHTML.ts
function cleanPastedHTML(htmlString) {
  const doc = parseHTMLString(htmlString);
  const origin = htmlPasteOrigin(doc);
  const cleaner = getCleaner(origin, doc);
  const output = cleaner.getOutput();
  return output;
}
function getCleaner(origin, doc) {
  switch (origin) {
    case "Google Docs" /* DOCS */:
      return new GoogleDocsClipboardCleaner(doc);
    case "Microsoft Word" /* WORD */:
      return new MicrosoftWordClipboardCleaner(doc);
    case "Unknown" /* UNKNOWN */:
    default:
      return new ClipboardCleaner(doc);
  }
}

// src/extensions/paste/paste.ts
var Paste = Extension2.create({
  name: "cadmus-paste",
  addOptions() {
    return {
      editorId: "body",
      annotatePaste: true
    };
  },
  addStorage() {
    return {
      editorId: this.options.editorId
    };
  },
  addCommands() {
    return {
      deletePasteChip: (mouseEvent) => ({ commands, view }) => {
        if (!this.editor?.isEditable) return false;
        const eventPos = view.posAtCoords({
          left: mouseEvent.clientX,
          top: mouseEvent.clientY
        });
        if (eventPos) {
          commands.deleteRange({
            from: eventPos.inside,
            to: eventPos.inside + 1
          });
        }
        return true;
      },
      setDropSelection: (mouseEvent) => ({ view, state, tr, dispatch }) => {
        let $mouse;
        const eventPos = view.posAtCoords({
          left: mouseEvent.clientX,
          top: mouseEvent.clientY
        });
        if (eventPos) {
          $mouse = state.doc.resolve(eventPos.pos);
        }
        if ($mouse) {
          tr.setSelection(new TextSelection($mouse));
          dispatch?.(tr);
        }
        return true;
      },
      handlePaste: (event, moved, slice, html, text) => ({ chain }) => {
        return chain().handlePasteMetadata(event, moved, slice, html, text).command(({ commands }) => {
          return commands.first([
            () => commands.handleVSCodePaste(text),
            () => commands.handleMarkdownPaste(text),
            () => commands.handleTablePaste(slice),
            () => commands.handlePasteFallback(moved, slice)
          ]);
        }).command(({ tr, dispatch }) => {
          const replaceSelection = wordCountChanges(tr);
          const pasteMeta = tr.getMeta(PastePluginKey);
          tr.setMeta(PastePluginKey, {
            ...pasteMeta,
            wordsAdded: replaceSelection.addedWords,
            wordsDeleted: replaceSelection.deletedWords
          });
          dispatch?.(tr);
          return true;
        }).addPasteChip().run();
      },
      handlePasteFallback: (moved, slice) => ({ chain }) => {
        if (moved) return true;
        return chain().command(({ tr, commands, dispatch, view }) => {
          const meta = tr.getMeta(PastePluginKey);
          const codeBlockPasteMeta = tr.getMeta(CodeBlockPastePluginKey);
          if (codeBlockPasteMeta?.insideCodeBlock && meta?.text) {
            const newSlice = new Slice2(
              Fragment3.from(
                view.state.schema.text(meta.text.replace(/\r\n?/g, "\n"))
              ),
              0,
              0
            );
            dispatch?.(tr.replaceSelection(newSlice));
            return true;
          }
          if (meta && meta.html) {
            const html = cleanPastedHTML(meta.html);
            const preserveWhitespace = meta.internal || meta.origin === "prosemirror" /* PROSEMIRROR */ ? "full" : false;
            commands.insertContent(html, {
              parseOptions: {
                preserveWhitespace
              }
            });
          } else {
            dispatch?.(tr.replaceSelection(slice));
          }
          return true;
        }).command(({ tr, dispatch }) => {
          dispatch?.(tr.scrollIntoView());
          return true;
        }).run();
      },
      handlePasteMetadata: (event, moved, slice, html, text) => ({ chain }) => {
        const timestamp = this.options.getTime?.() ?? (/* @__PURE__ */ new Date()).toISOString();
        return chain().handleVSCodePasteMetadata(event, moved, slice, html, text).handleMarkdownPasteMetadata(event, moved, slice, html, text).handleCodeBlockPasteMetadata(event, moved, slice, html, text).command(({ tr, dispatch }) => {
          const { annotatePaste } = this.options;
          let internal = moved;
          let pasteType = "allowed" /* allowed */;
          const clipboard = getLastClipboard();
          let wordCount = nodeWordCount(slice.content);
          const codeBlockPasteMeta = tr.getMeta(CodeBlockPastePluginKey);
          const insideCodeBlock = codeBlockPasteMeta?.insideCodeBlock && text;
          if (insideCodeBlock) {
            wordCount = textWordCount(text);
          }
          let origin = "Unknown" /* UNKNOWN */;
          if ((text && !html || !clipboard) && !internal) {
            internal = false;
          }
          if (codeBlockPasteMeta?.source === "vscode") {
            origin = "Visual Studio Code" /* VSCODE */;
          }
          let clipboardText;
          if (clipboard?.content) {
            clipboardText = parseHTMLString(clipboard?.content).body.innerText;
          }
          if (html && !insideCodeBlock) {
            const htmlEl = parseHTMLString(html);
            const htmlBody = htmlEl.getElementsByTagName("body")[0];
            if (clipboard) {
              const clipboardEL = parseHTMLString(clipboard.content);
              const clipboardBody = clipboardEL.getElementsByTagName("body")[0];
              if (htmlBody.innerHTML.replace(/<!--.*?-->/gi, "").trim() === clipboardBody.innerHTML.replace(/<!--.*?-->/gi, "").trim()) {
                internal = true;
                origin = clipboard.origin;
              } else {
                origin = htmlPasteOrigin(htmlEl);
              }
            } else {
              origin = htmlPasteOrigin(htmlEl);
            }
          } else if (text && clipboard?.content === text) {
            internal = true;
            origin = clipboard.origin;
          } else if (text && clipboardText && clipboardText === text && clipboard?.origin) {
            internal = true;
            origin = clipboard.origin;
          }
          if (internal || !annotatePaste) {
            pasteType = "allowed" /* allowed */;
          } else if (wordCount >= 30) {
            pasteType = "highlighted" /* highlighted */;
          } else if (wordCount >= 90) {
            pasteType = "locked" /* locked */;
          }
          const pasteMeta = {
            origin,
            pasteType,
            wordCount,
            internal,
            html,
            text,
            timestamp
          };
          tr.setMeta(PastePluginKey, pasteMeta);
          tr.setMeta("paste", true);
          dispatch?.(tr);
          return true;
        }).run();
      },
      addPasteChip: () => ({ commands }) => {
        const { annotatePaste } = this.options;
        if (!annotatePaste) return true;
        return commands.first([
          () => commands.addTablePasteChip(),
          () => commands.addCodeBlockPasteChip(),
          // fallback
          () => commands.command(({ tr, state, dispatch }) => {
            const pasteMeta = tr.getMeta(PastePluginKey);
            const { pasteType, wordCount } = pasteMeta;
            if (pasteType !== "allowed" /* allowed */) {
              const { selection } = state;
              const { $to } = selection;
              const pasteNode = state.schema.nodes.pasteChip.create({
                pasted: wordCount
              });
              if ($to.parent?.isTextblock) {
                tr.insert($to.pos, pasteNode);
              } else {
                const paragraph = state.schema.nodes.paragraph;
                const paragraphNode = paragraph.create(void 0, [
                  pasteNode
                ]);
                tr.insert($to.after($to.depth), paragraphNode);
              }
            }
            dispatch?.(tr);
            return true;
          })
        ]);
      },
      addPasteChipAfterPredicate: (selectionPredicate) => ({ tr, dispatch, state }) => {
        const pasteMeta = tr.getMeta(PastePluginKey);
        if (pasteMeta.pasteType === "allowed" /* allowed */) return false;
        const contentNodePos = selectionPredicate(state.selection);
        if (!contentNodePos) return false;
        const { pos, node } = contentNodePos;
        const $pos = state.doc.resolve(pos + node.nodeSize);
        const pasteChip = state.schema.nodes.pasteChip;
        const pasteNode = pasteChip.create({ pasted: pasteMeta.wordCount });
        tr.insert($pos.start($pos.depth + 1), pasteNode);
        dispatch?.(tr);
        return true;
      }
    };
  },
  addProseMirrorPlugins() {
    return [PastePlugin(this.editor, this.options)];
  }
});

// src/extensions/blank/blank.ts
var Blank = Node3.create({
  name: "blanks",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "inline",
  inline: true,
  draggable: false,
  atom: true,
  allowGapCursor: true,
  addAttributes() {
    return {
      //   Adds attributes for handling blanks.
      blanks: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute("data-cadmus-blank") ?? "0") || 0,
        renderHTML: (attributes) => {
          return { "data-cadmus-blank": attributes.blanks || 0 };
        }
      },
      id: {
        default: null,
        // Store ID
        parseHTML: (element) => element.getAttribute("id"),
        renderHTML: (attributes) => attributes.id ? { id: attributes.id } : {}
      }
    };
  },
  parseHTML() {
    return [{ tag: "span[data-cadmus-blank]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      "____________"
    ];
  },
  addProseMirrorPlugins() {
    return [
      // This plugin is responsible for replacing duplicate blank IDs when
      // content is pasted into the editor. This ensures that each blank element
      // has a unique identifier.
      new Plugin5({
        appendTransaction: (transactions, oldState, newState) => {
          const pasteTransaction = transactions.find(
            (tr2) => tr2.getMeta(PastePluginKey) && tr2.getMeta("uiEvent") == "paste"
          );
          if (!pasteTransaction) return;
          const tr = newState.tr;
          const existingBlankIds = listExistingBlanks(oldState);
          const { from = 0, to = 0 } = rangeFromTransaction(pasteTransaction);
          pasteTransaction.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "blanks") {
              let newId = node.attrs.id;
              if (existingBlankIds.includes(newId)) {
                newId = uuid();
              }
              tr.setNodeMarkup(pos, void 0, {
                ...node.attrs,
                id: newId
              });
            }
          });
          return tr;
        }
      })
    ];
  },
  addCommands() {
    return {
      insertBlank: () => ({ commands, editor }) => {
        if (!editor.isEditable) return false;
        return commands.insertContent({
          type: "blanks",
          attrs: { id: uuid() }
        });
      },
      deleteBlank: (id) => ({ tr, commands, editor }) => {
        if (!editor.isEditable) return false;
        const item = findChildren2(tr.doc, (node) => {
          {
            return node.type.name === "blanks" && id === node.attrs.id;
          }
        })?.[0];
        if (!item) {
          return true;
        }
        return commands.deleteRange({
          from: item.pos,
          to: item.pos + item.node.nodeSize
        });
      }
    };
  }
});
function listExistingBlanks(editorState) {
  const blanks = findChildren2(
    editorState.doc,
    (node) => node.type.name === "blanks"
  );
  return blanks.map((blank) => blank.node.attrs.id) || [];
}

// src/editor-typography.ts
var fontFamilySans = `acumin-pro, -apple-system, BlinkMacSystemFont,
Helvetica, Arial, "Noto Sans", "Noto Sans CJK", "Noto Sans JP", "Noto Sans KR",
"Noto Sans HK", "Noto Sans SC", "Noto Sans TC", "Twemoji Mozilla",
"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji",
"EmojiOne Color", "Android Emoji", sans-serif`;
var editorTypography = {
  displayOne: {
    fontFamily: "utopia-std-display",
    fontWeight: 600,
    fontSize: "3.8125rem",
    // 61px
    paddingTop: "9px",
    paddingBottom: "9px",
    lineHeight: "1.18",
    // 72px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h1"
  },
  displayTwo: {
    fontFamily: "utopia-std-display",
    fontWeight: 600,
    fontSize: "3.05rem",
    // 48.8px
    paddingTop: "9px",
    paddingBottom: "9px",
    lineHeight: "1.29",
    // 63px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h1"
  },
  displayThree: {
    fontFamily: "utopia-std-display",
    fontWeight: 400,
    fontSize: "2.25rem",
    // 36px
    paddingTop: "9px",
    paddingBottom: "9px",
    lineHeight: "1.33",
    // 48px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h1"
  },
  headingOne: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "2.4375rem",
    // 39px
    paddingTop: "9px",
    paddingBottom: "9px",
    lineHeight: "1.384",
    // 54px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h1"
  },
  headingTwo: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "1.953rem",
    // 31.25px
    paddingTop: "6px",
    paddingBottom: "6px",
    lineHeight: "1.44",
    // 45px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h2"
  },
  headingThree: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "1.5625rem",
    // 25px
    paddingTop: "6px",
    paddingBottom: "6px",
    lineHeight: "1.44",
    // 36px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h3"
  },
  headingFour: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "1.25rem",
    // 20px
    paddingTop: "6px",
    paddingBottom: "6px",
    lineHeight: "1.35",
    // 27px
    letterSpacing: "0px",
    textTransform: "none",
    element: "h4"
  },
  headingFive: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "1.0625rem",
    // 17px
    paddingTop: "6px",
    paddingBottom: "6px",
    lineHeight: "1.412",
    // 24px
    letterSpacing: "0.1px",
    textTransform: "none",
    element: "h5"
  },
  headingSix: {
    fontFamily: "acumin-pro",
    fontWeight: 600,
    fontSize: "0.875rem",
    // 14px
    paddingTop: "3px",
    paddingBottom: "3px",
    lineHeight: "1.5",
    // 21px
    letterSpacing: "0.2px",
    textTransform: "none",
    element: "h6"
  },
  blockquote: {
    fontFamily: "adobe-text-pro, times, serif",
    fontWeight: 400,
    fontSize: "1rem",
    // 16px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 24px
    letterSpacing: "0.2px",
    textTransform: "none",
    element: "p"
  },
  lead: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "1.25rem",
    //  20px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.35",
    // 27px
    letterSpacing: "0.4px",
    textTransform: "none",
    element: "p"
  },
  // previously "body"
  bodyLg: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "1.125rem",
    // 18px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 27px
    letterSpacing: "0.2px",
    textTransform: "none",
    element: "p"
  },
  // previously "paragraph"
  bodyMd: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "1rem",
    //  16px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 24px
    letterSpacing: "0.2px",
    textTransform: "none",
    element: "p"
  },
  // previously "system"
  bodySm: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "14px",
    // 0.875rem
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 21px
    letterSpacing: "0.2px",
    textTransform: "none",
    element: "p"
  },
  bodyLine: {
    fontFamily: "Open Sauce Two",
    fontWeight: 400,
    fontSize: "1rem",
    //  16px
    lineHeight: "150%",
    letterSpacing: "0px",
    paddingBottom: "0px",
    paddingTop: "0px",
    textTransform: "none",
    element: "span"
  },
  systemMd: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "0.8125rem",
    // 13px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 19.5px
    letterSpacing: "0.3px",
    textTransform: "none",
    element: "p"
  },
  // previously "subtle"
  systemSm: {
    fontFamily: "acumin-pro",
    fontWeight: 400,
    fontSize: "0.75rem",
    // 12px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 18px
    letterSpacing: "0.4px",
    textTransform: "none",
    element: "p"
  },
  label: {
    fontFamily: "acumin-pro-semi-condensed",
    fontWeight: 600,
    fontSize: "0.75rem",
    // 12px
    paddingTop: "0px",
    paddingBottom: "0px",
    lineHeight: "1.5",
    // 18px
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    element: "p"
  },
  ctaLg: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "1rem",
    // 16px
    lineHeight: "125%",
    letterSpacing: "0.8px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "uppercase",
    element: "span"
  },
  ctaMd: {
    fontFamily: "Open Sauce Two",
    fontWeight: 700,
    fontSize: "0.75rem",
    // 12px
    lineHeight: "150%",
    letterSpacing: "0.6px",
    paddingTop: "0px",
    paddingBottom: "0px",
    textTransform: "uppercase",
    element: "span"
  }
};

// src/extensions/history/history.ts
import { Extension as Extension3 } from "@tiptap/core";
import { history, redo, undo } from "@tiptap/pm/history";
import { PluginKey as PluginKey5 } from "@tiptap/pm/state";
var HistoryPluginKey = new PluginKey5("history");
var History = Extension3.create({
  name: "history",
  addOptions() {
    return {
      depth: 100,
      newGroupDelay: 500
    };
  },
  addCommands() {
    return {
      undo: () => ({ state, dispatch }) => {
        const timestamp = this.options.getTime?.() ?? (/* @__PURE__ */ new Date()).toISOString();
        return undo(state, (tr) => {
          const { addedWords, deletedWords } = wordCountChanges(tr);
          const meta = {
            type: "undo",
            wordsAdded: addedWords,
            wordsDeleted: deletedWords,
            timestamp
          };
          tr.setMeta(HistoryPluginKey, meta);
          dispatch?.(tr);
        });
      },
      redo: () => ({ state, dispatch }) => {
        const timestamp = this.options.getTime?.() ?? (/* @__PURE__ */ new Date()).toISOString();
        return redo(state, (tr) => {
          const { addedWords, deletedWords } = wordCountChanges(tr);
          const meta = {
            type: "redo",
            wordsAdded: addedWords,
            wordsDeleted: deletedWords,
            timestamp
          };
          tr.setMeta(HistoryPluginKey, meta);
          dispatch?.(tr);
        });
      }
    };
  },
  addProseMirrorPlugins() {
    return [history(this.options)];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-z": () => this.editor.commands.undo(),
      "Mod-y": () => this.editor.commands.redo(),
      "Shift-Mod-z": () => this.editor.commands.redo()
    };
  }
});

// src/extensions/collab/sendable-word-count-change.ts
function sendableWordCountChange(sendable) {
  const sendableMeta = {
    pastes: [],
    histories: []
  };
  if (!sendable || !sendable.origins) return sendableMeta;
  return [...new Set(sendable.origins)].reduce((acc, origin) => {
    const pasteMeta = origin.getMeta(PastePluginKey);
    if (pasteMeta) {
      acc.pastes.push({
        origin: pasteMeta.origin,
        count: pasteMeta.wordCount ?? 0,
        added: pasteMeta.wordsAdded ?? 0,
        deleted: pasteMeta.wordsDeleted ?? 0,
        internal: pasteMeta.internal,
        timestamp: pasteMeta.timestamp
      });
    }
    const historyMeta = origin.getMeta(HistoryPluginKey);
    if (historyMeta) {
      acc.histories.push({
        added: historyMeta.wordsAdded ?? 0,
        deleted: historyMeta.wordsDeleted ?? 0,
        timestamp: historyMeta.timestamp,
        type: historyMeta.type
      });
    }
    return acc;
  }, sendableMeta);
}

// src/extensions/collab/collab.ts
import { Extension as Extension4 } from "@tiptap/core";
import { collab, receiveTransaction } from "@tiptap/pm/collab";

// src/extensions/collab/caret-plugin.ts
import { getAvatarColourset } from "@vericus/cadmus-ui";
import { Plugin as Plugin6, PluginKey as PluginKey6 } from "@tiptap/pm/state";
import { Decoration as Decoration3, DecorationSet as DecorationSet3 } from "@tiptap/pm/view";
var caretPluginKey = new PluginKey6("collabCaret");
function caretPlugin(myClientId, presence) {
  return new Plugin6({
    key: caretPluginKey,
    state: {
      init: (_state, _editorState) => DecorationSet3.empty,
      apply: (tr, decoSet, _oldEditorState, newEditorState) => {
        const meta = tr.getMeta(caretPluginKey);
        if (meta?.updateCarets) {
          const carets = meta.updateCarets;
          const decorations = createDecorationWidgets(
            newEditorState,
            myClientId,
            carets,
            presence
          );
          return DecorationSet3.create(newEditorState.doc, decorations);
        }
        if (tr.docChanged) {
          return decoSet.map(tr.mapping, tr.doc);
        }
        return decoSet;
      }
    },
    props: {
      decorations: (state) => {
        return caretPluginKey.getState(state);
      }
    }
  });
}
function createDecorationWidgets(state, myClientId, carets, presence) {
  const decorations = [];
  const clients = presence.clients();
  if (clients.length === 0 || carets.length === 0) {
    return decorations;
  }
  for (const client of clients) {
    if (client.clientId === myClientId) {
      continue;
    }
    const caret = carets.find((caret2) => caret2.clientId === client.clientId);
    if (caret && caret.head >= 0 && caret.head <= state.doc.content.size) {
      const pos = state.doc.resolve(caret.head);
      if (pos.parent.isTextblock) {
        decorations.push(
          Decoration3.widget(caret.head, () => caretBuilder(client), {
            side: 10,
            key: caret.clientId
          })
        );
      }
    }
  }
  return decorations;
}
function caretBuilder(user) {
  const colourset = getAvatarColourset(user.colourset);
  const caret = document.createElement("span");
  caret.classList.add("ProseMirror-collab-caret");
  caret.setAttribute("style", `--caret-color: ${colourset.caret};`);
  const userDiv = document.createElement("div");
  userDiv.insertBefore(document.createTextNode(user.name.slice(0, 2)), null);
  const nonbreakingSpace1 = document.createTextNode("\u2060");
  const nonbreakingSpace2 = document.createTextNode("\u2060");
  caret.insertBefore(nonbreakingSpace1, null);
  caret.insertBefore(userDiv, null);
  caret.insertBefore(nonbreakingSpace2, null);
  return caret;
}

// src/extensions/collab/collab.ts
var Collab = Extension4.create({
  name: "Collab",
  addCommands() {
    return {
      receiveSteps: (steps, clientIds, onComplete) => ({ state }) => {
        const tr = receiveTransaction(state, steps, clientIds, {
          mapSelectionBackward: true
        });
        const newState = state.apply(tr);
        onComplete?.(newState);
        return true;
      },
      updateCarets: (carets) => ({ tr }) => {
        const meta = { updateCarets: carets };
        tr.setMeta(caretPluginKey, meta);
        return true;
      }
    };
  },
  addProseMirrorPlugins() {
    const clientID = this.options.clientId || Math.floor(Math.random() * 4294967295).toString();
    const plugins = [
      collab({
        version: this.options.version,
        clientID
      })
    ];
    if (this.options.presence) {
      plugins.push(caretPlugin(clientID, this.options.presence));
    }
    return plugins;
  }
});

// src/extensions/footnote/utils.ts
import { findChildren as findChildren3 } from "@tiptap/core";

// src/extensions/footnote/footnote-active-status-plugin.ts
import { NodeSelection, Plugin as Plugin7, PluginKey as PluginKey7 } from "@tiptap/pm/state";
import { Decoration as Decoration4, DecorationSet as DecorationSet4 } from "@tiptap/pm/view";
var FootnoteActiveStatusPluginKey = new PluginKey7(
  "FootnoteActiveStatus"
);
function FootnoteActiveStatusPlugin(props) {
  return new Plugin7({
    key: FootnoteActiveStatusPluginKey,
    state: {
      init() {
        return {
          activeFootnoteId: null
        };
      },
      apply(tr, value) {
        const focusedFtntId = tr.getMeta("activeFootnoteId");
        if (focusedFtntId !== void 0) {
          return {
            activeFootnoteId: focusedFtntId
          };
        }
        return value;
      }
    },
    /**
     * Adds a decoration to the active footnote node in the editor
     */
    props: {
      decorations(editorState) {
        if (!props.editor.isEditable) return void 0;
        const footnoteId = this.getState(editorState)?.activeFootnoteId;
        const footnoteNode = footnoteId ? findFootnoteNode(editorState.doc, footnoteId) : void 0;
        if (!footnoteNode) return DecorationSet4.empty;
        const { pos, node } = footnoteNode;
        const decoration = Decoration4.node(
          pos,
          pos + node.nodeSize,
          {
            class: "footnote-active"
          }
        );
        return DecorationSet4.create(editorState.doc, [decoration]);
      },
      /**
       * Handles click on the footnote superscript in the main editor
       */
      handleClickOn(view, _pos, node) {
        if (!props.editor.isEditable) return false;
        if (node.type.name === "footnote") {
          const footnoteId = node.attrs.id;
          if (footnoteId !== this.getState(view.state)?.activeFootnoteId) {
            view.dispatch(
              view.state.tr.setMeta("activeFootnoteId", footnoteId)
            );
          }
          return true;
        }
        return false;
      },
      /**
       * If the footnote node is double clicked, bring focus to the main editor,
       * and select the footnote node (so it can be copied, deleted, etc.)
       */
      handleDoubleClickOn(view, _pos, node, nodePos) {
        if (!props.editor.isEditable) return false;
        if (node.type.name === "footnote") {
          const selection = new NodeSelection(view.state.doc.resolve(nodePos));
          if (selection) {
            view.focus();
            view.dispatch(view.state.tr.setSelection(selection));
          }
          return true;
        }
        return false;
      }
    }
  });
}

// src/extensions/footnote/utils.ts
function listFootnotes(topNode) {
  const footnotes = findChildren3(
    topNode,
    (node) => node.type.name === "footnote"
  );
  const footnoteList = footnotes.map((footnote) => ({
    id: footnote.node.attrs.id,
    content: footnote.node.attrs.content,
    wordCount: getHTMLWordCount(footnote.node.attrs.content)
  }));
  return footnoteList;
}
function findFootnoteNode(node, footnoteId) {
  if (!footnoteId) return void 0;
  return findChildren3(
    node,
    (node2) => node2.type.name === "footnote" && footnoteId === node2.attrs.id
  )?.[0];
}
function currentActiveFootnoteId(state) {
  return FootnoteActiveStatusPluginKey.getState(state)?.activeFootnoteId ?? null;
}

// src/extensions/footnote/footnote-list-plugin.ts
import { Plugin as Plugin8, PluginKey as PluginKey8 } from "@tiptap/pm/state";
var FootnoteListPluginKey = new PluginKey8(
  "FootnoteList"
);
function FootnoteListPlugin() {
  return new Plugin8({
    key: FootnoteListPluginKey,
    state: {
      init(_, state) {
        return listFootnotes(state.doc);
      },
      apply(tr, currentList, _oldEditorState, newEditorState) {
        if (!tr.docChanged) return currentList;
        return listFootnotes(newEditorState.doc);
      }
    }
  });
}

// src/extensions/footnote/use-footnote-list.ts
import { useEditorState } from "@tiptap/react";
function useFootnoteList(editor) {
  return useEditorState({
    editor,
    selector: (ctx) => ({
      footnotes: FootnoteListPluginKey.getState(ctx.editor.state),
      activeFootnoteId: currentActiveFootnoteId(ctx.editor.state)
    })
  });
}

// src/extensions/footnote/footnote.ts
import { mergeAttributes as mergeAttributes3, Node as Node5 } from "@tiptap/core";
import { Plugin as Plugin9, TextSelection as TextSelection2 } from "@tiptap/pm/state";
import { v4 as uuid2 } from "uuid";

// src/extensions/table/utils/controlDimension.ts
import { TableMap } from "@tiptap/pm/tables";
var getRowHeights = (tableRef) => {
  const heights = [];
  if (tableRef.lastChild?.lastChild) {
    const rows = tableRef.lastChild?.lastChild?.childNodes;
    if (!rows) return heights;
    for (let i = 0, count = rows.length; i < count; i++) {
      const row = rows[i];
      if (i === 0 || i === rows.length - 1) {
        heights[i] = row.getBoundingClientRect().height + 1;
      } else {
        heights[i] = row.getBoundingClientRect().height;
      }
    }
  }
  return heights;
};
var getColWidths = (tableRef, tableNode) => {
  const widths = [];
  if (tableNode && tableRef.lastChild?.lastChild?.childNodes) {
    const map = TableMap.get(tableNode);
    const rows = tableRef.lastChild.lastChild.childNodes;
    if (!rows) return widths;
    for (let i = 0; i < map.width; i++) {
      const cells = map.cellsInRect({
        left: i,
        right: i + 1,
        top: 0,
        bottom: map.height
      });
      const cell = cells.find((cellPos) => {
        const cellRect = map.findCell(cellPos);
        if (cellRect) {
          return cellRect.right - cellRect.left === 1;
        }
      });
      if (cell) {
        const rowIndex = map.findCell(cell)?.top;
        const row = rows[rowIndex];
        if (!row) continue;
        const cells2 = map.cellsInRect({
          left: 0,
          right: map.width,
          top: rowIndex,
          bottom: rowIndex + 1
        });
        const colIndex = cells2.findIndex((cellPos) => cellPos === cell);
        const columns = row.childNodes;
        const column = columns[colIndex];
        if (!column) continue;
        if (i === 0) {
          widths[i] = column.getBoundingClientRect().width + 1;
        } else {
          widths[i] = column.getBoundingClientRect().width;
        }
      }
    }
  }
  return widths;
};

// src/extensions/table/utils/createCell.ts
function createCell(cellType, cellAttrs, cellContent) {
  if (cellContent) {
    return cellType.createChecked(cellAttrs, cellContent);
  }
  return cellType.createAndFill(cellAttrs);
}

// src/extensions/table/utils/getTableNodeTypes.ts
function getTableNodeTypes(schema) {
  if (schema.cached.tableNodeTypes) {
    return schema.cached.tableNodeTypes;
  }
  const roles = {};
  Object.keys(schema.nodes).forEach((type) => {
    const nodeType = schema.nodes[type];
    if (nodeType.spec.tableRole) {
      roles[nodeType.spec.tableRole] = nodeType;
    }
  });
  schema.cached.tableNodeTypes = roles;
  return roles;
}

// src/extensions/table/utils/createTable.ts
function createTable(schema, rowsCount, colsCount, withHeaderRow, cellContent) {
  const types = getTableNodeTypes(schema);
  const headerCells = [];
  const cells = [];
  const tableGroups = [];
  for (let index = 0; index < colsCount; index += 1) {
    const cell = createCell(
      types.cell,
      { colwidth: [710 / colsCount] },
      cellContent
    );
    if (cell) {
      cells.push(cell);
    }
    if (withHeaderRow) {
      const headerCell = createCell(
        types.header_cell,
        { colwidth: [710 / colsCount] },
        cellContent
      );
      if (headerCell) {
        headerCells.push(headerCell);
      }
    }
  }
  const rows = [];
  for (let index = 0; index < rowsCount; index += 1) {
    rows.push(
      types.row.createChecked(
        null,
        withHeaderRow && index === 0 ? headerCells : cells
      )
    );
  }
  const table = types.table.createChecked(null, rows);
  const caption = types.table_caption.createAndFill();
  const tableFootnote = types.table_footnote.createAndFill({
    hideText: true
  });
  if (caption) {
    tableGroups.push(caption);
  }
  if (table) {
    tableGroups.push(table);
  }
  if (tableFootnote) {
    tableGroups.push(tableFootnote);
  }
  return types.table_group.createChecked(null, tableGroups);
}

// src/extensions/table/utils/find.ts
import { findParentNode as findParentNode2, findParentNodeClosestToPos } from "@tiptap/core";
import { TableMap as TableMap2 } from "@tiptap/pm/tables";
var findTable = (selection) => findParentNode2(
  (node) => node.type.spec.tableRole && node.type.spec.tableRole === "table"
)(selection);
var findTableGroup = (selection) => findParentNode2(
  (node) => node.type.spec.tableRole && node.type.spec.tableRole === "table_group"
)(selection);
var findTableCaption = (selection) => findParentNode2(
  (node) => node.type.spec.tableRole && node.type.spec.tableRole === "table_caption"
)(selection);
var findTableFootnote = (selection) => findParentNode2(
  (node) => node.type.spec.tableRole && node.type.spec.tableRole === "table_footnote"
)(selection);
var findCellClosestToPos = ($pos) => {
  const predicate = (node) => node.type.spec.tableRole && /cell/i.test(node.type.spec.tableRole);
  return findParentNodeClosestToPos($pos, predicate);
};

// src/extensions/table/utils/getCells.ts
import { findParentNode as findParentNode3 } from "@tiptap/core";
import { TableMap as TableMap3 } from "@tiptap/pm/tables";

// src/extensions/table/utils/isSelectionType.ts
function isSelectionType(selection, type) {
  if (!selection) {
    return false;
  }
  const serialized = selection.toJSON();
  return serialized.type === type;
}

// src/extensions/table/utils/getCells.ts
var getContentNodePos = (table, nodePos) => {
  const node = table.node.nodeAt(nodePos);
  const pos = nodePos + table.start;
  return { pos, start: pos + 1, node, depth: table.depth + 2 };
};
var getCellsRect = (map, selection) => {
  if (!isSelectionType(selection, "cell")) {
    return;
  }
  const start = selection.$anchorCell.start(-1);
  return map.rectBetween(
    selection.$anchorCell.pos - start,
    selection.$headCell.pos - start
  );
};
var getCellsMap = (schema, selection) => {
  const table = findTable(selection);
  if (!table) return;
  const map = TableMap3.get(table.node);
  if (!isSelectionType(selection, "cell")) {
    const tableNodeTypes = getTableNodeTypes(schema);
    const cellType = tableNodeTypes.cell;
    const headerCellType = tableNodeTypes.header_cell;
    const cell = findParentNode3(
      (node) => node.type === cellType || node.type === headerCellType
    )(selection);
    if (!cell) return;
    return {
      width: 1,
      height: 1,
      map,
      cells: [
        {
          pos: cell.pos,
          start: cell.pos + 1,
          node: cell.node,
          depth: table.depth + 2
        }
      ]
    };
  }
  const rect = getCellsRect(map, selection);
  if (!rect) return;
  const cells = map.cellsInRect(rect);
  return {
    width: rect.right - rect.left,
    height: rect.bottom - rect.top,
    rect,
    map,
    cells: cells.map((nodePos) => getContentNodePos(table, nodePos))
  };
};

// src/extensions/table/utils/headers.ts
import { TableMap as TableMap4 } from "@tiptap/pm/tables";
function hasHeaderRow(state) {
  const table = findTable(state.selection);
  if (!table) return false;
  const map = TableMap4.get(table.node);
  for (let i = 0; i < map.width; i++) {
    const cell = table.node.nodeAt(map.map[i]);
    if (cell && cell.type !== state.schema.nodes.tableHeader) {
      return false;
    }
  }
  return true;
}
function hasHeaderColumn(state) {
  const table = findTable(state.selection);
  if (!table) return false;
  const map = TableMap4.get(table.node);
  const cellPositions = map.cellsInRect({
    left: 0,
    top: 0,
    right: 1,
    bottom: map.height
  });
  for (let i = 0; i < cellPositions.length; i++) {
    try {
      const cell = table.node.nodeAt(cellPositions[i]);
      if (cell && cell.type !== state.schema.nodes.tableHeader) {
        return false;
      }
    } catch (_e) {
      return false;
    }
  }
  return true;
}

// src/extensions/table/utils/isSelected.ts
import { findChildren as findChildren4 } from "@tiptap/core";
import { TableMap as TableMap5 } from "@tiptap/pm/tables";
var isRectSelected = (rect) => (selection) => {
  if (!isSelectionType(selection, "cell")) {
    return false;
  }
  const map = TableMap5.get(selection.$anchorCell.node(-1));
  const start = selection.$anchorCell.start(-1);
  const cells = map.cellsInRect(rect);
  const selectedCells = map.cellsInRect(
    map.rectBetween(
      selection.$anchorCell.pos - start,
      selection.$headCell.pos - start
    )
  );
  for (let i = 0, count = cells.length; i < count; i++) {
    if (selectedCells.indexOf(cells[i]) === -1) {
      return false;
    }
  }
  return true;
};
var isColumnSelected = (columnIndex) => (selection) => {
  if (isSelectionType(selection, "cell")) {
    const map = TableMap5.get(selection.$anchorCell.node(-1));
    return isRectSelected({
      left: columnIndex,
      right: columnIndex + 1,
      top: 0,
      bottom: map.height
    })(selection);
  }
  return false;
};
var isRowSelected = (rowIndex) => (selection) => {
  if (isSelectionType(selection, "cell")) {
    const map = TableMap5.get(selection.$anchorCell.node(-1));
    return isRectSelected({
      left: 0,
      right: map.width,
      top: rowIndex,
      bottom: rowIndex + 1
    })(selection);
  }
  return false;
};
var isTableSelected = (selection) => {
  if (isSelectionType(selection, "cell")) {
    const map = TableMap5.get(selection.$anchorCell.node(-1));
    return isRectSelected({
      left: 0,
      right: map.width,
      top: 0,
      bottom: map.height
    })(selection);
  }
  return false;
};
var isSelectionIncludeTable = (state) => {
  if (isTableSelected(state.selection)) return true;
  const tableGroup = findTableGroup(state.selection);
  if (!tableGroup?.node) return false;
  const tableType = getTableNodeTypes(state.schema).table;
  const table = findChildren4(
    tableGroup.node,
    (node) => node.type === tableType
  )[0];
  if (!table?.node) return false;
  const { from, to } = state.selection;
  const tableStart = tableGroup.start + table.pos;
  const tableEnd = tableStart + table.node.nodeSize;
  return from <= tableStart && to >= tableEnd;
};
var isSelectionInsideTableGroup = (selection) => {
  if (isSelectionType(selection, "cell")) {
    return true;
  } else if (isSelectionType(selection, "text") || isSelectionType(selection, "node")) {
    const tableGroup = findTableGroup(selection);
    return !!tableGroup;
  }
  return false;
};
var isSelectionInsideTable = (selection) => {
  if (isSelectionType(selection, "cell")) {
    return true;
  } else if (isSelectionType(selection, "text") || isSelectionType(selection, "node")) {
    const table = findTable(selection);
    return !!table;
  }
  return false;
};

// src/extensions/table/utils/selectNodes.ts
import { CellSelection, TableMap as TableMap6 } from "@tiptap/pm/tables";
var select = (type) => (index, expand) => (tr) => {
  const table = findTable(tr.selection);
  const isRowSelection = type === "row";
  if (table) {
    const map = TableMap6.get(table.node);
    if (index >= 0 && index < (isRowSelection ? map.height : map.width)) {
      let left = isRowSelection ? 0 : index;
      let top = isRowSelection ? index : 0;
      let right = isRowSelection ? map.width : index + 1;
      let bottom = isRowSelection ? index + 1 : map.height;
      if (expand) {
        const cell = findCellClosestToPos(tr.selection.$from);
        if (!cell) {
          return tr;
        }
        const selRect = map.findCell(cell.pos - table.start);
        if (isRowSelection) {
          top = Math.min(top, selRect.top);
          bottom = Math.max(bottom, selRect.bottom);
        } else {
          left = Math.min(left, selRect.left);
          right = Math.max(right, selRect.right);
        }
      }
      const cellsInFirstRow = map.cellsInRect({
        left,
        top,
        right: isRowSelection ? right : left + 1,
        bottom: isRowSelection ? top + 1 : bottom
      });
      const cellsInLastRow = bottom - top === 1 ? cellsInFirstRow : map.cellsInRect({
        left: isRowSelection ? left : right - 1,
        top: isRowSelection ? bottom - 1 : top,
        right,
        bottom
      });
      const head = table.start + cellsInFirstRow[0];
      const anchor = table.start + cellsInLastRow[cellsInLastRow.length - 1];
      const $head = tr.doc.resolve(head);
      const $anchor = tr.doc.resolve(anchor);
      return tr.setSelection(new CellSelection($anchor, $head));
    }
  }
  return tr;
};
var selectColumn = select("column");
var selectRow = select("row");
var selectTable = (tr) => {
  const table = findTable(tr.selection);
  if (table) {
    const { map } = TableMap6.get(table.node);
    if (map && map.length) {
      const head = table.start + map[0];
      const anchor = table.start + map[map.length - 1];
      const $head = tr.doc.resolve(head);
      const $anchor = tr.doc.resolve(anchor);
      return tr.setSelection(new CellSelection($anchor, $head));
    }
  }
  return tr;
};

// src/extensions/table/utils/setCellAttrs.ts
import { isInTable, selectedRect } from "@tiptap/pm/tables";

// src/extensions/table/utils/tableBorders.ts
var haveTableBorderStyle = (state, side) => {
  const table = findTable(state.selection);
  const cellsMap = getCellsMap(state.schema, state.selection);
  if (table && cellsMap) {
    const { cells, rect, height, map } = cellsMap;
    if (side === "top") {
      return cells.every((cell) => {
        if (!isTopCell(cell, table, map, rect)) return true;
        return cellHasBorder(cell, table, map, "top");
      });
    } else if (side === "bottom") {
      return cells.every((cell) => {
        if (!isBottomCell(cell, table, map, rect)) return true;
        return cellHasBorder(cell, table, map, "bottom");
      });
    } else if (side === "insideHorizontal") {
      if (height <= 1) return false;
      return cells.every((cell) => {
        if (!isBottomCell(cell, table, map, rect)) {
          return cellHasBorder(cell, table, map, "bottom");
        }
        return cellHasBorder(cell, table, map, "top");
      });
    } else if (side === "all") {
      return cells.every(
        (cell) => cellHasBorder(cell, table, map, "bottom") && cellHasBorder(cell, table, map, "top") && cellHasBorder(cell, table, map, "right") && cellHasBorder(cell, table, map, "left")
      );
    } else {
      return false;
    }
  }
  return false;
};
var isTopCell = (cell, table, map, rect) => {
  return !rect || rect.top === map.findCell(cell.pos - table.start).top;
};
var isBottomCell = (cell, table, map, rect) => {
  return !rect || rect.bottom === map.findCell(cell.pos - table.start).bottom;
};
var cellHasBorder = (cell, table, map, side) => {
  let neighbourCellPos;
  if (side === "left") {
    neighbourCellPos = map.nextCell(cell.pos - table.start, "horiz", -1);
    if (neighbourCellPos !== null) {
      const neighbourCell = table.node.nodeAt(neighbourCellPos);
      return cell.node.attrs.border.left || neighbourCell.attrs.border.right;
    }
    return cell.node.attrs.border.left;
  } else if (side === "top") {
    neighbourCellPos = map.nextCell(cell.pos - table.start, "vert", -1);
    if (neighbourCellPos !== null) {
      const neighbourCell = table.node.nodeAt(neighbourCellPos);
      return cell.node.attrs.border.top || neighbourCell.attrs.border.bottom;
    }
    return cell.node.attrs.border.top;
  } else if (side === "right") {
    neighbourCellPos = map.nextCell(cell.pos - table.start, "horiz", 1);
    if (neighbourCellPos !== null) {
      const neighbourCell = table.node.nodeAt(neighbourCellPos);
      return cell.node.attrs.border.right || neighbourCell.attrs.border.left;
    }
    return cell.node.attrs.border.right;
  } else {
    neighbourCellPos = map.nextCell(cell.pos - table.start, "vert", 1);
    if (neighbourCellPos !== null) {
      const neighbourCell = table.node.nodeAt(neighbourCellPos);
      return cell.node.attrs.border.bottom || neighbourCell.attrs.border.top;
    }
    return cell.node.attrs.border.bottom;
  }
};

// src/extensions/table/utils/setCellAttrs.ts
var setCellAttrs = (cell, attrs) => (tr) => {
  if (cell) {
    tr.setNodeMarkup(
      cell.pos,
      void 0,
      Object.assign({}, cell.node.attrs, attrs)
    );
  }
  return tr;
};
var setNeighbourCellsBorder = (map, cellPos, side, border) => (tr) => {
  const table = findTable(tr.selection);
  if (!table) return tr;
  let newTr = tr;
  let neighbourCellPos;
  const relativePos = cellPos - table.start;
  if (side === "left") {
    neighbourCellPos = map.nextCell(relativePos, "horiz", -1);
  } else if (side === "right") {
    neighbourCellPos = map.nextCell(relativePos, "horiz", 1);
  } else if (side === "bottom") {
    neighbourCellPos = map.nextCell(relativePos, "vert", 1);
  } else {
    neighbourCellPos = map.nextCell(relativePos, "vert", -1);
  }
  if (neighbourCellPos !== null) {
    const node = table.node.nodeAt(neighbourCellPos);
    const pos = table.start + neighbourCellPos;
    let newBorder;
    if (side === "left") {
      newBorder = {
        right: border
      };
    } else if (side === "right") {
      newBorder = {
        left: border
      };
    } else if (side === "bottom") {
      newBorder = {
        top: border
      };
    } else {
      newBorder = {
        bottom: border
      };
    }
    const newAttrs = {
      ...node.attrs,
      border: {
        ...node.attrs.border,
        ...newBorder
      }
    };
    newTr = setCellAttrs({ node, pos }, newAttrs)(newTr);
  }
  return newTr;
};
var setCellBorder = (cellsMap, index, side, border) => (tr) => {
  const table = findTable(tr.selection);
  if (!table) return tr;
  let newTr = tr;
  const { cells, map, rect } = cellsMap;
  const cell = cells[index];
  const { node, pos } = cell;
  let newBorder;
  if (side === "all") {
    newBorder = {
      left: border,
      right: border,
      top: border,
      bottom: border
    };
  } else if (side === "top") {
    if (isTopCell(cell, table, map, rect)) {
      newBorder = {
        top: border
      };
    }
  } else if (side === "bottom") {
    if (isBottomCell(cell, table, map, rect)) {
      newBorder = {
        bottom: border
      };
    }
  } else if (side === "insideHorizontal" && rect) {
    if (isBottomCell(cell, table, map, rect)) {
      newBorder = {
        top: border
      };
    } else {
      newBorder = {
        bottom: border
      };
    }
  }
  if (newBorder) {
    const newAttrs = {
      ...node.attrs,
      border: {
        ...node.attrs.border,
        ...newBorder
      }
    };
    newTr = setCellAttrs({ node, pos }, newAttrs)(newTr);
    if (side === "all") {
      newTr = setNeighbourCellsBorder(map, pos, "top", border)(newTr);
      newTr = setNeighbourCellsBorder(map, pos, "bottom", border)(newTr);
      newTr = setNeighbourCellsBorder(map, pos, "left", border)(newTr);
      newTr = setNeighbourCellsBorder(map, pos, "right", border)(newTr);
    } else if (side === "bottom") {
      newTr = setNeighbourCellsBorder(map, pos, "bottom", border)(newTr);
    } else if (side === "top") {
      newTr = setNeighbourCellsBorder(map, pos, "top", border)(newTr);
    }
  }
  return newTr;
};
var setCellsBackground = (color) => (state, dispatch) => {
  if (!isInTable(state)) return false;
  let tr = state.tr;
  const rect = selectedRect(state);
  if (!rect) return false;
  const { map } = rect;
  for (let row = rect.top; row < rect.bottom; row++) {
    for (let col = rect.left; col < rect.right; col++) {
      const cellPos = map.map[row * map.width + col];
      const cell = rect.table.nodeAt(cellPos);
      if (cell) {
        tr = setCellAttrs(
          { pos: cellPos + rect.tableStart, node: cell },
          { backgroundColor: color }
        )(tr);
      }
    }
  }
  dispatch?.(tr);
  return true;
};

// src/extensions/table/utils/tableStyles.ts
import { selectedRect as selectedRect2 } from "@tiptap/pm/tables";
var hasAlternateColor = (state) => {
  const table = findTable(state.selection);
  if (table) {
    return !!table.node.attrs.alternateBackground;
  }
  return false;
};
var tableColor = (state) => {
  const table = findTable(state.selection);
  if (table) {
    return table.node.attrs.backgroundColor;
  }
  return void 0;
};
var cellsColor = (state) => {
  const table = findTable(state.selection);
  let color;
  if (table) {
    const rect = selectedRect2(state);
    if (!rect) return color;
    const { map, table: table2 } = rect;
    const cellsPos = map.cellsInRect(rect);
    cellsPos.some((cellPos, index) => {
      const cell = table2.nodeAt(cellPos);
      if (cell) {
        if (!color && index === 0) {
          color = cell.attrs.backgroundColor;
        }
        if (color !== cell.attrs.backgroundColor && index !== 0) {
          color = void 0;
          return true;
        }
      }
    });
  }
  return color;
};

// src/extensions/task-list/task-list.ts
import { findParentNode as findParentNode4, getNodeType, mergeAttributes as mergeAttributes2, Node as Node4 } from "@tiptap/core";
var TaskList = Node4.create({
  name: "taskList",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block list",
  content: "taskItem+",
  parseHTML() {
    return [
      {
        tag: 'ul[data-type="taskList"]',
        priority: 51
      },
      // parse checklist from dropbox paper
      // this needs to be higher in priority compared to unordered list parser
      // as they're using the same `<ul>` tag
      {
        tag: "ul",
        priority: 51,
        getAttrs: (node) => {
          if (typeof node === "string") return false;
          const dom = node;
          const classNames = dom.className.split(" ");
          const isCheckList = classNames.some(
            (className) => /(checkList|list-task)/i.test(className)
          );
          if (!isCheckList) return false;
          return {};
        }
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "ul",
      mergeAttributes2(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "taskList"
      }),
      0
    ];
  },
  addCommands() {
    return {
      toggleTaskList: () => ({ commands }) => {
        return commands.toggleCadmusList(
          this.type,
          getNodeType("taskItem", this.editor.schema)
        );
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-9": () => this.editor.commands.toggleTaskList()
    };
  }
});
var isSelectionInsideTaskList = (selection) => {
  if (isSelectionType(selection, "text")) {
    const list = findTaskList(selection);
    return !!list;
  }
  return false;
};
var findTaskList = (selection) => findParentNode4((node) => node.type.name === "taskList")(selection);

// src/extensions/footnote/footnote.ts
var Footnote = Node5.create({
  name: "footnote",
  group: "inline",
  content: "inline*",
  inline: true,
  atom: true,
  selectable: true,
  gapCursor: true,
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-footnote-id"),
        renderHTML: (attributes) => attributes.id ? { "data-footnote-id": attributes.id } : {}
      },
      content: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-footnote-content") || "",
        renderHTML: (attributes) => ({
          "data-footnote-content": attributes.content
        })
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "footnote",
        priority: 51,
        context: "table//|taskList//",
        ignore: true,
        skip: true
      },
      {
        tag: "footnote",
        priority: 51
      }
    ];
  },
  renderHTML(attrs) {
    return [
      "footnote",
      mergeAttributes3(this.options.HTMLAttributes, attrs.HTMLAttributes),
      ""
    ];
  },
  addCommands() {
    return {
      insertFootnote: () => ({ tr, editor }) => {
        if (isSelectionInsideTable(tr.selection) || isSelectionInsideTableGroup(tr.selection) || isSelectionInsideTaskList(tr.selection)) {
          return false;
        }
        const sel = tr.selection;
        const newId = uuid2();
        const pasteNode = editor.schema.nodes.footnote.create({
          id: newId
        });
        tr.insert(sel.to, pasteNode).setMeta("activeFootnoteId", newId);
        return true;
      },
      deleteFootnote: (id) => ({ tr, commands }) => {
        const item = findFootnoteNode(tr.doc, id);
        if (!item) {
          return false;
        }
        return commands.deleteRange({
          from: item.pos,
          to: item.pos + item.node.nodeSize
        });
      },
      updateFootnote: (id, content) => ({ tr }) => {
        const item = findFootnoteNode(tr.doc, id);
        if (item) {
          tr.setNodeMarkup(item.pos, void 0, {
            ...item.node.attrs,
            content
          });
          return true;
        }
        return false;
      },
      focusFootnote: (id) => ({ tr }) => {
        const item = findFootnoteNode(tr.doc, id);
        if (item) {
          const newSelection = TextSelection2.create(
            tr.doc,
            item.pos + item.node.nodeSize
          );
          tr.setSelection(newSelection);
        }
        tr.setMeta("activeFootnoteId", item ? id : null);
        return true;
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      "Alt-Mod-f": ({ editor }) => editor.commands.insertFootnote()
    };
  },
  addProseMirrorPlugins() {
    return [
      // To ensure that each footnote has a unique ID
      new Plugin9({
        appendTransaction: (transactions, oldState, newState) => {
          const pasteTransaction = transactions.find(
            (tr2) => tr2.getMeta(PastePluginKey) && tr2.getMeta("uiEvent") == "paste"
          );
          if (!pasteTransaction) return;
          const tr = newState.tr;
          const existingFootnotes = listFootnotes(oldState.doc);
          const existingFootnoteIds = existingFootnotes.map(
            (footnote) => footnote.id
          );
          const { from = 0, to = 0 } = rangeFromTransaction(pasteTransaction);
          pasteTransaction.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === "footnote") {
              let newId = node.attrs.id;
              if (existingFootnoteIds.includes(newId)) {
                newId = uuid2();
              }
              tr.setNodeMarkup(pos, void 0, {
                ...node.attrs,
                id: newId
              });
            }
          });
          return tr;
        }
      }),
      // To track active status of the footnotes
      FootnoteActiveStatusPlugin({ editor: this.editor }),
      FootnoteListPlugin()
    ];
  }
});

// src/extensions/footnote/footnote-static.ts
import { mergeAttributes as mergeAttributes4, Node as Node6 } from "@tiptap/core";

// src/core/utilities/htmlToProsemirror/domParserConverter.ts
function namedNodeMapToRecord(attributes) {
  const output = {};
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    output[attribute.name] = attribute.value;
  }
  return output;
}
var convert = (nodes) => {
  const output = [];
  if (nodes.length === 0) {
    return output;
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    node.normalize();
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      output.push(node.textContent);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const attributes = node.attributes;
      output.push([
        node.nodeName.toLowerCase(),
        namedNodeMapToRecord(attributes),
        ...convert(node.childNodes)
      ]);
    }
  }
  return output;
};
var domParserConverter = (html) => {
  const domParser = new DOMParser();
  return convert(
    domParser.parseFromString(html, "text/html").querySelectorAll("body")[0].childNodes
  );
};

// src/core/utilities/htmlToProsemirror/htmlparserConverter.ts
import { ElementType } from "domelementtype";
import { getChildren, textContent } from "domutils";
import { parseDocument } from "htmlparser2";
var convert2 = (nodes) => {
  const output = [];
  if (nodes.length === 0) {
    return output;
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.type === ElementType.Text) {
      output.push(textContent(node));
    } else if (node.type === ElementType.Tag) {
      const attributes = node.attribs;
      output.push([
        node.name.toLowerCase(),
        attributes,
        ...convert2(getChildren(node))
      ]);
    }
  }
  return output;
};
var htmlparserConverter = (html) => convert2(getChildren(parseDocument(html)));

// src/core/utilities/htmlToProsemirror/htmlToProsemirror.ts
var defaultSpec = ["p", {}, ""];
function htmlToDOMOutputSpec(html, parser = domParserConverter) {
  if (html == null || html === "") {
    return [defaultSpec];
  }
  return parser(html);
}

// src/extensions/footnote/footnote-static.ts
var FootnoteStatic = Node6.create({
  name: "footnote",
  group: "inline",
  content: "inline*",
  inline: true,
  atom: true,
  selectable: true,
  gapCursor: true,
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-footnote-id"),
        renderHTML: (attributes) => attributes.id ? { "data-footnote-id": attributes.id } : {}
      },
      content: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-footnote-content") || "",
        renderHTML: (attributes) => ({
          "data-footnote-content": attributes.content
        })
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "footnote",
        priority: 51
      }
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    const { id, content } = node.attrs;
    const cleanedContent = removeParagraphTag(content);
    const insertedContent = cleanedContent === "" ? [""] : htmlToDOMOutputSpec(cleanedContent).map((item) => [
      "span",
      mergeAttributes4({ class: "footnote-content" }),
      item
    ]);
    return [
      "footnote",
      mergeAttributes4(
        this.options.HTMLAttributes,
        HTMLAttributes,
        id ? { id: `footnote-${id}` } : {}
      ),
      ...insertedContent
    ];
  }
});
function removeParagraphTag(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const p = doc.querySelector("p");
  return p ? p.innerHTML : html;
}

// src/extensions/suggestion/helpers.ts
function disableSpellCheck(editor) {
  return editor.commands.setSpellCheckSource(null);
}
function enableCadmusSpellCheck(editor) {
  return editor.commands.setSpellCheckSource("cadmus");
}
function canSpellCheckWithCadmus(editor) {
  return !!editor.view.someProp("attributes", (attributes) => {
    const attrs = typeof attributes === "object" ? attributes : attributes(editor.state);
    return attrs["data-cadmus-spellcheck"] === "true";
  });
}
function spellCheckAttrs(source) {
  let browser = "false";
  let cadmus = "false";
  if (source === "cadmus") {
    cadmus = "true";
    browser = "false";
  }
  if (source === "browser") {
    cadmus = "false";
    browser = "true";
  }
  return {
    spellcheck: browser,
    "data-cadmus-spellcheck": cadmus
  };
}
function getNodeBoundaries(doc, from, to) {
  const docSize = doc.content.size;
  const nodeBeforeFrom = doc.childBefore(from && docSize > from ? from : 0);
  const nodeBeforeTo = doc.childBefore(to && docSize > to ? to : docSize);
  const start = nodeBeforeFrom.offset;
  const end = nodeBeforeTo.node?.nodeSize ? nodeBeforeTo.offset + nodeBeforeTo.node?.nodeSize : docSize;
  return { start, end };
}

// src/extensions/suggestion/suggestion.ts
import { Extension as Extension5 } from "@tiptap/core";

// src/extensions/suggestion/suggestion-decoration-plugin.ts
import { Plugin as Plugin10, PluginKey as PluginKey9 } from "@tiptap/pm/state";
import { Decoration as Decoration5, DecorationSet as DecorationSet5 } from "@tiptap/pm/view";
var SuggestionDecorationPluginKey = new PluginKey9(
  "suggestionDecoration"
);
var SuggestionDecorationPlugin = (props) => new Plugin10({
  key: SuggestionDecorationPluginKey,
  state: {
    init() {
      return DecorationSet5.empty;
    },
    apply(tr, decoSet) {
      const payload = tr.getMeta(SuggestionDecorationPluginKey);
      switch (payload?.type) {
        case "ignoreWord":
          return handleIgnoreWordAction(decoSet, payload.word);
        case "refreshSuggestions":
          return handleRefreshSuggestionsAction(tr, decoSet, props.client);
        default:
          return processUpdatedNodes(tr, decoSet, props.client);
      }
    }
  },
  props: {
    decorations(state) {
      return canSpellCheckWithCadmus(props.editor) ? this.getState(state) : void 0;
    }
  }
});
function SuggestionDecoration(from, to, phrase, candidates) {
  return Decoration5.inline(
    from,
    to,
    {
      class: "suggestion",
      ["aria-label"]: "Press Option + m to open the suggestion menu",
      id: "suggestion-menu-button",
      ["aria-controls"]: "suggestion-menu",
      ["aria-haspopup"]: "true"
    },
    {
      phrase,
      candidates
    }
  );
}
function processUpdatedNodes(tr, decoSet, client) {
  if (tr.docChanged) {
    decoSet = decoSet.map(tr.mapping, tr.doc);
    const { to, from = 0 } = rangeFromTransaction(tr);
    const { start, end } = getNodeBoundaries(tr.doc, from, to);
    const outdatedDecos = decoSet.find(start, end);
    decoSet = decoSet.remove(outdatedDecos);
    const newDecos = getSuggestionDecorations(
      tr.doc,
      start,
      end,
      decoSet,
      client
    );
    return decoSet.add(tr.doc, newDecos);
  }
  return decoSet;
}
function handleIgnoreWordAction(decoSet, word) {
  const invalidDeco = decoSet.find(
    void 0,
    void 0,
    (spec) => spec.phrase === word
  );
  return decoSet.remove(invalidDeco);
}
function handleRefreshSuggestionsAction(tr, decoSet, client) {
  const newDecos = getSuggestionDecorations(
    tr.doc,
    0,
    tr.doc.content.size,
    decoSet,
    client
  );
  return decoSet.add(tr.doc, newDecos);
}
function getSuggestionDecorations(doc, from, to, existing, client) {
  const decorations = [];
  processTextBetween(
    doc,
    from,
    to,
    (text, textFrom) => {
      applySuggestionDecorations(text, textFrom, existing, client, decorations);
    },
    ignoreNodeForSuggestions
  );
  return decorations;
}
function applySuggestionDecorations(text, start, exiting, client, collection) {
  for (const match of wordRegexMatchAll(text ?? "")) {
    const matchText = match[0];
    const matchIndex = match.index;
    const matchLength = matchText.length;
    if (matchText && matchIndex !== void 0) {
      const from = start + matchIndex;
      const to = start + matchIndex + matchLength;
      const candidates = client.lookup(matchText);
      if (candidates?.length) {
        if (exiting.find(from, to, ({ phrase }) => phrase === matchText).length === 0) {
          collection.push(
            SuggestionDecoration(from, to, matchText, candidates)
          );
        }
      }
    }
  }
}
var MARKS_TO_IGNORE_FOR_SUGGESTION = [
  "link",
  "superscript",
  "subscript",
  "code"
];
var NODES_TO_IGNORE_FOR_SUGGESTION = [
  "inlineMath",
  "pasteChip",
  "blockMath",
  "codeBlock"
];
function ignoreNodeForSuggestions(node) {
  return node.marks.some(
    (mark) => MARKS_TO_IGNORE_FOR_SUGGESTION.includes(mark.type.name)
  ) || NODES_TO_IGNORE_FOR_SUGGESTION.includes(node.type.name);
}

// src/extensions/suggestion/suggestion-menu-plugin.ts
import { Plugin as Plugin11, PluginKey as PluginKey10 } from "@tiptap/pm/state";
var SuggestionMenuPluginKey = new PluginKey10("suggestionMenu");
var SuggestionMenuPlugin = (props) => {
  return new Plugin11({
    key: SuggestionMenuPluginKey,
    state: {
      init() {
        return null;
      },
      apply(tr, state) {
        const payload = tr.getMeta(SuggestionMenuPluginKey);
        switch (payload?.type) {
          case "triggerMenu":
            return payload.target;
          case "dismissMenu":
            return null;
          default:
            return state;
        }
      }
    },
    props: {
      // All non contextmenu clicks should be considered as dismissal of any
      // existing menu
      handleClick(view, _, event) {
        const state = this.getState(view.state);
        if (event.button !== 2) {
          if (state) {
            view.dispatch(
              view.state.tr.setMeta(this, {
                type: "dismissMenu"
              })
            );
          }
        }
        return false;
      },
      handleDOMEvents: {
        // Right-click context menu on a decorated word should render the
        // suggestion menu.
        contextmenu(view, event) {
          if (!canSpellCheckWithCadmus(props.editor)) return true;
          const pos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY
          });
          if (!pos) {
            return true;
          }
          const from = pos.pos;
          const decoSet = SuggestionDecorationPluginKey.getState(view.state);
          const deco = decoSet?.find(from, from).pop();
          if (deco) {
            const spec = deco.spec;
            if (textBetween(
              view.state.doc,
              "\n",
              deco.from,
              deco.to
            ).toLowerCase() === spec.phrase.toLowerCase()) {
              view.dispatch(
                view.state.tr.setMeta(this, {
                  type: "triggerMenu",
                  target: {
                    ...spec,
                    from: deco.from,
                    to: deco.to
                  }
                })
              );
              event.preventDefault();
              event.stopPropagation();
              return true;
            }
          }
          view.dispatch(
            view.state.tr.setMeta(this, {
              type: "dismissMenu"
            })
          );
          return true;
        }
      }
    }
  });
};

// src/extensions/suggestion/suggestion.ts
var Suggestion = Extension5.create({
  name: "suggestion",
  onBeforeCreate() {
    this.editor.setOptions({
      editorProps: {
        // keep the props that were set previously
        ...this.editor.options.editorProps,
        attributes: {
          ...this.editor.options.editorProps.attributes,
          ...spellCheckAttrs(this.options.spellCheckSource)
        }
      }
    });
  },
  /**
   * Upon creation of the editor instance, initialised word count &
   * suggestion without debouncing it.
   */
  onCreate() {
    const onNewSuggestions = () => this.editor.commands.refreshSuggestions();
    this.options.client.on("newSuggestions", onNewSuggestions);
    this.editor.on("destroy", () => {
      this.options.client.off("newSuggestions", onNewSuggestions);
    });
  },
  addKeyboardShortcuts() {
    return {
      "Alt-m": () => {
        if (!canSpellCheckWithCadmus(this.editor)) return false;
        const pos = this.editor.state.selection.anchor;
        const decoSet = SuggestionDecorationPluginKey.getState(
          this.editor.state
        );
        const deco = decoSet?.find(pos, pos).pop();
        if (deco) {
          const spec = deco.spec;
          if (textBetween(
            this.editor.state.doc,
            "\n",
            deco.from,
            deco.to
          ).toLowerCase() === spec.phrase.toLowerCase()) {
            this.editor.view.dispatch(
              this.editor.view.state.tr.setMeta(SuggestionMenuPluginKey, {
                type: "triggerMenu",
                target: {
                  ...spec,
                  from: deco.from,
                  to: deco.to
                }
              })
            );
            return true;
          }
        }
        this.editor.view.dispatch(
          this.editor.view.state.tr.setMeta(SuggestionMenuPluginKey, {
            type: "dismissMenu"
          })
        );
        return true;
      }
    };
  },
  addCommands() {
    return {
      // Ignore a word from current and future spelling suggestions
      ignoreSuggestion: (word) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(SuggestionDecorationPluginKey, {
            type: "ignoreWord",
            word
          });
          tr.setMeta(SuggestionMenuPluginKey, {
            type: "dismissMenu"
          });
          this.options.client.ignoreWord(word);
        }
        return true;
      },
      // Refresh suggestion decorations on the full document
      refreshSuggestions: () => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(SuggestionDecorationPluginKey, {
            type: "refreshSuggestions"
          });
        }
        return true;
      },
      // Accept a spelling suggestion replacing the given range with the
      // candidate.
      acceptSuggestion: (from, to, candidate) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.insertText(candidate, from, to);
          tr.setMeta(SuggestionMenuPluginKey, {
            type: "dismissMenu"
          });
        }
        return true;
      },
      setSpellCheckSource: (source) => ({ view }) => {
        const attributes = {
          ...view.props.attributes,
          ...spellCheckAttrs(source)
        };
        view.setProps({ attributes });
        return true;
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      SuggestionDecorationPlugin({
        client: this.options.client,
        editor: this.editor
      }),
      SuggestionMenuPlugin({ editor: this.editor })
    ];
  }
});

// src/extensions/suggestion/suggestion-menu-component.tsx
import { useCallback as useCallback2, useState as useState2 } from "react";
import { styled } from "styled-components";
import { Colored, DropdownMenu } from "@vericus/cadmus-ui";
import { posToDOMRect as posToDOMRect2 } from "@tiptap/core";
import { useEditorState as useEditorState2 } from "@tiptap/react";

// src/extensions/floating-menu/floating-menu-component.tsx
import { useCallback, useLayoutEffect } from "react";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  hide,
  offset,
  shift,
  useClick,
  useFloating,
  useFloatingNodeId,
  useFocus,
  useInteractions,
  useRole
} from "@floating-ui/react";
import { isNodeSelection, posToDOMRect } from "@tiptap/core";

// src/extensions/floating-menu/useFloatingMenuOpenState.ts
import { useEffect, useRef, useState } from "react";

// src/extensions/floating-menu/floating-menu-plugin.ts
import { Plugin as Plugin12, PluginKey as PluginKey11 } from "@tiptap/pm/state";
var FloatingMenuOpenStatePlugin = (options) => new Plugin12({
  key: typeof options.pluginKey === "string" ? new PluginKey11(options.pluginKey) : options.pluginKey,
  view: () => ({
    update: (view, oldState) => {
      const { isAsync, shouldShow, editor, setOpen } = options;
      const { state, composing } = view;
      const { doc, selection } = state;
      const isSame = oldState && oldState.doc.eq(doc) && oldState.selection.eq(selection);
      if (!isAsync && (composing || isSame)) {
        return;
      }
      const nextShow = getOpenState(editor, oldState, shouldShow);
      setOpen(nextShow);
    },
    destroy: () => {
      options.setOpen(false);
    }
  })
});
var getOpenState = (editor, oldState, shouldShow) => {
  const { view, state } = editor;
  const { selection } = state;
  const { ranges } = selection;
  const from = Math.min(...ranges.map((range) => range.$from.pos));
  const to = Math.max(...ranges.map((range) => range.$to.pos));
  return shouldShow({
    editor,
    view,
    state,
    oldState,
    from,
    to
  });
};

// src/extensions/floating-menu/useFloatingMenuOpenState.ts
function useFloatingMenuOpenState(props) {
  const { pluginKey, editor, shouldShow, isAsync, onOpenChange, onClose } = props;
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  useEffect(() => {
    if (!editor.isDestroyed) {
      editor.registerPlugin(
        FloatingMenuOpenStatePlugin({
          editor,
          pluginKey,
          setOpen,
          shouldShow,
          isAsync
        })
      );
      const nextShow = getOpenState(editor, editor.state, shouldShow);
      setTimeout(() => setOpen(nextShow), 0);
    }
    return () => {
      editor.unregisterPlugin(pluginKey);
    };
  }, [editor, pluginKey, shouldShow, isAsync, setOpen]);
  useEffect(() => {
    if (openRef.current !== open && !open) {
      onClose?.(editor);
    }
    if (openRef.current !== open) {
      onOpenChange?.(open);
    }
    openRef.current = open;
  }, [open, onOpenChange, onClose, editor]);
  return [open, setOpen];
}

// src/extensions/floating-menu/floating-menu-component.tsx
import { jsx } from "react/jsx-runtime";
function FloatingMenuComponent(props) {
  const {
    editor,
    getBoundingClientRect,
    portalId,
    mainAxisOffset = 8,
    placement = "bottom",
    modal = false,
    ...rest
  } = props;
  const [open, setOpen] = useFloatingMenuOpenState(props);
  const nodeId = useFloatingNodeId();
  const { x, y, refs, strategy, context } = useFloating({
    whileElementsMounted: (referenceEl, floatingEl, update) => autoUpdate(referenceEl, floatingEl, update, { animationFrame: true }),
    open,
    onOpenChange: setOpen,
    nodeId,
    // @ts-ignore
    // for some unknown reason the type got mixed up here but it is working
    middleware: [offset(mainAxisOffset), shift(), flip(), hide()],
    placement,
    ...rest
  });
  const { getFloatingProps } = useInteractions([
    useFocus(context),
    useRole(context, { role: "menu" }),
    useClick(context)
  ]);
  const defaultGetBoundingClientRect = useCallback(() => {
    const { state, view } = editor;
    const { selection } = state;
    const { ranges } = selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));
    if (isNodeSelection(selection)) {
      const node = view.nodeDOM(from);
      if (node) {
        return node.getBoundingClientRect();
      }
    }
    return posToDOMRect(view, from, to);
  }, [editor]);
  useLayoutEffect(() => {
    refs.setReference({
      getBoundingClientRect: getBoundingClientRect ?? defaultGetBoundingClientRect
    });
  }, [refs, editor, getBoundingClientRect, defaultGetBoundingClientRect]);
  return /* @__PURE__ */ jsx(FloatingTree, { children: /* @__PURE__ */ jsx(FloatingNode, { id: nodeId, children: open && /* @__PURE__ */ jsx(FloatingPortal, { id: portalId, children: /* @__PURE__ */ jsx(FloatingFocusManager, { context, disabled: !modal, children: /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        zIndex: 1e3
      },
      ref: refs.setFloating,
      ...getFloatingProps(),
      children: props.children
    }
  ) }) }) }) });
}

// src/extensions/floating-menu/floating-menu-extension.ts
import { Extension as Extension6 } from "@tiptap/core";
var FloatingMenuExtension = Extension6.create({
  name: "floatingMenu",
  addCommands() {
    return {
      dismissMenu: () => ({ chain, editor }) => {
        const enabledExtensions = editor.extensionManager.extensions.map(
          ({ name }) => name
        );
        return chain().command(({ commands }) => {
          if (enabledExtensions.includes("math")) {
            return commands.setEquationMenuTarget();
          }
          return true;
        }).command(({ commands }) => {
          if (enabledExtensions.includes("link")) {
            return commands.setHyperlinkMenuTarget();
          }
          return true;
        }).run();
      }
    };
  }
});

// src/extensions/suggestion/suggestion-menu-component.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function SuggestionMenuComponent(props) {
  const { editor, portalId } = props;
  const [menuOpened, setMenuOpened] = useState2(false);
  const shouldShow = useCallback2(({ state }) => {
    const suggestion = SuggestionMenuPluginKey.getState(state);
    return !!suggestion;
  }, []);
  const getBoundingClientRect = useCallback2(() => {
    const { state, view } = editor;
    const suggestion = SuggestionMenuPluginKey.getState(state);
    const { selection } = state;
    const { ranges } = selection;
    let from = Math.min(...ranges.map((range) => range.$from.pos));
    let to = Math.max(...ranges.map((range) => range.$to.pos));
    if (suggestion) {
      from = suggestion.from;
      to = suggestion.to;
    }
    return posToDOMRect2(view, from, to);
  }, [editor]);
  return /* @__PURE__ */ jsx2(
    FloatingMenuComponent,
    {
      pluginKey: "suggestionFloatingMenu",
      editor,
      shouldShow,
      getBoundingClientRect,
      portalId,
      isAsync: true,
      onOpenChange: (open) => setMenuOpened(open),
      children: /* @__PURE__ */ jsx2(SuggestionContextMenu, { editor: props.editor, open: menuOpened })
    }
  );
}
var SuggestionContextMenu = ({
  editor,
  open
}) => {
  const { suggestion } = useEditorState2({
    editor,
    selector: (ctx) => ({
      suggestion: SuggestionMenuPluginKey.getState(ctx.editor.state)
    })
  });
  const onSelectedCandidate = (candidate) => {
    if (suggestion) {
      const { from, to } = suggestion;
      editor.chain().acceptSuggestion(from, to, candidate).focus().run();
    }
  };
  const onIgnore = () => {
    if (suggestion) {
      editor.chain().ignoreSuggestion(suggestion.phrase).focus().run();
    }
  };
  return /* @__PURE__ */ jsxs(DropdownMenu.Root, { open, modal: false, children: [
    /* @__PURE__ */ jsx2(StyledTrigger, { disabled: true }),
    /* @__PURE__ */ jsxs(
      DropdownMenu.Content,
      {
        id: "suggestion-menu",
        "aria-labelledby": "suggestion-menu-button",
        align: "center",
        sideOffset: -20,
        onEscapeKeyDown: () => {
          editor.view.dispatch(
            editor.state.tr.setMeta(SuggestionMenuPluginKey, {
              type: "dismissMenu"
            })
          );
          editor.commands.focus();
        },
        children: [
          /* @__PURE__ */ jsx2(DropdownMenu.Label, { children: "Suggestions" }),
          suggestion?.candidates?.map((candidate) => /* @__PURE__ */ jsx2(
            DropdownMenu.Item,
            {
              onClick: () => onSelectedCandidate(candidate),
              children: candidate
            },
            candidate
          )),
          /* @__PURE__ */ jsx2(DropdownMenu.Separator, {}),
          /* @__PURE__ */ jsx2(DropdownMenu.Item, { onClick: onIgnore, asChild: true, children: /* @__PURE__ */ jsx2(Colored, { color: "red500", children: "Ignore phrase" }) })
        ]
      }
    )
  ] });
};
var StyledTrigger = styled(DropdownMenu.Trigger)`
  visibility: hidden;
  z-index: -1;
`;

// src/extensions/telemetry/word-count-plugin.ts
import { Plugin as Plugin13, PluginKey as PluginKey12 } from "@tiptap/pm/state";
var WordCountPluginKey = new PluginKey12("wordCount");
var WordCountPlugin = (editorId) => new Plugin13({
  key: WordCountPluginKey,
  state: {
    init(_, editorState) {
      return {
        editorId,
        selected: selectionWordCount(editorState),
        total: nodeWordCount(editorState.doc)
      };
    },
    apply(tr, state, oldEditorState, newEditorState) {
      const meta = tr.getMeta(WordCountPluginKey);
      if (oldEditorState.selection !== newEditorState.selection) {
        state.selected = selectionWordCount(newEditorState);
      }
      if (meta?.type === "setWordCount" && typeof meta.total === "number") {
        state.total = meta.total;
      }
      return state;
    }
  }
});

// src/extensions/telemetry/editorWordCount.ts
function editorWordCount(editor) {
  return WordCountPluginKey.getState(editor.state) ?? null;
}

// src/extensions/telemetry/telemetry.ts
import { Extension as Extension7 } from "@tiptap/core";

// src/extensions/telemetry/emitTransactionEvents.ts
function emitTransactionEvents(transaction, opts) {
  const historyMeta = transaction.getMeta(HistoryPluginKey);
  const pasteMeta = transaction.getMeta(PastePluginKey);
  if (pasteMeta || historyMeta) {
    setTimeout(() => {
      if (pasteMeta) {
        opts.onEditorEvent?.({
          type: "paste",
          payload: {
            ...pasteMeta,
            origin: pasteMeta.origin === "prosemirror" /* PROSEMIRROR */ && !pasteMeta.internal ? "Unknown" /* UNKNOWN */ : pasteMeta.origin,
            editorId: opts.editorId,
            addedWords: pasteMeta.wordsAdded ?? 0,
            deletedWords: pasteMeta.wordsDeleted ?? 0
          }
        });
      }
      if (historyMeta) {
        opts.onEditorEvent?.({
          type: "history",
          payload: {
            historyType: historyMeta.type,
            addedWords: historyMeta.wordsAdded ?? 0,
            deletedWords: historyMeta.wordsDeleted ?? 0,
            editorId: opts.editorId
          }
        });
      }
    });
  }
}

// src/extensions/telemetry/processDocWords.ts
import { debounce } from "ts-debounce";
function processDocWords(editor, doc, opts) {
  const [words, wordCount] = tokeniseUniqueWords(doc, 0, doc.content.size);
  editor.commands.setWordCount(wordCount);
  if (opts.withCharacterCount || opts.withSummary) {
    const text = editor.getText();
    if (opts.withCharacterCount) {
      editor.storage.telemetry.characterCount = text.length;
    }
    if (opts.withSummary?.length) {
      editor.storage.telemetry.summary = text.slice(0, opts.withSummary.length);
    }
  }
  opts.onSave?.(opts.editorId, editor);
  editor.storage.telemetry.pendingSave = false;
  if (opts.suggestionClient && canSpellCheckWithCadmus(editor)) {
    opts.suggestionClient.requestSuggestions(words);
  }
}
var debouncedProcessDocWords = debounce(processDocWords, 800, {
  maxWait: 4e3
});

// src/extensions/telemetry/telemetry.ts
var Telemetry = Extension7.create({
  name: "telemetry",
  addStorage() {
    return {
      pendingSave: false,
      ...this.options.withCharacterCount ? { characterCount: 0 } : {},
      ...this.options.withSummary ? { summary: "" } : {}
    };
  },
  /**
   * On each transaction that cause changes in document, debounce word count
   * calculation and suggestion request to the server and also send undo/redo/paste
   * event to the server
   */
  onTransaction({ transaction, editor }) {
    if (transaction.docChanged) {
      editor.storage.telemetry.pendingSave = true;
      debouncedProcessDocWords(editor, transaction.doc, this.options);
    }
    emitTransactionEvents(transaction, this.options);
  },
  /**
   * Upon creation of the editor instance, initialised word count &
   * suggestion without debouncing it.
   */
  onCreate() {
    processDocWords(this.editor, this.editor.state.doc, {
      ...this.options,
      // Do not fire a save on creation, just update the word count
      onSave: void 0
    });
  },
  addCommands() {
    return {
      setWordCount: (total) => ({ dispatch, tr }) => {
        if (dispatch) {
          tr.setMeta(WordCountPluginKey, {
            type: "setWordCount",
            total
          });
        }
        return true;
      }
    };
  },
  addProseMirrorPlugins() {
    return [WordCountPlugin(this.options.editorId)];
  }
});

// src/extensions/thread/utils.ts
import { findChildren as findChildren5 } from "@tiptap/core";

// src/extensions/thread/thread-active-status-plugin.ts
import { Plugin as Plugin14, PluginKey as PluginKey13 } from "@tiptap/pm/state";
import { Decoration as Decoration6, DecorationSet as DecorationSet6 } from "@tiptap/pm/view";
var ThreadActiveStatusPluginKey = new PluginKey13(
  "ThreadActiveStatus"
);
function ThreadActiveStatusPlugin() {
  return new Plugin14({
    key: ThreadActiveStatusPluginKey,
    state: {
      init() {
        return {
          activeThreadId: null
        };
      },
      apply(tr, prevState) {
        const focusedThreadId = tr.getMeta("activeThreadId");
        if (focusedThreadId === void 0) {
          return prevState;
        }
        return {
          activeThreadId: focusedThreadId
        };
      }
    },
    props: {
      /**
       * Adds a decoration to the active thread mark in the editor
       */
      decorations(editorState) {
        const newDecorations = getDecorationsForState(
          editorState.doc,
          this.getState(editorState)
        );
        return newDecorations;
      },
      /**
       * Handle click on a thread SPAN element.
       */
      handleClick(view, _pos, event) {
        if (!event || !event.target) return;
        const target = event.target;
        const closestThreadId = target.closest("[data-thread-id]");
        if (closestThreadId) {
          const dataThreadId = closestThreadId.getAttribute("data-thread-id");
          view.dispatch(view.state.tr.setMeta("activeThreadId", dataThreadId));
        } else {
          view.dispatch(view.state.tr.setMeta("activeThreadId", null));
        }
        return true;
      }
    }
  });
}
function getDecorationsForState(document2, threadActiveState) {
  const threadId = threadActiveState?.activeThreadId;
  if (!threadId) return DecorationSet6.empty;
  const activeThreadNodes = findThreadNodes(document2, threadId);
  if (Array.isArray(activeThreadNodes) && activeThreadNodes.length === 0)
    return DecorationSet6.empty;
  const decorations = activeThreadNodes.map(
    (activeThreadNode) => Decoration6.inline(
      activeThreadNode.pos,
      activeThreadNode.pos + activeThreadNode.node.nodeSize,
      {
        class: "thread-active"
      },
      { inclusiveStart: true, inclusiveEnd: true }
    )
  );
  return DecorationSet6.create(document2, decorations);
}

// src/extensions/thread/utils.ts
function listThreads(editorState) {
  const childrenWithThread = findChildren5(
    editorState.doc,
    (node) => node.marks.some((mark) => mark.type.name === "thread")
  );
  const threadIdSet = new Set(
    childrenWithThread?.flatMap(
      (nodeWithThread) => nodeWithThread.node.marks.filter((mark) => mark.type.name === "thread" && mark.attrs.id).map((mark) => mark.attrs.id)
    )
  );
  return [...threadIdSet];
}
function findThreadMarkRanges(node, threadId) {
  const nodeWithThreads = findThreadNodes(node, threadId);
  if (Array.isArray(nodeWithThreads) && nodeWithThreads.length === 0) return [];
  return nodeWithThreads.map((nodeWithThread) => {
    const threadMark = nodeWithThread.node.marks.find(
      (mark) => mark.type.name === "thread" && mark.attrs.id === threadId
    );
    return {
      from: nodeWithThread.pos,
      to: nodeWithThread.pos + nodeWithThread.node.nodeSize,
      mark: threadMark
    };
  });
}
function findThreadNodes(node, threadId) {
  if (!threadId) return [];
  return findChildren5(
    node,
    (node2) => node2.marks.some(
      (mark) => mark.type.name === "thread" && mark.attrs.id === threadId
    )
  );
}
function currentActiveThreadId(state) {
  return ThreadActiveStatusPluginKey.getState(state)?.activeThreadId ?? null;
}

// src/extensions/thread/thread-list-plugin.ts
import { Plugin as Plugin15, PluginKey as PluginKey14 } from "@tiptap/pm/state";
var ThreadListPluginKey = new PluginKey14("ThreadList");
function ThreadListPlugin() {
  return new Plugin15({
    key: ThreadListPluginKey,
    state: {
      init(_, state) {
        return listThreads(state);
      },
      apply(tr, currentList, _oldEditorState, newEditorState) {
        if (!tr.docChanged) return currentList;
        return listThreads(newEditorState);
      }
    }
  });
}

// src/extensions/thread/use-thread-list.ts
import { useEditorState as useEditorState3 } from "@tiptap/react";
function useThreadList(editor) {
  return useEditorState3({
    editor,
    selector: (ctx) => ({
      threads: ctx.editor ? ThreadListPluginKey.getState(ctx.editor.state) ?? [] : [],
      activeThreadId: ctx.editor ? ThreadActiveStatusPluginKey.getState(ctx.editor.state)?.activeThreadId ?? null : null
    })
  }) || { threads: [], activeThreadId: null };
}

// src/extensions/thread/thread.ts
import { Mark } from "@tiptap/core";
import { TextSelection as TextSelection3 } from "@tiptap/pm/state";

// src/extensions/thread/thread-paste-plugin.ts
import { Plugin as Plugin16, PluginKey as PluginKey15 } from "@tiptap/pm/state";
var ThreadPastePluginKey = new PluginKey15("threadPaste");
var ThreadPastePlugin = () => {
  return new Plugin16({
    key: ThreadPastePluginKey,
    props: {
      transformPastedHTML(html) {
        return html.replace(/<\/?thread[^>]*>/g, "");
      },
      handlePaste(view, _event, _slice) {
        const { state } = view;
        const { selection, schema } = state;
        const { $from } = selection;
        const threadMarkType = schema.marks.thread;
        const threadMark = $from.marks().find((mark) => mark.type.name === threadMarkType.name);
        if (threadMark) {
          return true;
        }
        return false;
      }
    }
  });
};

// src/extensions/thread/thread.ts
var Thread = Mark.create({
  name: "thread",
  priority: 1e3,
  inclusive: true,
  // Changed to true to prevent splitting at boundaries
  spanning: true,
  // Prevents the mark from being split when editing inside
  excludes: "thread",
  // Prevents overlapping thread marks
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-thread-id"),
        renderHTML: (attributes) => attributes.id ? { "data-thread-id": attributes.id } : {}
      },
      resolved: {
        default: "false",
        parseHTML: (element) => element.getAttribute("data-thread-resolved"),
        renderHTML: (attributes) => attributes.resolved ? { "data-thread-resolved": attributes.resolved } : {}
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "thread"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["thread", HTMLAttributes, ""];
  },
  addCommands() {
    return {
      insertThread: (threadId) => ({ tr, editor }) => {
        if (!validateThreadInsertion(tr, editor)) {
          return false;
        }
        const threadMark = editor.schema.marks.thread.create({
          id: threadId,
          resolved: "false"
        });
        const sel = tr.selection;
        tr.addMark(sel.from, sel.to, threadMark);
        tr.setMeta("activeThreadId", threadId);
        return true;
      },
      deleteThread: (threadId) => ({ tr }) => {
        const items = findThreadMarkRanges(tr.doc, threadId);
        if (Array.isArray(items) && items.length === 0) {
          return false;
        }
        items.forEach((item) => {
          tr.removeMark(item.from, item.to, item.mark.type);
        });
        return true;
      },
      focusThread: (threadId) => ({ tr }) => {
        if (!threadId) {
          tr.setMeta("activeThreadId", null);
          return false;
        }
        const items = findThreadMarkRanges(tr.doc, threadId);
        if (Array.isArray(items) && items.length === 0) {
          return false;
        }
        const firstThreadMark = items[0];
        if (firstThreadMark) {
          const newSelection = TextSelection3.create(
            tr.doc,
            firstThreadMark.from
          );
          tr.setSelection(newSelection);
          tr.setMeta("activeThreadId", firstThreadMark ? threadId : null);
          return true;
        }
        return false;
      },
      resolveThread: (threadId) => ({ tr, editor }) => {
        const items = findThreadMarkRanges(tr.doc, threadId);
        if (Array.isArray(items) && items.length === 0) {
          return false;
        }
        items.forEach((item) => {
          tr.removeMark(item.from, item.to, item.mark.type);
          const threadMark = editor.schema.marks.thread.create({
            id: threadId,
            resolved: "true"
          });
          tr.addMark(item.from, item.to, threadMark);
        });
        tr.setMeta("activeThreadId", null);
        return true;
      },
      unresolveThread: (threadId) => ({ tr, editor }) => {
        const items = findThreadMarkRanges(tr.doc, threadId);
        if (Array.isArray(items) && items.length === 0) {
          return false;
        }
        items.forEach((item) => {
          tr.removeMark(item.from, item.to, item.mark.type);
          const threadMark = editor.schema.marks.thread.create({
            id: threadId,
            resolved: "false"
          });
          tr.addMark(item.from, item.to, threadMark);
        });
        tr.setMeta("activeThreadId", threadId);
        return true;
      },
      deleteAllThreads: () => ({ tr, editor }) => {
        const threadMarkType = editor.schema.marks.thread;
        let hasChanges = false;
        tr.doc.descendants((node, pos) => {
          if (node.marks && node.marks.length > 0) {
            node.marks.forEach((mark) => {
              if (mark.type === threadMarkType) {
                tr.removeMark(pos, pos + node.nodeSize, threadMarkType);
                hasChanges = true;
              }
            });
          }
        });
        tr.setMeta("activeThreadId", null);
        return hasChanges;
      },
      canInsertThread: () => ({ tr, editor }) => {
        return validateThreadInsertion(tr, editor);
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      // To track active status of the threads
      ThreadActiveStatusPlugin(),
      // To track list of threads in the editor
      ThreadListPlugin(),
      // To handle paste operations inside thread marks
      ThreadPastePlugin()
    ];
  }
});
function validateThreadInsertion(tr, editor) {
  const sel = tr.selection;
  if (!sel || sel.empty) {
    return false;
  }
  const { from, to } = sel;
  if (hasConflictingThreadMarks(tr, editor, from, to)) {
    return false;
  }
  if (wouldCauseMarkSplitting(tr, from, to)) {
    return false;
  }
  return true;
}
function hasConflictingThreadMarks(tr, editor, from, to) {
  const threadMarkType = editor.schema.marks.thread;
  let hasExistingThreadMark = false;
  tr.doc.nodesBetween(from, to, (node) => {
    if (node.marks) {
      node.marks.forEach((mark) => {
        if (mark.type === threadMarkType) {
          hasExistingThreadMark = true;
        }
      });
    }
  });
  const $from = tr.doc.resolve(from);
  const $to = tr.doc.resolve(to);
  const marksAtStart = $from.marks();
  const marksAtEnd = $to.marks();
  for (const mark of marksAtStart) {
    if (mark.type === threadMarkType) {
      hasExistingThreadMark = true;
      break;
    }
  }
  for (const mark of marksAtEnd) {
    if (mark.type === threadMarkType) {
      hasExistingThreadMark = true;
      break;
    }
  }
  return hasExistingThreadMark;
}
function wouldCauseMarkSplitting(tr, from, to) {
  const nodeAtFrom = tr.doc.resolve(from).parent;
  return !(nodeAtFrom === tr.doc.resolve(to).parent && nodeAtFrom.isTextblock);
}

// src/react/CadmusEditorWrapper.tsx
import { css as css11, keyframes, styled as styled2 } from "styled-components";

// src/extensions/code-block/styles/index.ts
import { css as css5 } from "styled-components";

// src/extensions/code-block/styles/cadmus-dark-styles.ts
import { css } from "styled-components";
import { colors as colors2 } from "@vericus/cadmus-ui";
var darkColors = {
  red500: "#ff4d87",
  orange500: "#FF974D",
  blue300: "#86D7FF",
  pink500: "#FF4D87",
  green300: "#A6D279",
  yellow500: "#FFCC3E",
  cyan500: "#61D1D1",
  grey500: "#9DADCA",
  white100: "#FFFFFF",
  white500: "#E8E8F7",
  background: "#0f172e",
  border: "#0f172e",
  preDeleteBackground: "#371932",
  preDeleteBorder: colors2.red500,
  preDeleteGutter: "#482d4a",
  selectedBackground: colors2.grey200,
  gutterBackground: "#242f4b",
  lineNumber: "#f2f5fc",
  languageDisplay: "#9dadca",
  cursor: "#61d1d1",
  activeLineBackground: "#1b2338",
  activeLineGutterBackground: "#2c3852"
};
var cadmusDarkCodeBlockStyles = css`
  /* basic */
  .cm-s-cadmus-dark {
    color: ${darkColors.white100};
    background-color: ${darkColors.background};
    border: solid 1px ${darkColors.border} !important;
  }

  .cm-s-cadmus-dark.code-block-pre-delete {
    background-color: ${darkColors.preDeleteBackground};
    border: 1px solid ${darkColors.preDeleteBorder} !important;
    .CodeMirror-activeline-background {
      background-color: ${darkColors.preDeleteBackground};
    }
    .CodeMirror-linenumbers {
      background-color: ${darkColors.preDeleteGutter};
    }
    .CodeMirror-activeline-gutter {
      background-color: ${darkColors.preDeleteGutter};
    }
  }

  .cm-s-cadmus-dark .CodeMirror-selected {
    color: ${darkColors.white100} !important;
    background: ${darkColors.selectedBackground} !important;
  }

  /** user select styling */
  .cm-s-cadmus-dark .CodeMirror-focused .CodeMirror-selected,
  .cm-s-cadmus-dark .CodeMirror-line::selection,
  .cm-s-cadmus-dark .CodeMirror-line > span::selection,
  .cm-s-cadmus-dark .CodeMirror-line > span > span::selection,
  .cm-s-cadmus-dark .CodeMirror-line::-moz-selection,
  .cm-s-cadmus-dark .CodeMirror-line > span::-moz-selection,
  .cm-s-cadmus-dark .CodeMirror-line > span > span::-moz-selection {
    background: ${darkColors.selectedBackground};
  }

  .cm-s-cadmus-dark .CodeMirror-gutter,
  .cm-s-cadmus-dark .CodeMirror-gutters {
    border: none;
    background-color: ${darkColors.gutterBackground};
  }
  .cm-s-cadmus-dark .CodeMirror-linenumber,
  .cm-s-cadmus-dark .CodeMirror-linenumbers {
    color: ${darkColors.lineNumber} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-dark .CodeMirror-lines {
    color: ${darkColors.white500} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-dark .CodeMirror-cursor {
    border-left: 2px solid ${darkColors.cursor} !important;
  }

  .cm-s-cadmus-dark .language-display {
    color: ${darkColors.languageDisplay} !important;
  }

  /* addon: edit/machingbrackets.js & addon: edit/matchtags.js */
  .cm-s-cadmus-dark .CodeMirror-matchingbracket,
  .cm-s-cadmus-dark .CodeMirror-matchingtag {
    border-bottom: 2px solid ${darkColors.white500};
    color: ${darkColors.white500} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-dark .CodeMirror-nonmatchingbracket {
    border-bottom: 2px solid ${darkColors.red500};
    color: ${darkColors.red500} !important;
    background-color: transparent;
  }

  /* addon: fold/foldgutter.js */
  .cm-s-cadmus-dark .CodeMirror-foldmarker,
  .cm-s-cadmus-dark .CodeMirror-foldgutter,
  .cm-s-cadmus-dark .CodeMirror-foldgutter-open,
  .cm-s-cadmus-dark .CodeMirror-foldgutter-folded {
    border: none;
    text-shadow: none;
    color: ${darkColors.grey500} !important;
    background-color: transparent;
  }

  /* addon: selection/active-line.js */
  .cm-s-cadmus-dark .CodeMirror-activeline-background {
    background-color: ${darkColors.activeLineBackground};
  }
  .cm-s-cadmus-dark .CodeMirror-activeline-gutter {
    background-color: ${darkColors.activeLineGutterBackground};
  }

  /* basic syntax */
  .cm-s-cadmus-dark span.cm-comment {
    color: ${darkColors.grey500};
  }
  .cm-s-cadmus-dark span.cm-atom {
    color: ${darkColors.pink500};
  }
  .cm-s-cadmus-dark span.cm-number,
  .cm-s-cadmus-dark span.cm-attribute,
  .cm-s-cadmus-dark span.cm-qualifier {
    color: ${darkColors.yellow500};
  }
  .cm-s-cadmus-dark span.cm-keyword,
  .cm-s-cadmus-dark span.cm-operator {
    color: ${darkColors.pink500};
  }
  .cm-s-cadmus-dark span.cm-string,
  .cm-s-cadmus-dark span.cm-quote {
    color: ${darkColors.green300};
  }
  .cm-s-cadmus-dark span.cm-meta {
    color: ${darkColors.pink500};
  }
  .cm-s-cadmus-dark span.cm-variable-2,
  .cm-s-cadmus-dark span.cm-variable-3,
  .cm-s-cadmus-dark span.cm-variable {
    color: ${darkColors.white500};
  }
  .cm-s-cadmus-dark span.cm-tag {
    color: ${darkColors.blue300};
  }
  .cm-s-cadmus-dark span.cm-def {
    color: ${darkColors.cyan500};
  }
  .cm-s-cadmus-dark span.cm-type,
  .cm-s-cadmus-dark span.cm-punctuation {
    color: ${darkColors.white500};
  }
  .cm-s-cadmus-dark span.cm-bracket {
    color: ${darkColors.white500};
  }
  .cm-s-cadmus-dark span.cm-builtin,
  .cm-s-cadmus-dark span.cm-special {
    color: ${darkColors.orange500};
  }
  .cm-s-cadmus-dark span.cm-link {
    color: ${darkColors.pink500};
    border-bottom: solid 1px ${darkColors.pink500};
  }
  .cm-s-cadmus-dark span.cm-error {
    color: ${darkColors.red500};
  }

  .cm-s-cadmus-dark span.cm-header {
    color: ${darkColors.red500};
  }

  .cm-s-cadmus-dark span.cm-em {
    font-style: italic;
  }

  .cm-s-cadmus-dark span.cm-strong {
    font-style: bold;
  }

  .cm-s-cadmus-dark span.cm-property {
    color: ${darkColors.orange500};
  }
`;

// src/extensions/code-block/styles/cadmus-light-styles.ts
import { css as css2 } from "styled-components";
import { colors as colors3 } from "@vericus/cadmus-ui";
var lightColors = {
  magenta100: "#B81466",
  red500: "#D92644",
  red300: "#4639DA",
  blue500: "#0066cc",
  blue800: "#0D2F73",
  black100: colors3.black100,
  black200: "#0F172E",
  cyan500: "#00807F",
  pink500: "#CC00AA",
  yellow800: "#805500",
  grey500: "#5B6F98",
  background: "#f4f5f880",
  border: "#dee3ed",
  preDeleteBackground: colors3.red100,
  preDeleteBorder: colors3.red500,
  preDeleteGutter: colors3.red200,
  selectedBackground: colors3.blue200,
  gutterBackground: "#5973a60f",
  lineNumber: "#5b6f98",
  languageDisplay: "#5b6f98",
  cursor: "#00a3a3",
  activeLineBackground: "#fefefe",
  activeLineGutterBackground: "#f9fafb"
};
var cadmusLightCodeBlockStyles = css2`
  /* basic */
  .cm-s-cadmus-light {
    color: ${colors3.black100};
    background-color: ${lightColors.background};
    border: solid 1px ${lightColors.border} !important;
  }

  .cm-s-cadmus-light.code-block-pre-delete {
    background-color: ${lightColors.preDeleteBackground};
    border: 1px solid ${lightColors.preDeleteBorder} !important;
    .CodeMirror-activeline-background {
      background-color: ${lightColors.preDeleteBackground};
    }
    .CodeMirror-linenumbers {
      background-color: ${lightColors.preDeleteGutter};
    }
    .CodeMirror-activeline-gutter {
      background-color: ${lightColors.preDeleteGutter};
    }
  }

  .cm-s-cadmus-light .CodeMirror-selected {
    color: ${lightColors.black200} !important;
    background: ${lightColors.selectedBackground} !important;
  }

  /** user select styling */
  .cm-s-cadmus-light .CodeMirror-focused .CodeMirror-selected,
  .cm-s-cadmus-light .CodeMirror-line::selection,
  .cm-s-cadmus-light .CodeMirror-line > span::selection,
  .cm-s-cadmus-light .CodeMirror-line > span > span::selection,
  .cm-s-cadmus-light .CodeMirror-line::-moz-selection,
  .cm-s-cadmus-light .CodeMirror-line > span::-moz-selection,
  .cm-s-cadmus-light .CodeMirror-line > span > span::-moz-selection {
    background: ${lightColors.selectedBackground};
  }

  .cm-s-cadmus-light .CodeMirror-gutter,
  .cm-s-cadmus-light .CodeMirror-gutters {
    border: none;
    background-color: ${lightColors.gutterBackground};
  }
  .cm-s-cadmus-light .CodeMirror-linenumber,
  .cm-s-cadmus-light .CodeMirror-linenumbers {
    color: ${lightColors.lineNumber} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-light .CodeMirror-lines {
    color: ${lightColors.black100} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-light .CodeMirror-cursor {
    border-left: 2px solid ${lightColors.cursor} !important;
  }

  .cm-s-cadmus-light .language-display {
    color: ${lightColors.languageDisplay} !important;
  }

  /* addon: edit/machingbrackets.js & addon: edit/matchtags.js */
  .cm-s-cadmus-light .CodeMirror-matchingbracket,
  .cm-s-cadmus-light .CodeMirror-matchingtag {
    border-bottom: 2px solid ${lightColors.grey500};
    color: ${lightColors.grey500} !important;
    background-color: transparent;
  }
  .cm-s-cadmus-light .CodeMirror-nonmatchingbracket {
    border-bottom: 2px solid ${lightColors.red500};
    color: ${lightColors.red500} !important;
    background-color: transparent;
  }

  /* addon: fold/foldgutter.js */
  .cm-s-cadmus-light .CodeMirror-foldmarker,
  .cm-s-cadmus-light .CodeMirror-foldgutter,
  .cm-s-cadmus-light .CodeMirror-foldgutter-open,
  .cm-s-cadmus-light .CodeMirror-foldgutter-folded {
    border: none;
    text-shadow: none;
    color: ${lightColors.grey500} !important;
    background-color: transparent;
  }

  /* addon: selection/active-line.js */
  .cm-s-cadmus-light .CodeMirror-activeline-background {
    background-color: ${lightColors.activeLineBackground};
  }
  .cm-s-cadmus-light .CodeMirror-activeline-gutter {
    background-color: ${lightColors.activeLineGutterBackground};
  }

  /* basic syntaxs */
  .cm-s-cadmus-light span.cm-comment {
    color: ${lightColors.grey500};
  }
  .cm-s-cadmus-light span.cm-atom {
    color: ${lightColors.pink500};
  }
  .cm-s-cadmus-light span.cm-number,
  .cm-s-cadmus-light span.cm-attribute,
  .cm-s-cadmus-light span.cm-qualifier {
    color: ${lightColors.pink500};
  }
  .cm-s-cadmus-light span.cm-keyword,
  .cm-s-cadmus-light span.cm-operator {
    color: ${lightColors.pink500};
  }
  .cm-s-cadmus-light span.cm-string,
  .cm-s-cadmus-light span.cm-quote {
    color: ${lightColors.cyan500};
  }
  .cm-s-cadmus-light span.cm-meta {
    color: ${lightColors.pink500};
  }
  .cm-s-cadmus-light span.cm-variable-2,
  .cm-s-cadmus-light span.cm-variable-3,
  .cm-s-cadmus-light span.cm-variable {
    color: ${lightColors.blue800};
  }
  .cm-s-cadmus-light span.cm-tag {
    color: ${lightColors.blue500};
  }
  .cm-s-cadmus-light span.cm-def {
    color: ${lightColors.blue500};
  }
  .cm-s-cadmus-light span.cm-type,
  .cm-s-cadmus-light span.cm-punctuation {
    color: ${lightColors.black200};
  }
  .cm-s-cadmus-light span.cm-bracket {
    color: ${lightColors.grey500};
  }
  .cm-s-cadmus-light span.cm-builtin,
  .cm-s-cadmus-light span.cm-special {
    color: ${lightColors.magenta100};
  }
  .cm-s-cadmus-light span.cm-link {
    color: ${lightColors.blue800};
    border-bottom: solid 1px ${lightColors.blue800};
  }
  .cm-s-cadmus-light span.cm-error {
    color: ${lightColors.red500};
  }

  .cm-s-cadmus-light span.cm-header {
    color: ${lightColors.red500};
  }

  .cm-s-cadmus-light span.cm-em {
    font-style: italic;
  }

  .cm-s-cadmus-light span.cm-strong {
    font-style: bold;
  }

  .cm-s-cadmus-light span.cm-property {
    color: ${lightColors.yellow800};
  }
`;

// src/extensions/code-block/styles/code-block-shared-styles.ts
import { css as css3 } from "styled-components";
var config = {
  fontFamily: "ibm-plex-mono, ui-monospace, monospace",
  fontFamilyLanguageCaption: editorTypography.headingOne.fontFamily,
  fontSizeText: "14px",
  fontSizeLineNumber: "14px",
  fontSizeLanguageCaption: "12px",
  fontWeight: "400"
};
var codeBlockSharedStyles = css3`
  .CodeMirror {
    border-radius: 3px;
    width: 710px;
    max-width: 90%; /* allow for some margin*/
    height: auto;
    margin: 24px auto;
    line-height: 24px;

    font-family: ${config.fontFamily};
    font-weight: ${config.fontWeight};
    font-size: ${config.fontSizeText};

    user-select: text;
  }

  .CodeMirror .language-display {
    font-size: ${config.fontSizeLanguageCaption} !important;

    position: absolute;
    top: 12px;
    right: 12px;
    line-height: 12px !important;
    font-family: ${config.fontFamilyLanguageCaption} !important;
  }

  .CodeMirror-sizer {
    min-height: 64px !important ;
  }

  .CodeMirror-lines {
    padding: 28px 0px 20px 0px;
  }

  .language-display-off .CodeMirror-lines {
    padding: 20px 0px !important;
  }

  .CodeMirror-line {
    font-size: ${config.fontSizeText} !important ;
    padding: 0 16px !important;
  }

  .CodeMirror-linenumber {
    font-size: ${config.fontSizeLineNumber} !important;
    display: inline-flex;
    justify-content: right;
  }
`;

// src/extensions/code-block/styles/codemirror-styles.ts
import { css as css4 } from "styled-components";
var codemirrorStyles = css4`
  /* BASICS */

  .CodeMirror {
    /* Set height, width, borders, and global font properties here */
    font-family: monospace;
    height: 300px;
    color: black;
    direction: ltr;
  }

  /* PADDING */

  .CodeMirror-lines {
    padding: 4px 0; /* Vertical padding around content */
  }
  .CodeMirror pre.CodeMirror-line,
  .CodeMirror pre.CodeMirror-line-like {
    padding: 0 4px; /* Horizontal padding of content */
  }

  .CodeMirror-scrollbar-filler,
  .CodeMirror-gutter-filler {
    background-color: white; /* The little square between H and V scrollbars */
  }

  /* GUTTER */

  .CodeMirror-gutters {
    border-right: 1px solid #ddd;
    background-color: #f7f7f7;
    white-space: nowrap;
  }
  .CodeMirror-linenumbers {
  }
  .CodeMirror-linenumber {
    padding: 0 4px 0 4px;
    min-width: 20px;
    text-align: right;
    color: #999;
    white-space: nowrap;
  }

  .CodeMirror-guttermarker {
    color: black;
  }
  .CodeMirror-guttermarker-subtle {
    color: #999;
  }

  /* CURSOR */

  .CodeMirror-cursor {
    border-left: 1px solid black;
    border-right: none;
    width: 0;
  }
  /* Shown when moving in bi-directional text */
  .CodeMirror div.CodeMirror-secondarycursor {
    border-left: 1px solid silver;
  }
  .cm-fat-cursor .CodeMirror-cursor {
    width: auto;
    border: 0 !important;
    background: #7e7;
  }
  .cm-fat-cursor div.CodeMirror-cursors {
    z-index: 1;
  }
  .cm-fat-cursor .CodeMirror-line::selection,
  .cm-fat-cursor .CodeMirror-line > span::selection,
  .cm-fat-cursor .CodeMirror-line > span > span::selection {
    background: transparent;
  }
  .cm-fat-cursor .CodeMirror-line::-moz-selection,
  .cm-fat-cursor .CodeMirror-line > span::-moz-selection,
  .cm-fat-cursor .CodeMirror-line > span > span::-moz-selection {
    background: transparent;
  }
  .cm-fat-cursor {
    caret-color: transparent;
  }
  @-moz-keyframes blink {
    0% {
    }
    50% {
      background-color: transparent;
    }
    100% {
    }
  }
  @-webkit-keyframes blink {
    0% {
    }
    50% {
      background-color: transparent;
    }
    100% {
    }
  }
  @keyframes blink {
    0% {
    }
    50% {
      background-color: transparent;
    }
    100% {
    }
  }

  /* Can style cursor different in overwrite (non-insert) mode */
  .CodeMirror-overwrite .CodeMirror-cursor {
  }

  .cm-tab {
    display: inline-block;
    text-decoration: inherit;
  }

  .CodeMirror-rulers {
    position: absolute;
    left: 0;
    right: 0;
    top: -50px;
    bottom: 0;
    overflow: hidden;
  }
  .CodeMirror-ruler {
    border-left: 1px solid #ccc;
    top: 0;
    bottom: 0;
    position: absolute;
  }

  /* DEFAULT THEME */

  .cm-s-default .cm-header {
    color: blue;
  }
  .cm-s-default .cm-quote {
    color: #090;
  }
  .cm-negative {
    color: #d44;
  }
  .cm-positive {
    color: #292;
  }
  .cm-header,
  .cm-strong {
    font-weight: bold;
  }
  .cm-em {
    font-style: italic;
  }
  .cm-link {
    text-decoration: underline;
  }
  .cm-strikethrough {
    text-decoration: line-through;
  }

  .cm-s-default .cm-keyword {
    color: #708;
  }
  .cm-s-default .cm-atom {
    color: #219;
  }
  .cm-s-default .cm-number {
    color: #164;
  }
  .cm-s-default .cm-def {
    color: #00f;
  }
  .cm-s-default .cm-variable,
  .cm-s-default .cm-punctuation,
  .cm-s-default .cm-property,
  .cm-s-default .cm-operator {
  }
  .cm-s-default .cm-variable-2 {
    color: #05a;
  }
  .cm-s-default .cm-variable-3,
  .cm-s-default .cm-type {
    color: #085;
  }
  .cm-s-default .cm-comment {
    color: #a50;
  }
  .cm-s-default .cm-string {
    color: #a11;
  }
  .cm-s-default .cm-string-2 {
    color: #f50;
  }
  .cm-s-default .cm-meta {
    color: #555;
  }
  .cm-s-default .cm-qualifier {
    color: #555;
  }
  .cm-s-default .cm-builtin {
    color: #30a;
  }
  .cm-s-default .cm-bracket {
    color: #997;
  }
  .cm-s-default .cm-tag {
    color: #170;
  }
  .cm-s-default .cm-attribute {
    color: #00c;
  }
  .cm-s-default .cm-hr {
    color: #999;
  }
  .cm-s-default .cm-link {
    color: #00c;
  }

  .cm-s-default .cm-error {
    color: #f00;
  }
  .cm-invalidchar {
    color: #f00;
  }

  .CodeMirror-composing {
    border-bottom: 2px solid;
  }

  /* Default styles for common addons */

  div.CodeMirror span.CodeMirror-matchingbracket {
    color: #0b0;
  }
  div.CodeMirror span.CodeMirror-nonmatchingbracket {
    color: #a22;
  }
  .CodeMirror-matchingtag {
    background: rgba(255, 150, 0, 0.3);
  }
  .CodeMirror-activeline-background {
    background: #e8f2ff;
  }

  /* STOP */

  /* The rest of this file contains styles related to the mechanics of
   the editor. You probably shouldn't touch them. */

  .CodeMirror {
    position: relative;
    overflow: hidden;
    background: white;
  }

  .CodeMirror-scroll {
    overflow: scroll !important; /* Things will break if this is overridden */
    /* 50px is the magic margin used to hide the element's real scrollbars */
    /* See overflow: hidden in .CodeMirror */
    margin-bottom: -50px;
    margin-right: -50px;
    padding-bottom: 50px;
    height: 100%;
    outline: none; /* Prevent dragging from highlighting the element */
    position: relative;
    z-index: 0;
  }
  .CodeMirror-sizer {
    position: relative;
    border-right: 50px solid transparent;
  }

  /* The fake, visible scrollbars. Used to force redraw during scrolling
   before actual scrolling happens, thus preventing shaking and
   flickering artifacts. */
  .CodeMirror-vscrollbar,
  .CodeMirror-hscrollbar,
  .CodeMirror-scrollbar-filler,
  .CodeMirror-gutter-filler {
    position: absolute;
    z-index: 6;
    display: none;
    outline: none;
  }
  .CodeMirror-vscrollbar {
    right: 0;
    top: 0;
    overflow-x: hidden;
    overflow-y: scroll;
  }
  .CodeMirror-hscrollbar {
    bottom: 0;
    left: 0;
    overflow-y: hidden;
    overflow-x: scroll;
  }
  .CodeMirror-scrollbar-filler {
    right: 0;
    bottom: 0;
  }
  .CodeMirror-gutter-filler {
    left: 0;
    bottom: 0;
  }

  .CodeMirror-gutters {
    position: absolute;
    left: 0;
    top: 0;
    min-height: 100%;
    z-index: 3;
  }
  .CodeMirror-gutter {
    white-space: normal;
    height: 100%;
    display: inline-block;
    vertical-align: top;
    margin-bottom: -50px;
  }
  .CodeMirror-gutter-wrapper {
    position: absolute;
    z-index: 4;
    background: none !important;
    border: none !important;
  }
  .CodeMirror-gutter-background {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 4;
  }
  .CodeMirror-gutter-elt {
    position: absolute;
    cursor: default;
    z-index: 4;
  }
  .CodeMirror-gutter-wrapper ::selection {
    background-color: transparent;
  }
  .CodeMirror-gutter-wrapper ::-moz-selection {
    background-color: transparent;
  }

  .CodeMirror-lines {
    cursor: text;
    min-height: 1px; /* prevents collapsing before first draw */
  }
  .CodeMirror pre.CodeMirror-line,
  .CodeMirror pre.CodeMirror-line-like {
    /* Reset some styles that the rest of the page might have set */
    -moz-border-radius: 0;
    -webkit-border-radius: 0;
    border-radius: 0;
    border-width: 0;
    background: transparent;
    font-family: inherit;
    font-size: inherit;
    margin: 0;
    white-space: pre;
    word-wrap: normal;
    line-height: inherit;
    color: inherit;
    z-index: 2;
    position: relative;
    overflow: visible;
    -webkit-tap-highlight-color: transparent;
    -webkit-font-variant-ligatures: contextual;
    font-variant-ligatures: contextual;
  }
  .CodeMirror-wrap pre.CodeMirror-line,
  .CodeMirror-wrap pre.CodeMirror-line-like {
    word-wrap: break-word;
    white-space: pre-wrap;
    word-break: normal;
  }

  .CodeMirror-linebackground {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 0;
  }

  .CodeMirror-linewidget {
    position: relative;
    z-index: 2;
    padding: 0.1px; /* Force widget margins to stay inside of the container */
  }

  .CodeMirror-widget {
  }

  .CodeMirror-rtl pre {
    direction: rtl;
  }

  .CodeMirror-code {
    outline: none;
  }

  /* Force content-box sizing for the elements where we expect it */
  .CodeMirror-scroll,
  .CodeMirror-sizer,
  .CodeMirror-gutter,
  .CodeMirror-gutters,
  .CodeMirror-linenumber {
    -moz-box-sizing: content-box;
    box-sizing: content-box;
  }

  .CodeMirror-measure {
    position: absolute;
    width: 100%;
    height: 0;
    overflow: hidden;
    visibility: hidden;
  }

  .CodeMirror-cursor {
    position: absolute;
    pointer-events: none;
  }
  .CodeMirror-measure pre {
    position: static;
  }

  div.CodeMirror-cursors {
    visibility: hidden;
    position: relative;
    z-index: 3;
  }
  div.CodeMirror-dragcursors {
    visibility: visible;
  }

  .CodeMirror-focused div.CodeMirror-cursors {
    visibility: visible;
  }

  .CodeMirror-selected {
    background: #d9d9d9;
  }
  .CodeMirror-focused .CodeMirror-selected {
    background: #d7d4f0;
  }
  .CodeMirror-crosshair {
    cursor: crosshair;
  }

  .cm-searching {
    background-color: #ffa;
    background-color: rgba(255, 255, 0, 0.4);
  }

  /* Used to force a border model for a node */
  .cm-force-border {
    padding-right: 0.1px;
  }

  @media print {
    /* Hide the cursor when printing */
    .CodeMirror div.CodeMirror-cursors {
      visibility: hidden;
    }
  }

  .CodeMirror.CodeMirror-Readonly div.CodeMirror-cursors {
    visibility: hidden;
  }

  /* See issue #2901 */
  .cm-tab-wrap-hack:after {
    content: "";
  }

  /* Help users use markselection to safely style text background */
  span.CodeMirror-selectedtext {
    background: none;
  }
`;

// src/extensions/code-block/styles/index.ts
var codeBlockStyles = css5`
  ${codemirrorStyles}
  ${codeBlockSharedStyles}
  ${cadmusDarkCodeBlockStyles}
  ${cadmusLightCodeBlockStyles}
`;

// src/extensions/code/styles.ts
import { css as css6 } from "styled-components";
import { colors as colors4 } from "@vericus/cadmus-ui";
var codeStyles = css6`
  code {
    font-family: ibm-plex-mono, ui-monospace, monospace;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    border: 1px solid ${colors4.grey50};
    line-height: 24px;
    color: ${colors4.indigo800};
    background: #eff2f6;
    border-radius: 3px;
    padding: 1px 4px 1.5px 4px;
  }

  h1 > code {
    font-size: calc(${editorTypography.headingOne.fontSize} - 1px);
    font-weight: 600;
  }

  h2 > code {
    font-size: calc(${editorTypography.headingTwo.fontSize} - 1px);
    font-weight: 600;
  }

  h3 > code {
    font-size: calc(${editorTypography.headingThree.fontSize} - 1px);
    font-weight: 600;
  }

  h4 > code {
    font-size: calc(${editorTypography.headingFour.fontSize} - 1px);
    font-weight: 600;
  }

  h5 > code {
    font-size: calc(${editorTypography.headingFive.fontSize} - 1px);
    font-weight: 600;
  }

  h6 > code {
    font-size: calc(${editorTypography.headingSix.fontSize} - 1px);
    font-weight: 600;
  }
`;

// src/extensions/math/styles.ts
import { css as css7 } from "styled-components";
import { colors as colors5 } from "@vericus/cadmus-ui";
var mathStyles = css7`
  figure[data-math-group] {
    width: 90%;
    max-width: 710px;
    box-sizing: border-box;
  }

  .empty-math-node [data-node-view-wrapper]::after {
    content: " ";
    display: inline-block;
    pointer-events: none;
    width: 100%;
    background-position: center;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background-repeat: no-repeat;
  }

  .node-inlineMath.empty-math-node [data-node-view-wrapper]::after {
    background-image: url("data:image/svg+xml, %3Csvg%20width=%22191%22%20height=%2232%22%20viewBox=%220%200%20191%2032%22%20fill=%22none%22%20xmlns=%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Crect%20x=%220.0965576%22%20width=%22190%22%20height=%2232%22%20rx=%223%22%20fill=%22%23F4F5F8%22%20fill-opacity=%220.5%22%2F%3E%0A%3Cpath%20d=%22M31.5686%209.864V21H33.0406V9.864H31.5686ZM35.6466%2021H37.0546V16.712C37.0546%2015.08%2037.6626%2013.912%2039.1986%2013.912C40.4306%2013.912%2040.9106%2014.728%2040.9106%2015.96V21H42.3186V15.784C42.3186%2013.624%2041.1346%2012.696%2039.5986%2012.696C38.0306%2012.696%2037.3746%2013.528%2037.0546%2014.216C37.0546%2013.976%2037.0386%2013.128%2036.9906%2012.888H35.6306C35.6466%2013.512%2035.6466%2014.232%2035.6466%2014.936V21ZM44.0109%2018.68C44.2189%2020.136%2045.2589%2021.192%2047.3549%2021.192C49.3709%2021.192%2050.5069%2020.152%2050.5069%2018.728C50.5069%2017.528%2050.0109%2016.712%2047.5469%2016.12C46.0589%2015.768%2045.7229%2015.464%2045.7229%2014.856C45.7229%2014.328%2046.0749%2013.784%2047.2109%2013.784C48.4429%2013.784%2048.8429%2014.52%2048.9709%2015.032H50.3149C50.2189%2013.912%2049.3549%2012.696%2047.2589%2012.696C45.1789%2012.696%2044.3309%2013.832%2044.3309%2014.968C44.3309%2016.072%2044.8109%2016.792%2047.1309%2017.352C48.6829%2017.72%2049.0509%2018.072%2049.0509%2018.84C49.0509%2019.576%2048.5869%2020.088%2047.4029%2020.088C46.2989%2020.088%2045.6429%2019.56%2045.4189%2018.68H44.0109ZM59.0003%2017.192C59.0163%2016.984%2059.0163%2016.776%2059.0163%2016.52C59.0163%2014.792%2058.1683%2012.696%2055.5443%2012.696C53.0803%2012.696%2051.8643%2014.616%2051.8643%2017.032C51.8643%2019.112%2052.9043%2021.192%2055.4483%2021.192C57.6723%2021.192%2058.5523%2019.8%2058.8723%2018.76H57.4483C57.1923%2019.352%2056.7763%2020.024%2055.4963%2020.024C54.1203%2020.024%2053.2883%2018.84%2053.2723%2017.192H59.0003ZM53.3203%2016.216C53.4643%2014.792%2054.0883%2013.8%2055.5283%2013.8C56.9043%2013.8%2057.5763%2014.872%2057.5923%2016.216H53.3203ZM60.9153%2021H62.3233V17.064C62.3233%2015.016%2063.3793%2014.136%2065.0433%2014.088V12.696C63.6993%2012.728%2062.7393%2013.448%2062.3233%2014.536C62.3233%2013.752%2062.2913%2013.112%2062.2753%2012.888H60.8993C60.8993%2013.32%2060.9153%2013.96%2060.9153%2015.096V21ZM66.0378%2014.024H67.4458V19.112C67.4458%2020.456%2067.9898%2021.112%2069.2538%2021.112C69.6538%2021.112%2070.1338%2021.096%2070.4698%2020.968V19.912C70.2458%2019.96%2069.9578%2019.976%2069.7658%2019.976C69.0298%2019.976%2068.8378%2019.576%2068.8378%2018.776V14.024H70.6298V12.888H68.8378V10.488H67.4458V12.888H66.0378V14.024ZM76.4341%2021H77.8421V12.888H76.4341V21ZM77.8421%2011V9.256H76.4341V11H77.8421ZM80.3309%2021H81.7389V16.712C81.7389%2015.08%2082.3469%2013.912%2083.8829%2013.912C85.1149%2013.912%2085.5949%2014.728%2085.5949%2015.96V21H87.0029V15.784C87.0029%2013.624%2085.8189%2012.696%2084.2829%2012.696C82.7149%2012.696%2082.0589%2013.528%2081.7389%2014.216C81.7389%2013.976%2081.7229%2013.128%2081.6749%2012.888H80.3149C80.3309%2013.512%2080.3309%2014.232%2080.3309%2014.936V21ZM90.9014%2021V9.256H89.4934V21H90.9014ZM93.3903%2021H94.7983V12.888H93.3903V21ZM94.7983%2011V9.256H93.3903V11H94.7983ZM97.2872%2021H98.6952V16.712C98.6952%2015.08%2099.3032%2013.912%20100.839%2013.912C102.071%2013.912%20102.551%2014.728%20102.551%2015.96V21H103.959V15.784C103.959%2013.624%20102.775%2012.696%20101.239%2012.696C99.6712%2012.696%2099.0152%2013.528%2098.6952%2014.216C98.6952%2013.976%2098.6792%2013.128%2098.6312%2012.888H97.2712C97.2872%2013.512%2097.2872%2014.232%2097.2872%2014.936V21ZM113.01%2017.192C113.026%2016.984%20113.026%2016.776%20113.026%2016.52C113.026%2014.792%20112.178%2012.696%20109.554%2012.696C107.09%2012.696%20105.874%2014.616%20105.874%2017.032C105.874%2019.112%20106.914%2021.192%20109.458%2021.192C111.682%2021.192%20112.562%2019.8%20112.882%2018.76H111.458C111.202%2019.352%20110.786%2020.024%20109.506%2020.024C108.13%2020.024%20107.298%2018.84%20107.282%2017.192H113.01ZM107.33%2016.216C107.474%2014.792%20108.098%2013.8%20109.538%2013.8C110.914%2013.8%20111.586%2014.872%20111.602%2016.216H107.33ZM125.632%2017.192C125.648%2016.984%20125.648%2016.776%20125.648%2016.52C125.648%2014.792%20124.8%2012.696%20122.176%2012.696C119.712%2012.696%20118.496%2014.616%20118.496%2017.032C118.496%2019.112%20119.536%2021.192%20122.08%2021.192C124.304%2021.192%20125.184%2019.8%20125.504%2018.76H124.08C123.824%2019.352%20123.408%2020.024%20122.128%2020.024C120.752%2020.024%20119.92%2018.84%20119.904%2017.192H125.632ZM119.952%2016.216C120.096%2014.792%20120.72%2013.8%20122.16%2013.8C123.536%2013.8%20124.208%2014.872%20124.224%2016.216H119.952ZM133%2024.184H134.36V15.128C134.36%2014.392%20134.36%2013.736%20134.376%2012.888H133.048C133.016%2013.224%20132.984%2013.624%20133%2013.912C132.808%2013.384%20132.136%2012.696%20130.632%2012.696C128.68%2012.696%20127.064%2014.312%20127.064%2017.032C127.064%2019.736%20128.584%2021.192%20130.424%2021.192C131.832%2021.192%20132.584%2020.68%20133%2019.832V24.184ZM128.52%2016.968C128.52%2015.176%20129.304%2013.896%20130.824%2013.896C132.568%2013.896%20133.032%2015%20133.032%2016.904C133.032%2018.792%20132.52%2020.008%20130.76%2020.008C129.224%2020.008%20128.52%2018.616%20128.52%2016.968ZM143.377%2012.888H141.969V17.112C141.969%2019.144%20141.393%2019.976%20139.905%2019.976C138.561%2019.976%20138.225%2018.984%20138.225%2017.928V12.888H136.833V18.216C136.833%2020.504%20138.145%2021.192%20139.521%2021.192C140.801%2021.192%20141.569%2020.68%20141.969%2019.784C141.985%2020.296%20142.001%2020.792%20142.033%2021H143.393C143.393%2020.52%20143.377%2019.432%20143.377%2018.632V12.888ZM151.92%2015.352C151.92%2013.512%20150.816%2012.696%20148.832%2012.696C146.496%2012.696%20145.808%2013.976%20145.696%2015.096H147.056C147.2%2014.472%20147.44%2013.8%20148.784%2013.8C150.304%2013.8%20150.528%2014.664%20150.528%2015.384V16.072H149.152C146.736%2016.072%20145.312%2016.904%20145.312%2018.712C145.312%2019.944%20146.128%2021.192%20148.048%2021.192C149.616%2021.192%20150.272%2020.472%20150.56%2019.944C150.576%2020.44%20150.624%2020.824%20150.688%2021H152.032C151.984%2020.76%20151.92%2019.976%20151.92%2019.224V15.352ZM150.544%2017.304C150.544%2018.776%20150.288%2020.088%20148.336%2020.088C147.328%2020.088%20146.752%2019.48%20146.752%2018.632C146.752%2017.496%20147.616%2017.048%20149.232%2017.048H150.544V17.304ZM153.291%2014.024H154.699V19.112C154.699%2020.456%20155.243%2021.112%20156.507%2021.112C156.907%2021.112%20157.387%2021.096%20157.723%2020.968V19.912C157.499%2019.96%20157.211%2019.976%20157.019%2019.976C156.283%2019.976%20156.091%2019.576%20156.091%2018.776V14.024H157.883V12.888H156.091V10.488H154.699V12.888H153.291V14.024ZM159.478%2021H160.886V12.888H159.478V21ZM160.886%2011V9.256H159.478V11H160.886ZM166.623%2012.696C164.175%2012.696%20162.799%2014.552%20162.799%2016.952C162.799%2019.4%20164.207%2021.192%20166.527%2021.192C169.007%2021.192%20170.351%2019.272%20170.351%2016.92C170.351%2014.376%20168.879%2012.696%20166.623%2012.696ZM166.559%2013.864C168.079%2013.864%20168.911%2015.128%20168.911%2016.952C168.911%2018.792%20168.063%2020.024%20166.591%2020.024C165.135%2020.024%20164.239%2018.728%20164.239%2016.936C164.239%2015.096%20165.039%2013.864%20166.559%2013.864ZM172.272%2021H173.68V16.712C173.68%2015.08%20174.288%2013.912%20175.824%2013.912C177.056%2013.912%20177.536%2014.728%20177.536%2015.96V21H178.944V15.784C178.944%2013.624%20177.76%2012.696%20176.224%2012.696C174.656%2012.696%20174%2013.528%20173.68%2014.216C173.68%2013.976%20173.664%2013.128%20173.616%2012.888H172.256C172.272%2013.512%20172.272%2014.232%20172.272%2014.936V21Z%22%20fill=%22%235B6F98%22%2F%3E%0A%3Cpath%20d=%22M12.7011%2021.0403C12.4676%2021.0403%2012.2925%2020.9694%2012.1758%2020.8277C12.0591%2020.686%2011.9424%2020.3441%2011.8257%2019.8022L11.5755%2018.6392L10.6876%2019.8898C10.4292%2020.2566%2010.1874%2020.5359%209.96229%2020.7277C9.74552%2020.9111%209.49958%2021.0028%209.22445%2021.0028C9.05771%2021.0028%208.92431%2020.9569%208.82426%2020.8652C8.72422%2020.7652%208.67419%2020.6318%208.67419%2020.465C8.67419%2020.315%208.72422%2020.1899%208.82426%2020.0899C8.91597%2019.9898%209.03269%2019.9398%209.17443%2019.9398C9.29115%2019.9398%209.39536%2019.969%209.48707%2020.0273C9.59545%2020.094%209.69967%2020.1274%209.79972%2020.1274C9.96646%2020.1274%2010.1582%2019.9815%2010.375%2019.6897L11.488%2018.139L11.1128%2016.3256C11.0545%2016.0588%2010.9669%2015.8671%2010.8502%2015.7503C10.7418%2015.6336%2010.5751%2015.5753%2010.35%2015.5753C10.2416%2015.5753%2010.1374%2015.5794%2010.0373%2015.5878C9.94562%2015.5961%209.85808%2015.6086%209.7747%2015.6253L9.81222%2015.3877C10.3041%2015.3293%2010.7001%2015.2459%2011.0003%2015.1376C11.142%2015.0875%2011.2629%2015.0542%2011.3629%2015.0375C11.4713%2015.0125%2011.5547%2015%2011.6131%2015C11.7631%2015%2011.8632%2015.1209%2011.9132%2015.3627L12.2634%2017.051C12.6135%2016.5424%2012.9012%2016.1422%2013.1263%2015.8504C13.3514%2015.5503%2013.5098%2015.3585%2013.6015%2015.2751C13.6932%2015.1834%2013.7932%2015.1209%2013.9016%2015.0875C14.0183%2015.0459%2014.1392%2015.025%2014.2643%2015.025C14.6561%2015.025%2014.8521%2015.1918%2014.8521%2015.5252C14.8521%2015.6753%2014.8062%2015.7962%2014.7145%2015.8879C14.6228%2015.9796%2014.5061%2016.0255%2014.3643%2016.0255C14.2393%2016.0255%2014.1267%2015.9963%2014.0267%2015.9379C13.9683%2015.9046%2013.9183%2015.8837%2013.8766%2015.8754C13.8433%2015.8671%2013.8057%2015.8629%2013.7641%2015.8629C13.6807%2015.8629%2013.589%2015.9046%2013.4889%2015.988C13.4139%2016.0547%2013.0345%2016.5674%2012.3509%2017.5262L12.7261%2019.2645C12.7678%2019.4229%2012.8053%2019.5646%2012.8386%2019.6897C12.872%2019.8064%2012.9053%2019.9064%2012.9387%2019.9898C13.0137%2020.1566%2013.1221%2020.2399%2013.2638%2020.2399C13.4973%2020.2399%2013.8308%2019.9231%2014.2643%2019.2895L14.4394%2019.4896C13.7557%2020.5234%2013.1763%2021.0403%2012.7011%2021.0403Z%22%20fill=%22%235B6F98%22%20stroke=%22%235B6F98%22%20stroke-width=%220.6%22%2F%3E%0A%3Cpath%20d=%22M18.0431%2021.8063H17.0832C16.9508%2021.8063%2016.8433%2021.6993%2016.8433%2021.5674C16.8433%2021.4355%2016.9508%2021.3285%2017.0832%2021.3285H18.0431C18.1756%2021.3285%2018.2831%2021.2214%2018.2831%2021.0895V11.0546C18.283%2010.9226%2018.1755%2010.8156%2018.0431%2010.8156H17.0832C16.9508%2010.8156%2016.8433%2010.7086%2016.8433%2010.5767C16.8433%2010.4448%2016.9508%2010.3378%2017.0832%2010.3378H18.0431C18.44%2010.3378%2018.763%2010.6594%2018.763%2011.0546V21.0895C18.763%2021.4847%2018.44%2021.8063%2018.0431%2021.8063Z%22%20fill=%22%235B6F98%22%20stroke=%22%235B6F98%22%20stroke-width=%220.65%22%2F%3E%0A%3Cpath%20d=%22M19.9178%2021.8063H18.958C18.5611%2021.8063%2018.2381%2021.4847%2018.2381%2021.0895V11.0546C18.2381%2010.6594%2018.5611%2010.3378%2018.958%2010.3378H19.9178C20.0503%2010.3378%2020.1578%2010.4448%2020.1578%2010.5767C20.1578%2010.7086%2020.0503%2010.8156%2019.9178%2010.8156H18.958C18.8255%2010.8156%2018.718%2010.9226%2018.718%2011.0546V21.0895C18.718%2021.2214%2018.8255%2021.3285%2018.958%2021.3285H19.9178C20.0503%2021.3285%2020.1578%2021.4355%2020.1578%2021.5674C20.1578%2021.6993%2020.0503%2021.8063%2019.9178%2021.8063Z%22%20fill=%22%235B6F98%22%20stroke=%22%235B6F98%22%20stroke-width=%220.65%22%2F%3E%0A%3Cpath%20d=%22M17.7517%2016.072H19.2517%22%20stroke=%22%235B6F98%22%20stroke-width=%220.75%22%20stroke-linecap=%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A");
  }

  .node-inlineMath.empty-math-node math-field {
    min-width: 190px;
  }

  .node-blockMath.empty-math-node [data-node-view-wrapper]::after {
    background-image: url("data:image/svg+xml, %3Csvg%20width=%22175%22%20height=%2226%22%20viewBox=%220%200%20175%2026%22%20fill=%22none%22%20xmlns=%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d=%22M12.7966%2019.0403C12.5632%2019.0403%2012.3881%2018.9694%2012.2714%2018.8277C12.1547%2018.686%2012.038%2018.3441%2011.9212%2017.8022L11.6711%2016.6392L10.7832%2017.8898C10.5248%2018.2566%2010.283%2018.5359%2010.0579%2018.7277C9.84111%2018.9111%209.59516%2019.0028%209.32003%2019.0028C9.15329%2019.0028%209.01989%2018.9569%208.91984%2018.8652C8.8198%2018.7652%208.76978%2018.6318%208.76978%2018.465C8.76978%2018.315%208.8198%2018.1899%208.91984%2018.0899C9.01155%2017.9898%209.12827%2017.9398%209.27001%2017.9398C9.38673%2017.9398%209.49094%2017.969%209.58265%2018.0273C9.69104%2018.094%209.79525%2018.1274%209.8953%2018.1274C10.062%2018.1274%2010.2538%2017.9815%2010.4706%2017.6897L11.5836%2016.139L11.2084%2014.3256C11.15%2014.0588%2011.0625%2013.8671%2010.9458%2013.7503C10.8374%2013.6336%2010.6707%2013.5753%2010.4456%2013.5753C10.3372%2013.5753%2010.233%2013.5794%2010.1329%2013.5878C10.0412%2013.5961%209.95366%2013.6086%209.87029%2013.6253L9.9078%2013.3877C10.3997%2013.3293%2010.7957%2013.2459%2011.0959%2013.1376C11.2376%2013.0875%2011.3585%2013.0542%2011.4585%2013.0375C11.5669%2013.0125%2011.6503%2013%2011.7086%2013C11.8587%2013%2011.9588%2013.1209%2012.0088%2013.3627L12.3589%2015.051C12.7091%2014.5424%2012.9967%2014.1422%2013.2218%2013.8504C13.4469%2013.5503%2013.6053%2013.3585%2013.6971%2013.2751C13.7888%2013.1834%2013.8888%2013.1209%2013.9972%2013.0875C14.1139%2013.0459%2014.2348%2013.025%2014.3599%2013.025C14.7517%2013.025%2014.9476%2013.1918%2014.9476%2013.5252C14.9476%2013.6753%2014.9018%2013.7962%2014.8101%2013.8879C14.7184%2013.9796%2014.6016%2014.0255%2014.4599%2014.0255C14.3349%2014.0255%2014.2223%2013.9963%2014.1223%2013.9379C14.0639%2013.9046%2014.0139%2013.8837%2013.9722%2013.8754C13.9388%2013.8671%2013.9013%2013.8629%2013.8596%2013.8629C13.7763%2013.8629%2013.6846%2013.9046%2013.5845%2013.988C13.5095%2014.0547%2013.1301%2014.5674%2012.4465%2015.5262L12.8217%2017.2645C12.8633%2017.4229%2012.9009%2017.5646%2012.9342%2017.6897C12.9676%2017.8064%2013.0009%2017.9064%2013.0343%2017.9898C13.1093%2018.1566%2013.2177%2018.2399%2013.3594%2018.2399C13.5928%2018.2399%2013.9263%2017.9231%2014.3599%2017.2895L14.5349%2017.4896C13.8513%2018.5234%2013.2719%2019.0403%2012.7966%2019.0403Z%22%20fill=%22%235B6F98%22%20stroke=%22%235B6F98%22%20stroke-width=%220.6%22%2F%3E%0A%3Cpath%20fill-rule=%22evenodd%22%20clip-rule=%22evenodd%22%20d=%22M9.50803%208.7435H14.8501V7.37574L8.54773%207.24897L8.27615%207.23468L4.89584%2016.8778L3.55365%2012.97H0.943359V14.6541H2.28346L4.18143%2019.9965H5.59112L9.50803%208.7435Z%22%20fill=%22%235B6F98%22%2F%3E%0A%3Cpath%20d=%22M25.9486%205.864V17H27.4206V5.864H25.9486ZM30.0266%2017H31.4346V12.712C31.4346%2011.08%2032.0426%209.912%2033.5786%209.912C34.8106%209.912%2035.2906%2010.728%2035.2906%2011.96V17H36.6986V11.784C36.6986%209.624%2035.5146%208.696%2033.9786%208.696C32.4106%208.696%2031.7546%209.528%2031.4346%2010.216C31.4346%209.976%2031.4186%209.128%2031.3706%208.888H30.0106C30.0266%209.512%2030.0266%2010.232%2030.0266%2010.936V17ZM38.3909%2014.68C38.5989%2016.136%2039.6389%2017.192%2041.7349%2017.192C43.7509%2017.192%2044.8869%2016.152%2044.8869%2014.728C44.8869%2013.528%2044.3909%2012.712%2041.9269%2012.12C40.4389%2011.768%2040.1029%2011.464%2040.1029%2010.856C40.1029%2010.328%2040.4549%209.784%2041.5909%209.784C42.8229%209.784%2043.2229%2010.52%2043.3509%2011.032H44.6949C44.5989%209.912%2043.7349%208.696%2041.6389%208.696C39.5589%208.696%2038.7109%209.832%2038.7109%2010.968C38.7109%2012.072%2039.1909%2012.792%2041.5109%2013.352C43.0629%2013.72%2043.4309%2014.072%2043.4309%2014.84C43.4309%2015.576%2042.9669%2016.088%2041.7829%2016.088C40.6789%2016.088%2040.0229%2015.56%2039.7989%2014.68H38.3909ZM53.3803%2013.192C53.3963%2012.984%2053.3963%2012.776%2053.3963%2012.52C53.3963%2010.792%2052.5483%208.696%2049.9243%208.696C47.4603%208.696%2046.2443%2010.616%2046.2443%2013.032C46.2443%2015.112%2047.2843%2017.192%2049.8283%2017.192C52.0523%2017.192%2052.9323%2015.8%2053.2523%2014.76H51.8283C51.5723%2015.352%2051.1563%2016.024%2049.8763%2016.024C48.5003%2016.024%2047.6683%2014.84%2047.6523%2013.192H53.3803ZM47.7003%2012.216C47.8443%2010.792%2048.4683%209.8%2049.9083%209.8C51.2843%209.8%2051.9563%2010.872%2051.9723%2012.216H47.7003ZM55.2953%2017H56.7033V13.064C56.7033%2011.016%2057.7593%2010.136%2059.4233%2010.088V8.696C58.0793%208.728%2057.1193%209.448%2056.7033%2010.536C56.7033%209.752%2056.6713%209.112%2056.6553%208.888H55.2793C55.2793%209.32%2055.2953%209.96%2055.2953%2011.096V17ZM60.4178%2010.024H61.8258V15.112C61.8258%2016.456%2062.3698%2017.112%2063.6338%2017.112C64.0338%2017.112%2064.5138%2017.096%2064.8498%2016.968V15.912C64.6258%2015.96%2064.3378%2015.976%2064.1458%2015.976C63.4098%2015.976%2063.2178%2015.576%2063.2178%2014.776V10.024H65.0098V8.888H63.2178V6.488H61.8258V8.888H60.4178V10.024ZM70.8141%2014.28C70.8141%2015.176%2070.8141%2016.088%2070.7981%2017H72.1421C72.1741%2016.776%2072.2061%2016.344%2072.2061%2015.928C72.6061%2016.712%2073.2941%2017.192%2074.5901%2017.192C76.8781%2017.192%2078.1261%2015.24%2078.1261%2012.776C78.1261%2010.376%2076.8461%208.696%2074.7981%208.696C73.3261%208.696%2072.5741%209.352%2072.2221%2010.04V5.256H70.8141V14.28ZM76.6861%2012.808C76.6861%2014.776%2075.8381%2016.008%2074.3981%2016.008C72.6861%2016.008%2072.1421%2014.664%2072.1421%2012.968C72.1421%2011.032%2072.7501%209.88%2074.4781%209.88C75.8861%209.88%2076.6861%2011.048%2076.6861%2012.808ZM81.4627%2017V5.256H80.0547V17H81.4627ZM87.1996%208.696C84.7516%208.696%2083.3756%2010.552%2083.3756%2012.952C83.3756%2015.4%2084.7836%2017.192%2087.1036%2017.192C89.5836%2017.192%2090.9276%2015.272%2090.9276%2012.92C90.9276%2010.376%2089.4556%208.696%2087.1996%208.696ZM87.1356%209.864C88.6556%209.864%2089.4876%2011.128%2089.4876%2012.952C89.4876%2014.792%2088.6396%2016.024%2087.1676%2016.024C85.7116%2016.024%2084.8156%2014.728%2084.8156%2012.936C84.8156%2011.096%2085.6156%209.864%2087.1356%209.864ZM97.9502%2014.52C97.6142%2015.48%2097.0222%2016.024%2096.0142%2016.024C94.6062%2016.024%2093.8222%2014.792%2093.8222%2012.936C93.8222%2011.096%2094.6382%209.864%2096.0622%209.864C97.2462%209.864%2097.7582%2010.584%2097.9502%2011.4H99.3422C99.2462%2010.36%2098.4302%208.696%2096.0942%208.696C93.6782%208.696%2092.3822%2010.696%2092.3822%2012.984C92.3822%2015.592%2093.7582%2017.192%2095.9982%2017.192C98.0622%2017.192%2099.0062%2015.912%2099.3422%2014.52H97.9502ZM102.544%205.256H101.136V17H102.544V14.12L103.76%2012.952L106.48%2017H108.128L104.72%2012.04L107.776%208.888H106.096C105.008%2010.088%20103.216%2011.96%20102.544%2012.76V5.256ZM120.021%2013.192C120.037%2012.984%20120.037%2012.776%20120.037%2012.52C120.037%2010.792%20119.189%208.696%20116.565%208.696C114.101%208.696%20112.885%2010.616%20112.885%2013.032C112.885%2015.112%20113.925%2017.192%20116.469%2017.192C118.693%2017.192%20119.573%2015.8%20119.893%2014.76H118.469C118.213%2015.352%20117.797%2016.024%20116.517%2016.024C115.141%2016.024%20114.309%2014.84%20114.293%2013.192H120.021ZM114.341%2012.216C114.485%2010.792%20115.109%209.8%20116.549%209.8C117.925%209.8%20118.597%2010.872%20118.613%2012.216H114.341ZM127.39%2020.184H128.75V11.128C128.75%2010.392%20128.75%209.736%20128.766%208.888H127.438C127.406%209.224%20127.374%209.624%20127.39%209.912C127.198%209.384%20126.526%208.696%20125.022%208.696C123.07%208.696%20121.454%2010.312%20121.454%2013.032C121.454%2015.736%20122.974%2017.192%20124.814%2017.192C126.222%2017.192%20126.974%2016.68%20127.39%2015.832V20.184ZM122.91%2012.968C122.91%2011.176%20123.694%209.896%20125.214%209.896C126.958%209.896%20127.422%2011%20127.422%2012.904C127.422%2014.792%20126.91%2016.008%20125.15%2016.008C123.614%2016.008%20122.91%2014.616%20122.91%2012.968ZM137.767%208.888H136.359V13.112C136.359%2015.144%20135.783%2015.976%20134.295%2015.976C132.951%2015.976%20132.615%2014.984%20132.615%2013.928V8.888H131.223V14.216C131.223%2016.504%20132.535%2017.192%20133.911%2017.192C135.191%2017.192%20135.959%2016.68%20136.359%2015.784C136.375%2016.296%20136.391%2016.792%20136.423%2017H137.783C137.783%2016.52%20137.767%2015.432%20137.767%2014.632V8.888ZM146.309%2011.352C146.309%209.512%20145.205%208.696%20143.221%208.696C140.885%208.696%20140.197%209.976%20140.085%2011.096H141.445C141.589%2010.472%20141.829%209.8%20143.173%209.8C144.693%209.8%20144.917%2010.664%20144.917%2011.384V12.072H143.541C141.125%2012.072%20139.701%2012.904%20139.701%2014.712C139.701%2015.944%20140.517%2017.192%20142.437%2017.192C144.005%2017.192%20144.661%2016.472%20144.949%2015.944C144.965%2016.44%20145.013%2016.824%20145.077%2017H146.421C146.373%2016.76%20146.309%2015.976%20146.309%2015.224V11.352ZM144.933%2013.304C144.933%2014.776%20144.677%2016.088%20142.725%2016.088C141.717%2016.088%20141.141%2015.48%20141.141%2014.632C141.141%2013.496%20142.005%2013.048%20143.621%2013.048H144.933V13.304ZM147.68%2010.024H149.088V15.112C149.088%2016.456%20149.632%2017.112%20150.896%2017.112C151.296%2017.112%20151.776%2017.096%20152.112%2016.968V15.912C151.888%2015.96%20151.6%2015.976%20151.408%2015.976C150.672%2015.976%20150.48%2015.576%20150.48%2014.776V10.024H152.272V8.888H150.48V6.488H149.088V8.888H147.68V10.024ZM153.867%2017H155.275V8.888H153.867V17ZM155.275%207V5.256H153.867V7H155.275ZM161.012%208.696C158.564%208.696%20157.188%2010.552%20157.188%2012.952C157.188%2015.4%20158.596%2017.192%20160.916%2017.192C163.396%2017.192%20164.74%2015.272%20164.74%2012.92C164.74%2010.376%20163.268%208.696%20161.012%208.696ZM160.948%209.864C162.468%209.864%20163.3%2011.128%20163.3%2012.952C163.3%2014.792%20162.452%2016.024%20160.98%2016.024C159.524%2016.024%20158.628%2014.728%20158.628%2012.936C158.628%2011.096%20159.428%209.864%20160.948%209.864ZM166.661%2017H168.069V12.712C168.069%2011.08%20168.677%209.912%20170.213%209.912C171.445%209.912%20171.925%2010.728%20171.925%2011.96V17H173.333V11.784C173.333%209.624%20172.149%208.696%20170.613%208.696C169.045%208.696%20168.389%209.528%20168.069%2010.216C168.069%209.976%20168.053%209.128%20168.005%208.888H166.645C166.661%209.512%20166.661%2010.232%20166.661%2010.936V17Z%22%20fill=%22%235B6F98%22%2F%3E%0A%3C%2Fsvg%3E%0A");
  }

  .node-inlineMath,
  .node-blockMath {
    &.ProseMirror-selectednode [data-node-view-wrapper] {
      background: rgba(244, 245, 248, 0.5);
    }
  }

  .math-predelete {
    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
      box-shadow: 0 0 0 1px ${colors5.red500};
      background: #d9264408;
    }
  }
  .node-inlineMath {
    position: relative;
  }

  .node-blockMath {
    position: relative;
    display: flex;
    *[data-node-view-wrapper] {
      flex-grow: 1;
      width: 100%;
    }
  }

  .math-block-wrapper {
    position: relative;
    display: flex;
    flex-grow: 1;
    justify-content: center;
    padding: 24px 0px;
    margin: auto;
    &::after {
      position: absolute;
      right: 8px;
      top: 0;
      display: inline-flex;
      height: 100%;
      align-items: center;
      z-index: 400;
      font-size: 14px;
      line-height: 21px;
      content: var(--math-caption, "");
    }
  }

  .math-node[data-math-latex=""] {
    display: none;
  }
`;

// src/extensions/selection/styles.ts
import { css as css8 } from "styled-components";
var selectionStyles = css8`
  .selection-highlight {
    background: #f0f9ff;
    border-bottom: 2px solid #bae6fd;
    transition:
      background 0.2s ease,
      border 0.2s ease;
  }
`;

// src/extensions/table/styles.ts
import { css as css9 } from "styled-components";
import { colors as colors6 } from "@vericus/cadmus-ui";
var tableStyles = css9`
  figure[data-table-group] {
    color: ${(p) => p.theme.text.main};
    box-sizing: border-box;
    position: relative;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 36px;
    margin-bottom: 36px;

    .table-group-wrapper {
      display: flex;
      flex-direction: column;
    }

    figcaption {
      padding: unset;
      max-width: 100%;
      padding: 0px 45px;
    }
  }

  .tableWrapper {
    display: flex;
    justify-content: center;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    border: 10px;
    clear: both;
    margin: auto;
    margin-top: 20px;
    margin-bottom: 20px;

    blockquote {
      min-width: 0;
      padding-left: 0;
      padding-right: 0;
      * {
        padding-left: 9px;
      }
    }

    tbody > div {
      display: contents;
    }

    p,
    li {
      font-size: 14px; /* Table has smaller font size */
    }

    td,
    th {
      padding-top: 8px;
      padding-bottom: 8px;
    }

    ul,
    ol {
      min-width: unset;
      max-width: unset;
      padding-right: unset;
      padding-inline-start: 20px;
    }

    &[data-table-alternate-background="true"] {
      th {
        background-color: var(--background-color);
        filter: contrast(0.9);
      }
      tr:nth-child(even) > td {
        background-color: transparent;
      }
      tr:nth-child(odd) > td {
        background-color: var(--background-color);
      }
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: -1px;
      bottom: -1px;
      width: 4px;
      z-index: 9999;
      background-color: ${colors6.indigo500};
    }

    .col-insert-handle {
      position: absolute;
      left: -2px;
      top: 0;
      bottom: 0;
      width: 4px;
      z-index: 9999;
      background-color: ${colors6.indigo500};
    }

    .row-insert-handle {
      position: absolute;
      right: 0;
      left: 0;
      top: -2px;
      width: 100%;
      height: 4px;
      z-index: 9999;
      background-color: ${colors6.indigo500};
    }

    .selectedCell:not(.cell-predelete) {
      &:after {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        content: "";
        box-shadow: 0 0 0 1px ${colors6.indigo500};
        background: #6355f610;
      }
    }

    .cell-predelete {
      &:after {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        content: "";
        box-shadow: 0 0 0 1px ${colors6.red500};
        background: #d9264408;
      }
    }
  }

  & {
    td:not(.ProseMirror-widget),
    th:not(.ProseMirror-widget) {
      word-break: break-word;
    }
  }

  /** Table cell borders when editable */
  &[contenteditable="true"] {
    td:not(.ProseMirror-widget),
    th:not(.ProseMirror-widget) {
      border-color: #c3cddf;

      &:not([data-left-border="true"]) {
        border-left-color: ${colors6.grey50};
      }
      &:not([data-right-border="true"]) {
        border-right-color: ${colors6.grey50};
      }
      &:not([data-top-border="true"]) {
        border-top-color: ${colors6.grey50};
      }
      &:not([data-bottom-border="true"]) {
        border-bottom-color: ${colors6.grey50};
      }

      /** APA thick black borders when either top or bottom border is selected */
      &[data-top-border="true"][data-left-border="false"][data-right-border="false"] {
        border-top: 1.25px solid black;
      }
      &[data-bottom-border="true"][data-left-border="false"][data-right-border="false"] {
        border-bottom: 1.25px solid black;
      }
    }
  }

  /** Table cell borders when readonly */
  &[contenteditable="false"] {
    td:not(.ProseMirror-widget),
    th:not(.ProseMirror-widget) {
      border-color: #c3cddf;

      &:not([data-left-border="true"]) {
        border-left-color: transparent;
      }
      &:not([data-right-border="true"]) {
        border-right-color: transparent;
      }
      &:not([data-top-border="true"]) {
        border-top-color: transparent;
      }
      &:not([data-bottom-border="true"]) {
        border-bottom-color: transparent;
      }

      /** APA thick black borders when either top or bottom border is selected */
      &[data-top-border="true"][data-left-border="false"][data-right-border="false"] {
        border-top: 1.25px solid black;
      }
      &[data-bottom-border="true"][data-left-border="false"][data-right-border="false"] {
        border-bottom: 1.25px solid black;
      }
    }
  }

  td,
  th {
    padding: 8px;
    position: relative;
    border: 1px solid;
    text-align: inherit;
    vertical-align: baseline;

    &[data-cadmus-background-color],
    &[data-cadmus-background-color] {
      background-color: var(--background-color) !important;
    }
  }

  div[data-table-footer="true"] {
    width: 100%;
    max-width: 100%;
    margin-right: auto;
    margin-left: auto;
    box-sizing: border-box;
    position: relative;
    margin-top: 16px;
    padding: 0px 45px;
    p {
      font-weight: ${editorTypography["systemSm"].fontWeight};
      font-size: ${editorTypography["systemSm"].fontSize};
      line-height: ${editorTypography["systemSm"].lineHeight};
      letter-spacing: ${editorTypography["systemSm"].letterSpacing};
      padding-top: ${editorTypography["systemSm"].paddingTop};
      padding-bottom: ${editorTypography["systemSm"].paddingBottom};
      min-height: calc(
        ${editorTypography["systemSm"].fontSize} *
          ${editorTypography["systemSm"].lineHeight}
      );
    }
  }

  th {
    background-color: ${colors6.grey50};
    font-weight: 600;
    p,
    h1,
    h2,
    h3,
    h4 {
      font-weight: inherit;
    }
  }
`;

// src/extensions/thread/styles.ts
import { css as css10 } from "styled-components";
var threadStyles = css10`
  @keyframes pendingPulse {
    0%,
    100% {
      background: #fff7e0;
      border-bottom-color: #f2ebd4;
    }
  }

  thread {
    background: #fff7e0;
    border-bottom: 2px solid #f2ebd4;
    transition:
      background 0.2s ease,
      border 0.2s ease;

    &:hover:not([data-thread-resolved="true"]):not(
        [data-thread-pending="true"]
      ) {
      background: #ffeecc;
      border-bottom: 2px solid #e6d9b8;
    }

    &.thread-active:not([data-thread-resolved="true"]):not(
        [data-thread-pending="true"]
      ) {
      background: #ffe697;
      border-bottom: 2px solid #d3bb70;
    }

    & span.thread-active {
      background: #ffe4ad;
      padding: 2px 0;
      border-bottom: 2px solid #f0d295;
    }

    /* Pending thread styles - animated amber pulse */
    &[data-thread-pending="true"] {
      animation: pendingPulse 0.4s ease-in-out infinite;

      &:hover {
        animation-play-state: paused;
        background: #ffeecc;
        border-bottom: 2px solid #e6d9b8;
      }

      &.thread-active {
        animation-play-state: paused;
        background: #ffe697;
        border-bottom: 2px solid #d3bb70;
      }

      /* Element highlighted as thread selected at Marking-Feedback page */
      & span.thread-active {
        background: #ffe4ad;
        border-bottom: 2px solid #f0d295;
      }
    }

    /* Resolved thread styles - pastel green with precedence */
    &[data-thread-resolved="true"] {
      background: #e8f5e8;
      border-bottom: 2px solid #c8e6c8;

      &:hover {
        background: #d4edd4;
        border-bottom: 2px solid #b2d4b2;
      }

      &.thread-active {
        background: #c1e4c1;
        border-bottom: 2px solid #9cc89c;
      }

      /* Element highlighted as thread selected at Marking-Feedback page */
      & span.thread-active {
        background: #c1e4c1;
      }
    }
  }
`;

// src/react/CadmusEditorWrapper.tsx
var MAX_INDENT = 8;
var blockIndentStyles = () => Array.from(Array(MAX_INDENT + 1).keys()).map(
  (indent) => css11`
      & > *[data-indent="${indent}"] {
        padding-left: ${45 + indent * 27}px;
      }

      & > *[data-indent-special="hanging"] {
        &[data-indent="${indent}"] {
          padding-left: ${45 + (indent + 1) * 27}px;
        }
      }
    `
);
var indentation = css11`
  ${blockIndentStyles}
  tab-size: 27px;
  & > *[data-indent-special="hanging"] {
    text-indent: -27px;
    padding-left: calc(45px + 27px);
  }
  & > *[data-indent-special="firstLine"] {
    text-indent: 27px;
  }
  & > *[data-indent-special="normal"] {
    text-indent: 0px;
  }
`;
var blocks = (fullWidth) => css11`
  & > .ProseMirror-gapcursor {
    max-width: ${fullWidth ? "100%" : "800px"};
    min-width: 320px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    padding-left: 45px;
    padding-right: 45px;
    box-sizing: border-box;
    display: inline-block;
  }
  & > h1,
  & > h2,
  & > h3,
  & > h4,
  & > blockquote,
  & > p,
  & > ul,
  & > ol,
  & > hr,
  & > pre {
    padding-right: 45px;
    padding-left: 45px;
    color: ${(p) => p.theme.text.main};
    box-sizing: border-box;
    max-width: ${fullWidth ? "100%" : "min(800px, 100%)"};
    margin-left: auto;
    margin-right: auto;
    position: relative;
    margin-top: 0;
    margin-bottom: 0;
  }

  a,
  a:any-link {
    color: ${(p) => p.theme.text.accent};
  }

  .selected-link {
    color: ${(p) => p.theme.text.accent};
    text-decoration: underline;
  }

  .outer-placeholder {
    white-space: nowrap;
    color: ${(p) => p.theme.text.shade1};
  }

  .inner-placeholder {
    position: absolute;
    left: 0;
    white-space: pre-wrap;
    width: 100%;
    text-indent: initial;
    max-height: 270px;
    overflow: hidden;
    padding: 0 45px;
    box-sizing: border-box;
  }

  p {
    font-weight: ${editorTypography["bodyMd"].fontWeight};
    font-size: ${editorTypography["bodyMd"].fontSize};
    line-height: ${editorTypography["bodyMd"].lineHeight};
    letter-spacing: ${editorTypography["bodyMd"].letterSpacing};
    padding-top: ${editorTypography["bodyMd"].paddingTop};
    padding-bottom: ${editorTypography["bodyMd"].paddingBottom};
    min-height: calc(
      ${editorTypography["bodyMd"].fontSize} *
        ${editorTypography["bodyMd"].lineHeight}
    );
    font-family: ${editorTypography["bodyMd"].fontFamily};
    margin-block-start: 0;
    margin-block-end: 0;
  }

  h1 {
    color: ${(p) => p.theme.text.heading};
    font-weight: ${editorTypography.headingOne.fontWeight};
    font-size: ${editorTypography.headingOne.fontSize};
    line-height: ${editorTypography.headingOne.lineHeight};
    letter-spacing: ${editorTypography.headingOne.letterSpacing};
    padding-top: ${editorTypography.headingOne.paddingTop};
    padding-bottom: ${editorTypography.headingOne.paddingBottom};
    font-family: ${editorTypography.headingOne.fontFamily};
    min-height: calc(
      ${editorTypography.headingOne.fontSize} *
        ${editorTypography.headingOne.lineHeight}
    );
  }

  h2 {
    color: ${(p) => p.theme.text.heading};
    font-weight: ${editorTypography.headingTwo.fontWeight};
    font-size: ${editorTypography.headingTwo.fontSize};
    line-height: ${editorTypography.headingTwo.lineHeight};
    letter-spacing: ${editorTypography.headingTwo.letterSpacing};
    padding-top: ${editorTypography.headingTwo.paddingTop};
    padding-bottom: ${editorTypography.headingTwo.paddingBottom};
    font-family: ${editorTypography.headingTwo.fontFamily};
    min-height: calc(
      ${editorTypography.headingTwo.fontSize} *
        ${editorTypography.headingTwo.lineHeight}
    );
  }

  h3 {
    color: ${(p) => p.theme.text.heading};
    font-weight: ${editorTypography.headingThree.fontWeight};
    font-size: ${editorTypography.headingThree.fontSize};
    line-height: ${editorTypography.headingThree.lineHeight};
    letter-spacing: ${editorTypography.headingThree.letterSpacing};
    padding-top: ${editorTypography.headingThree.paddingTop};
    padding-bottom: ${editorTypography.headingThree.paddingBottom};
    font-family: ${editorTypography.headingThree.fontFamily};
    min-height: calc(
      ${editorTypography.headingThree.fontSize} *
        ${editorTypography.headingThree.lineHeight}
    );
  }

  h4 {
    color: ${(p) => p.theme.text.heading};
    font-weight: ${editorTypography.headingFour.fontWeight};
    font-size: ${editorTypography.headingFour.fontSize};
    line-height: ${editorTypography.headingFour.lineHeight};
    letter-spacing: ${editorTypography.headingFour.letterSpacing};
    padding-top: ${editorTypography.headingFour.paddingTop};
    padding-bottom: ${editorTypography.headingFour.paddingBottom};
    font-family: ${editorTypography.headingFour.fontFamily};
    min-height: calc(
      ${editorTypography.headingFour.fontSize} *
        ${editorTypography.headingFour.lineHeight}
    );
  }

  blockquote {
    max-width: 800px;
    min-width: 320px;
    padding-top: 18px;
    padding-bottom: 18px;
    padding-left: 45px;
    padding-right: 45px;
    box-sizing: border-box;
    margin: 0;
    margin-left: auto;
    margin-right: auto;
    font-family: ${editorTypography.blockquote.fontFamily};
    display: block;
    position: relative;
    & > * {
      margin: 0;
      padding: 0;
      padding-left: 36px;
      border-left: 1px solid ${(p) => p.theme.text.shade1};
      color: ${(p) => p.theme.text.shade1};
    }
  }

  pre {
    padding-top: 18px;
    padding-bottom: 18px;
  }

  hr {
    border: 0;
    height: 1px;
    margin-top: 18px;
    margin-bottom: 18px;
    background-color: ${(p) => p.theme.divider};
  }

  li {
    font-weight: ${editorTypography["bodyMd"].fontWeight};
    font-size: ${editorTypography["bodyMd"].fontSize};
    line-height: ${editorTypography["bodyMd"].lineHeight};
    letter-spacing: ${editorTypography["bodyMd"].letterSpacing};
    padding-top: ${editorTypography["bodyMd"].paddingTop};
    padding-bottom: ${editorTypography["bodyMd"].paddingBottom};
    font-family: ${editorTypography.bodyMd.fontFamily};
    list-style-image: none;

    p {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    padding-inline-start: 40px;
  }

  ul[data-type="taskList"] {
    padding-inline-start: 18px;
  }

  /* First level OLs and ULs are padded */
  & > ol,
  & > ul {
    padding-inline-start: 81px;
  }
  & > ul[data-type="taskList"] {
    padding-inline-start: 63px;
  }

  ol {
    list-style-type: decimal;
    /* Editor layout styles */
    max-width: 800px;
    min-width: 320px;
    padding-right: 45px;
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    display: block;

    /* UnorderedList styles */
    margin-top: 0;
    margin-bottom: 0;
  }

  ol ol {
    list-style-type: lower-alpha;
  }
  ol ol ol {
    list-style-type: lower-roman;
  }
  ol ol ol ol {
    list-style-type: decimal;
  }
  ol ol ol ol ol {
    list-style-type: lower-alpha;
  }
  ol ol ol ol ol ol {
    list-style-type: lower-roman;
  }
  ol ol ol ol ol ol ol {
    list-style-type: decimal;
  }
  ol ol ol ol ol ol ol ol {
    list-style-type: lower-alpha;
  }

  ul {
    list-style-type: disc;
    /* Editor layout styles */
    max-width: min(800px, 100%);
    padding-right: 45px;
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    display: block;
    /* UnorderedList styles */
    margin-top: 0;
    margin-bottom: 0;
  }

  ul[data-type="taskList"] {
    list-style: none;

    & > li[data-checked="true"] {
      & > div > p:first-child {
        text-decoration: line-through;
      }
    }

    & > li {
      display: flex;
      @media print {
        display: list-item;
        margin-inline-start: 16px;
        list-style-position: outside;
        padding-left: 0.6ch;
        & > label {
          display: none;
        }
        list-style-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBzdHlsZT0ic3Ryb2tlLXdpZHRoOiAxcHg7IHN0cm9rZTogYmxhY2s7Ij4KICA8ZyBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgPHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiBmaWxsPSJpbmhlcml0IiBmaWxsLW9wYWNpdHk9Ii43MiIgcng9IjIiIC8+CiAgICA8cmVjdCB3aWR0aD0iMTQiIGhlaWdodD0iMTQiIHg9IjEiIHk9IjEiIGZpbGw9IiNGRkYiIHJ4PSIxIiAvPgogIDwvZz4KPC9zdmc+Cg==");
        &[data-checked="true"] {
          list-style-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZyBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgPHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjE2IiBmaWxsPSIjMDBGIiBmaWxsLW9wYWNpdHk9Ii41NCIgb3BhY2l0eT0iLjU0IiByeD0iMiIgLz4KICAgIDxyZWN0IHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgeD0iMSIgeT0iMSIgZmlsbD0iIzAwRiIgcng9IjEiIC8+CiAgICA8cG9seWdvbiBmaWxsPSIjRkZGIiBwb2ludHM9IjYuNTM1IDkuMDY1IDQuNDggNy4zNCAzIDguODA4IDYuNTM1IDEyIDEzIDUuNDY4IDExLjUyIDQiIC8+CiAgPC9nPgo8L3N2Zz4K");
        }
      }
      margin-top: 9px;

      & > label {
        flex: 0 0 auto;
        margin-right: 0.5rem;
        align-self: baseline;
      }
      & > div {
        max-width: min(800px, 100%);
        &:not(ul[data-type="task-list"], li, label, input) {
          margin: 0;
        }
      }
    }
  }

  ul ul {
    list-style-type: circle;
  }
  ul ul ul {
    list-style-type: square;
  }
  ul ul ul ul {
    list-style-type: disc;
  }
  ul ul ul ul ul {
    list-style-type: circle;
  }
  ul ul ul ul ul ul {
    list-style-type: square;
  }
  ul ul ul ul ul ul ul {
    list-style-type: disc;
  }
  ul ul ul ul ul ul ul ul {
    list-style-type: circle;
  }
`;
var spinner = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
var loader = css11`
  display: block;
  width: 18px;
  height: 18px;
  border-right: 2px solid ${(p) => p.theme.text.shade1};
  border-top: 2px solid ${(p) => p.theme.background.action3};
  border-bottom: 2px solid ${(p) => p.theme.background.action3};
  border-left: 2px solid ${(p) => p.theme.background.action3};
  border-radius: 50%;
  position: absolute;
  top: calc(50% - 9px);
  left: calc(50% - 9px);
  transform: translate(-50%, -50%);
  animation: ${spinner} 1s linear infinite;
`;
var mediaStyles = css11`
  figure {
    display: block;
    margin: 1em auto;
    padding: 0;
    position: relative;
    max-width: 800px;

    figcaption {
      border: 0;
      display: block;
      font-size: 14px;
      line-height: 24px;
      width: 100%;
      max-width: 800px;
      margin: 1em auto 0;
      padding: 0 45px;
      box-sizing: border-box;
      /* for absolute positioning of ::before placeholder */
      position: relative;
      outline: none;
      background: none;
      resize: none;
      user-select: text;

      &.no-caption {
        margin: 0;
        padding: 0;
        font-size: 0;
        /* Resize handles are difficult to hit without this*/
        line-height: 0;
      }
    }

    img:not(.ProseMirror-separator) {
      display: block;
      box-sizing: border-box;
      min-width: 160px;
      width: 100%;
      min-height: 63px;
      object-fit: contain;
      margin-left: auto;
      margin-right: auto;
      cursor: pointer;

      &[data-image-uploading="true"] {
        opacity: 0.36;
      }
    }

    span[data-loading="true"] {
      ${loader}
    }

    &.ProseMirror-selectednode {
      img {
        outline: 1.5px solid ${(p) => p.theme.text.accent};
        outline-offset: 3px;
      }
      outline: none;
    }
  }

  figure[data-loading="true"] {
    img {
      opacity: 0.36;
    }
  }

  :not(.ProseMirror-focused) > figure.ProseMirror-selectednode > img {
    outline: 3px solid ${(p) => p.theme.background.action3};
    outline-offset: 2px;
  }

  figure[data-empty-node] {
    figcaption::before {
      content: "Add a caption...";
      color: ${(p) => p.theme.text.shade1};
      /* place the text behind text cursor */
      z-index: -1;
    }
  }

  figure:not([data-table-group]) {
    page-break-inside: avoid;
  }

  figure {
    &[data-loading],
    &[data-video],
    &[data-audio],
    &[data-image-width="fitToText"],
    &[data-image-width="original"] {
      padding: 0px 45px;
    }

    &[data-video],
    &[data-audio] {
      position: relative;

      &[contenteditable="true"]:hover > .media-floating-menu,
      &[contenteditable="true"].ProseMirror-selectednode > .media-floating-menu,
      &[contenteditable="true"]:focus-within > .media-floating-menu {
        display: flex;
      }
    }
  }

  .media-floating-menu {
    position: absolute;
    top: 4px;
    right: 49px;
    display: none;
    align-items: center;
    gap: 2px;
    z-index: 10;
    background: white;
    box-shadow: 0 0 1px 0 rgba(71, 92, 133, 0.54),
      0 2px 9px -2px rgba(71, 92, 133, 0.15),
      0 12px 12px -3px rgba(71, 92, 133, 0.06);
    border-radius: 6px;
    padding: 2px;

    @media print {
      display: none !important;
    }
  }

  .media-floating-menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 4px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: ${(p) => p.theme.text.shade1};
    cursor: pointer;
    outline: 0;

    &:hover {
      background: ${(p) => p.theme.colors.grey50};
    }

    &:active {
      background: ${(p) => p.theme.colors.grey100};
    }

    &:focus-visible {
      ${(p) => p.theme.focus};
      outline: 0;
    }

    svg {
      display: block;
    }
  }

  figure .media-src-caption {
    display: none;
    width: 100%;
    text-decoration: none;
    text-align: center;
    color: inherit;
    position: relative;
    margin-top: 4px;
    color: ${(p) => p.theme.text.shade2};
    word-break: break-all;

    @media print {
      display: block;
    }
  }

  .img-wrapper {
    position: relative;
    display: block;
    width: 710px; /** 800px - 45px * 2 horizontal padding */
    max-width: 100%;

    &[data-image-width="full"] {
      width: 800px;
      object-fit: cover;
    }

    &[data-image-width="fitToText"] {
      object-fit: contain;
    }

    &[data-image-width="original"] {
      object-fit: contain;
    }

    &[data-image-width="custom"] {
      object-fit: cover;
    }

    &[data-image-align="left"] {
      margin: 0px auto 0px 0px;
    }

    &[data-image-align="right"] {
      margin: 0px 0px 0px auto;
    }

    &[data-image-align="center"] {
      margin: 0px auto;
    }
  }

  .resize-point {
    width: 9px;
    height: 9px;
    background: white;
    border: 1.5px solid ${(p) => p.theme.text.accent};
    box-sizing: border-box;
    border-radius: 2px;
    position: absolute;
  }

  .resize-point--ne {
    left: -7px;
    top: -7px;
    cursor: nwse-resize; /*resize-point cursor*/
  }

  .resize-point--nw {
    right: -7px;
    top: -7px;
    cursor: nesw-resize;
  }

  .resize-point--sw {
    left: -7px;
    bottom: -7px;
    cursor: nesw-resize;
    background: white;
  }

  .resize-point--se {
    right: -7px;
    bottom: -7px;
    cursor: nwse-resize;
  }
`;
var widgetStyles = css11`
  .ProseMirror-widget.image-uploader-error {
    width: 100%;
    padding: 45px;
    box-sizing: border-box;
    max-width: 710px;
    min-height: 99px;
    margin: 1em auto;
    text-align: center;
    background: ${(p) => p.theme.background.action1};

    & > p {
      margin: 18px 0;
    }

    & > img {
      width: 100%;
      max-width: 540px;
    }
  }

  .cui-LinkButton {
    /* Apply editorTypography styles based on inline inheritance or bodySm default */
    font-size: ${editorTypography["bodySm"].fontSize};
    letter-spacing: ${editorTypography["bodySm"].letterSpacing};
    font-weight: initial;
    line-height: ${editorTypography["bodySm"].lineHeight};
    font-family: ${editorTypography.bodySm.fontFamily};

    text-align: left;
    background: transparent;
    text-decoration: none;
    border: none;
    padding: 0;
    outline: 0;
    margin-left: 0;
    margin-right: 0;
    position: relative;
    cursor: pointer;
  }

  .cui-LinkButton > span {
    display: inline;

    color: ${(p) => p.theme.text.accent};
    fill: ${(p) => p.theme.text.accent};
    background-image: linear-gradient(
        to right,
        ${(p) => p.theme.text.accent} 0%,
        ${(p) => p.theme.text.accent} 100%
      ),
      linear-gradient(
        to right,
        ${(p) => p.theme.text.shade2} 0%,
        ${(p) => p.theme.text.shade2} 50%,
        transparent 50%,
        transparent 100%
      );

    background-size:
      0% 1px,
      2px 1px;
    background-repeat: no-repeat, repeat-x;
    background-position: bottom left;

    transform: translateZ(0);
    will-change: background;
    transition: background 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  /* On hover, animate the underline of the LinkButtonText */
  .cui-LinkButton:hover > span {
    background-size:
      100% 1px,
      2px 1px;
  }
  .cui-LinkButton:active > span,
  .cui-LinkButton:focus > span {
    background-size:
      100% 2px,
      2px 1px;
  }
`;
var suggestionStyles = css11`
  .suggestion {
    text-decoration: solid underline red;
    &:hover {
      background: pink;
    }
  }
`;
var blink = keyframes`
  to {
    visibility: hidden;
  }
`;
var CadmusEditorWrapper = styled2.div`
  /* Grow to occupy full height */
  flex: 1;

  .ProseMirror {
    /* Reset numberrings counter */
    counter-reset: blank-counter footnote-node;
    --table-background-color: ${(p) => p.theme.colors.blue200};
    position: relative;
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0; /* the above doesn't seem to work in Edge */
    outline: none;
    white-space: pre-wrap;
    word-break: break-word;
    min-width: 320px;
    font-family: ${fontFamilySans};

    &:focus {
      outline: none;
    }

    ${codeBlockStyles}

    ${codeStyles}

    ${indentation}

    /* Pass the prop inside the blocks */
    ${({ fullWidth }) => blocks(fullWidth)}

    ${tableStyles}

    ${mediaStyles}

    ${mathStyles}

    ${widgetStyles}

    ${suggestionStyles}

    ${threadStyles}

    ${selectionStyles}

    &:not([data-suggestable="false"]) {
      ${suggestionStyles}
    }

    .empty-node {
      &::before {
        content: attr(data-placeholder-text);
        display: inline-block;
        pointer-events: none;
        color: ${(p) => p.theme.text.shade1};
      }
    }

    [data-emoji-text] {
      img.emoji {
        height: 1em;
        width: 1em;
        margin: 0 0.05em 0 0.1em;
        vertical-align: -0.1em;
      }
    }
    /* Style individual blank underlines */
    [data-cadmus-blank] {
      user-select: none;
      color: ${(p) => p.theme.colors.pink800};
      padding: 2px 16px 1px 8px;
      border-radius: 2px;
      background: ${(p) => p.theme.colors.npink200};
      display: inline-block;
      margin: 1.5px 5px;
      counter-increment: blank-counter;
      &:before {
        content: counter(blank-counter) ". ";
      }
    }

    footnote {
      background: #fcf6fc;
      padding: 1px 4px 3px;
      vertical-align: super;
      font-size: 80%;
      counter-increment: footnote-node;

      &:before {
        content: counter(footnote-node);
      }

      &:hover {
        background: ${(p) => p.theme.colors.lilac300};
      }
    }
    /**
     * When the footnote editor is active, don't show the default outline
     * around the footnote superscript.
     */
    footnote.ProseMirror-selectednode.footnote-active {
      outline: none; /* Remove default outline */
    }
    footnote.footnote-active {
      background: #ffe59e;
    }

    .footnote-content {
      display: none;
    }

    [data-cadmus-pasted] {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      vertical-align: middle;
      gap: 3px;
      max-width: 100%;
      padding-left: 9px;
      padding-right: 2px;
      margin: 0 2px;
      box-sizing: border-box;
      border-radius: 12px;
      line-height: 24px;
      text-indent: 0;

      font-size: ${editorTypography["bodySm"].fontSize};
      letter-spacing: ${editorTypography["bodySm"].letterSpacing};
      font-family: ${editorTypography.bodySm.fontFamily};
      white-space: initial;
      word-break: break-all;

      color: ${(p) => p.theme.text.shade1};
      background: ${(p) => p.theme.background.action2};

      .icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        height: 20px;
        width: 20px;
        border-radius: 50%;
        border: none;
        background: transparent;

        cursor: pointer;

        &:hover {
          background: ${(p) => p.theme.background.action3};
        }

        span {
          color: ${(p) => p.theme.text.shade1};
          fill: ${(p) => p.theme.text.shade1};
        }
      }
    }
    &[contenteditable="false"] {
      [data-cadmus-pasted] {
        display: none;
      }
      figure[data-empty-node="true"] {
        figcaption {
          display: none;
        }
      }
      footnote {
        pointer-events: none;
      }
    }
  }

  img.ProseMirror-separator {
    display: inline !important;
    border: none !important;
    margin: 0 !important;
  }

  .ProseMirror-hideselection *::selection {
    background: transparent;
  }

  .ProseMirror-hideselection *::-moz-selection {
    background: transparent;
  }

  .ProseMirror-hideselection {
    caret-color: transparent;
  }

  .ProseMirror-selectednode {
    outline: 2px solid #b3d7fe;
  }

  .ProseMirror-gapcursor {
    display: none;
    pointer-events: none;
    position: absolute;
    margin: 0;
  }

  .ProseMirror-gapcursor:after {
    content: "";
    display: block;
    position: absolute;
    top: -2px;
    width: 20px;
    border-top: 1px solid black;
    animation: ${blink} 1.1s steps(2, start) infinite;
  }

  .ProseMirror-focused .ProseMirror-gapcursor {
    display: block;
  }

  &[data-page-mode="true"]::after {
    content: "";
    display: block;
    margin-top: 108px;
    border-top: 1px solid transparent;
    width: 100%;
  }

  &[data-page-mode="true"]::before {
    content: "";
    display: block;
    margin-bottom: 63px;
    border-bottom: 1px solid transparent;
    width: 100%;
  }

  li::marker {
    font-variant-numeric: normal;
  }

  .resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }

  .ProseMirror-collab-caret {
    position: relative;
    margin-left: -1px;
    margin-right: -1px;
    border: 1px solid var(--caret-color);
    word-break: normal;
    pointer-events: none;
    height: 100%;
  }

  /* This renders the username above the caret */
  .ProseMirror-collab-caret > div {
    position: absolute;
    top: -1.05em;
    left: -1px;
    font-size: 12px;
    font-family: "Open Sauce Two", sans-serif;
    font-style: normal;
    font-weight: normal;
    line-height: normal;
    user-select: none;
    color: white;
    white-space: nowrap;

    padding-left: 4px;
    padding-right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: var(--caret-color);
    color: white;
  }
`;

// src/extensions/audio/audio.ts
import { findParentNode as findParentNode6, Node as Node7 } from "@tiptap/core";
import { Plugin as Plugin18 } from "@tiptap/pm/state";

// src/core/helpers/findSelectedNodeByType.ts
import { findParentNode as findParentNode5, isNodeSelection as isNodeSelection2 } from "@tiptap/core";
import { NodeSelection as NodeSelection2 } from "@tiptap/pm/state";
function findSelectedNodeByType(state, type) {
  const { selection } = state;
  const { $from, $to } = state.selection;
  let nodeSelection;
  let from;
  const predicate = (node) => node.type === state.schema.nodes[type];
  if (isNodeSelection2(selection) && selection.node.type === state.schema.nodes[type]) {
    nodeSelection = selection;
    from = $from.start($from.depth + 1) - 1;
  }
  const parent = findParentNode5(predicate)(selection);
  if ($from.node() === $to.node() && parent) {
    nodeSelection = NodeSelection2.create(state.doc, parent.pos);
    from = parent.pos;
  }
  if (nodeSelection && from !== void 0) {
    return {
      node: nodeSelection.node,
      selection: nodeSelection,
      from
    };
  }
  return null;
}

// src/core/helpers/renderMediaHTML.ts
import { colors as colors7 } from "@vericus/cadmus-ui";
var svgAttrs = {
  xmlns: "http://www.w3.org/2000/svg",
  fill: colors7.icon,
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  "aria-hidden": "true"
};
var OPEN_LINK_SVG = [
  "svg",
  svgAttrs,
  [
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M17.185 5.5H5.5V18.5H18.5V6.81502L12.315 13L11 11.685L17.185 5.5ZM20 14V20H4V4H10L11 5.5H15L14 4H20V10L18.5 9V13L20 14Z"
    }
  ]
];
var VIDEO_SVG = [
  "svg",
  svgAttrs,
  [
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M16.5001 9.4798L21.1918 6.1948C21.2593 6.14751 21.3384 6.11965 21.4206 6.11426C21.5028 6.10886 21.5849 6.12613 21.658 6.1642C21.7311 6.20226 21.7923 6.25966 21.835 6.33012C21.8777 6.40059 21.9002 6.48142 21.9001 6.5638V17.4358C21.9002 17.5182 21.8777 17.599 21.835 17.6695C21.7923 17.74 21.7311 17.7973 21.658 17.8354C21.5849 17.8735 21.5028 17.8908 21.4206 17.8854C21.3384 17.88 21.2593 17.8521 21.1918 17.8048L16.5001 14.5198V18.2998C16.5001 18.5385 16.4053 18.7674 16.2365 18.9362C16.0677 19.105 15.8388 19.1998 15.6001 19.1998H3.0001C2.7614 19.1998 2.53248 19.105 2.3637 18.9362C2.19492 18.7674 2.1001 18.5385 2.1001 18.2998V5.6998C2.1001 5.46111 2.19492 5.23219 2.3637 5.06341C2.53248 4.89463 2.7614 4.7998 3.0001 4.7998H15.6001C15.8388 4.7998 16.0677 4.89463 16.2365 5.06341C16.4053 5.23219 16.5001 5.46111 16.5001 5.6998V9.4798ZM5.7001 8.3998V10.1998H7.5001V8.3998H5.7001Z"
    }
  ]
];
var AUDIO_SVG = [
  "svg",
  svgAttrs,
  [
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M6.5002 15.6002H3.0001C2.7614 15.6002 2.53248 15.5054 2.3637 15.3366C2.19492 15.1678 2.1001 14.9389 2.1001 14.7002V9.30021C2.1001 9.06152 2.19492 8.8326 2.3637 8.66382C2.53248 8.49504 2.7614 8.40021 3.0001 8.40021H6.5002L11.2648 4.50141C11.3307 4.44737 11.4106 4.41316 11.4953 4.40277C11.5799 4.39238 11.6657 4.40624 11.7427 4.44273C11.8198 4.47923 11.8849 4.53685 11.9304 4.6089C11.976 4.68095 12.0002 4.76447 12.0001 4.84971V19.1507C12.0002 19.236 11.976 19.3195 11.9304 19.3915C11.8849 19.4636 11.8198 19.5212 11.7427 19.5577C11.6657 19.5942 11.5799 19.608 11.4953 19.5977C11.4106 19.5873 11.3307 19.5531 11.2648 19.499L6.5011 15.6002H6.5002ZM18.6655 19.3208L17.3911 18.0464C18.2443 17.2873 18.9268 16.3559 19.3938 15.3137C19.8607 14.2716 20.1015 13.1422 20.1001 12.0002C20.1012 10.7997 19.8349 9.614 19.3206 8.52925C18.8062 7.4445 18.0567 6.48793 17.1265 5.72901L18.4045 4.45101C19.5011 5.37941 20.382 6.53585 20.9858 7.83969C21.5895 9.14353 21.9015 10.5634 21.9001 12.0002C21.9001 14.9009 20.6527 17.51 18.6655 19.3208ZM15.4768 16.1321L14.197 14.8523C14.6339 14.5163 14.9877 14.0844 15.231 13.5898C15.4743 13.0953 15.6006 12.5514 15.6001 12.0002C15.6001 10.7132 14.9251 9.58371 13.9081 8.94741L15.2032 7.65231C15.8849 8.15379 16.439 8.80868 16.8207 9.56402C17.2023 10.3194 17.4008 11.1539 17.4001 12.0002C17.4001 13.658 16.6531 15.1412 15.4768 16.1321Z"
    }
  ]
];
function wrapSvg(tag, attrs, svg) {
  return [
    tag,
    {
      ...attrs,
      style: [
        attrs.style || "",
        "display: flex",
        "justify-content: center",
        "align-items: center",
        "width: 40px",
        "height: 40px",
        `background-color: ${colors7.white}`,
        "border-radius: 4px",
        "flex-shrink: 0"
      ].filter(Boolean).join("; ")
    },
    svg
  ];
}
function renderMediaHTML(mediaType, src, hasCaption) {
  const captionTextNode = [
    "span",
    {
      style: [
        "text-overflow: ellipsis",
        "white-space: nowrap",
        "overflow: hidden",
        `color: ${colors7.primary}`
      ].join("; ")
    },
    hasCaption ? ["span", { class: "media-caption" }, 0] : src
  ];
  return [
    "figure",
    {
      [`data-${mediaType}`]: true,
      ...src ? { "data-src": src } : {},
      style: [
        "display: flex",
        "align-items: center",
        "justify-content: space-between",
        "gap: 16px",
        `background-color: ${colors7.bgrey400}`,
        "padding: 8px",
        "border-radius: 4px",
        "margin: 4px 45px"
      ].join("; "),
      class: "media-card"
    },
    wrapSvg("span", {}, mediaType === "video" ? VIDEO_SVG : AUDIO_SVG),
    captionTextNode,
    wrapSvg(
      "a",
      {
        ...src ? { href: src } : {},
        target: "_blank",
        rel: "noreferrer noopener"
      },
      OPEN_LINK_SVG
    )
  ];
}

// src/core/helpers/media-floating-menu.ts
function createMediaFloatingMenu(mediaType, editor, getPos) {
  const menu = document.createElement("div");
  menu.className = "media-floating-menu";
  menu.setAttribute("contenteditable", "false");
  menu.addEventListener("click", (e) => e.stopPropagation());
  menu.addEventListener("mousedown", (e) => {
    e.stopPropagation();
    e.preventDefault();
  });
  function handleToggleCaption() {
    if (!editor.isEditable) return;
    if (typeof getPos === "function") {
      const pos = getPos();
      if (pos !== void 0) {
        editor.chain().focus().setNodeSelection(pos)[mediaType === "audio" ? "toggleAudioCaption" : "toggleVideoCaption"]().run();
      }
    }
  }
  function handleDelete() {
    if (!editor.isEditable) return;
    if (typeof getPos === "function") {
      const pos = getPos();
      if (pos !== void 0) {
        editor.chain().focus().setNodeSelection(pos)[mediaType === "audio" ? "deleteAudio" : "deleteVideo"]().run();
      }
    }
  }
  const captionBtn = createMenuButton(
    "Show caption",
    CAPTION_ICON_SVG,
    handleToggleCaption
  );
  const deleteBtn = createMenuButton("Delete", TRASH_ICON_SVG, handleDelete);
  menu.append(captionBtn, deleteBtn);
  return {
    dom: menu,
    setCaptionState(hasCaption) {
      const label = hasCaption ? "Hide caption" : "Show caption";
      captionBtn.title = label;
      captionBtn.setAttribute("aria-label", label);
      captionBtn.setAttribute("aria-pressed", String(hasCaption));
    }
  };
}
function createMenuButton(title, iconSvg, onClick) {
  const button = document.createElement("button");
  button.type = "button";
  button.title = title;
  button.setAttribute("aria-label", title);
  button.className = "media-floating-menu-btn";
  button.innerHTML = iconSvg;
  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return button;
}
var CAPTION_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 21" width="16" height="16" fill="currentColor"><path d="M2.4866562,12.685112 C2.75458009,12.685112 2.97226499,12.6078181 3.13971743,12.4532279 C3.30716986,12.2986378 3.39424382,12.0931062 3.40094192,11.8366271 L4.76232339,11.8366271 C4.75897434,12.2231025 4.65850439,12.5770735 4.46091052,12.8985507 C4.26331665,13.220028 3.99288502,13.4694765 3.64960754,13.6469038 C3.30633005,13.8243311 2.92705597,13.9130435 2.51177394,13.9130435 C1.73479465,13.9130435 1.12192794,13.6539332 0.673155416,13.1357049 C0.224382895,12.6174765 0,11.9016295 0,10.9881423 L0,10.8880105 C0,10.0096574 0.222708396,9.30874219 0.668131868,8.78524374 C1.11355534,8.2617453 1.72474755,8 2.50172684,8 C3.18158372,8 3.72663322,8.20289652 4.13689168,8.60869565 C4.54715014,9.01449478 4.75562529,9.55467383 4.76232339,10.229249 L3.40094192,10.229249 C3.39424382,9.93412237 3.30716986,9.69433557 3.13971743,9.50988142 C2.97226499,9.32542727 2.7512311,9.23320158 2.47660911,9.23320158 C2.13835519,9.23320158 1.88299406,9.36231755 1.71051805,9.62055336 C1.53804205,9.87878917 1.45180534,10.2977573 1.45180534,10.8774704 L1.45180534,11.0355731 C1.45180534,11.622313 1.5372048,12.0439162 1.70800628,12.3003953 C1.87880776,12.5568744 2.13835514,12.685112 2.4866562,12.685112 Z M8.74097331,13.8076416 C8.67399234,13.6706186 8.62543186,13.5002206 8.59529042,13.2964427 C8.24364031,13.7075119 7.78650203,13.9130435 7.22386185,13.9130435 C6.69136311,13.9130435 6.25013257,13.7514289 5.90015699,13.428195 C5.5501814,13.1049611 5.37519623,12.6974113 5.37519623,12.2055336 C5.37519623,11.6012267 5.58869488,11.1374632 6.01569859,10.8142292 C6.44270229,10.4909953 7.05975525,10.3276241 7.86687598,10.3241107 L8.53500785,10.3241107 L8.53500785,9.99736495 C8.53500785,9.73385903 8.47053963,9.52305744 8.34160126,9.36495389 C8.21266288,9.20685033 8.00921123,9.12779974 7.73124019,9.12779974 C7.48675964,9.12779974 7.29502948,9.18928353 7.15604396,9.31225296 C7.01705844,9.4352224 6.94756672,9.60386366 6.94756672,9.81818182 L5.49576138,9.81818182 C5.49576138,9.48792105 5.59288234,9.18225876 5.78712716,8.90118577 C5.98137198,8.62011278 6.25598985,8.39964946 6.61098901,8.2397892 C6.96598817,8.07992893 7.36451898,8 7.80659341,8 C8.47640314,8 9.00805664,8.17654632 9.40156986,8.52964427 C9.79508308,8.88274221 9.99183673,9.37900427 9.99183673,10.0184453 L9.99183673,12.4901186 C9.99518578,13.0311841 10.0671892,13.4404905 10.2078493,13.7180501 L10.2078493,13.8076416 L8.74097331,13.8076416 Z M7.54034537,12.7483531 C7.75468448,12.7483531 7.95227539,12.6982877 8.13312402,12.5981555 C8.31397265,12.4980232 8.44793258,12.3636372 8.53500785,12.1949934 L8.53500785,11.2147563 L7.99246468,11.2147563 C7.26572112,11.2147563 6.8789118,11.4782582 6.83202512,12.0052701 L6.82700157,12.0948617 C6.82700157,12.2845859 6.89063254,12.4409304 7.01789639,12.5638999 C7.14516024,12.6868693 7.31930816,12.7483531 7.54034537,12.7483531 Z M16,11.0092227 C16,11.8875758 15.8099443,12.591126 15.4298273,13.1198946 C15.0497103,13.6486632 14.5364763,13.9130435 13.8901099,13.9130435 C13.3408659,13.9130435 12.8971236,13.712782 12.5588697,13.312253 L12.5588697,16 L11.1070644,16 L11.1070644,8.10540184 L12.4533752,8.10540184 L12.5036107,8.66403162 C12.8552608,8.22134166 13.3140736,8 13.8800628,8 C14.5498725,8 15.0706418,8.25998862 15.4423862,8.77997365 C15.8141306,9.29995868 16,10.0166841 16,10.9301713 L16,11.0092227 Z M14.5481947,10.8985507 C14.5481947,10.3680255 14.458609,9.95871905 14.2794349,9.67061924 C14.1002607,9.38251942 13.8398761,9.23847167 13.4982732,9.23847167 C13.0428025,9.23847167 12.7296712,9.42116638 12.5588697,9.78656126 L12.5588697,12.1212121 C12.7363693,12.4971472 13.0528496,12.685112 13.5083203,12.685112 C14.2015733,12.685112 14.5481947,12.0895975 14.5481947,10.8985507 Z" /></svg>`;
var TRASH_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 21" width="16" height="16" fill="currentColor"><path d="M5.03846154,5 L5.03846154,4 L10.9615385,4 L10.9615385,5 L14,5 L14,6.5 L2,6.5 L2,5 L5.03846154,5 Z M2.67986425,8 L13.5011312,8 L12.3427254,17 L3.63680821,17 L2.67986425,8 Z" /></svg>`;

// src/extensions/audio/audio-node-view.ts
var AudioNodeView = class {
  dom;
  contentDOM;
  node;
  editor;
  audio;
  menu;
  constructor(node, getPos, HTMLAttributes, editor) {
    this.node = node;
    this.editor = editor;
    const figcaption = document.createElement("figcaption");
    figcaption.style.textAlign = "center";
    const audio = document.createElement("audio");
    this.audio = audio;
    const baseStyle = "position: relative; width: 100%;";
    const attrStyle = typeof HTMLAttributes.style === "string" ? HTMLAttributes.style : "";
    audio.setAttribute(
      "style",
      [baseStyle, attrStyle].filter(Boolean).join(" ")
    );
    Object.entries(HTMLAttributes).forEach(([key, value]) => {
      if (value == null || key === "style") return;
      audio.setAttribute(key, String(value));
    });
    audio.setAttribute("contenteditable", "false");
    audio.setAttribute("controls", "true");
    audio.setAttribute("controlsList", "nodownload noplaybackrate");
    this.menu = createMediaFloatingMenu("audio", editor, getPos);
    const figure = document.createElement("figure");
    figure.setAttribute("data-audio", "true");
    figure.append(audio, this.menu.dom, figcaption);
    this.dom = figure;
    this.contentDOM = figcaption;
    this.update(node);
  }
  update(node) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    if (node.attrs.src !== this.audio.getAttribute("src")) {
      if (node.attrs.src) {
        this.audio.setAttribute("src", String(node.attrs.src));
      } else {
        this.audio.removeAttribute("src");
      }
    }
    if (this.contentDOM) {
      this.contentDOM.style.display = node.attrs.hasCaption ? "" : "none";
    }
    if (this.dom) {
      this.dom.contentEditable = this.editor.isEditable ? "true" : "false";
    }
    this.menu.setCaptionState(!!node.attrs.hasCaption);
    return true;
  }
  ignoreMutation(mutation) {
    if (this.menu.dom.contains(mutation.target)) return true;
    return false;
  }
  stopEvent(event) {
    if (this.menu.dom.contains(event.target)) {
      if (event.type === "focus" || event.type === "blur") return false;
      return true;
    }
    return false;
  }
};

// src/extensions/audio/audio-plugin.ts
import { NodeSelection as NodeSelection3, Plugin as Plugin17 } from "@tiptap/pm/state";

// src/core/helpers/files.ts
function isPastedFile(event) {
  const { clipboardData } = event;
  if (!clipboardData) return false;
  return clipboardData.types.indexOf("Files") !== -1;
}
function isDroppedFile(event) {
  const { dataTransfer } = event;
  if (!dataTransfer) return false;
  return Array.from(dataTransfer.types).indexOf("Files") !== -1;
}
function getDataTransferFiles(event) {
  let dataTransferItemsList = [];
  if (event.dataTransfer) {
    const dt = event.dataTransfer;
    if (dt.files && dt.files.length) {
      dataTransferItemsList = dt.files;
    } else if (dt.items && dt.items.length) {
      dataTransferItemsList = dt.items;
    }
  } else if (event.target && event.target.files) {
    dataTransferItemsList = event.target.files;
  }
  return Array.prototype.slice.call(dataTransferItemsList);
}

// src/extensions/audio/audio-plugin.ts
var AudioPlugin = (editor) => new Plugin17({
  appendTransaction: (_transactions, _oldState, newState) => {
    const sel = findSelectedNodeByType(newState, "audio");
    if (!sel) return null;
    const { node, from } = sel;
    if (node.attrs.hasCaption === false) {
      const nodeSelection = NodeSelection3.create(newState.doc, from);
      return newState.tr.setSelection(nodeSelection);
    }
    return null;
  },
  props: {
    handleDOMEvents: {
      paste(view, event) {
        if (!event.clipboardData || !isPastedFile(event)) return false;
        if (event.clipboardData?.getData("text/html")) return false;
        const files = Array.from(event.clipboardData.items).map((dt) => dt.getAsFile()).filter((file) => file !== null);
        if (files.length === 0) return false;
        const { tr } = view.state;
        if (!tr.selection.empty) {
          tr.deleteSelection();
        }
        const pos = tr.selection.from;
        const audios = files.filter((file) => /audio/i.test(file.type)).map((file) => [file, ""]);
        if (audios.length === 0) return false;
        insertMedia(editor, audios, "audio", pos);
        return true;
      },
      drop(view, event) {
        if (!isDroppedFile(event)) return false;
        if (event.dataTransfer?.getData("text/html")) return false;
        const audios = getDataTransferFiles(event).filter((file) => /audio/i.test(file.type)).map((file) => [file, ""]);
        if (audios.length === 0) {
          return false;
        }
        const result = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        });
        if (result) {
          event.preventDefault();
          insertMedia(editor, audios, "audio", result.pos);
          return true;
        }
        return false;
      }
    }
  }
});

// src/extensions/audio/audio.ts
var Audio = Node7.create({
  name: "audio",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block",
  content: "inline*",
  selectable: true,
  draggable: true,
  defining: true,
  allowGapCursor: true,
  addNodeView() {
    return ({ node, getPos, editor, HTMLAttributes }) => new AudioNodeView(node, getPos, HTMLAttributes, editor);
  },
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-src")
      },
      hasCaption: {
        default: false,
        parseHTML: (element) => {
          const captionNode = element.querySelector(".media-caption");
          return !!captionNode;
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "figure[data-audio]"
      }
    ];
  },
  renderHTML({ node }) {
    const { src, hasCaption } = node.attrs;
    return renderMediaHTML("audio", src, hasCaption);
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const sel = editor.state.selection;
        const parentAudioNode = findParentNode6((node) => {
          return node.type == editor.schema.nodes.audio;
        })(sel);
        if (!parentAudioNode) return false;
        const endPos = parentAudioNode.pos + parentAudioNode.node.nodeSize;
        editor.chain().insertContentAt(endPos, { type: "paragraph" }).run();
        return true;
      }
    };
  },
  addCommands() {
    return {
      setAudio: (options) => ({ tr, dispatch, state }) => {
        const { caption } = options;
        let node;
        if (caption?.length) {
          node = this.type.create(options, state.schema.text(caption));
        } else {
          node = this.type.create(options);
        }
        if (options.position !== void 0) {
          if (dispatch) tr.insert(options.position, node);
        } else {
          const { selection } = tr;
          if (dispatch)
            tr.replaceRangeWith(selection.from, selection.to, node);
        }
        return true;
      },
      setAudioSrc: (options) => ({ tr, dispatch }) => {
        const nodeType = this.type;
        if (tr.doc.nodeAt(options.position)?.type === nodeType) {
          if (dispatch)
            tr.setNodeAttribute(options.position, "src", options.src);
          return true;
        }
        return false;
      },
      toggleAudioCaption: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "audio");
        if (!sel) return false;
        const { node, from } = sel;
        const { hasCaption } = node.attrs;
        if (hasCaption === true) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: false }).deleteRange({ from: from + 1, to: from + node.nodeSize }).run();
        }
        if (hasCaption === false) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: true }).run();
        }
        return true;
      },
      deleteAudio: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "audio");
        if (sel) {
          const { node, from } = sel;
          return chain().deleteRange({ from, to: from + node.nodeSize }).run();
        }
        return false;
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin18({
        appendTransaction: (_, __, state) => {
          const { doc, tr, schema } = state;
          if (doc.nodeAt(0)?.type.name === "audio") {
            const type = schema.nodes["paragraph"];
            return tr.insert(0, type.create());
          }
          return null;
        }
      }),
      AudioPlugin(this.editor)
    ];
  }
});

// src/extensions/block-background/block-background.ts
import { Extension as Extension8 } from "@tiptap/core";
var BlockBackground = Extension8.create({
  name: "BlockBackground",
  addOptions() {
    return {
      types: []
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          backgroundColor: {
            default: null,
            parseHTML: (element) => element.style.backgroundColor || element.getAttribute("data-cadmus-background-color"),
            renderHTML: (attributes) => {
              if (attributes.backgroundColor) {
                return {
                  style: `--background-color: ${attributes.backgroundColor};`,
                  "data-cadmus-background-color": attributes.backgroundColor
                };
              }
              return {};
            }
          }
        }
      }
    ];
  }
});

// src/extensions/block-text-visibility/block-text-visibility.ts
import { Extension as Extension9, findChildren as findChildren6, getNodeType as getNodeType2 } from "@tiptap/core";
var BlockTextVisibility = Extension9.create({
  name: "blockTextVisibility",
  addOptions() {
    return {
      types: []
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          hideText: {
            default: null,
            parseHTML: (element) => element.style.display === "none" || element.getAttribute("data-cadmus-hidden") === "true",
            renderHTML: (attributes) => {
              if (attributes.hideText) {
                return {
                  style: `display: none;`,
                  "data-cadmus-hidden": true,
                  contenteditable: false
                };
              }
              return {};
            }
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      hideText: (parentNodePos, blockType) => ({ tr, state }) => {
        const { doc } = state;
        const parentNode = doc.nodeAt(parentNodePos);
        const nodeType = getNodeType2(blockType, state.schema);
        if (parentNode && nodeType) {
          const nodePos = findChildren6(
            parentNode,
            (node) => node.type === nodeType
          )[0];
          if (nodePos) {
            tr.setNodeMarkup(
              parentNodePos + nodePos.pos + 1,
              void 0,
              Object.assign({}, nodePos.node.attrs, {
                hideText: true
              })
            );
            if (nodePos.node.attrs.hideText) return false;
            return true;
          }
        }
        return false;
      },
      showText: (parentNodePos, blockType) => ({ tr, state }) => {
        const { doc } = state;
        const parentNode = doc.nodeAt(parentNodePos);
        const nodeType = getNodeType2(blockType, state.schema);
        if (parentNode && nodeType) {
          const nodePos = findChildren6(
            parentNode,
            (node) => node.type === nodeType
          )[0];
          if (nodePos) {
            tr.setNodeMarkup(
              parentNodePos + nodePos.pos + 1,
              void 0,
              Object.assign({}, nodePos.node.attrs, {
                hideText: false
              })
            );
            return true;
          }
        }
        return false;
      },
      toggleShowText: (parentNodePos, blockType) => ({ tr, state }) => {
        const { doc } = state;
        const parentNode = doc.nodeAt(parentNodePos);
        const nodeType = getNodeType2(blockType, state.schema);
        if (parentNode && nodeType) {
          const nodePos = findChildren6(
            parentNode,
            (node) => node.type === nodeType
          )[0];
          if (nodePos) {
            tr.setNodeMarkup(
              parentNodePos + nodePos.pos + 1,
              void 0,
              Object.assign({}, nodePos.node.attrs, {
                hideText: !nodePos.node.attrs.hideText
              })
            );
            return true;
          }
        }
        return false;
      }
    };
  }
});

// src/extensions/blockquote/blockquote.ts
import { mergeAttributes as mergeAttributes5, Node as Node8, textblockTypeInputRule } from "@tiptap/core";
var inputRegex = /^\s*>\s$/gm;
var Blockquote = Node8.create({
  name: "blockquote",
  content: "inline*",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block",
  defining: true,
  parseHTML() {
    return [{ tag: "blockquote" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "blockquote",
      mergeAttributes5(this.options.HTMLAttributes, HTMLAttributes),
      ["p", 0]
    ];
  },
  addCommands() {
    return {
      toggleBlockquote: () => ({ commands }) => {
        return commands.toggleNode("blockquote", "paragraph");
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-b": () => this.editor.commands.toggleBlockquote()
    };
  },
  addInputRules() {
    return [textblockTypeInputRule({ find: inputRegex, type: this.type })];
  }
});

// src/extensions/list/list.ts
import { Extension as Extension10, findParentNode as findParentNode8 } from "@tiptap/core";
import { Plugin as Plugin19 } from "@tiptap/pm/state";

// src/extensions/list/commands/indentList.ts
import { isList as isList4 } from "@tiptap/core";
import { Fragment as Fragment4 } from "@tiptap/pm/model";
import { TextSelection as TextSelection4 } from "@tiptap/pm/state";

// src/extensions/list/helpers/calculateItemRange.ts
import { isList } from "@tiptap/core";
function calculateItemRange(editor, selection) {
  const { $from, $to } = selection;
  const isListNode = (node) => isList(node.type.name, editor.extensionManager.extensions);
  return $from.blockRange($to, isListNode);
}

// src/extensions/list/helpers/findParentItem.ts
import { isList as isList2 } from "@tiptap/core";

// src/extensions/list/helpers/isListItem.ts
import {
  callOrReturn,
  getExtensionField
} from "@tiptap/core";
function isListItem(name, extensions) {
  const extension = extensions.filter((extension2) => extension2.type === "node").find((item) => item.name === name);
  if (!extension) return false;
  const context = {
    name: extension.name,
    options: extension.options,
    storage: extension.storage
  };
  const group = callOrReturn(
    getExtensionField(extension, "group", context)
  );
  if (typeof group !== "string") {
    return false;
  }
  return group.split(" ").includes("listItemGroup");
}

// src/extensions/list/helpers/findParentItem.ts
function findParentItem(editor, $from, range) {
  const parentItem = $from.node(range.depth - 1);
  const parentList = $from.node(range.depth - 2);
  if (!isListItem(parentItem.type.name, editor.extensionManager.extensions) || !isList2(parentList.type.name, editor.extensionManager.extensions)) {
    return false;
  }
  return { parentItem, parentList };
}

// src/extensions/list/helpers/findPreviousItem.ts
import { isList as isList3 } from "@tiptap/core";
function findPreviousItem(editor, selectedList, $from, range) {
  let previousItem;
  let previousList;
  let previousItemStart;
  let previousListStart;
  const { doc } = $from;
  if (range.startIndex >= 1) {
    previousItem = selectedList.child(range.startIndex - 1);
    previousList = selectedList;
    previousListStart = doc.resolve(range.start).start(range.depth);
    previousItemStart = previousListStart + 1;
    for (let i = 0; i < range.startIndex - 1; i++) {
      previousItemStart += previousList.child(i).nodeSize;
    }
  } else {
    const listIndex = $from.index(range.depth - 1);
    if (listIndex >= 1) {
      const listParent = $from.node(range.depth - 1);
      const listParentStart = $from.start(range.depth - 1);
      previousList = listParent.child(listIndex - 1);
      if (!previousItem || !isList3(previousItem.type.name, editor.extensionManager.extensions)) {
        return false;
      }
      previousListStart = listParentStart + 1;
      for (let i = 0; i < listIndex - 1; i++) {
        previousListStart += listParent.child(i).nodeSize;
      }
      previousItem = previousList.child(previousList.childCount - 1);
      previousItemStart = previousListStart + previousList.nodeSize - previousItem.nodeSize - 1;
      if (!isListItem(previousItem.type.name, editor.extensionManager.extensions)) {
        return false;
      }
    } else {
      return false;
    }
  }
  return {
    previousItem,
    previousList,
    previousItemStart,
    previousListStart
  };
}

// src/extensions/list/helpers/sliceSelectedItems.ts
function sliceSelectedItems(doc, $to, range) {
  let selectedSlice;
  let unselectedSlice;
  const start = range.start;
  const mid = $to.depth >= range.depth + 2 ? $to.end(range.depth + 2) : range.end - 1;
  const end = range.end;
  if (mid + 1 >= end) {
    selectedSlice = doc.slice(start, end);
    unselectedSlice = null;
  } else {
    selectedSlice = doc.slice(start, mid);
    unselectedSlice = doc.slice(mid + 1, end - 1);
  }
  return { selectedSlice, unselectedSlice };
}

// src/extensions/list/commands/indentList.ts
function indentList(editor, tr) {
  const { selection, doc } = tr;
  const { $from, $to } = selection;
  const range = calculateItemRange(editor, selection);
  if (!range) return false;
  const selectedList = doc.resolve(range.start).node();
  if (!isList4(selectedList.type.name, editor.extensionManager.extensions))
    return false;
  const findPreviousItemResult = findPreviousItem(
    editor,
    selectedList,
    $from,
    range
  );
  if (!findPreviousItemResult) return false;
  const { previousItem, previousList, previousItemStart } = findPreviousItemResult;
  const { selectedSlice, unselectedSlice } = sliceSelectedItems(
    doc,
    $to,
    range
  );
  const newPreviousItemContent = previousItem.content.append(Fragment4.fromArray([selectedList.copy(selectedSlice.content)])).append(unselectedSlice ? unselectedSlice.content : Fragment4.empty);
  tr.deleteRange(range.start, range.end);
  const previousItemEnd = previousItemStart + previousItem.nodeSize - 2;
  const newPreviousItem = previousItem.copy(newPreviousItemContent);
  newPreviousItem.check();
  tr.replaceRangeWith(
    previousItemStart - 1,
    previousItemEnd + 1,
    newPreviousItem
  );
  tr.setSelection(
    previousList === selectedList ? TextSelection4.create(tr.doc, $from.pos, $to.pos) : TextSelection4.create(tr.doc, $from.pos - 2, $to.pos - 2)
  );
  return true;
}

// src/extensions/list/commands/liftListItemOutOfList.ts
import { Fragment as Fragment5, Slice as Slice3 } from "@tiptap/pm/model";
import { ReplaceAroundStep } from "@tiptap/pm/transform";
function liftListItemOutOfList(itemType) {
  return ({ tr, dispatch, state }) => {
    const { selection } = tr;
    const { $from, $to } = selection;
    const range = $from.blockRange(
      $to,
      (node) => node.firstChild?.type === itemType
    );
    if (!range) return false;
    if (!dispatch) return true;
    return liftOutOfList(state, dispatch, range);
  };
}
function liftOutOfList(state, dispatch, range) {
  const tr = state.tr, list = range.parent;
  const originMappingLength = tr.mapping.maps.length;
  for (let pos = range.end, i = range.endIndex - 1, e = range.startIndex; i > e; i--) {
    pos -= list.child(i).nodeSize;
    tr.delete(pos - 1, pos + 1);
  }
  const $start = tr.doc.resolve(range.start), item = $start.nodeAfter;
  if (!item) {
    return false;
  }
  if (tr.mapping.slice(originMappingLength).map(range.end) !== range.start + item.nodeSize) {
    return false;
  }
  const atStart = range.startIndex === 0, atEnd = range.endIndex === list.childCount;
  const parent = $start.node(-1), indexBefore = $start.index(-1);
  if (!parent.canReplace(
    indexBefore + (atStart ? 0 : 1),
    indexBefore + 1,
    item.content.append(atEnd ? Fragment5.empty : Fragment5.from(list))
  )) {
    return false;
  }
  const start = $start.pos, end = start + item.nodeSize;
  tr.step(
    new ReplaceAroundStep(
      start - (atStart ? 1 : 0),
      end + (atEnd ? 1 : 0),
      start + 1,
      end - 1,
      new Slice3(
        (atStart ? Fragment5.empty : Fragment5.from(list.copy(Fragment5.empty))).append(
          atEnd ? Fragment5.empty : Fragment5.from(list.copy(Fragment5.empty))
        ),
        atStart ? 0 : 1,
        atEnd ? 0 : 1
      ),
      atStart ? 0 : 1
    )
  );
  dispatch(tr.scrollIntoView());
  return true;
}

// src/extensions/list/commands/wrapListBackward.ts
import { isList as isList5 } from "@tiptap/core";

// src/extensions/list/commands/wrapSelectedItems.ts
import {
  Fragment as Fragment6,
  Slice as Slice4
} from "@tiptap/pm/model";
import { TextSelection as TextSelection5 } from "@tiptap/pm/state";
function wrapItems({
  listType,
  itemType,
  tr,
  range
}) {
  const oldList = range.parent;
  const slice = tr.doc.slice(range.start, range.end);
  if (oldList.type === listType && slice.content.firstChild?.type === itemType) {
    return false;
  }
  const newItems = [];
  for (let i = 0; i < slice.content.childCount; i++) {
    const oldItem = slice.content.child(i);
    if (!itemType.validContent(oldItem.content)) {
      return false;
    }
    const newItem = itemType.createChecked(null, oldItem.content);
    newItems.push(newItem);
  }
  const newList = listType.createChecked(null, newItems);
  tr.replaceRange(
    range.start,
    range.end,
    new Slice4(Fragment6.from(newList), 0, 0)
  );
  return true;
}
function wrapSelectedItems({
  editor,
  listType,
  itemType,
  tr
}) {
  const range = calculateItemRange(editor, tr.selection);
  if (!range) {
    return false;
  }
  const atStart = range.startIndex === 0;
  const { from, to } = tr.selection;
  if (!wrapItems({ listType, itemType, tr, range })) {
    return false;
  }
  tr.setSelection(
    new TextSelection5(
      tr.doc.resolve(atStart ? from : from + 2),
      tr.doc.resolve(atStart ? to : to + 2)
    )
  );
  tr.scrollIntoView();
  return true;
}

// src/extensions/list/commands/wrapListBackward.ts
function wrapListBackward(editor, tr) {
  const $cursor = tr.selection.$from;
  const range = $cursor.blockRange();
  if (!range || !isListItem(range.parent.type.name, editor.extensionManager.extensions) || range.startIndex !== 0) {
    return false;
  }
  const root = $cursor.node(range.depth - 2);
  const itemIndex = $cursor.index(range.depth);
  const listIndex = $cursor.index(range.depth - 1);
  const rootIndex = $cursor.index(range.depth - 2);
  const previousList = root.maybeChild(rootIndex - 1);
  const previousListItem = previousList?.lastChild;
  if (
    // current node must be the first node in its parent list item;
    itemIndex !== 0 || // current list item must be the first list item in its parent list;
    listIndex !== 0
  ) {
    return false;
  }
  if (
    // there is a list before current list;
    previousList && isList5(previousList.type.name, editor.extensionManager.extensions) && // we can find the list item type for previousList;
    previousListItem && isListItem(previousListItem.type.name, editor.extensionManager.extensions)
  ) {
    return wrapSelectedItems({
      editor,
      listType: previousList.type,
      itemType: previousListItem.type,
      tr
    });
  }
  if (isListItem(root.type.name, editor.extensionManager.extensions)) {
    const parentListItem = root;
    const parentList = $cursor.node(range.depth - 3);
    if (isList5(parentList.type.name, editor.extensionManager.extensions)) {
      return wrapSelectedItems({
        editor,
        listType: parentList.type,
        itemType: parentListItem.type,
        tr
      });
    }
  }
  return false;
}

// src/extensions/list/commands/listBackspace.ts
function listBackspace(editor) {
  return ({ chain }) => {
    return chain().command(({ view }) => {
      if (!view) return false;
      return true;
    }).command(({ state }) => {
      const $cursor = state.selection.$cursor;
      if (!$cursor || $cursor.parentOffset > 0) {
        return false;
      }
      const range = $cursor.blockRange();
      if (!range || !isListItem(
        range.parent.type.name,
        editor.extensionManager.extensions
      ) || range.startIndex !== 0) {
        return false;
      }
      return true;
    }).command(({ tr }) => {
      if (!tr.steps.length) return false;
      wrapListBackward(editor, tr);
      return true;
    }).command(({ tr, state, commands }) => {
      if (!tr.steps.length) return false;
      const $cursor = state.selection.$cursor;
      if (!$cursor || $cursor.parentOffset > 0) {
        return false;
      }
      const range = $cursor.blockRange();
      if (!range || !isListItem(
        range.parent.type.name,
        editor.extensionManager.extensions
      ) || range.startIndex !== 0) {
        return false;
      }
      const itemIndex = $cursor.index(range.depth);
      const listIndex = $cursor.index(range.depth - 1);
      const rootIndex = $cursor.index(range.depth - 2);
      if (itemIndex === 0 && listIndex === 0 && rootIndex <= 1) {
        commands.liftListItem(range.parent.type);
      }
      return true;
    }).command(({ commands, tr }) => {
      if (!tr.steps.length) return false;
      return commands.joinBackward();
    }).run();
  };
}

// src/extensions/list/commands/maybeJoinList.ts
import { isList as isList6 } from "@tiptap/core";
import { canJoin } from "@tiptap/pm/transform";
function maybeJoinList(editor, tr, $pos) {
  const $from = $pos || tr.selection.$from;
  let joinable = [];
  let index;
  let parent;
  let before;
  let after;
  for (let depth = $from.depth; depth >= 0; depth--) {
    parent = $from.node(depth);
    index = $from.index(depth);
    before = parent.maybeChild(index - 1);
    after = parent.maybeChild(index);
    if (before && after && before.type.name === after.type.name && isList6(before.type.name, editor.extensionManager.extensions)) {
      const pos = $from.before(depth + 1);
      joinable.push(pos);
    }
    index = $from.indexAfter(depth);
    before = parent.maybeChild(index - 1);
    after = parent.maybeChild(index);
    if (before && after && before.type.name === after.type.name && isList6(before.type.name, editor.extensionManager.extensions)) {
      const pos = $from.after(depth + 1);
      joinable.push(pos);
    }
  }
  joinable = [...new Set(joinable)].sort((a, b) => b - a);
  let updated = false;
  for (const pos of joinable) {
    if (canJoin(tr.doc, pos)) {
      tr.join(pos);
      updated = true;
    }
  }
  return updated;
}

// src/extensions/list/commands/outdentList.ts
import { liftTarget } from "@tiptap/pm/transform";

// src/extensions/list/commands/changeItemsType.ts
import { NodeRange as NodeRange3 } from "@tiptap/pm/model";
function changeItemsType(editor, tr, range, parentList, parentItem) {
  const wrapped = wrapSelectedItems({
    editor,
    listType: parentList.type,
    itemType: parentItem.type,
    tr
  });
  if (wrapped) {
    return new NodeRange3(tr.selection.$from, tr.selection.$to, range.depth);
  }
  return range;
}

// src/extensions/list/commands/indentSiblingsOfItems.ts
import { Fragment as Fragment7, NodeRange as NodeRange4, Slice as Slice5 } from "@tiptap/pm/model";
import { ReplaceAroundStep as ReplaceAroundStep2 } from "@tiptap/pm/transform";
function indentSiblingsOfItems(tr, range) {
  const selectedList = range.parent;
  const lastSelectedItem = range.parent.child(range.endIndex - 1);
  const endOfRange = range.end;
  const endOfSelectedList = range.$to.end(range.depth);
  if (endOfRange < endOfSelectedList) {
    tr.step(
      new ReplaceAroundStep2(
        endOfRange - 1,
        endOfSelectedList,
        endOfRange,
        endOfSelectedList,
        new Slice5(
          Fragment7.from(
            lastSelectedItem.type.create(null, selectedList.copy())
          ),
          1,
          0
        ),
        1,
        true
      )
    );
    return new NodeRange4(
      tr.doc.resolve(range.$from.pos),
      tr.doc.resolve(endOfSelectedList),
      range.depth
    );
  }
  return range;
}

// src/extensions/list/commands/indentSiblingsOfList.ts
import { Fragment as Fragment8, NodeRange as NodeRange5, Slice as Slice6 } from "@tiptap/pm/model";
import { ReplaceAroundStep as ReplaceAroundStep3 } from "@tiptap/pm/transform";
function indentSiblingsOfList(tr, range) {
  const selectedList = range.parent;
  const lastSelectedItem = range.parent.child(range.endIndex - 1);
  const endOfSelectedList = range.end;
  const endOfParentListItem = range.$to.end(range.depth - 1);
  if (endOfSelectedList + 1 < endOfParentListItem) {
    tr.step(
      new ReplaceAroundStep3(
        endOfSelectedList - 1,
        endOfParentListItem,
        endOfSelectedList + 1,
        endOfParentListItem,
        new Slice6(
          Fragment8.from(
            selectedList.type.create(null, lastSelectedItem.type.create(null))
          ),
          2,
          0
        ),
        0,
        true
      )
    );
    return new NodeRange5(tr.selection.$from, tr.selection.$to, range.depth);
  }
  return range;
}

// src/extensions/list/commands/outdentList.ts
function outdentList(editor, tr) {
  let range = calculateItemRange(editor, tr.selection);
  if (!range) return false;
  const findParentItemResult = findParentItem(
    editor,
    tr.selection.$from,
    range
  );
  if (!findParentItemResult) return false;
  const { parentItem, parentList } = findParentItemResult;
  range = indentSiblingsOfItems(tr, range);
  range = indentSiblingsOfList(tr, range);
  range = changeItemsType(editor, tr, range, parentList, parentItem);
  const target = liftTarget(range);
  if (typeof target !== "number") return true;
  tr.lift(range, target);
  range = calculateItemRange(editor, tr.selection);
  if (range) {
    maybeJoinList(editor, tr, tr.doc.resolve(range.end - 2));
  }
  return true;
}

// src/extensions/list/commands/splitListItem.ts
import { getNodeType as getNodeType3, isNodeSelection as isNodeSelection3 } from "@tiptap/core";
import { Fragment as Fragment9, Slice as Slice7 } from "@tiptap/pm/model";
import { canSplit } from "@tiptap/pm/transform";
function splitListItem(listItemTypeOrName, ignoreAttrs = ["checked"]) {
  return ({ tr, dispatch, state }) => {
    const listItemType = getNodeType3(listItemTypeOrName, state.schema);
    const { $from, $to } = tr.selection;
    if (
      // Don't apply to node selection where the selected node is a block (inline nodes might be okay)
      isNodeSelection3(tr.selection) && tr.selection.node.isBlock || // List items can only exists at a depth of 2 or greater
      $from.depth < 2 || // Don't apply to a selection which spans multiple nodes.
      !$from.sameParent($to)
    ) {
      return false;
    }
    const grandParent = $from.node(-1);
    if (grandParent.type !== listItemType) {
      return false;
    }
    if ($from.parent.content.size === 0 && $from.node(-1).childCount === $from.indexAfter(-1)) {
      if ($from.depth === 2 || $from.node(-3).type !== listItemType || $from.index(-2) !== $from.node(-2).childCount - 1) {
        return false;
      }
      if (dispatch) {
        const keepItem = $from.index(-1) > 0;
        let wrap = Fragment9.empty;
        for (let depth = $from.depth - (keepItem ? 1 : 2); depth >= $from.depth - 3; depth--) {
          wrap = Fragment9.from($from.node(depth).copy(wrap));
        }
        const content = listItemType.contentMatch.defaultType?.createAndFill() || void 0;
        wrap = wrap.append(
          Fragment9.from(listItemType.createAndFill(null, content) || void 0)
        );
        tr.replace(
          $from.before(keepItem ? void 0 : -1),
          $from.after(-3),
          new Slice7(wrap, keepItem ? 3 : 2, 2)
        );
        tr.setSelection(
          tr.selection.constructor.near(
            tr.doc.resolve($from.pos + (keepItem ? 3 : 2))
          )
        );
        dispatch(tr.scrollIntoView());
      }
      return true;
    }
    const listItemAttributes = Object.fromEntries(
      Object.entries(grandParent.attrs).filter(
        ([attr]) => !ignoreAttrs.includes(attr)
      )
    );
    const contentType = $to.pos === $from.end() ? grandParent.contentMatchAt(0).defaultType : null;
    const contentAttributes = { ...$from.node().attrs };
    tr.delete($from.pos, $to.pos);
    const types = contentType ? [
      { type: listItemType, attrs: listItemAttributes },
      { type: contentType, attrs: contentAttributes }
    ] : [{ type: listItemType, attrs: listItemAttributes }];
    if (!canSplit(tr.doc, $from.pos, 2)) {
      return false;
    }
    if (dispatch) {
      dispatch(tr.split($from.pos, 2, types).scrollIntoView());
    }
    return true;
  };
}

// src/extensions/list/commands/toggleList.ts
import { findParentNode as findParentNode7, isList as isList7 } from "@tiptap/core";

// src/extensions/list/commands/deepChangeListType.ts
import { Fragment as Fragment10 } from "@tiptap/pm/model";
function deepChangeListType(tr, foundList, listType, itemType) {
  const oldList = foundList.node;
  const $start = tr.doc.resolve(foundList.start);
  const listParent = $start.node(-1);
  const indexBefore = $start.index(-1);
  if (!listParent) {
    return false;
  }
  if (!listParent.canReplace(
    indexBefore,
    indexBefore + 1,
    Fragment10.from(listType.create())
  )) {
    return false;
  }
  const newItems = [];
  for (let index = 0; index < oldList.childCount; index++) {
    const oldItem = oldList.child(index);
    if (!itemType.validContent(oldItem.content)) {
      return false;
    }
    const newItem = itemType.createChecked(null, oldItem.content);
    newItems.push(newItem);
  }
  const newList = listType.createChecked(null, newItems);
  const start = foundList.pos;
  const end = start + oldList.nodeSize;
  const from = tr.selection.from;
  tr.replaceRangeWith(start, end, newList);
  tr.setSelection(
    tr.selection.constructor.near(tr.doc.resolve(from))
  );
  return true;
}

// src/extensions/list/commands/toggleList.ts
function toggleList(editor, listType, itemType) {
  return (props) => {
    const { tr, dispatch, commands } = props;
    const { selection } = tr;
    const { $from, $to } = selection;
    const range = $from.blockRange($to);
    if (!range) return false;
    const parentList = findParentNode7(
      (node) => isList7(node.type.name, editor.extensionManager.extensions)
    )(selection);
    if (
      // the selection range is right inside the list
      parentList && range.depth - parentList.depth <= 1 && // the selectron range is the first child of the list
      range.startIndex === 0
    ) {
      if (parentList.node.type === listType) {
        return commands.liftListItemOutOfList(itemType);
      }
      if (isList7(parentList.node.type.name, editor.extensionManager.extensions)) {
        if (listType.validContent(parentList.node.content)) {
          dispatch?.(tr.setNodeMarkup(parentList.pos, listType));
          return true;
        }
        if (deepChangeListType(tr, parentList, listType, itemType)) {
          dispatch?.(tr.scrollIntoView());
          return true;
        }
        return false;
      }
    }
    commands.wrapInList(listType);
    return true;
  };
}

// src/extensions/list/list.ts
var List = Extension10.create({
  name: "list",
  addCommands() {
    return {
      cadmusSplitListItem: (listItemTypeOrName) => splitListItem(listItemTypeOrName),
      indentList: () => ({ tr, dispatch }) => {
        if (indentList(this.editor, tr)) {
          dispatch?.(tr.scrollIntoView());
          return true;
        }
        return false;
      },
      outdentList: () => ({ tr, dispatch, commands }) => {
        if (outdentList(this.editor, tr)) {
          dispatch?.(tr.scrollIntoView());
          return true;
        } else {
          const parentListItem = findParentNode8(
            (node) => isListItem(
              node.type.name,
              this.editor.extensionManager.extensions
            )
          )(tr.selection);
          if (parentListItem) {
            commands.liftListItemOutOfList(parentListItem.node.type);
            return true;
          }
        }
        return false;
      },
      toggleCadmusList: (listType, listItemType) => toggleList(this.editor, listType, listItemType),
      liftListItemOutOfList: (listItemType) => liftListItemOutOfList(listItemType),
      listBackspace: () => listBackspace(this.editor)
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.indentList(),
      "Shift-Tab": () => this.editor.commands.outdentList(),
      Backspace: () => this.editor.commands.listBackspace(),
      "Mod-Backspace": () => this.editor.commands.listBackspace(),
      "ctrl-h": () => this.editor.commands.listBackspace()
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin19({
        appendTransaction: (_transaction, _oldState, newState) => {
          const { tr } = newState;
          const updated = maybeJoinList(this.editor, tr);
          return updated ? tr : null;
        }
      })
    ];
  }
});

// src/extensions/bullet-list/bullet-list.ts
import {
  getNodeType as getNodeType4,
  InputRule,
  inputRulesPlugin,
  mergeAttributes as mergeAttributes6,
  Node as Node10,
  wrappingInputRule
} from "@tiptap/core";
var inputRegex2 = /^\s*([-+*])\s$/;
var BulletList = Node10.create({
  name: "bulletList",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block list",
  content: "listItem+",
  parseHTML() {
    return [{ tag: "ul" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "ul",
      mergeAttributes6(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  addCommands() {
    return {
      toggleBulletList: () => ({ commands }) => {
        return commands.toggleCadmusList(
          this.type,
          getNodeType4("listItem", this.editor.schema)
        );
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-8": () => this.editor.commands.toggleBulletList()
    };
  },
  addProseMirrorPlugins() {
    return [
      inputRulesPlugin({
        editor: this.editor,
        rules: [
          wrappingInputRule({
            find: inputRegex2,
            type: this.type
          }),
          new InputRule({
            find: inputRegex2,
            handler: ({ state, range }) => {
              const { tr } = state;
              tr.deleteRange(range.from, range.to);
              const canUpdate = wrapSelectedItems({
                listType: this.type,
                itemType: getNodeType4("listItem", this.editor.schema),
                tr,
                editor: this.editor
              });
              if (!canUpdate) {
                const $cursor = tr.doc.resolve(range.from);
                if ($cursor.depth > 2) {
                  const maybeListItem = $cursor.node($cursor.depth - 1);
                  if (!isListItem(
                    maybeListItem.type.name,
                    this.editor.extensionManager.extensions
                  )) {
                    return null;
                  }
                } else {
                  return null;
                }
              }
            }
          })
        ]
      })
    ];
  }
});

// src/extensions/code/code.ts
import { Code as TipTapCode } from "@tiptap/extension-code";
var Code = TipTapCode.extend({
  priority: 101,
  // needs to be greater than other inline marks
  addKeyboardShortcuts() {
    return {
      ArrowLeft: () => {
        const state = this.editor.state;
        const { from, to } = state.selection;
        if (from >= 1 && from === to) {
          const codeOnRight = isCodeMarkPresent(
            state,
            from + 1,
            to + 1,
            this.type
          );
          const noCodeUnderCursor = !isCodeMarkPresent(
            state,
            from,
            to,
            this.type
          );
          let nothingOnLeft = true;
          state.doc.nodesBetween(from - 1, to - 1, (node) => {
            if (node) nothingOnLeft = false;
          });
          if (codeOnRight && noCodeUnderCursor && nothingOnLeft) {
            return this.editor.chain().unsetCode().insertContent(" ").setTextSelection(from - 1).run();
          }
        }
        return false;
      },
      ArrowRight: () => {
        const state = this.editor.state;
        const { from, to } = state.selection;
        if (from > 1 && from === to) {
          const codeOnLeft = isCodeMarkPresent(
            state,
            from - 1,
            to - 1,
            this.type
          );
          const noCodeUnderCursor = !isCodeMarkPresent(
            state,
            from,
            to,
            this.type
          );
          let nothingOnRight = true;
          state.doc.nodesBetween(from + 1, to + 1, (node) => {
            if (node) nothingOnRight = false;
          });
          if (codeOnLeft && noCodeUnderCursor && nothingOnRight) {
            return this.editor.chain().unsetCode().insertContent(" ").run();
          }
        }
        return false;
      }
    };
  }
});
var isCodeMarkPresent = (state, from, to, type) => {
  let flag = false;
  state.doc.nodesBetween(from, to, (node) => {
    const code = node.marks.find((markItem) => markItem.type === type);
    if (code) flag = true;
  });
  return flag;
};

// src/extensions/document/document.ts
import { Node as Node11 } from "@tiptap/core";
var Document = Node11.create({
  name: "doc",
  topNode: true,
  content: "(block | tableBlock | codeBlock | mathBlock)+"
});
var SlimDocument = Node11.create({
  name: "doc",
  topNode: true,
  content: "block+"
});
var SingleBlockDocument = Node11.create({
  name: "doc",
  topNode: true,
  content: "block"
});

// src/extensions/emoji/emoji.ts
import { InputRule as InputRule2, mergeAttributes as mergeAttributes7, Node as Node12 } from "@tiptap/core";
import twemoji from "@twemoji/api";
import EMOJI_REGEX from "emojibase-regex";

// src/paste-rules/nodePasteRule.ts
import { Fragment as Fragment11, Slice as Slice8 } from "@tiptap/pm/model";
import { Plugin as Plugin20, PluginKey as PluginKey16 } from "@tiptap/pm/state";
function NodePasteRule(regexp, transformFn) {
  const handler = (fragment) => {
    const nodes = [];
    fragment.forEach((child) => {
      if (child.isText && child.text) {
        const matches = Array.from(child.text.matchAll(regexp));
        const pos = matches.reduce(
          (pos2, match) => {
            const start = match.index ?? 0;
            const end = start + match[0].length;
            if (start > 0) nodes.push(child.cut(pos2, start));
            const transformedNode = transformFn(child.cut(start, end), match);
            nodes.push(transformedNode ?? child.cut(start, end));
            return end;
          },
          // keep track of the end of a match
          0
        );
        if (pos < child.text.length) {
          nodes.push(child.cut(pos));
        }
      } else {
        const transformedNode = handler(child.content);
        if (child.type.validContent(Fragment11.from(transformedNode))) {
          nodes.push(child.copy(transformedNode));
        } else {
          nodes.push(child);
        }
      }
    });
    return Fragment11.fromArray(nodes);
  };
  return new Plugin20({
    key: new PluginKey16("nodePasteRule"),
    props: {
      transformPasted: (slice) => {
        return new Slice8(
          handler(slice.content),
          slice.openStart,
          slice.openEnd
        );
      }
    }
  });
}

// src/extensions/emoji/emoji.ts
var Emoji = Node12.create({
  name: "emoji",
  inline: true,
  group: "inline",
  selectable: false,
  atom: true,
  addAttributes() {
    return {
      text: {
        default: "",
        renderHTML: (attributes) => ({
          "data-emoji-text": attributes.text
        })
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "span[data-emoji-text]",
        getAttrs: (domNode) => {
          if (domNode instanceof Element) {
            const text = domNode.getAttribute("data-emoji-text");
            if (text) {
              return { text };
            }
          }
          return false;
        }
      },
      {
        tag: "img[data-emoji-ch]",
        getAttrs: (domNode) => {
          if (domNode instanceof Element) {
            const text = domNode.getAttribute("data-emoji-ch");
            if (text) {
              return { text };
            }
          }
          return false;
        }
      },
      // handle paste from dropbox paper
      {
        tag: "img",
        getAttrs: (domNode) => {
          const dom = domNode;
          const { src } = dom;
          const emojiRegex = /https:\/\/paper.dropboxstatic.com\/static\/img\/ace\/emoji\/(.*).png/;
          const maybeEmoji = src.match(emojiRegex);
          if (maybeEmoji?.length !== 2) return false;
          const emojiCode = maybeEmoji[1];
          let emojiChar = "";
          try {
            emojiChar = String.fromCodePoint(parseInt(`0x${emojiCode}`, 16));
          } catch (_ex) {
            return false;
          }
          return {
            text: emojiChar
          };
        }
      }
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const { text } = node.attrs;
    const attrs = mergeAttributes7(HTMLAttributes, {
      contentEditable: "false"
    });
    const div = document.createElement("div");
    div.textContent = text;
    twemoji.parse(div);
    const img = div.querySelector("img");
    if (!img) {
      return ["span", attrs, text];
    }
    return [
      "span",
      attrs,
      [
        "img",
        {
          src: img.getAttribute("src"),
          alt: img.getAttribute("alt"),
          class: "emoji"
        }
      ]
    ];
  },
  addProseMirrorPlugins() {
    return [
      NodePasteRule(new RegExp(EMOJI_REGEX, "g"), (_node, match) => {
        const emojiText = match[0];
        return this.type.create({ text: emojiText });
      })
    ];
  },
  addInputRules() {
    return [
      new InputRule2({
        find: EMOJI_REGEX,
        handler: ({ state, match, range }) => {
          const { tr, doc } = state;
          const { from, to } = range;
          const emojiText = match[0];
          const start = doc.resolve(from).start() + (match.index ?? 0);
          tr.replaceWith(start, to, this.type.create({ text: emojiText }));
        }
      })
    ];
  }
});

// src/extensions/empty-media-node/empty-media-node.ts
import { Extension as Extension11, getNodeType as getNodeType5 } from "@tiptap/core";
import { Plugin as Plugin21, PluginKey as PluginKey17 } from "@tiptap/pm/state";
import { Decoration as Decoration7, DecorationSet as DecorationSet7 } from "@tiptap/pm/view";
var emptyMediaNodeKey = new PluginKey17("emptyMediaNode");
function getMediaTypes(schema, nodeNames) {
  return nodeNames.filter((name) => !!schema.nodes[name]).map((name) => getNodeType5(name, schema));
}
function isEmptyMediaNode(node, mediaTypes) {
  return mediaTypes.includes(node.type) && node.textContent === "";
}
var EmptyMediaNode = Extension11.create({
  name: "emptyMediaNode",
  addOptions() {
    return {
      nodeNames: ["image", "audio", "video"]
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin21({
        key: emptyMediaNodeKey,
        state: {
          init: (_, state) => {
            const { doc, schema } = state;
            const decorations = [];
            const mediaTypes = getMediaTypes(schema, this.options.nodeNames);
            doc.descendants((node, pos) => {
              if (isEmptyMediaNode(node, mediaTypes)) {
                const decoration = Decoration7.node(pos, pos + node.nodeSize, {
                  "data-empty-node": "true"
                });
                decorations.push(decoration);
              }
            });
            return DecorationSet7.create(doc, decorations);
          },
          apply: (tr, decoSet, _, newState) => {
            if (tr.docChanged) {
              let updatedDecoSet = decoSet.map(tr.mapping, tr.doc);
              const { doc, schema } = newState;
              const mediaTypes = getMediaTypes(schema, this.options.nodeNames);
              const newDecorations = [];
              updatedDecoSet = updatedDecoSet.remove(
                updatedDecoSet.find().filter((deco) => {
                  const node = doc.nodeAt(deco.from);
                  return !node || !isEmptyMediaNode(node, mediaTypes);
                })
              );
              let from;
              let to;
              for (let i = 0; i < tr.steps.length; i++) {
                const step = tr.steps[i];
                const stepMapping = step.getMap();
                const stepFrom = stepMapping.map(step.from || step.pos, -1);
                const stepTo = stepMapping.map(step.to || step.pos, 1);
                if (from !== void 0) {
                  from = Math.min(stepMapping.map(from, -1), stepFrom);
                } else {
                  from = stepFrom;
                }
                if (to !== void 0) {
                  to = Math.max(stepMapping.map(to, 1), stepTo);
                } else {
                  to = stepTo;
                }
              }
              if (from !== void 0 && to !== void 0) {
                doc.nodesBetween(from, to, (node, pos) => {
                  if (isEmptyMediaNode(node, mediaTypes)) {
                    const decoration = Decoration7.node(
                      pos,
                      pos + node.nodeSize,
                      {
                        "data-empty-node": "true"
                      }
                    );
                    newDecorations.push(decoration);
                  }
                });
              }
              return updatedDecoSet.add(doc, newDecorations);
            }
            return decoSet;
          }
        },
        props: {
          decorations: (state) => emptyMediaNodeKey.getState(state)
        }
      })
    ];
  }
});

// src/extensions/highlight/highlight.ts
import { HIGHLIGHT_COLORS } from "@vericus/cadmus-ui";
import { markInputRule, markPasteRule } from "@tiptap/core";
import {
  inputRegex as inputRegex3,
  pasteRegex,
  Highlight as TiptapHighlight
} from "@tiptap/extension-highlight";
import tinycolor from "tinycolor2";
var Highlight = TiptapHighlight.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      multicolor: true,
      HTMLAttributes: {},
      defaultHighlightColor: HIGHLIGHT_COLORS[2],
      highlightColors: HIGHLIGHT_COLORS
    };
  },
  addAttributes() {
    if (!this.options.multicolor) {
      return {};
    }
    const highlightColors = this.options.highlightColors.map(
      (c) => tinycolor(c).toHexString()
    );
    const defaultHighlightColor = tinycolor(
      this.options.defaultHighlightColor
    ).toHexString();
    const getColor = (color) => {
      return color && highlightColors.includes(tinycolor(color).toHexString()) ? color : defaultHighlightColor;
    };
    return {
      color: {
        default: null,
        parseHTML: (element) => {
          const color = element.getAttribute("data-color") || element.style.backgroundColor;
          if (color || element.tagName.toLowerCase() === "mark") {
            return getColor(color);
          }
          return false;
        },
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {};
          }
          return {
            "data-color": getColor(attributes.color),
            style: `background-color: ${tinycolor(getColor(attributes.color)).setAlpha(0.54).toHex8String()}`
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "mark"
      },
      // this is to make highlight color parsed correctly instead
      // of being parsed as textStyle
      {
        tag: "span",
        priority: 51,
        getAttrs: (element) => {
          const hasStyles = element.hasAttribute("style");
          if (!hasStyles) return false;
          const color = element.style.backgroundColor;
          if (!color) return false;
          const highlightColors = this.options.highlightColors.map(
            (c) => tinycolor(c).toHexString()
          );
          return highlightColors.includes(tinycolor(color).toHexString()) ? {} : false;
        }
      }
    ];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () => this.editor.commands.toggleHighlight({
        color: this.options.defaultHighlightColor
      })
    };
  },
  addInputRules() {
    return [
      markInputRule({
        find: inputRegex3,
        type: this.type,
        getAttributes: () => ({
          color: this.options.defaultHighlightColor
        })
      })
    ];
  },
  addPasteRules() {
    return [
      markPasteRule({
        find: pasteRegex,
        type: this.type,
        getAttributes: () => ({
          color: this.options.defaultHighlightColor
        })
      })
    ];
  }
});

// src/extensions/hyperlink/hyperlink.ts
import {
  getMarkAttributes,
  getMarkRange as getMarkRange2,
  getMarksBetween as getMarksBetween2,
  getMarkType as getMarkType2,
  InputRule as InputRule3,
  Mark as Mark2,
  mergeAttributes as mergeAttributes8
} from "@tiptap/core";

// src/extensions/hyperlink/helpers.ts
function openLink(href) {
  const link = /https?/iu.test(href) ? href : `//${href}`;
  try {
    window.open(link, "_blank");
  } catch (e) {
    if (e instanceof SyntaxError) {
      return;
    }
    throw e;
  }
}
function getValidURL(href) {
  const undecodableURLRegex = /^(.*)(%.*)$/;
  if (isURLDecodable(href)) {
    return href;
  } else if (!undecodableURLRegex.test(href)) {
    return "";
  } else {
    return getValidURL(href.replace(undecodableURLRegex, "$1"));
  }
}
function isURLDecodable(href) {
  let isDecodable = true;
  try {
    decodeURIComponent(href);
  } catch (_e) {
    isDecodable = false;
  }
  return isDecodable;
}

// src/extensions/hyperlink/hyperlink-menu-plugin.ts
import {
  getMarkRange,
  getMarksBetween,
  getMarkType
} from "@tiptap/core";
import { Plugin as Plugin22, PluginKey as PluginKey18 } from "@tiptap/pm/state";
import { Decoration as Decoration8, DecorationSet as DecorationSet8 } from "@tiptap/pm/view";
var HyperlinkMenuPluginKey = new PluginKey18(
  "hyperlinkMenu"
);
var mac = typeof navigator != "undefined" ? /Mac/.test(navigator.platform) : false;
var HyperlinkMenuPlugin = (props) => {
  return new Plugin22({
    key: HyperlinkMenuPluginKey,
    state: {
      init() {
        return null;
      },
      apply(tr, state) {
        const payload = tr.getMeta(HyperlinkMenuPluginKey);
        switch (payload?.type) {
          case "triggerMenu":
            return payload.target;
          case "dismissMenu":
            return null;
          default: {
            return state;
          }
        }
      }
    },
    props: {
      // Clicking existing link marks should bring up the menu. Cmd+clicking the
      // link marks directly opens the `href` by calling `props.onOpenLink`.
      handleClick(view, pos, event) {
        const { state } = view;
        const { schema, doc } = state;
        const pluginState = this.getState(state);
        const markType = getMarkType("link", schema);
        const $from = doc.resolve(pos);
        const range = getMarkRange($from, markType);
        const marks = range ? getMarksBetween(range.from, range.to, doc) : [];
        const linkMark = marks.find(({ mark }) => mark.type === markType);
        const { href } = linkMark?.mark?.attrs ?? {};
        const link = event.target?.closest("a");
        const validLinkClicked = link && href !== void 0;
        if (validLinkClicked && (mac && event.metaKey || !mac && event.ctrlKey)) {
          props.onOpenLink(href);
          return true;
        }
        if (validLinkClicked && range) {
          const { from, to } = range;
          const target = { href, from, to };
          props.editor.chain().focus().dismissMenu().setTextSelection({ from, to }).setHyperlinkMenuTarget(target).run();
        } else if (pluginState) {
          props.editor.commands.setHyperlinkMenuTarget();
        }
        return false;
      },
      // When a menu is rendered, the range should be decorated. This is useful
      // if the range is currently not a link but would be made one if the user
      // inputted link is valid. During that process, the range will be
      // decorated as a temporary link.
      decorations(editorState) {
        const menuState = this.getState(editorState);
        if (menuState) {
          const { from, to } = menuState;
          return DecorationSet8.create(editorState.doc, [
            Decoration8.inline(
              from,
              to,
              { class: "selected-link" },
              { inclusiveStart: true, inclusiveEnd: true }
            )
          ]);
        }
        return DecorationSet8.empty;
      }
    }
  });
};

// src/extensions/hyperlink/hyperlink.ts
var Hyperlink = Mark2.create({
  name: "link",
  priority: 1e3,
  inclusive: false,
  // Hyperlinks should not be combined with other inline marks
  excludes: "_",
  addOptions() {
    return {
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
        ["data-cadmus-link"]: "true",
        "aria-label": "Press Option + m to open the link menu"
      },
      // Callback to open a link with `href` value. Defaults to opening the href
      // in a new tab.
      onOpenLink: openLink,
      role: "menu"
    };
  },
  addAttributes() {
    return {
      href: {
        default: null
      },
      target: {
        default: this.options.HTMLAttributes.target
      }
    };
  },
  parseHTML() {
    return [{ tag: "a[href]:not([href *= 'javascript:' i])" }];
  },
  renderHTML({ HTMLAttributes }) {
    const href = HTMLAttributes.href;
    const attrs = HTMLAttributes;
    attrs.href = getValidURL(href);
    return ["a", mergeAttributes8(this.options.HTMLAttributes, attrs), 0];
  },
  addCommands() {
    return {
      setLink: (href) => ({ commands }) => {
        return commands.setMark(this.name, { href });
      },
      unsetLink: () => ({ commands }) => {
        return commands.unsetMark(this.name, { extendEmptyMarkRange: true });
      },
      // Toggle a link mark on a text selection. Links are inserted via the
      // bubble menu if they don't exist on the current selection range.
      toggleLink: () => ({ commands, state }) => {
        const { selection, doc } = state;
        const { from, to } = selection;
        const text = doc.textBetween(from, to);
        const href = isValidURL(text) ? text : "";
        const attrs = getMarkAttributes(state, this.name);
        if (attrs.href === void 0) {
          if (selection.empty) {
            return false;
          }
          return commands.setHyperlinkMenuTarget({ href, from, to });
        }
        return commands.toggleMark(
          this.name,
          { href },
          {
            extendEmptyMarkRange: true
          }
        );
      },
      // Update existing link mark, removing it if the updated URL is invalid.
      updateLink: (href) => ({ state, dispatch, commands, chain }) => {
        if (dispatch) {
          const attrs = getMarkAttributes(state, this.name);
          const isValid = isValidURL(href);
          href = /^https?/.test(href) ? href : `https://${href}`;
          if (attrs.href !== void 0) {
            if (isValid) {
              return chain().extendMarkRange(this.name, attrs).updateAttributes(this.name, { href }).run();
            } else {
              return chain().extendMarkRange(this.name, attrs).unsetLink().run();
            }
          }
          if (isValid) {
            return commands.setLink(href);
          }
        }
        return true;
      },
      setHyperlinkMenuTarget: (target) => ({ tr, dispatch }) => {
        const payload = target ? { type: "triggerMenu", target } : { type: "dismissMenu" };
        dispatch?.(tr.setMeta(HyperlinkMenuPluginKey, payload));
        return true;
      }
    };
  },
  addInputRules() {
    return [
      // Input rule for markdown style link
      new InputRule3({
        find: buildRegex(BASE_URL_REGEX, "\\[(.*)\\]\\((", ")\\)$", "ui"),
        handler: ({ state, match, range }) => {
          const { from, to } = range;
          const { tr } = state;
          const text = match[1];
          const href = /^https?/.test(match[2]) ? match[2] : `https://${match[2]}`;
          const mark = this.type.create({
            href
          });
          tr.replaceWith(from, to, this.editor.schema.text(text, [mark]));
        }
      }),
      // Input rule for text link
      new InputRule3({
        find: buildRegex(BASE_URL_REGEX, "", "$", "ui"),
        handler: ({ state, match, range }) => {
          const { tr } = state;
          const { from, to } = range;
          const href = /^https?/.test(match[0]) ? match[0] : `https://${match[0]}`;
          const mark = this.type.create({
            href
          });
          tr.replaceWith(from, to, this.editor.schema.text(match[0], [mark]));
        }
      })
    ];
  },
  addProseMirrorPlugins() {
    return [
      // Paste rule for markdown style link
      NodePasteRule(
        buildRegex(BASE_URL_REGEX, "\\[(.*)\\]\\((", ")\\)", "gui"),
        (_node, match) => {
          const text = match[1];
          const href = /^https?/.test(match[2]) ? match[2] : `https://${match[2]}`;
          const mark = this.type.create({
            href
          });
          return this.editor.schema.text(text, [mark]);
        }
      ),
      // Paste rule for text link
      NodePasteRule(
        buildRegex(BASE_URL_REGEX, "", "", "gui"),
        (_node, match) => {
          const href = /^https?/.test(match[0]) ? match[0] : `https://${match[0]}`;
          const mark = this.type.create({
            href
          });
          return this.editor.schema.text(match[0], [mark]);
        }
      ),
      HyperlinkMenuPlugin({
        onOpenLink: this.options.onOpenLink,
        editor: this.editor
      })
    ];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-k": () => this.editor.commands.toggleLink(),
      // Open the hyperlink menu on Alt + ArrowDown for a11y
      "Alt-m": () => {
        const { schema, doc } = this.editor.state;
        const markType = getMarkType2("link", schema);
        const pos = this.editor.state.selection.anchor;
        const $from = doc.resolve(pos);
        const range = getMarkRange2($from, markType);
        const marks = range ? getMarksBetween2(range.from, range.to, doc) : [];
        const linkMark = marks.find(({ mark }) => mark.type === markType);
        const { href } = linkMark?.mark?.attrs ?? {};
        if (href && range) {
          const { from, to } = range;
          const target = { href, from, to };
          this.editor.chain().focus().dismissMenu().setTextSelection({ from, to }).setHyperlinkMenuTarget(target).run();
          return true;
        }
        return false;
      }
    };
  }
});

// src/extensions/hyperlink/hyperlink-menu-component.tsx
import { useCallback as useCallback3, useEffect as useEffect2, useMemo, useRef as useRef2, useState as useState3 } from "react";
import { styled as styled3 } from "styled-components";
import { ControlButton, Input, levels } from "@vericus/cadmus-ui";
import { posToDOMRect as posToDOMRect3 } from "@tiptap/core";
import { useEditorState as useEditorState4 } from "@tiptap/react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function HyperlinkMenuComponent(props) {
  const { editor, portalId } = props;
  const isLinkEnabled = useMemo(
    () => editor.extensionManager.extensions.findIndex(
      ({ name }) => name === "link"
    ) !== -1,
    [editor.extensionManager.extensions]
  );
  const shouldShow = useCallback3(
    ({ state }) => {
      const hyperlink = HyperlinkMenuPluginKey.getState(state);
      return !!hyperlink && isLinkEnabled;
    },
    [isLinkEnabled]
  );
  const getBoundingClientRect = useCallback3(() => {
    const { state, view } = editor;
    const hyperlink = HyperlinkMenuPluginKey.getState(state);
    const { selection } = state;
    let { from, to } = selection;
    if (hyperlink) {
      from = hyperlink.from;
      to = hyperlink.to;
    }
    return posToDOMRect3(view, from, to);
  }, [editor]);
  return /* @__PURE__ */ jsx3(
    FloatingMenuComponent,
    {
      pluginKey: "hyperlinkFloatingMenu",
      portalId,
      editor,
      shouldShow,
      getBoundingClientRect,
      isAsync: true,
      modal: true,
      children: /* @__PURE__ */ jsx3(HyperlinkMenu, { editor })
    }
  );
}
var HyperlinkMenu = ({ editor }) => {
  const [inputHref, setInputHref] = useState3("");
  const { hyperlink } = useEditorState4({
    editor,
    selector: (ctx) => ({
      hyperlink: HyperlinkMenuPluginKey.getState(ctx.editor.state)
    })
  });
  const inputRef = useRef2(null);
  useEffect2(() => {
    const href = hyperlink?.href ?? "";
    inputRef.current?.focus({ preventScroll: true });
    setInputHref(href);
  }, [setInputHref, hyperlink]);
  const onConfirm = useCallback3(
    (href) => {
      editor.chain().updateLink(href).command(({ tr }) => {
        const action = { type: "dismissMenu" };
        tr.setMeta(HyperlinkMenuPluginKey, action);
        return true;
      }).focus().run();
    },
    [editor]
  );
  const onTrash = useCallback3(() => {
    editor.chain().unsetLink().command(({ tr }) => {
      const action = { type: "dismissMenu" };
      tr.setMeta(HyperlinkMenuPluginKey, action);
      return true;
    }).focus().run();
  }, [editor]);
  const onExit = useCallback3(() => {
    editor.commands.setHyperlinkMenuTarget();
    editor.commands.focus();
  }, [editor]);
  return /* @__PURE__ */ jsxs2(
    Container,
    {
      onKeyDown: (e) => {
        if (e.code === "Escape") {
          onExit();
        }
      },
      children: [
        /* @__PURE__ */ jsx3(
          Input,
          {
            "aria-label": "link url",
            placeholder: "Paste web address",
            type: "url",
            fullWidth: true,
            value: inputHref,
            onChange: (event) => setInputHref(event.target.value),
            onKeyDown: (event) => {
              if (event.code === "Enter") {
                onConfirm(inputHref);
              }
            },
            ref: inputRef
          }
        ),
        /* @__PURE__ */ jsx3(
          ControlButton,
          {
            "aria-label": "confirm url change",
            onClick: () => onConfirm(inputHref),
            iconName: "Tick"
          }
        ),
        /* @__PURE__ */ jsx3(
          ControlButton,
          {
            "aria-label": "open in new tab",
            disabled: !isValidURL(inputHref),
            onClick: () => openLink?.(inputHref),
            iconName: "Open"
          }
        ),
        /* @__PURE__ */ jsx3(
          ControlButton,
          {
            "aria-label": "remove link",
            onClick: onTrash,
            iconName: "Trash"
          }
        )
      ]
    }
  );
};
var Container = styled3.div`
  display: grid;
  grid-template-columns: 1fr repeat(3, min-content);
  grid-gap: 4px;
  padding: 6px;
  box-shadow: ${levels.two};
  background: white;
  border-radius: 4px;
  width: 450px;
  box-sizing: border-box;
  align-items: center;
`;

// src/extensions/image/image.ts
import { findParentNode as findParentNode9, mergeAttributes as mergeAttributes9, Node as Node13 } from "@tiptap/core";
import { Fragment as Fragment12 } from "@tiptap/pm/model";
import { Plugin as Plugin24 } from "@tiptap/pm/state";

// src/extensions/image/constants.ts
var MIN_CUSTOM_WIDTH = 160;
var TEXT_WIDTH = 710;
var MAX_CUSTOM_WIDTH = TEXT_WIDTH;
var DEFAULT_CUSTOM_WIDTH = TEXT_WIDTH;

// src/extensions/image/image-node-view.ts
import crel3 from "crelt";

// src/extensions/image/image-resize-handles.ts
import crel2 from "crelt";
var ImageResizeHandles = class {
  imgWrapperDiv;
  resizePointsParent;
  originalWidth;
  newWidth;
  editor;
  mouseXPos = 0;
  activeResizeDirection = void 0;
  constructor(imgWrapperDiv, editor) {
    this.imgWrapperDiv = imgWrapperDiv;
    this.editor = editor;
    const ne = crel2("div", { class: "resize-point resize-point--ne" });
    const nw = crel2("div", { class: "resize-point resize-point--nw" });
    const se = crel2("div", { class: "resize-point resize-point--se" });
    const sw = crel2("div", { class: "resize-point resize-point--sw" });
    this.addMouseDownListeners(se, "se" /* BottomRight */);
    this.addMouseDownListeners(sw, "sw" /* BottomLeft */);
    this.addMouseDownListeners(ne, "ne" /* TopRight */);
    this.addMouseDownListeners(nw, "nw" /* TopLeft */);
    this.resizePointsParent = crel2("div", { class: "resize-points" }, [
      ne,
      nw,
      se,
      sw
    ]);
    crel2(this.imgWrapperDiv, this.resizePointsParent);
    this.setVisible(false);
  }
  /**
   * Sets the visibility of the resize points by changing display property
   */
  setVisible(flag) {
    if (flag) {
      this.resizePointsParent.style.removeProperty("display");
    } else {
      this.resizePointsParent.style.display = "none";
    }
  }
  /**
   * Adds the mouse down listener to a given `resizePoint`.
   */
  addMouseDownListeners(resizePoint, resizeDirection) {
    resizePoint.addEventListener("mousedown", (e) => {
      e.preventDefault();
      this.activeResizeDirection = resizeDirection;
      this.originalWidth = this.imgWrapperDiv.getBoundingClientRect().width;
      this.newWidth = this.originalWidth;
      this.mouseXPos = e.pageX;
      window.addEventListener("mousemove", this.handleMoouseMoveResize);
      window.addEventListener("mouseup", this.handleMouseUp);
    });
  }
  /**
   * Handle the mousemove event to resize the imgDivWrapper element
   * @param e
   */
  handleMoouseMoveResize = (e) => {
    e.preventDefault();
    const resizingSmoothnessFactor = 1.5;
    let width;
    if (this.originalWidth)
      switch (this.activeResizeDirection) {
        case "ne" /* TopRight */:
          width = this.originalWidth - resizingSmoothnessFactor * (e.pageX - this.mouseXPos);
          break;
        case "nw" /* TopLeft */:
          width = this.originalWidth + resizingSmoothnessFactor * (e.pageX - this.mouseXPos);
          break;
        case "sw" /* BottomLeft */:
          width = this.originalWidth - resizingSmoothnessFactor * (e.pageX - this.mouseXPos);
          break;
        case "se" /* BottomRight */:
          width = this.originalWidth + resizingSmoothnessFactor * (e.pageX - this.mouseXPos);
          break;
        default:
          width = this.originalWidth;
      }
    else width = MAX_CUSTOM_WIDTH;
    if (width < MIN_CUSTOM_WIDTH) {
      this.newWidth = MIN_CUSTOM_WIDTH;
    } else {
      this.newWidth = width;
    }
    this.imgWrapperDiv.style.width = `${this.newWidth}px`;
  };
  /**
   * Handle the `mouseup` event after resizing is done to dispatch the size update
   * command to the editor and remove the event listeners from the window.
   */
  handleMouseUp = () => {
    if (!this.newWidth || this.newWidth >= MAX_CUSTOM_WIDTH) {
      this.editor.chain().focus().updateImageWidth("fitToText" /* FitToText */).run();
    } else {
      this.editor.chain().focus().updateCustomWidth(this.newWidth).run();
    }
    window.removeEventListener("mousemove", this.handleMoouseMoveResize);
    window.removeEventListener("mouseup", this.handleMouseUp);
  };
};

// src/extensions/image/image-node-view.ts
var ImageNodeView = class {
  /** Top level `<figure />` element. */
  dom;
  /** Editable `<figcaption />` element. */
  contentDOM;
  /** Prosemirror `image` node */
  node;
  /** Tiptap editor reference */
  editor;
  /** Wrapping div `.img-wrapper` on the img. */
  imgWrapperDiv;
  /** Resize handles on the wrapper. */
  imageResizeHandles;
  constructor(node, getPos, HTMLAttributes, editor) {
    this.node = node;
    this.editor = editor;
    const figcaption = crel3("figcaption", {
      class: node.attrs.hasCaption ? "" : "no-caption"
    });
    figcaption.style.textAlign = node.attrs.textAlign;
    const img = crel3("img", {
      ...HTMLAttributes,
      contentEditable: false
    });
    img.addEventListener("click", () => {
      if (typeof getPos === "function" && this.editor.isEditable) {
        if (getPos()) {
          this.editor.commands.setNodeSelection(getPos());
        }
      }
    });
    const wrapper = crel3(
      "div",
      {
        "data-image-width": node.attrs.width,
        "data-image-align": node.attrs.imageAlign,
        class: "img-wrapper"
      },
      [img]
    );
    this.imgWrapperDiv = wrapper;
    this.updateWrapperWidth(img);
    const resizeHandles = new ImageResizeHandles(wrapper, editor);
    this.imageResizeHandles = resizeHandles;
    const figure = crel3(
      "figure",
      {
        draggable: "true",
        contentEditable: this.editor.isEditable,
        "data-image-width": node.attrs.width
      },
      [wrapper, figcaption]
    );
    this.dom = figure;
    this.contentDOM = figcaption;
  }
  /**
   * Updates the wrapper width based on the value of the `width` and
   * `customWidth` attributes of the image node.
   *
   * @param imgElement wrapped `<img />` element
   */
  updateWrapperWidth(imgElement) {
    if (this.node.attrs.width == "custom" /* Custom */) {
      this.imgWrapperDiv.style.width = `${this.node.attrs.customWidth}px`;
    } else if (this.node.attrs.width == "original" /* Original */) {
      this.imgWrapperDiv.style.width = `${imgElement.naturalWidth}px`;
      imgElement.addEventListener(
        "load",
        () => {
          this.imgWrapperDiv.style.width = `${imgElement.naturalWidth}px`;
        },
        { once: true }
      );
    } else {
      this.imgWrapperDiv.style.removeProperty("width");
    }
  }
  update(node) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    if (this.dom) {
      if (this.editor.isEditable) {
        this.dom.contentEditable = "true";
      } else {
        this.dom.contentEditable = "false";
      }
      crel3(this.dom, {
        "data-image-width": node.attrs.width
      });
    }
    const imgs = this.dom.getElementsByTagName("img");
    const img = imgs && imgs[0];
    if (img) {
      crel3(img, {
        src: node.attrs.src,
        alt: node.textContent
      });
    }
    if (this.imgWrapperDiv) {
      crel3(this.imgWrapperDiv, {
        "data-image-width": node.attrs.width,
        "data-image-align": node.attrs.imageAlign
      });
      if (img) {
        this.updateWrapperWidth(img);
      }
    }
    if (this.dom && this.contentDOM) {
      if (!node.attrs.hasCaption) {
        this.contentDOM.classList.add("no-caption");
      } else {
        this.contentDOM.classList.remove("no-caption");
        this.contentDOM.style.textAlign = node.attrs.textAlign;
      }
    }
    return true;
  }
  selectNode() {
    if (!this.editor.isEditable) return;
    this.dom.classList.add("ProseMirror-selectednode");
    this.imageResizeHandles.setVisible(true);
  }
  deselectNode() {
    this.dom.classList.remove("ProseMirror-selectednode");
    this.imageResizeHandles.setVisible(false);
  }
  /**
   * Ignores mutation to the imgWrapperDiv Dom element in order to prevent
   * re-rendering when drag reszing changes its width property.
   */
  ignoreMutation = (mutation) => {
    let returnFlag = false;
    mutation.target.childNodes.forEach((childNode) => {
      if (childNode.nodeName === "IMG") returnFlag = true;
    });
    return returnFlag;
  };
};

// src/extensions/image/image-plugin.ts
import { NodeSelection as NodeSelection4, Plugin as Plugin23 } from "@tiptap/pm/state";
var ImagePlugin = (editor) => new Plugin23({
  //  Selects image node when selection is inside image node and image has no caption
  appendTransaction: (_transactions, _oldState, newState) => {
    const sel = findSelectedNodeByType(newState, "image");
    if (!sel) return null;
    const { node, from } = sel;
    if (node.attrs.hasCaption === false) {
      const nodeSelection = NodeSelection4.create(newState.doc, from);
      return newState.tr.setSelection(nodeSelection);
    }
    return null;
  },
  props: {
    handleDOMEvents: {
      paste(view, event) {
        if (!event.clipboardData || !isPastedFile(event)) return false;
        if (event.clipboardData?.getData("text/html")) return false;
        const files = Array.from(event.clipboardData.items).map((dt) => dt.getAsFile()).filter((file) => file !== null);
        if (files.length === 0) return false;
        const { tr } = view.state;
        if (!tr.selection.empty) {
          tr.deleteSelection();
        }
        const pos = tr.selection.from;
        const images = files.filter((file) => /image/i.test(file.type)).map((file) => [file, ""]);
        if (images.length === 0) return false;
        insertMedia(editor, images, "image", pos);
        return true;
      },
      drop(view, event) {
        if (!isDroppedFile(event)) return false;
        if (event.dataTransfer?.getData("text/html")) return false;
        const images = getDataTransferFiles(event).filter((file) => /image/i.test(file.type)).map((file) => [file, ""]);
        if (images.length === 0) {
          return false;
        }
        const result = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        });
        if (result) {
          event.preventDefault();
          insertMedia(editor, images, "image", result.pos);
          return true;
        }
        return false;
      }
    }
  }
});

// src/extensions/image/image.ts
var Image2 = Node13.create({
  name: "image",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block",
  content: "inline*",
  selectable: true,
  draggable: true,
  defining: true,
  allowGapCursor: true,
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src")
      },
      width: {
        default: "fitToText" /* FitToText */,
        parseHTML: (element) => {
          const width = element.getAttribute("data-image-width");
          switch (width) {
            case "fitToText" /* FitToText */:
            case "full" /* Full */:
            case "original" /* Original */:
            case "custom" /* Custom */:
              return width;
            default:
              return null;
          }
        },
        renderHTML: (attributes) => ({
          "data-image-width": attributes.width
        })
      },
      customWidth: {
        default: DEFAULT_CUSTOM_WIDTH,
        parseHTML: (element) => {
          const customWidth = element.getAttribute("data-image-custom-width");
          if (!customWidth) return null;
          try {
            const customWidthVal = parseInt(customWidth);
            return customWidthVal;
          } catch (_e) {
            return null;
          }
        },
        renderHTML: (attributes) => ({
          "data-image-custom-width": attributes.customWidth
        })
      },
      imageAlign: {
        default: "center" /* Center */,
        parseHTML: (element) => {
          const alignment = element.getAttribute("data-image-align");
          switch (alignment) {
            case "right" /* Right */:
            case "center" /* Center */:
            case "left" /* Left */:
              return alignment;
            default:
              return null;
          }
        },
        renderHTML: (attributes) => ({
          "data-image-align": attributes.imageAlign
        })
      },
      textAlign: {
        default: "center" /* Center */,
        parseHTML: (element) => {
          const alignment = element.style.textAlign;
          switch (alignment) {
            case "center" /* Center */:
            case "left" /* Left */:
            case "right" /* Right */:
              return alignment;
            default:
              return "center" /* Center */;
          }
        }
      },
      hasCaption: {
        default: false,
        parseHTML: (element) => {
          const captionNode = element.parentElement?.querySelector("figcaption") || element.parentElement?.parentElement?.parentElement?.querySelector(
            "figcaption"
          );
          return element.hasAttribute("alt") || !!captionNode;
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "figcaption",
        ignore: true
      },
      {
        tag: "img[src]",
        // The contents of this Node goes into the caption
        getContent: (element, schema) => {
          let figureNode = element.parentElement;
          if (figureNode && figureNode.tagName.toLowerCase() === "div") {
            figureNode = element.parentElement?.parentElement?.parentElement ?? null;
          }
          if (figureNode && figureNode.tagName.toLowerCase() === "figure") {
            const captionNode = figureNode.querySelector("figcaption");
            if (captionNode?.textContent) {
              const text = schema.text(captionNode.textContent);
              return Fragment12.from(text);
            }
          }
          const alt = element.getAttribute("alt") ?? "";
          if (alt) {
            const text = schema.text(alt);
            return Fragment12.from(text);
          }
          return Fragment12.empty;
        }
      }
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    const { hasCaption } = node.attrs;
    let customWidthAttributes = {};
    switch (node.attrs.width) {
      case "custom" /* Custom */:
        customWidthAttributes = {
          style: `width:${node.attrs.customWidth}px`
        };
        break;
      case "original" /* Original */:
        customWidthAttributes = {
          style: `width:auto`
        };
        break;
      case "fitToText" /* FitToText */:
        customWidthAttributes = {
          style: `width:710px`
        };
        break;
      default:
        customWidthAttributes = {};
    }
    let imageAlignAttributes = {};
    switch (node.attrs.imageAlign) {
      case "left" /* Left */:
        imageAlignAttributes = {
          style: "margin: 0px auto 0px 45px;"
        };
        break;
      case "right" /* Right */:
        imageAlignAttributes = {
          style: "margin: 0px 45px 0px auto;"
        };
        break;
      case "center" /* Center */:
      default:
        imageAlignAttributes = {
          style: "margin: 0px auto;"
        };
    }
    const captionAttribs = { style: `text-align: ${node.attrs.textAlign}` };
    const imgAttrs = mergeAttributes9(
      this.options.HTMLAttributes,
      HTMLAttributes,
      {
        contentEditable: "false",
        style: "max-width: 100%; position: relative"
      },
      customWidthAttributes,
      imageAlignAttributes
    );
    return [
      "figure",
      {},
      ["img", imgAttrs],
      hasCaption ? ["figcaption", captionAttribs, 0] : ""
    ];
  },
  addKeyboardShortcuts() {
    return {
      // When pressing enter while typing in caption, a new paragraph node
      // should be created with default alignment. Without intercepting the
      // enter key, the new paragraph has the same alignment as caption and if
      // enter is pressed in middle of caption, the image node gets duplicated
      // which is not the desired behaviour.
      Enter: ({ editor }) => {
        const sel = editor.state.selection;
        const parentImageNode = findParentNode9((node) => {
          return node.type == editor.schema.nodes.image;
        })(sel);
        if (!parentImageNode) return false;
        const endPos = parentImageNode.pos + parentImageNode.node.nodeSize;
        editor.chain().insertContentAt(endPos, { type: "paragraph" }).run();
        return true;
      }
    };
  },
  addCommands() {
    return {
      setImage: (options) => ({ tr, dispatch, state }) => {
        const { caption } = options;
        let node;
        if (caption?.length) {
          node = this.type.create(options, state.schema.text(caption));
        } else {
          node = this.type.create(options);
        }
        if (options.position !== void 0) {
          if (dispatch) tr.insert(options.position, node);
        } else {
          const { selection } = tr;
          if (dispatch)
            tr.replaceRangeWith(selection.from, selection.to, node);
        }
        return true;
      },
      setImageSrc: (options) => ({ tr, dispatch }) => {
        const nodeType = this.type;
        if (tr.doc.nodeAt(options.position)?.type === nodeType) {
          if (dispatch)
            tr.setNodeAttribute(options.position, "src", options.src);
          return true;
        }
        return false;
      },
      updateImageWidth: (width) => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "image");
        if (!sel) return false;
        const { from } = sel;
        return chain().updateAttributes(this.name, { width }).setNodeSelection(from).run();
      },
      updateCustomWidth: (customWidth) => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "image");
        if (!sel) return false;
        const { from } = sel;
        return chain().updateAttributes(this.name, {
          customWidth,
          width: "custom" /* Custom */
        }).setNodeSelection(from).run();
      },
      updateImageAlignment: (alignment) => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "image");
        if (!sel) return false;
        const { from } = sel;
        return chain().updateAttributes(this.name, {
          imageAlign: alignment,
          textAlign: alignment
          // changing image alignment should also change text alignment
        }).setNodeSelection(from).run();
      },
      toggleImageCaption: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "image");
        if (!sel) return false;
        const { node, from } = sel;
        const { hasCaption } = node.attrs;
        if (hasCaption === true) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: false }).deleteRange({ from: from + 1, to: from + node.nodeSize }).run();
        }
        if (hasCaption === false) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: true }).run();
        }
        return true;
      },
      deleteImage: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "image");
        if (sel) {
          const { node, from } = sel;
          return chain().deleteRange({ from, to: from + node.nodeSize }).run();
        }
        return false;
      }
    };
  },
  addNodeView() {
    return ({ editor, node, getPos, HTMLAttributes }) => {
      return new ImageNodeView(node, getPos, HTMLAttributes, editor);
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin24({
        appendTransaction: (_, __, state) => {
          const { doc, tr, schema } = state;
          if (doc.nodeAt(0)?.type.name === "image") {
            const type = schema.nodes["paragraph"];
            return tr.insert(0, type.create());
          }
          return null;
        }
      }),
      ImagePlugin(this.editor)
    ];
  }
});

// src/extensions/image/image-menu-component.tsx
import { useCallback as useCallback4, useEffect as useEffect3, useMemo as useMemo2, useRef as useRef3 } from "react";
import { styled as styled4 } from "styled-components";
import {
  ControlButton as ControlButton2,
  Icon,
  levels as levels2,
  Text,
  ToggleGroup,
  Tooltip
} from "@vericus/cadmus-ui";
import { isNodeSelection as isNodeSelection4 } from "@tiptap/core";
import { useEditorState as useEditorState5 } from "@tiptap/react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function ImageMenuComponent(props) {
  const { editor, portalId } = props;
  const isImageEnabled = useMemo2(
    () => editor.extensionManager.extensions.findIndex(
      ({ name }) => name === "image"
    ) !== -1,
    [editor.extensionManager.extensions]
  );
  const shouldShow = useCallback4(
    ({ state }) => {
      const { selection } = state;
      const { empty, $anchor } = selection;
      if (!isImageEnabled || empty || !$anchor.parent) return false;
      if (isNodeSelection4(selection) && selection.node.type == state.schema.nodes.image) {
        return true;
      }
      return false;
    },
    [isImageEnabled]
  );
  return /* @__PURE__ */ jsx4(
    FloatingMenuComponent,
    {
      pluginKey: "imageFloatingMenu",
      editor,
      shouldShow,
      portalId,
      children: /* @__PURE__ */ jsx4(ImageMenuItems, { editor })
    }
  );
}
function ImageMenuItems({ editor }) {
  const ref = useRef3(null);
  const onImageAlignmentChange = (value) => {
    switch (value) {
      case "left":
        editor.chain().updateImageAlignment("left" /* Left */).run();
        break;
      case "center":
        editor.chain().updateImageAlignment("center" /* Center */).run();
        break;
      case "right":
        editor.chain().updateImageAlignment("right" /* Right */).run();
        break;
    }
  };
  const onImageSizeChange = (value) => {
    switch (value) {
      case "fitText":
        editor.chain().updateImageWidth("fitToText" /* FitToText */).run();
        break;
      case "fullWidth":
        editor.chain().updateImageWidth("full" /* Full */).updateImageAlignment("center" /* Center */).run();
        break;
      case "original":
        editor.chain().updateImageWidth("original" /* Original */).run();
        break;
    }
  };
  const onCaptionClick = () => editor.chain().focus().toggleImageCaption().run();
  const onDeleteImage = () => editor.chain().focus().deleteImage().run();
  const {
    isCenterAlign,
    isLeftAlign,
    isRightAlign,
    isFitToText,
    isFullWidth,
    isOriginalWidth,
    hasCaption
  } = useEditorState5({
    editor,
    selector: (ctx) => ({
      isCenterAlign: ctx.editor.isActive("image", {
        imageAlign: "center" /* Center */
      }),
      isLeftAlign: ctx.editor.isActive("image", {
        imageAlign: "left" /* Left */
      }),
      isRightAlign: ctx.editor.isActive("image", {
        imageAlign: "right" /* Right */
      }),
      isFitToText: ctx.editor.isActive("image", {
        width: "fitToText" /* FitToText */
      }),
      isFullWidth: ctx.editor.isActive("image", {
        width: "full" /* Full */
      }),
      isOriginalWidth: ctx.editor.isActive("image", {
        width: "original" /* Original */
      }),
      hasCaption: ctx.editor.isActive("image", {
        hasCaption: true
      })
    })
  });
  const imageAlignment = useMemo2(() => {
    if (isLeftAlign) return "left";
    else if (isRightAlign) return "right";
    return "center";
  }, [isLeftAlign, isRightAlign]);
  const imageSize = useMemo2(() => {
    if (isOriginalWidth) return "original";
    if (isFullWidth) return "fullWidth";
    return "fitText";
  }, [isFullWidth, isOriginalWidth]);
  useEffect3(() => {
    ref.current?.focus();
  }, []);
  return /* @__PURE__ */ jsxs3(
    ButtonGroups,
    {
      onKeyDown: (e) => {
        if (e.code === "Escape") {
          editor.commands.focus();
        }
      },
      children: [
        /* @__PURE__ */ jsxs3(
          StyledToggleGroupRoot,
          {
            type: "single",
            ref: (r) => ref.current = r,
            "aria-label": "Image alignment",
            onValueChange: onImageAlignmentChange,
            value: imageAlignment,
            loop: true,
            children: [
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "left",
                  disabled: isFullWidth,
                  "aria-disabled": isFullWidth,
                  "aria-label": "Left Align Image",
                  selected: isLeftAlign,
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageAlignLeft" })
                }
              ),
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "center",
                  "aria-label": "Center Align Image",
                  selected: isCenterAlign,
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageAlignCenter" })
                }
              ),
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "right",
                  "aria-label": "Right Align Image",
                  disabled: isFullWidth,
                  selected: isRightAlign,
                  "aria-disabled": isFullWidth,
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageAlignRight" })
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx4(GroupDivider, {}),
        /* @__PURE__ */ jsxs3(
          StyledToggleGroupRoot,
          {
            type: "single",
            "aria-label": "Image size",
            value: imageSize,
            onValueChange: onImageSizeChange,
            loop: true,
            children: [
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "fitText",
                  selected: isFitToText,
                  "aria-label": "Fit To Text",
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageFit" })
                }
              ),
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "fullWidth",
                  selected: isFullWidth,
                  "aria-label": "Banner Size",
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageCover" })
                }
              ),
              /* @__PURE__ */ jsx4(
                ToggleGroupItem,
                {
                  value: "original",
                  selected: isOriginalWidth,
                  "aria-label": "Original Image Size",
                  children: /* @__PURE__ */ jsx4(Icon, { iconName: "ImageOriginal" })
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx4(GroupDivider, {}),
        /* @__PURE__ */ jsx4(
          ControlButton2,
          {
            "aria-label": "Toggle Image Caption",
            selected: hasCaption,
            onClick: onCaptionClick,
            iconName: "Caption"
          }
        ),
        /* @__PURE__ */ jsx4(GroupDivider, {}),
        /* @__PURE__ */ jsx4(
          ControlButton2,
          {
            "aria-label": "Delete Image",
            onClick: onDeleteImage,
            iconName: "Trash"
          }
        )
      ]
    }
  );
}
var ToggleGroupItem = (props) => /* @__PURE__ */ jsx4(
  Tooltip,
  {
    size: "sm",
    width: "auto",
    side: "bottom",
    content: /* @__PURE__ */ jsx4(Text, { kind: "systemSm", children: props["aria-label"] }),
    children: /* @__PURE__ */ jsx4(
      StyledRadixToggleGroupItem,
      {
        value: props.value,
        disabled: props.disabled,
        "aria-disabled": props["aria-disabled"],
        "aria-label": props["aria-label"],
        selected: props.selected,
        children: props.children
      }
    )
  }
);
var ButtonGroups = styled4.div`
  background: white;
  box-shadow: ${levels2.two};
`;
var GroupDivider = styled4.div`
  display: inline-flex;
  width: 1px;
  height: 20px;
  background: rgba(89, 115, 166, 0.15);
  margin: 7px 2px;
`;
var StyledRadixToggleGroupItem = styled4(ToggleGroup.Item)`
  --size: 36px;
  min-width: var(--size);
  height: var(--size);
  padding: 4px;

  border-radius: 6px;
  border: none;
  outline: 0;
  position: relative;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background-clip: content-box;
  background-color: ${(p) => p.selected && !p.disabled ? p.theme.background.action1 : "transparent"};
  fill: ${(p) => p.selected && !p.disabled ? p.theme.text.accent : p.theme.text.shade1};
  color: ${(p) => p.selected && !p.disabled ? p.theme.text.accent : p.theme.text.main};
  transition:
    background 0.1s ease-out,
    fill 0.1s ease-out;

  &:not([aria-disabled="true"]) {
    &:focus {
      background-color: ${(p) => p.theme.colors.grey50};
    }

    &:focus-visible {
      ${(p) => p.theme.focus};
      outline: 0;
    }

    &:hover {
      background-color: ${(p) => p.theme.colors.grey50};
    }

    &:active,
    &[aria-expanded="true"] {
      background-color: ${(p) => p.theme.colors.grey100};
    }
  }

  &[aria-disabled="true"] {
    opacity: 0.36;
    cursor: not-allowed;
  }
`;
var StyledToggleGroupRoot = styled4(ToggleGroup.Root)`
  display: inline-block;
`;

// src/extensions/indentation/helpers/getEnabledIndentStyles.ts
function getEnabledIndentStyles(editor) {
  return editor.storage.specialIndentation?.enabledStyles;
}

// src/extensions/indentation/helpers/parseLevelIndent.ts
function parseLevelIndent(maxIndent, indentStyle = "normal") {
  return (element) => {
    const { marginLeft, textIndent } = element.style;
    let indent = 0;
    const classNames = element.className && typeof element.className === "string" && element.className.split(" ") || [];
    classNames.forEach((classname) => {
      const result = classname.match(
        /(.*)(indentation|indent|level)([^0-9]*)(\d+)(.*)$/
      );
      const indentation2 = result && result[4] && parseInt(result[4], 10);
      if (indentation2 && indentation2 > 1) {
        if (indentation2 - 1 <= maxIndent) {
          indent = indentation2 - 1;
        } else {
          indent = maxIndent;
        }
      }
    });
    if (!indent) {
      const indentData = element.getAttribute("data-indent");
      indent = indentData ? parseInt(indentData, 10) : 0;
    }
    if (marginLeft && !indent) {
      let indentOffset = 0;
      if (indentStyle !== "normal" && textIndent) {
        indentOffset = parseFloat(textIndent);
      }
      const indentation2 = Math.floor(
        (parseFloat(marginLeft) - indentOffset) / 27
      );
      if (indentation2) {
        if (indentation2 <= maxIndent) {
          indent = indentation2;
        } else {
          indent = maxIndent;
        }
      }
    }
    return indent;
  };
}

// src/extensions/indentation/helpers/parseSpecialIndent.ts
function coalesceIndentStyle(enabledStyles, defaultStyle) {
  return (style) => enabledStyles.find((enabledStyle) => enabledStyle === style) ? style : defaultStyle;
}
function parseSpecialIndent(enabledStyles, defaultStyle) {
  const getIndentStyle = coalesceIndentStyle(enabledStyles, defaultStyle);
  return (element) => {
    const { textIndent } = element.style;
    if (!textIndent) {
      const indentData = element.getAttribute("data-indent-special");
      return indentData ? getIndentStyle(indentData) : getIndentStyle(defaultStyle);
    }
    const indentSize = parseFloat(textIndent);
    if (indentSize > 0) {
      return getIndentStyle("firstLine");
    } else if (indentSize < 0) {
      return getIndentStyle("hanging");
    }
    return defaultStyle;
  };
}

// src/extensions/indentation/helpers/renderLevelIndent.ts
function renderLevelIndent(maxIndentation) {
  return (attributes) => {
    const level = attributes.indentLevel ?? 0;
    const indentLevel = level < maxIndentation ? level : maxIndentation - 1;
    return {
      "data-indent": indentLevel
    };
  };
}

// src/extensions/indentation/helpers/renderSpecialIndent.ts
function coalesceIndentStyle2(enabledStyles, defaultStyle) {
  return (style) => style && enabledStyles.includes(style) ? style : defaultStyle;
}
function renderSpecialIndent(enabledStyles, defaultStyle) {
  const getIndentStyle = coalesceIndentStyle2(enabledStyles, defaultStyle);
  return (attributes) => {
    const style = getIndentStyle(attributes.indentSpecial);
    return {
      "data-indent-special": style
    };
  };
}

// src/extensions/indentation/indentation.ts
import { Extension as Extension15 } from "@tiptap/core";

// src/extensions/indentation/commands/decreaseIndent.ts
function decreaseIndent(indentableNodes) {
  return ({ tr, dispatch, state }) => {
    let can = false;
    tr.selection.ranges.forEach((range) => {
      const from = range.$from.pos;
      const to = range.$to.pos;
      state.doc.nodesBetween(from, to, (node, pos, parent) => {
        const $pos = state.doc.resolve(pos);
        if (parent === state.doc && indentableNodes.includes(node.type.name) && node.attrs.indentLevel > 0 && $pos.depth <= 1) {
          can = true;
          tr.setNodeMarkup(pos, void 0, {
            ...node.attrs,
            indentLevel: node.attrs.indentLevel - 1
          });
        }
      });
    });
    if (can) {
      dispatch?.(tr);
    }
    return can;
  };
}

// src/extensions/indentation/commands/increaseIndent.ts
function increaseIndent(indentableNodes, maxIndent) {
  return ({ tr, dispatch, state }) => {
    let can = false;
    tr.selection.ranges.forEach((range) => {
      const from = range.$from.pos;
      const to = range.$to.pos;
      state.doc.nodesBetween(from, to, (node, pos, parent) => {
        const $pos = state.doc.resolve(pos);
        if (parent === state.doc && indentableNodes.includes(node.type.name) && node.attrs.indentLevel < maxIndent - 1 && $pos.depth <= 1) {
          can = true;
          tr.setNodeMarkup(pos, void 0, {
            ...node.attrs,
            indentLevel: node.attrs.indentLevel + 1
          });
        }
      });
    });
    if (can) {
      dispatch?.(tr);
    }
    return can;
  };
}

// src/extensions/indentation/commands/setIndentStyle.ts
function setIndentStyle(enabledStyles, stylableNodes, style, direction) {
  return ({ tr, dispatch, state }) => {
    if (!enabledStyles.includes(style)) return false;
    let can = false;
    tr.selection.ranges.forEach((range) => {
      const from = range.$from.pos;
      const to = range.$to.pos;
      state.doc.nodesBetween(from, to, (node, pos, parent) => {
        const $pos = state.doc.resolve(pos);
        if (parent === state.doc && canSetIndentStyle(stylableNodes, node, style, direction) && $pos.depth <= 1) {
          can = true;
          tr.setNodeMarkup(pos, void 0, {
            ...node.attrs,
            indentSpecial: style
          });
        }
      });
    });
    if (can) {
      dispatch?.(tr);
    }
    return can;
  };
}
function indentationOrder(style) {
  switch (style) {
    case "hanging":
      return 0;
    case "normal":
      return 1;
    case "firstLine":
      return 2;
  }
}
function canSetIndentStyle(stylableNodes, node, style, direction) {
  if (!stylableNodes.includes(node.type.name)) return false;
  if (direction === 0 /* UNKNOWN */) {
    return node.attrs["indentSpecial"] !== style;
  }
  if (direction === 1 /* FORWARD */) {
    return indentationOrder(node.attrs["indentSpecial"]) + 1 === indentationOrder(style);
  }
  return indentationOrder(node.attrs["indentSpecial"]) - 1 === indentationOrder(style);
}

// src/extensions/indentation/commands/toggleIndentStyle.ts
function toggleIndentStyle(enabledSytles, defaultIndentation, stylableNodes, style) {
  return ({ tr, dispatch, state }) => {
    if (!enabledSytles.includes(style)) return false;
    let can = false;
    tr.selection.ranges.forEach((range) => {
      const from = range.$from.pos;
      const to = range.$to.pos;
      state.doc.nodesBetween(from, to, (node, pos, parent) => {
        const $pos = state.doc.resolve(pos);
        if (parent === state.doc && $pos.depth <= 1 && canToggleIndentStyle(enabledSytles, stylableNodes, node, style)) {
          can = true;
          const newAttrs = {
            ...node.attrs,
            indentSpecial: node.attrs["indentSpecial"] === style ? defaultIndentation : style
          };
          tr.setNodeMarkup(pos, void 0, newAttrs);
        }
      });
    });
    if (can) {
      dispatch?.(tr);
    }
    return can;
  };
}
function canToggleIndentStyle(enabledSytles, stylableNodes, node, style) {
  if (!stylableNodes.includes(node.type.name)) return false;
  if (!(enabledSytles.includes(node.attrs["indentSpecial"]) || enabledSytles.includes(style)))
    return false;
  return true;
}

// src/extensions/indentation/commands/unsetIndentStyle.ts
function unsetIndentStyle(stylableNodes, defaultStyle) {
  return ({ tr, dispatch, state }) => {
    let can = false;
    tr.selection.ranges.forEach((range) => {
      const from = range.$from.pos;
      const to = range.$to.pos;
      state.doc.nodesBetween(from, to, (node, pos, parent) => {
        if (parent === state.doc && stylableNodes.includes(node.type.name)) {
          can = true;
          tr.setNodeMarkup(pos, void 0, {
            ...node.attrs,
            indentSpecial: defaultStyle
          });
        }
      });
    });
    if (can) {
      dispatch?.(tr);
    }
    return can;
  };
}

// src/extensions/indentation/level-indentation.ts
import { Extension as Extension12 } from "@tiptap/core";
var LevelIndentation = Extension12.create({
  name: "levelIndentation",
  addGlobalAttributes() {
    if (this.options.types.length) {
      return [
        {
          types: this.options.types,
          attributes: {
            indentLevel: {
              default: 0,
              renderHTML: (attributes) => renderLevelIndent(this.options.maxIndentation)(attributes),
              parseHTML: (element) => parseLevelIndent(this.options.maxIndentation)(element)
            }
          }
        }
      ];
    }
    return [];
  }
});

// src/extensions/indentation/mix-indentation.ts
import { Extension as Extension13 } from "@tiptap/core";
var MixIndentation = Extension13.create({
  name: "mixIndentation",
  addOptions() {
    return {
      types: ["paragraph"],
      indentationStyles: ["normal", "firstLine", "hanging"],
      defaultIndentation: "normal",
      maxIndentation: 8
    };
  },
  addGlobalAttributes() {
    if (this.options.types.length) {
      return [
        {
          types: this.options.types,
          attributes: {
            indentSpecial: {
              default: this.options.defaultIndentation,
              renderHTML: (attributes) => renderSpecialIndent(
                this.options.indentationStyles,
                this.options.defaultIndentation
              )(attributes),
              parseHTML: parseSpecialIndent(
                this.options.indentationStyles,
                this.options.defaultIndentation
              )
            },
            indentLevel: {
              default: 0,
              renderHTML: (attributes) => renderLevelIndent(this.options.maxIndentation)(attributes),
              parseHTML: (element) => parseLevelIndent(
                this.options.maxIndentation,
                this.options.defaultIndentation
              )(element)
            }
          }
        }
      ];
    }
    return [];
  }
});

// src/extensions/indentation/special-indentation.ts
import { Extension as Extension14 } from "@tiptap/core";
var SpecialIndentation = Extension14.create({
  name: "specialIndentation",
  addOptions() {
    return {
      types: ["paragraph"],
      indentationStyles: ["normal", "firstLine"],
      defaultIndentation: "normal"
    };
  },
  addStorage() {
    return {
      enabledStyles: this.options.indentationStyles
    };
  },
  addKeyboardShortcuts() {
    const shortcuts = {};
    if (this.options.indentationStyles.includes("hanging")) {
      shortcuts["Mod-Alt-6"] = ({ editor }) => editor.commands.toggleIndentStyle("hanging");
    }
    return shortcuts;
  },
  addGlobalAttributes() {
    if (this.options.types.length) {
      return [
        {
          types: this.options.types,
          attributes: {
            indentSpecial: {
              default: this.options.defaultIndentation,
              renderHTML: (attributes) => renderSpecialIndent(
                this.options.indentationStyles,
                this.options.defaultIndentation
              )(attributes),
              parseHTML: parseSpecialIndent(
                this.options.indentationStyles,
                this.options.defaultIndentation
              )
            }
          }
        }
      ];
    }
    return [];
  }
});

// src/extensions/indentation/indentation.ts
var Indentation = Extension15.create({
  name: "indentation",
  addOptions() {
    return {
      specialTypes: ["paragraph"],
      levelTypes: ["paragraph", "heading", "blockquote"],
      indentationStyles: ["normal", "firstLine", "hanging"],
      defaultIndentation: "normal",
      maxIndentation: 8
    };
  },
  addExtensions() {
    const mixTypes = this.options.specialTypes.filter(
      (t) => this.options.levelTypes.includes(t)
    );
    const specialTypes = this.options.specialTypes.filter(
      (t) => !this.options.levelTypes.includes(t)
    );
    const levelTypes = this.options.levelTypes.filter(
      (t) => !this.options.specialTypes.includes(t)
    );
    return [
      SpecialIndentation.configure({
        types: specialTypes,
        indentationStyles: this.options.indentationStyles,
        defaultIndentation: this.options.defaultIndentation
      }),
      MixIndentation.configure({
        types: mixTypes,
        maxIndentation: this.options.maxIndentation,
        indentationStyles: this.options.indentationStyles,
        defaultIndentation: this.options.defaultIndentation
      }),
      LevelIndentation.configure({
        types: levelTypes,
        maxIndentation: this.options.maxIndentation,
        defaultIndentation: this.options.defaultIndentation
      })
    ];
  },
  addCommands() {
    const {
      specialTypes,
      levelTypes,
      maxIndentation,
      defaultIndentation,
      indentationStyles
    } = this.options;
    return {
      increaseIndent: () => increaseIndent(levelTypes, maxIndentation),
      decreaseIndent: () => decreaseIndent(levelTypes),
      setIndentStyle: (style, direction = 0 /* UNKNOWN */) => setIndentStyle(indentationStyles, specialTypes, style, direction),
      unsetIndentStyle: () => unsetIndentStyle(specialTypes, defaultIndentation),
      toggleIndentStyle: (style) => toggleIndentStyle(
        indentationStyles,
        defaultIndentation,
        specialTypes,
        style
      )
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const { selection } = editor.state;
        if (selection.empty && selection.$anchor.parentOffset === 0 && selection.$anchor.depth === 1) {
          return editor.commands.first(({ commands }) => [
            () => commands.setIndentStyle("firstLine", 1 /* FORWARD */),
            () => commands.increaseIndent()
          ]);
        } else {
          return editor.commands.increaseIndent();
        }
      },
      "Shift-Tab": ({ editor }) => {
        const { selection } = editor.state;
        if (selection.empty && selection.$anchor.parentOffset === 0 && selection.$anchor.depth === 1) {
          return editor.commands.first(({ commands }) => [
            () => commands.setIndentStyle("normal", -1 /* BACKWARD */),
            () => commands.decreaseIndent()
          ]);
        } else {
          return editor.commands.decreaseIndent();
        }
      },
      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        if (selection.empty && selection.$anchor.parentOffset === 0 && selection.$anchor.depth === 1) {
          return editor.commands.first(({ commands }) => [
            () => commands.setIndentStyle("normal", -1 /* BACKWARD */),
            () => commands.decreaseIndent()
          ]);
        } else {
          return editor.commands.decreaseIndent();
        }
      }
    };
  }
});

// src/extensions/list-item/list-item.ts
import { mergeAttributes as mergeAttributes10, Node as Node14 } from "@tiptap/core";
var ListItem = Node14.create({
  name: "listItem",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  content: "paragraph block*",
  defining: true,
  group: "listItemGroup",
  parseHTML() {
    return [
      {
        tag: "li"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "li",
      mergeAttributes10(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.cadmusSplitListItem("listItem")
    };
  }
});

// src/extensions/math/math-server-extension.ts
import { Extension as Extension16 } from "@tiptap/core";
import { convertLatexToMarkup } from "mathlive";

// src/extensions/math/nodes/block-math-server.ts
import { mergeAttributes as mergeAttributes11 } from "@tiptap/core";

// src/extensions/math/nodes/block-math-base.ts
import { Node as Node15 } from "@tiptap/core";
var BlockMathBase = Node15.create({
  name: "blockMath",
  atom: true,
  selectable: true,
  draggable: true,
  group: "math_block",
  mathRole: "block",
  addOptions() {
    return {
      HTMLAttributes: {
        class: `node-${this.name}`
      },
      convertLatexToMarkup: (latex) => `<code>${latex}</code>`
    };
  },
  addAttributes() {
    return {
      equation: {
        default: "",
        parseHTML: (element) => {
          if (element.hasAttribute("data-math-latex"))
            return element.getAttribute("data-math-latex");
          if (element.hasAttribute("data-equation-content"))
            return element.getAttribute("data-equation-content");
          return element.textContent;
        },
        renderHTML: ({ equation }) => ({
          "data-math-latex": equation
        })
      },
      caption: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-math-caption"),
        renderHTML: ({ caption }) => ({
          "data-math-caption": caption,
          style: `--math-caption: "${caption}";`
        })
      }
    };
  },
  renderText({ node }) {
    return `$$${node.attrs.equation}$$
`;
  },
  parseHTML() {
    return [
      {
        tag: "div",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const element = node;
          if (element.hasAttribute("data-math-latex"))
            return {
              equation: element.getAttribute("data-math-latex"),
              caption: element.getAttribute("data-math-caption")
            };
          return false;
        }
      },
      {
        tag: "img",
        context: "mathFigure/",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const element = node;
          if (element.hasAttribute("data-equation-content")) {
            return {
              equation: element.getAttribute("data-equation-content")
            };
          }
          return false;
        }
      },
      {
        tag: "math-field",
        context: "mathFigure/"
      }
    ];
  }
});

// src/extensions/math/nodes/block-math-server.ts
var BlockMathServer = BlockMathBase.extend({
  addOptions() {
    return {
      HTMLAttributes: {
        class: `node-${this.name}`
      },
      convertLatexToMarkup: (latex) => latex
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      "div",
      mergeAttributes11(this.options.HTMLAttributes, HTMLAttributes, {
        class: "math-block-wrapper math-node"
      }),
      ...htmlToDOMOutputSpec(
        this.options.convertLatexToMarkup(node.attrs.equation),
        htmlparserConverter
      ),
      [
        "div",
        mergeAttributes11(this.options.HTMLAttributes, HTMLAttributes, {
          class: "math-block-caption"
        })
      ]
    ];
  }
});

// src/extensions/math/nodes/inline-math-server.ts
import { mergeAttributes as mergeAttributes12 } from "@tiptap/core";

// src/extensions/math/nodes/inline-math-base.ts
import { Node as Node16 } from "@tiptap/core";
var InlineMathBase = Node16.create({
  name: "inlineMath",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  group: "inline",
  mathRole: "inline",
  addOptions() {
    return {
      HTMLAttributes: {
        class: `node-${this.name}`
      },
      convertLatexToMarkup: (latex) => `<code>${latex}</code>`
    };
  },
  addAttributes() {
    return {
      equation: {
        default: "",
        parseHTML: (element) => {
          if (element.hasAttribute("data-math-latex"))
            return element.getAttribute("data-math-latex");
          if (element.hasAttribute("data-equation-content"))
            return element.getAttribute("data-equation-content");
          return element.textContent;
        },
        renderHTML: ({ equation }) => ({
          "data-math-latex": equation
        })
      }
    };
  },
  renderText({ node }) {
    return `$${node.attrs.equation}$`;
  },
  parseHTML() {
    return [
      {
        tag: "math-field"
      },
      {
        tag: "span",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const element = node;
          if (element.hasAttribute("data-math-latex"))
            return {
              equation: element.getAttribute("data-math-latex")
            };
          return false;
        }
      },
      {
        tag: "img",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const element = node;
          if (element.hasAttribute("data-equation-content")) {
            return {
              equation: element.getAttribute("data-equation-content")
            };
          }
          return false;
        }
      }
    ];
  }
});

// src/extensions/math/nodes/inline-math-server.ts
var InlineMathServer = InlineMathBase.extend({
  renderHTML({ HTMLAttributes, node }) {
    return [
      "span",
      mergeAttributes12(this.options.HTMLAttributes, HTMLAttributes, {
        class: "inline-math-node"
      }),
      ...htmlToDOMOutputSpec(
        this.options.convertLatexToMarkup(node.attrs.equation),
        htmlparserConverter
      )
    ];
  }
});

// src/extensions/math/nodes/math-figure.ts
import { mergeAttributes as mergeAttributes13, Node as Node17 } from "@tiptap/core";
var MathFigure = Node17.create({
  name: "mathFigure",
  content: "blockMath",
  group: "mathBlock",
  mathRole: "figure",
  defining: true,
  addOptions() {
    return {
      HTMLAttributes: {
        "data-math-group": true
      }
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "figure",
      mergeAttributes13(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const dom = node;
          if (dom.getAttribute("data-math-group") === "true") return {};
          if (dom.querySelectorAll("[data-equation-content]").length) return {};
          return false;
        }
      }
    ];
  }
});

// src/extensions/math/math-server-extension.ts
var MathServerExtension = Extension16.create({
  name: "math",
  addExtensions: () => [
    MathFigure,
    BlockMathServer.configure({
      convertLatexToMarkup: (latex) => {
        return convertLatexToMarkup(latex, { mathstyle: "displaystyle" });
      }
    }),
    InlineMathServer.configure({
      convertLatexToMarkup: (latex) => {
        return convertLatexToMarkup(latex, { mathstyle: "textstyle" });
      }
    })
  ]
});

// src/extensions/ordered-list/ordered-list.ts
import {
  findParentNode as findParentNode10,
  getNodeType as getNodeType6,
  InputRule as InputRule4,
  inputRulesPlugin as inputRulesPlugin2,
  mergeAttributes as mergeAttributes14,
  Node as Node18,
  wrappingInputRule as wrappingInputRule2
} from "@tiptap/core";
var inputRegex4 = /^(\d+)\.\s$/;
var OrderedList = Node18.create({
  name: "orderedList",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block list",
  content: "listItem+",
  addAttributes() {
    return {
      start: {
        default: 1,
        parseHTML: (element) => {
          return element.hasAttribute("start") ? parseInt(element.getAttribute("start") || "", 10) : 1;
        },
        keepOnSplit: false
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "ol"
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { start, ...attributesWithoutStart } = HTMLAttributes;
    return start === 1 ? [
      "ol",
      mergeAttributes14(this.options.HTMLAttributes, attributesWithoutStart),
      0
    ] : ["ol", mergeAttributes14(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addCommands() {
    return {
      toggleOrderedList: () => ({ commands }) => {
        return commands.toggleCadmusList(
          this.type,
          getNodeType6("listItem", this.editor.schema)
        );
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-7": () => this.editor.commands.toggleOrderedList()
    };
  },
  addProseMirrorPlugins() {
    return [
      inputRulesPlugin2({
        editor: this.editor,
        rules: [
          new InputRule4({
            find: inputRegex4,
            handler: ({ state, match, range }) => {
              const { tr } = state;
              tr.deleteRange(range.from, range.to);
              const listType = this.type;
              const listItemType = getNodeType6("listItem", this.editor.schema);
              const canUpdate = wrapSelectedItems({
                listType,
                itemType: listItemType,
                tr,
                editor: this.editor
              });
              const start = +match[1] > 1 ? +match[1] : 1;
              if (!canUpdate) {
                const $cursor = tr.doc.resolve(range.from);
                if ($cursor.depth > 2) {
                  const maybeListItem = $cursor.node($cursor.depth - 1);
                  if (!isListItem(
                    maybeListItem.type.name,
                    this.editor.extensionManager.extensions
                  )) {
                    return null;
                  }
                } else {
                  return null;
                }
              }
              const found = findParentNode10((node) => node.type === this.type)(
                tr.selection
              );
              if (found) {
                if (start !== 1) {
                  tr.setNodeMarkup(found.pos, void 0, { start });
                } else {
                  tr.setNodeMarkup(found.pos, void 0, {});
                }
              }
            }
          }),
          wrappingInputRule2({
            find: inputRegex4,
            type: this.type,
            getAttributes: (match) => {
              if (+match[1] > 1) {
                return { start: +match[1] };
              }
              return;
            },
            joinPredicate: (match, node) => node.childCount + node.attrs.start === +match[1]
          })
        ]
      })
    ];
  }
});

// src/extensions/paste-chip/paste-chip.ts
import { mergeAttributes as mergeAttributes15, Node as Node19 } from "@tiptap/core";
import crel4 from "crelt";
var PasteChip = Node19.create({
  name: "pasteChip",
  addOptions() {
    return {
      HTMLAttributes: {},
      enableManual: false
    };
  },
  group: "inline",
  inline: true,
  draggable: false,
  atom: true,
  allowGapCursor: true,
  addAttributes() {
    return {
      // Track number of words pasted in the dom attribute `data-cadmus-pasted`
      pasted: {
        default: 0,
        parseHTML: (element) => parseInt(element.getAttribute("data-cadmus-pasted") ?? "0") || 0,
        renderHTML: (attributes) => {
          return {
            "data-cadmus-pasted": attributes.pasted || 0
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "span[data-cadmus-pasted]"
      }
    ];
  },
  // Render span container for the paste chip NodeView
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes15(this.options.HTMLAttributes, HTMLAttributes)
    ];
  },
  addNodeView() {
    return ({ node, editor }) => {
      const { pasted } = node.attrs;
      let paraphrasingLink = crel4("span", "paraphrasing");
      if (this.options.enableManual) {
        paraphrasingLink = crel4(
          "button",
          {
            tabindex: "-1",
            class: "cui-LinkButton",
            onclick: () => editor.commands.setMeta("openManualPreset", "paraphrasing")
          },
          paraphrasingLink
        );
      }
      let quotingLink = crel4("span", "quoting");
      if (this.options.enableManual) {
        quotingLink = crel4(
          "button",
          {
            tabindex: "-1",
            class: "cui-LinkButton",
            onclick: () => editor.commands.setMeta("openManualPreset", "quoting")
          },
          quotingLink
        );
      }
      const closeIcon = createCloseIcon();
      const closeButton = crel4(
        "button",
        {
          tabindex: "-1",
          onclick: (event) => editor.commands.deletePasteChip(event),
          class: "icon-button",
          "aria-label": "close"
        },
        closeIcon
      );
      const pasteChip = crel4(
        "span",
        { "data-cadmus-pasted": `${pasted}` },
        `${pasted} words pasted. Consider `,
        paraphrasingLink,
        " or ",
        quotingLink,
        closeButton
      );
      return {
        dom: pasteChip
      };
    };
  }
});
function createCloseIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttributeNS(null, "viewBox", "0 0 16 21");
  svg.setAttributeNS(null, "width", "1em");
  svg.setAttributeNS(null, "height", "100%");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttributeNS(
    null,
    "d",
    "M8,9.57142857 L10.8643218,6.70710678 C11.2548461,6.31658249 11.8880111,6.31658249 12.2785354,6.70710678 L12.2928932,6.72146465 C12.6834175,7.11198894 12.6834175,7.74515392 12.2928932,8.13567821 L9.42857143,11 L9.42857143,11 L12.2928932,13.8643218 C12.6834175,14.2548461 12.6834175,14.8880111 12.2928932,15.2785354 L12.2785354,15.2928932 C11.8880111,15.6834175 11.2548461,15.6834175 10.8643218,15.2928932 L8,12.4285714 L8,12.4285714 L5.13567821,15.2928932 C4.74515392,15.6834175 4.11198894,15.6834175 3.72146465,15.2928932 L3.70710678,15.2785354 C3.31658249,14.8880111 3.31658249,14.2548461 3.70710678,13.8643218 L6.57142857,11 L6.57142857,11 L3.70710678,8.13567821 C3.31658249,7.74515392 3.31658249,7.11198894 3.70710678,6.72146465 L3.72146465,6.70710678 C4.11198894,6.31658249 4.74515392,6.31658249 5.13567821,6.70710678 L8,9.57142857 L8,9.57142857 Z"
  );
  svg.appendChild(path);
  return svg;
}

// src/extensions/subscript/subscript.ts
import { Subscript as TiptapSubscript } from "@tiptap/extension-subscript";
var Subscript = TiptapSubscript.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-'": () => this.editor.commands.toggleSubscript()
    };
  }
});

// src/extensions/superscript/superscript.ts
import { Superscript as TiptapSuperscript } from "@tiptap/extension-superscript";
var Superscript = TiptapSuperscript.extend({
  addKeyboardShortcuts() {
    return {
      "Mod-;": ({ editor }) => editor.commands.toggleSuperscript()
    };
  }
});

// src/extensions/table/components/TableMenu.tsx
import { useCallback as useCallback5 } from "react";
import { styled as styled8 } from "styled-components";
import { findChildren as findChildren7, posToDOMRect as posToDOMRect4 } from "@tiptap/core";

// src/extensions/table/components/BlockMenu.tsx
import { ControlButton as ControlButton3, Toolbar as Toolbar2 } from "@vericus/cadmus-ui";
import { useEditorState as useEditorState6 } from "@tiptap/react";

// src/extensions/table/components/styles.tsx
import { styled as styled5 } from "styled-components";
import { levels as levels3, Toolbar } from "@vericus/cadmus-ui";
var ButtonGroups2 = styled5(Toolbar.Root)`
  background: white;
  box-shadow: ${levels3.two};
  display: inline-flex;
  align-items: center;
`;
var GroupDivider2 = styled5(Toolbar.Separator)`
  &:not(:last-child) {
    display: inline-flex;
    width: 1px;
    height: 20px;
    background: rgba(89, 115, 166, 0.15);
    margin: 7px 2px;
  }
`;

// src/extensions/table/components/BlockMenu.tsx
import { Fragment as Fragment13, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function BlockMenu(props) {
  const { editor } = props;
  const { canHideCaption, canHideFootnote } = useEditorState6({
    editor,
    selector: (ctx) => ({
      canHideCaption: ctx.editor.can().hideTableCaption(),
      canHideFootnote: ctx.editor.can().hideTableFootnote()
    })
  });
  if (!isSelectionType(editor.state.selection, "text")) return null;
  return /* @__PURE__ */ jsxs4(Fragment13, { children: [
    /* @__PURE__ */ jsx5(Toolbar2.Button, { asChild: true, children: /* @__PURE__ */ jsx5(
      ControlButton3,
      {
        "aria-label": "Toggle Table Caption",
        iconName: "Caption",
        selected: canHideCaption,
        onClick: () => editor.chain().toggleTableCaption().focus().run()
      }
    ) }),
    /* @__PURE__ */ jsx5(Toolbar2.Button, { asChild: true, children: /* @__PURE__ */ jsx5(
      ControlButton3,
      {
        "aria-label": "Toggle Table Footnotes ",
        iconName: "Footer",
        selected: canHideFootnote,
        onClick: () => editor.chain().toggleTableFootnote().focus().run()
      }
    ) }),
    /* @__PURE__ */ jsx5(GroupDivider2, {})
  ] });
}

// src/extensions/table/components/BorderMenu.tsx
import { ControlButton as ControlButton4, DropdownMenu as DropdownMenu2, Text as Text2, Toolbar as Toolbar3 } from "@vericus/cadmus-ui";
import { useEditorState as useEditorState7 } from "@tiptap/react";
import { Fragment as Fragment14, jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function BorderMenu(props) {
  const { editor } = props;
  const { shouldShow } = useEditorState7({
    editor,
    selector: (ctx) => ({
      shouldShow: isSelectionInsideTable(ctx.editor.state.selection)
    })
  });
  if (!shouldShow) return null;
  return /* @__PURE__ */ jsxs5(Fragment14, { children: [
    /* @__PURE__ */ jsxs5(DropdownMenu2.Root, { children: [
      /* @__PURE__ */ jsx6(Toolbar3.Button, { asChild: true, children: /* @__PURE__ */ jsx6(DropdownMenu2.Trigger, { asChild: true, children: /* @__PURE__ */ jsx6(ControlButton4, { iconName: "BorderBottom", "aria-label": "Borders" }) }) }),
      /* @__PURE__ */ jsx6(DropdownMenu2.Content, { children: /* @__PURE__ */ jsx6(BorderDropDownContent, { editor }) })
    ] }),
    /* @__PURE__ */ jsx6(GroupDivider2, {})
  ] });
}
function BorderDropDownContent(props) {
  const { editor } = props;
  const { haveTop, haveBottom, haveAll } = useEditorState7({
    editor,
    selector: (ctx) => ({
      haveTop: haveTableBorderStyle(ctx.editor.state, "top"),
      haveBottom: haveTableBorderStyle(ctx.editor.state, "bottom"),
      haveAll: haveTableBorderStyle(ctx.editor.state, "all")
    })
  });
  return /* @__PURE__ */ jsxs5(Fragment14, { children: [
    /* @__PURE__ */ jsx6(
      BorderItem,
      {
        onClick: () => editor.commands.setTableCellBorder("all", true),
        active: haveAll,
        ariaLabel: "Apply border to all sides",
        iconName: "BorderAll",
        text: "All borders"
      }
    ),
    /* @__PURE__ */ jsx6(DropdownMenu2.Separator, {}),
    /* @__PURE__ */ jsx6(DropdownMenu2.Label, { children: /* @__PURE__ */ jsx6(Text2, { kind: "bodySm", color: "grey500", style: { fontWeight: 400 }, children: "APA Style" }) }),
    /* @__PURE__ */ jsx6(
      BorderItem,
      {
        onClick: () => editor.commands.setTableCellBorder("all", false),
        ariaLabel: "Remove borders",
        iconName: "BorderNone",
        text: "Remove borders"
      }
    ),
    /* @__PURE__ */ jsx6(
      BorderItem,
      {
        onClick: () => editor.commands.setTableCellBorder("top", !haveTop),
        active: haveTop,
        ariaLabel: "Toggle border top",
        iconName: "BorderTop",
        text: "Top Border"
      }
    ),
    /* @__PURE__ */ jsx6(
      BorderItem,
      {
        onClick: () => editor.commands.setTableCellBorder("bottom", !haveBottom),
        active: haveBottom,
        ariaLabel: "Toggle border bottom",
        iconName: "BorderBottom",
        text: "Bottom Border"
      }
    ),
    /* @__PURE__ */ jsx6(BorderInsideHorizontalMenu, { editor })
  ] });
}
function BorderInsideHorizontalMenu(props) {
  const { editor } = props;
  if (!editor.can().setTableCellBorder("insideHorizontal", true) && !editor.can().setTableCellBorder("insideHorizontal", false)) {
    return null;
  }
  const isActive = haveTableBorderStyle(editor.state, "insideHorizontal");
  return /* @__PURE__ */ jsxs5(Fragment14, { children: [
    /* @__PURE__ */ jsx6(DropdownMenu2.Separator, {}),
    /* @__PURE__ */ jsx6(
      BorderItem,
      {
        onClick: () => isActive ? editor.commands.setTableCellBorder("insideHorizontal", false) : editor.commands.setTableCellBorder("insideHorizontal", true),
        active: isActive,
        ariaLabel: "Toggle horizontal inside borders",
        iconName: "BorderInsideHorizontal",
        text: "Horizontal Inside Border"
      }
    )
  ] });
}
function BorderItem(props) {
  const { iconName, active, text, onClick, ariaLabel } = props;
  return /* @__PURE__ */ jsx6(
    DropdownMenu2.CheckboxItem,
    {
      onSelect: (e) => {
        e.preventDefault();
        onClick();
      },
      checked: active,
      tickable: true,
      iconName,
      "aria-label": ariaLabel,
      children: /* @__PURE__ */ jsx6(Text2, { kind: "bodySm", color: active ? "lilac500" : void 0, bold: active, children: text })
    }
  );
}

// src/extensions/table/components/CellColorMenu.tsx
import { ControlButton as ControlButton5, DropdownMenu as DropdownMenu4, Toolbar as Toolbar4 } from "@vericus/cadmus-ui";
import { useEditorState as useEditorState8 } from "@tiptap/react";

// src/extensions/table/components/FormatRowColor.tsx
import { styled as styled6 } from "styled-components";
import { Icon as Icon2 } from "@vericus/cadmus-icons";
import { DropdownMenu as DropdownMenu3, IconButton } from "@vericus/cadmus-ui";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
function FormatRowColor(props) {
  const { editor, isCell, activeColor } = props;
  return /* @__PURE__ */ jsxs6(ItemsContainer, { "aria-label": "Set Color", value: activeColor ?? "unset", children: [
    /* @__PURE__ */ jsx7(ColorButtonGroups, { children: tableColors.map(({ button, actual }, index) => {
      return /* @__PURE__ */ jsx7(
        StyledRadioItem,
        {
          value: actual,
          "aria-label": actual,
          asChild: true,
          children: /* @__PURE__ */ jsx7(
            ColorButton,
            {
              color: button,
              selected: activeColor === actual,
              onClick: (e) => {
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
                return isCell ? editor.chain().setCellsBackground(actual).focus().run() : editor.chain().setTableColor(actual).focus().run();
              }
            }
          )
        },
        index
      );
    }) }),
    /* @__PURE__ */ jsxs6(FormatMenuButton, { children: [
      /* @__PURE__ */ jsx7(GroupDivider2, {}),
      /* @__PURE__ */ jsx7(StyledRadioItem, { value: "unset", "aria-label": "without color", asChild: true, children: /* @__PURE__ */ jsx7(
        UnsetButton,
        {
          "aria-label": "Remove alternate table background",
          onMouseDown: (e) => {
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            if (isCell) {
              editor.chain().setCellsBackground().focus().run();
            } else {
              editor.chain().toggleTableAlternateColor().focus().run();
            }
          },
          children: /* @__PURE__ */ jsx7(Icon2, { iconName: "Unset" })
        }
      ) })
    ] })
  ] });
}
var UnsetButton = styled6(IconButton)`
  width: 28px;
  height: 28px;
`;
var FormatMenuButton = styled6.div`
  height: 100%;
  display: inline-flex;
  align-items: center;
`;
var ItemsContainer = styled6(DropdownMenu3.RadioGroup)`
  display: flex;
  height: 32px;
  flex-grow: 1;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
  align-items: center;
`;
var ColorButtonGroups = styled6(ButtonGroups2)`
  box-shadow: none;
  padding: 0px 4px;
  gap: 8px;
`;
var StyledRadioItem = styled6(DropdownMenu3.RadioItem)`
  outline: none;
`;
var ColorButton = styled6.button`
  display: inline-flex;
  width: 16px;
  height: 16px;
  padding: 0px;

  border-radius: 50%;
  border: 4px solid transparent;
  background: ${(p) => p.color};
  position: relative;

  &:after {
    content: "";
    display: block;
    position: absolute;
    top: -7px;
    left: -7px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid
      ${(p) => p.selected ? p.theme.text.accent : "transparent"};
  }

  &:disabled {
    opacity: 0.36;
  }

  &:focus {
    ${(p) => p.theme.focus}
    z-index: 1;
  }

  &:hover {
    cursor: pointer;
  }
`;

// src/extensions/table/components/CellColorMenu.tsx
import { Fragment as Fragment15, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function CellColorMenu(props) {
  const { editor } = props;
  if (!isSelectionType(editor.state.selection, "cell")) return null;
  return /* @__PURE__ */ jsxs7(Fragment15, { children: [
    /* @__PURE__ */ jsxs7(DropdownMenu4.Root, { children: [
      /* @__PURE__ */ jsx8(Toolbar4.Button, { asChild: true, children: /* @__PURE__ */ jsx8(DropdownMenu4.Trigger, { asChild: true, children: /* @__PURE__ */ jsx8(ControlButton5, { iconName: "Paint", "aria-label": "Colour" }) }) }),
      /* @__PURE__ */ jsx8(DropdownMenu4.Content, { children: /* @__PURE__ */ jsx8(CellColorDropDownContent, { editor }) })
    ] }),
    /* @__PURE__ */ jsx8(GroupDivider2, {})
  ] });
}
function CellColorDropDownContent(props) {
  const { editor } = props;
  const { activeColor } = useEditorState8({
    editor,
    selector: (ctx) => ({
      activeColor: cellsColor(ctx.editor.state)
    })
  });
  return /* @__PURE__ */ jsx8(FormatRowColor, { isCell: true, activeColor, editor });
}

// src/extensions/table/components/DeleteMenu.tsx
import { ControlButton as ControlButton6, Toolbar as Toolbar5 } from "@vericus/cadmus-ui";
import { useEditorState as useEditorState9 } from "@tiptap/react";
import { Fragment as Fragment16, jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
function DeleteMenu(props) {
  const { editor } = props;
  const { selection } = useEditorState9({
    editor,
    selector: (ctx) => ({ selection: ctx.editor.state.selection })
  });
  const isCellSelection = isSelectionType(selection, "cell");
  if (!isCellSelection) return null;
  const cellSelection = selection;
  const canDelete = cellSelection.isRowSelection() || cellSelection.isColSelection() || isTableSelected(editor.state.selection);
  if (!canDelete) return null;
  return /* @__PURE__ */ jsxs8(Fragment16, { children: [
    /* @__PURE__ */ jsx9(Toolbar5.Button, { asChild: true, children: /* @__PURE__ */ jsx9(
      ControlButton6,
      {
        "aria-label": "Delete",
        iconName: "Trash",
        onClick: () => editor.chain().deleteSelectedTable().focus().run(),
        onMouseEnter: () => editor.commands.predeleteHighlightTable(true),
        onMouseLeave: () => editor.commands.predeleteHighlightTable(false)
      }
    ) }),
    /* @__PURE__ */ jsx9(GroupDivider2, {})
  ] });
}

// src/extensions/table/components/FormatMenu.tsx
import { styled as styled7 } from "styled-components";
import {
  colors as colors8,
  ControlButton as ControlButton7,
  ControlButtonText,
  DropdownMenu as DropdownMenu5,
  Text as Text3,
  Toolbar as Toolbar6
} from "@vericus/cadmus-ui";
import { useEditorState as useEditorState10 } from "@tiptap/react";
import { Fragment as Fragment17, jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
function FormatMenu(props) {
  const { editor } = props;
  if (!isSelectionInsideTable(editor.state.selection)) return null;
  return /* @__PURE__ */ jsxs9(Fragment17, { children: [
    /* @__PURE__ */ jsxs9(DropdownMenu5.Root, { children: [
      /* @__PURE__ */ jsx10(Toolbar6.Button, { asChild: true, children: /* @__PURE__ */ jsx10(DropdownMenu5.Trigger, { asChild: true, children: /* @__PURE__ */ jsx10(
        StyledControlButton,
        {
          withDownCaret: true,
          iconName: "PaintRoller",
          "aria-label": "Format Table",
          children: "Format"
        }
      ) }) }),
      /* @__PURE__ */ jsx10(DropdownMenu5.Content, { children: /* @__PURE__ */ jsx10(FormatDropDownContent, { editor }) })
    ] }),
    /* @__PURE__ */ jsx10(GroupDivider2, {})
  ] });
}
var StyledControlButton = styled7(ControlButton7)`
  ${ControlButtonText} {
    min-width: 50px;
    display: inline-flex;
    align-items: center;
    color: ${colors8.grey500};
    font-weight: 600;
  }
`;
function FormatDropDownContent(props) {
  const { editor } = props;
  const { hasRowHeader, hasColumnHeader } = useEditorState10({
    editor,
    selector: (ctx) => ({
      hasRowHeader: hasHeaderRow(ctx.editor.state),
      hasColumnHeader: hasHeaderColumn(ctx.editor.state)
    })
  });
  return /* @__PURE__ */ jsxs9(Fragment17, { children: [
    /* @__PURE__ */ jsx10(
      DropdownMenu5.CheckboxItem,
      {
        toggleable: true,
        iconName: "HeaderRow",
        "aria-label": "toggle header row",
        checked: hasRowHeader,
        onSelect: (e) => {
          e.preventDefault();
          editor.commands.toggleHeaderRow();
        },
        children: /* @__PURE__ */ jsx10(Text3, { kind: "bodySm", color: "grey500", children: "Header Row" })
      },
      "HeaderRow"
    ),
    /* @__PURE__ */ jsx10(
      DropdownMenu5.CheckboxItem,
      {
        toggleable: true,
        iconName: "HeaderColumn",
        "aria-label": "toggle header column",
        checked: hasColumnHeader,
        onSelect: (e) => {
          e.preventDefault();
          editor.commands.toggleHeaderColumn();
        },
        children: /* @__PURE__ */ jsx10(Text3, { kind: "bodySm", color: "grey500", children: "Header Column" })
      },
      "HeaderColumn"
    ),
    /* @__PURE__ */ jsx10(DropdownMenu5.Separator, {}),
    /* @__PURE__ */ jsx10(FormatRowAlternateColor, { editor })
  ] });
}
function FormatRowAlternateColor(props) {
  const { editor } = props;
  const { alternate, activeColor } = useEditorState10({
    editor,
    selector: (ctx) => ({
      alternate: hasAlternateColor(ctx.editor.state),
      activeColor: tableColor(ctx.editor.state)
    })
  });
  return /* @__PURE__ */ jsxs9(Fragment17, { children: [
    /* @__PURE__ */ jsx10(
      DropdownMenu5.CheckboxItem,
      {
        toggleable: true,
        checked: alternate,
        onSelect: (e) => {
          e.preventDefault();
          editor.commands.toggleTableAlternateColor();
        },
        iconName: "Paint",
        "aria-label": "alternate row colour",
        children: /* @__PURE__ */ jsx10(Text3, { kind: "bodySm", color: "grey500", children: "Alternating row colour" })
      }
    ),
    alternate && /* @__PURE__ */ jsx10(FormatRowColor, { activeColor, editor })
  ] });
}

// src/extensions/table/components/MergeSplitMenu.tsx
import { ControlButton as ControlButton8, Toolbar as Toolbar7 } from "@vericus/cadmus-ui";
import { useEditorState as useEditorState11 } from "@tiptap/react";
import { Fragment as Fragment18, jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
function MergeSplitMenu(props) {
  const { editor } = props;
  const { canMerge, canSplit: canSplit2, canMergeOrSplit } = useEditorState11({
    editor,
    selector: (ctx) => ({
      canMergeOrSplit: ctx.editor.can().mergeOrSplit(),
      canMerge: ctx.editor.can().mergeCells(),
      canSplit: ctx.editor.can().splitCell()
    })
  });
  if (!canMergeOrSplit) return null;
  return /* @__PURE__ */ jsxs10(Fragment18, { children: [
    canMerge && /* @__PURE__ */ jsx11(Toolbar7.Button, { asChild: true, children: /* @__PURE__ */ jsx11(
      ControlButton8,
      {
        "aria-label": "Merge cells",
        iconName: "MergeCells",
        onClick: () => editor.chain().mergeCells().focus().run()
      }
    ) }),
    canSplit2 && /* @__PURE__ */ jsx11(Toolbar7.Button, { asChild: true, children: /* @__PURE__ */ jsx11(
      ControlButton8,
      {
        "aria-label": "Split Cell",
        iconName: "SplitCells",
        onClick: () => editor.chain().splitCell().focus().run()
      }
    ) }),
    /* @__PURE__ */ jsx11(GroupDivider2, {})
  ] });
}

// src/extensions/table/components/TableMenu.tsx
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
function TableMenuComponent(props) {
  const { editor, portalId } = props;
  const shouldShow = useCallback5(({ state }) => {
    return isSelectionInsideTableGroup(state.selection);
  }, []);
  const getBoundingClientRect = useCallback5(() => {
    const { state } = editor.view;
    const { selection } = state;
    const tableGroup = findTableGroup(selection);
    const { ranges } = selection;
    const from = Math.min(...ranges.map((range) => range.$from.pos));
    const to = Math.max(...ranges.map((range) => range.$to.pos));
    if (tableGroup) {
      const { table_caption: tableCaptionType } = getTableNodeTypes(
        state.schema
      );
      const tableCaption = findChildren7(
        tableGroup.node,
        (node) => node.type === tableCaptionType
      )[0];
      const captionOffset = tableCaption && tableCaption.node.attrs.hideText ? tableCaption.node.nodeSize + 2 : 0;
      return posToDOMRect4(
        editor.view,
        tableGroup.start + captionOffset,
        tableGroup.pos + tableGroup.node.nodeSize
      );
    }
    return posToDOMRect4(editor.view, from, to);
  }, [editor]);
  return /* @__PURE__ */ jsx12(
    FloatingMenuComponent,
    {
      pluginKey: "tableFloatingMenu",
      editor,
      shouldShow,
      getBoundingClientRect,
      portalId,
      isAsync: true,
      mainAxisOffset: 25,
      children: /* @__PURE__ */ jsx12(MenuWrapper, { children: /* @__PURE__ */ jsxs11(
        ButtonGroups2,
        {
          id: "editor-table-menu",
          "aria-label": "Table menu",
          onKeyDown: (e) => {
            if (e.key === "Escape") {
              editor.commands.focus();
            }
          },
          "aria-keyshortcuts": "Alt+ArrowDown",
          children: [
            /* @__PURE__ */ jsx12(CellColorMenu, { editor }),
            /* @__PURE__ */ jsx12(BlockMenu, { editor }),
            /* @__PURE__ */ jsx12(FormatMenu, { editor }),
            /* @__PURE__ */ jsx12(MergeSplitMenu, { editor }),
            /* @__PURE__ */ jsx12(BorderMenu, { editor }),
            /* @__PURE__ */ jsx12(DeleteMenu, { editor })
          ]
        }
      ) })
    }
  );
}
var MenuWrapper = styled8.div`
  display: inline-flex;
  background: transparent;
  border: none;
  outline: none;
  width: 500px;
  justify-content: center;
  align-items: center;
`;

// src/extensions/table/table.ts
import { Extension as Extension19, findChildren as findChildren9 } from "@tiptap/core";
import { TableRow } from "@tiptap/extension-table-row";
import { Fragment as Fragment19 } from "@tiptap/pm/model";
import { TextSelection as TextSelection6 } from "@tiptap/pm/state";
import {
  __clipCells,
  __insertCells,
  __pastedCells,
  addColumn,
  addRow,
  CellSelection as CellSelection3,
  isInTable as isInTable2,
  selectionCell,
  TableMap as TableMap13
} from "@tiptap/pm/tables";
import { Transform } from "@tiptap/pm/transform";

// src/extensions/table/extensions/nodes/table.ts
import { Table as TiptapTable } from "@tiptap/extension-table";
import { tableEditing } from "@tiptap/pm/tables";
import { mergeAttributes as mergeAttributes16, ReactNodeViewRenderer } from "@tiptap/react";

// src/extensions/table/components/TableComponent.tsx
import { useEffect as useEffect6, useRef as useRef5, useState as useState5 } from "react";
import { styled as styled10 } from "styled-components";
import { NodeViewContent, NodeViewWrapper } from "@tiptap/react";

// src/extensions/table/components/TableControl.tsx
import { forwardRef, useCallback as useCallback6, useEffect as useEffect5, useRef as useRef4 } from "react";
import { styled as styled9, css as css12 } from "styled-components";
import { colors as colors9, Toolbar as Toolbar8 } from "@vericus/cadmus-ui";
import { TableMap as TableMap11 } from "@tiptap/pm/tables";

// src/extensions/table/components/useTableControl.ts
import {
  useEffect as useEffect4,
  useLayoutEffect as useLayoutEffect2,
  useState as useState4
} from "react";
import { TableMap as TableMap10 } from "@tiptap/pm/tables";
import useResizeObserver from "use-resize-observer/polyfilled.js";

// src/extensions/table/plugins/table-column-resizing-plugin.ts
import { Plugin as Plugin25, PluginKey as PluginKey19 } from "@tiptap/pm/state";
import { cellAround, TableMap as TableMap8 } from "@tiptap/pm/tables";
import { Decoration as Decoration9, DecorationSet as DecorationSet9 } from "@tiptap/pm/view";
function pointsAtCell($pos) {
  return $pos.parent.type.spec.tableRole == "row" && $pos.nodeAfter;
}
function setAttr(attrs, name, value) {
  const result = {};
  for (const prop in attrs) result[prop] = attrs[prop];
  result[name] = value;
  return result;
}
var columnResizingPluginKey = new PluginKey19("tableColumnResizing");
function columnResizing({
  editor,
  handleWidth = 5,
  cellMinWidth: cellMinWidth2 = 25,
  lastColumnResizable = true
} = {}) {
  return new Plugin25({
    key: columnResizingPluginKey,
    state: {
      init() {
        return new ResizeState(-1, false);
      },
      apply(tr, prev) {
        return prev.apply(tr);
      }
    },
    props: {
      attributes(state) {
        const pluginState = columnResizingPluginKey.getState(state);
        return pluginState.activeHandle > -1 ? { class: "resize-cursor" } : { class: "" };
      },
      handleDOMEvents: {
        mousemove(view, event) {
          if (editor?.isEditable) {
            handleMouseMove(
              view,
              event,
              handleWidth,
              cellMinWidth2,
              lastColumnResizable
            );
          }
          return false;
        },
        mouseleave(view) {
          if (editor?.isEditable) {
            handleMouseLeave(view);
          }
          return false;
        },
        mousedown(view, event) {
          if (editor?.isEditable) {
            handleMouseDown(view, event, cellMinWidth2);
          }
          return false;
        }
      },
      decorations(state) {
        const pluginState = columnResizingPluginKey.getState(state);
        if (pluginState.activeHandle > -1)
          return handleDecorations(state, pluginState.activeHandle);
      },
      nodeViews: {}
    }
  });
}
var ResizeState = class _ResizeState {
  activeHandle;
  dragging;
  constructor(activeHandle, dragging) {
    this.activeHandle = activeHandle;
    this.dragging = dragging;
  }
  apply(tr) {
    let state = this;
    const action = tr.getMeta(columnResizingPluginKey);
    if (action && action.setHandle != null)
      return new _ResizeState(action.setHandle, null);
    if (action && action.setDragging !== void 0)
      return new _ResizeState(state.activeHandle, action.setDragging);
    if (state.activeHandle && state.activeHandle > -1 && tr.docChanged) {
      let handle = tr.mapping.map(state.activeHandle, -1);
      if (handle !== null && !pointsAtCell(tr.doc.resolve(handle))) handle = -1;
      state = new _ResizeState(handle, state.dragging);
    }
    return state;
  }
};
function handleMouseMove(view, event, handleWidth, _cellMinWidth, lastColumnResizable) {
  const pluginState = columnResizingPluginKey.getState(view.state);
  if (!pluginState) return;
  if (!pluginState.dragging) {
    const target = domCellAround(event.target);
    let cell = -1;
    if (target) {
      const { left, right } = target.getBoundingClientRect();
      if (event.clientX - left <= handleWidth)
        cell = edgeCell(view, event, "left", handleWidth);
      else if (right - event.clientX <= handleWidth)
        cell = edgeCell(view, event, "right", handleWidth);
    }
    if (cell != pluginState.activeHandle) {
      if (!lastColumnResizable && cell !== -1) {
        const $cell = view.state.doc.resolve(cell);
        const table = $cell.node(-1), map = TableMap8.get(table), start = $cell.start(-1);
        const col = map.colCount($cell.pos - start) + $cell?.nodeAfter?.attrs.colspan - 1;
        if (col == map.width - 1) {
          return;
        }
      }
      updateHandle(view, cell);
    }
  }
}
function handleMouseLeave(view) {
  const pluginState = columnResizingPluginKey.getState(view.state);
  if (pluginState.activeHandle > -1 && !pluginState.dragging)
    updateHandle(view, -1);
}
function handleMouseDown(view, event, cellMinWidth2) {
  const win = view.dom.ownerDocument.defaultView ?? window;
  const pluginState = columnResizingPluginKey.getState(view.state);
  if (!pluginState || pluginState.activeHandle == -1 || pluginState.dragging)
    return false;
  const cell = view.state.doc.nodeAt(pluginState.activeHandle);
  if (cell) {
    const width = currentColWidth(
      view,
      pluginState.activeHandle,
      cell.attrs
    );
    view.dispatch(
      view.state.tr.setMeta(columnResizingPluginKey, {
        setDragging: { startX: event.clientX, startWidth: width }
      })
    );
  }
  function finish(event2) {
    win.removeEventListener("mouseup", finish);
    win.removeEventListener("mousemove", move);
    const pluginState2 = columnResizingPluginKey.getState(view.state);
    if (pluginState2?.dragging) {
      updateColumnWidth(
        view,
        pluginState2.activeHandle,
        draggedWidth(pluginState2.dragging, event2, cellMinWidth2)
      );
      view.dispatch(
        view.state.tr.setMeta(columnResizingPluginKey, { setDragging: null })
      );
    }
  }
  function move(event2) {
    if (!event2.which) return finish(event2);
    const pluginState2 = columnResizingPluginKey.getState(view.state);
    if (!pluginState2) return;
    const dragged = draggedWidth(pluginState2.dragging, event2, cellMinWidth2);
    displayColumnWidth(view, pluginState2.activeHandle, dragged, cellMinWidth2);
  }
  win.addEventListener("mouseup", finish);
  win.addEventListener("mousemove", move);
  event.preventDefault();
  return true;
}
function currentColWidth(view, cellPos, { colspan, colwidth }) {
  const width = colwidth && colwidth[colwidth.length - 1];
  if (width) return width;
  const dom = view.domAtPos(cellPos);
  const node = dom.node.childNodes[dom.offset];
  let domWidth = node.offsetWidth, parts = colspan;
  if (colwidth) {
    for (let i = 0; i < colspan; i++)
      if (colwidth[i]) {
        domWidth -= colwidth[i];
        parts--;
      }
  }
  return domWidth / parts;
}
function domCellAround(target) {
  while (target && target.nodeName != "TD" && target.nodeName != "TH")
    target = target.classList.contains("ProseMirror") ? null : target.parentNode;
  return target;
}
function edgeCell(view, event, side, handleWidth) {
  const offset2 = side == "right" ? -handleWidth : handleWidth;
  const found = view.posAtCoords({
    left: event.clientX + offset2,
    top: event.clientY
  });
  if (!found) return -1;
  const { pos } = found;
  const $cell = cellAround(view.state.doc.resolve(pos));
  if (!$cell) return -1;
  if (side == "right") return $cell.pos;
  const map = TableMap8.get($cell.node(-1)), start = $cell.start(-1);
  const index = map.map.indexOf($cell.pos - start);
  return index % map.width == 0 ? -1 : start + map.map[index - 1];
}
function draggedWidth(dragging, event, cellMinWidth2) {
  const offset2 = event.clientX - dragging.startX;
  return Math.max(cellMinWidth2, dragging.startWidth + offset2);
}
function updateHandle(view, value) {
  view.dispatch(
    view.state.tr.setMeta(columnResizingPluginKey, { setHandle: value })
  );
}
function updateColumnWidth(view, cell, width) {
  const $cell = view.state.doc.resolve(cell);
  if (!$cell) return;
  const table = $cell.node(-1), map = TableMap8.get(table), start = $cell.start(-1);
  const col = map.colCount($cell.pos - start) + $cell.nodeAfter?.attrs.colspan - 1;
  const tr = view.state.tr;
  for (let row = 0; row < map.height; row++) {
    const mapIndex = row * map.width + col;
    if (row && map.map[mapIndex] == map.map[mapIndex - map.width]) continue;
    const pos = map.map[mapIndex];
    const cell2 = table.nodeAt(pos);
    if (!cell2) continue;
    const { attrs } = cell2;
    const index = attrs.colspan == 1 ? 0 : col - map.colCount(pos);
    if (attrs.colwidth && attrs.colwidth[index] == width) continue;
    const colwidth = attrs.colwidth ? attrs.colwidth.slice() : zeroes(attrs.colspan);
    colwidth[index] = width;
    tr.setNodeMarkup(start + pos, null, setAttr(attrs, "colwidth", colwidth));
  }
  if (tr.docChanged) view.dispatch(tr);
}
function displayColumnWidth(view, cell, width, cellMinWidth2) {
  const $cell = view.state.doc.resolve(cell);
  const table = $cell.node(-1), start = $cell.start(-1);
  const col = TableMap8.get(table).colCount($cell.pos - start) + $cell.nodeAfter?.attrs.colspan - 1;
  let dom = view.domAtPos($cell.start(-1)).node;
  while (dom?.nodeName != "TABLE") dom = dom.parentNode;
  if (!dom) return;
  updateColumns(
    table,
    dom.firstChild,
    dom,
    cellMinWidth2,
    col,
    width
  );
}
function zeroes(n) {
  return new Array(n).fill(0);
}
function handleDecorations(state, cell) {
  const decorations = [];
  const $cell = state.doc.resolve(cell);
  const table = $cell.node(-1), map = TableMap8.get(table), start = $cell.start(-1);
  const col = map.colCount($cell.pos - start) + $cell.nodeAfter?.attrs.colspan - 1;
  for (let row = 0; row < map.height; row++) {
    const index = col + row * map.width;
    if ((col == map.width || map.map[index] != map.map[index + 1]) && (row == 0 || map.map[index] != map.map[index - map.width])) {
      const cellPos = map.map[index];
      const cell2 = table.nodeAt(cellPos);
      if (cell2) {
        const pos = start + cellPos + cell2.nodeSize - 1;
        const dom = document.createElement("div");
        dom.className = "column-resize-handle";
        decorations.push(Decoration9.widget(pos, dom));
      }
    }
  }
  return DecorationSet9.create(state.doc, decorations);
}
function updateColumns(node, colgroup, table, cellMinWidth2, overrideCol, overrideValue) {
  let totalWidth = 0;
  let fixedWidth = true;
  let nextDOM = colgroup.firstChild;
  const row = node.firstChild;
  if (!row) return;
  for (let i = 0, col = 0; i < row.childCount; i += 1) {
    const { colspan, colwidth } = row.child(i).attrs;
    for (let j = 0; j < colspan; j += 1, col += 1) {
      const hasWidth = overrideCol === col ? overrideValue : colwidth && colwidth[j];
      const cssWidth = hasWidth ? `${hasWidth}px` : "";
      totalWidth += hasWidth || cellMinWidth2;
      if (!hasWidth) {
        fixedWidth = false;
      }
      if (!nextDOM) {
        colgroup.appendChild(document.createElement("col")).style.width = cssWidth;
      } else {
        if (nextDOM.style.width !== cssWidth) {
          nextDOM.style.width = cssWidth;
        }
        nextDOM = nextDOM.nextSibling;
      }
    }
  }
  while (nextDOM) {
    const after = nextDOM.nextSibling;
    nextDOM.parentNode?.removeChild(nextDOM);
    nextDOM = after;
  }
  if (fixedWidth) {
    table.style.width = `${totalWidth > 800 ? 800 : totalWidth}px`;
    table.style.minWidth = "";
  } else {
    table.style.width = "";
    table.style.minWidth = `${totalWidth > 800 ? 800 : totalWidth}px`;
  }
}

// src/extensions/table/plugins/table-control-plugin.ts
import { Plugin as Plugin26, PluginKey as PluginKey20 } from "@tiptap/pm/state";
import { TableMap as TableMap9 } from "@tiptap/pm/tables";
import { Decoration as Decoration10, DecorationSet as DecorationSet10 } from "@tiptap/pm/view";
import crelt from "crelt";
var TableControlPluginKey = new PluginKey20("tableControl");
var TableControlPlugin = (opts) => new Plugin26({
  key: TableControlPluginKey,
  state: {
    init() {
      return {
        row: null,
        col: null
      };
    },
    apply(tr, oldState) {
      const payload = tr.getMeta(TableControlPluginKey);
      if (payload) {
        return payload;
      }
      return oldState;
    }
  },
  props: {
    decorations: (state) => {
      if (!opts.editor.isEditable) return DecorationSet10.empty;
      const { doc, selection } = state;
      const decorations = [];
      const table = findTable(selection);
      const isResizing = !!columnResizingPluginKey.getState(state)?.dragging;
      const pluginState = TableControlPluginKey.getState(state);
      if (table && !isResizing) {
        const map = TableMap9.get(table.node);
        if (pluginState.row !== null && pluginState.row !== map.height) {
          map.cellsInRect({
            left: 0,
            right: map.width,
            top: pluginState.row,
            bottom: pluginState.row + 1
          }).map((cellPos) => {
            decorations.push(
              Decoration10.widget(
                table.start + cellPos + 1,
                crelt("div", {
                  class: "row-insert-handle"
                })
              )
            );
          });
        }
        if (pluginState.col !== null && pluginState.col !== map.width) {
          map.cellsInRect({
            left: pluginState.col,
            right: pluginState.col + 1,
            top: 0,
            bottom: map.height
          }).map((cellPos) => {
            decorations.push(
              Decoration10.widget(
                table.start + cellPos + 1,
                crelt("div", {
                  class: "col-insert-handle"
                })
              )
            );
          });
        }
      }
      return DecorationSet10.create(doc, decorations);
    }
  }
});

// src/extensions/table/plugins/table-delete-indicator-plugin.ts
import { Plugin as Plugin27, PluginKey as PluginKey21 } from "@tiptap/pm/state";
import { Decoration as Decoration11, DecorationSet as DecorationSet11 } from "@tiptap/pm/view";
var TableDeleteIndicatorPluginKey = new PluginKey21(
  "TableDeleteIndicator"
);
var TableDeleteIndicatorPlugin = () => new Plugin27({
  key: TableDeleteIndicatorPluginKey,
  state: {
    init() {
      return {
        highlight: false
      };
    },
    apply(tr, currentState) {
      const payload = tr.getMeta(TableDeleteIndicatorPluginKey);
      if (payload) {
        return payload;
      }
      return currentState;
    }
  },
  props: {
    decorations(editorState) {
      const deleteIndicatorState = this.getState(editorState);
      if (deleteIndicatorState?.highlight) {
        const table = findTable(editorState.selection);
        const decorations = [];
        if (table) {
          const cellsMap = getCellsMap(
            editorState.schema,
            editorState.selection
          );
          cellsMap?.cells?.forEach((cell) => {
            decorations.push(
              Decoration11.node(cell.pos, cell.pos + cell.node.nodeSize, {
                class: "cell-predelete"
              })
            );
          });
          return DecorationSet11.create(editorState.doc, decorations);
        }
      }
      return DecorationSet11.empty;
    }
  }
});

// src/extensions/table/plugins/table-ensure-group-plugin.ts
import { findChildren as findChildren8, findParentNodeClosestToPos as findParentNodeClosestToPos2 } from "@tiptap/core";
import { Plugin as Plugin28, PluginKey as PluginKey22 } from "@tiptap/pm/state";
var TableEnsureGroupPlugin = () => new Plugin28({
  key: new PluginKey22("tableEnsureGroup"),
  appendTransaction: (_tr, oldState, editorState) => {
    if (oldState.doc === editorState.doc) return;
    const { doc, schema, tr } = editorState;
    const tableNodeTypes = getTableNodeTypes(schema);
    const tables = findChildren8(doc, (node) => {
      return node.type === getTableNodeTypes(schema).table;
    });
    let fixing = false;
    tables.sort((a, b) => {
      if (a.pos > b.pos) {
        return -1;
      } else if (a.pos < b.pos) {
        return 1;
      }
      return 0;
    }).map(({ node, pos }) => {
      const $pos = doc.resolve(pos);
      const tableGroup = findParentNodeClosestToPos2(
        $pos,
        (node2) => node2.type === tableNodeTypes.table_group
      );
      if (!tableGroup) {
        const emptyCaption = tableNodeTypes.table_caption.createAndFill();
        const emptyFootnote = tableNodeTypes.table_footnote.createAndFill();
        if (emptyCaption && emptyFootnote) {
          const newNode = tableNodeTypes.table_group.createChecked(
            void 0,
            [emptyCaption, node, emptyFootnote]
          );
          tr.replaceWith(pos, pos + node.nodeSize, newNode);
          fixing = true;
        }
      } else if (
        // delete orphaned tableGroup
        findChildren8(
          tableGroup.node,
          (node2) => node2.type === tableNodeTypes.cell
        ).length === 0
      ) {
        tr.delete(
          tableGroup.pos,
          tableGroup.pos + tableGroup.node.nodeSize
        );
        fixing = true;
      }
    });
    if (fixing) return tr;
    return;
  }
});

// src/extensions/table/components/useTableControl.ts
var useComponentBoundingRect = (target) => {
  const [boundingRect, setBoundingRect] = useState4(null);
  useLayoutEffect2(() => {
    if (target.current) {
      setBoundingRect(target.current.getBoundingClientRect());
    }
  }, [target]);
  useResizeObserver({
    ref: target.current,
    onResize: () => target.current && setBoundingRect(target.current?.getBoundingClientRect())
  });
  return boundingRect;
};
var useTableControl = (props) => {
  const { editor, node, tableRef, colNum, getPos } = props;
  const [colWidths, setColWidths] = useState4([]);
  const [rowHeights, setRowHeights] = useState4();
  const [isResizing, setIsResizing] = useState4(false);
  const [isEditing, setIsEditing] = useState4(false);
  const [selectedRows, setSelectedRows] = useState4([]);
  const [selectedColumns, setSelectedColumns] = useState4([]);
  const [isSelected, setIsSelected] = useState4(false);
  const [predelete, setPredelete] = useState4(false);
  const tableRect = useComponentBoundingRect(tableRef);
  useEffect4(() => {
    if (tableRef.current) {
      setRowHeights(getRowHeights(tableRef.current));
      setColWidths(getColWidths(tableRef.current, node));
    }
  }, [tableRef, node, tableRect, colNum, isResizing]);
  useEffect4(() => {
    const handleResize = ({ transaction }) => {
      const pos = getPos();
      const { selection } = transaction;
      const resizingMeta = transaction.getMeta(columnResizingPluginKey);
      if (resizingMeta) {
        const resizingState = columnResizingPluginKey.getState(editor.state);
        setIsResizing(!!resizingState?.dragging);
      }
      const predeleteMeta = transaction.getMeta(TableDeleteIndicatorPluginKey);
      if (predeleteMeta) {
        setPredelete(!!predeleteMeta.highlight);
      }
      const table = findTable(transaction.selection);
      if (table?.pos === pos && table?.node) {
        setIsEditing(true);
        const map = TableMap10.get(table.node);
        const newSelectedRows = [];
        const newSelectedColumns = [];
        for (let row = 0; row < map.height; row++) {
          const rowSelected = isRowSelected(row)(selection);
          if (rowSelected) {
            newSelectedRows.push(row);
          }
        }
        for (let col = 0; col < map.width; col++) {
          const colSelected = isColumnSelected(col)(selection);
          if (colSelected) {
            newSelectedColumns.push(col);
          }
        }
        setSelectedRows(newSelectedRows);
        setSelectedColumns(newSelectedColumns);
        setIsSelected(isTableSelected(selection));
      } else {
        setIsEditing(false);
        setSelectedRows([]);
        setSelectedColumns([]);
        setIsSelected(false);
      }
    };
    editor.on("transaction", handleResize);
    return () => {
      editor?.off("transaction", handleResize);
    };
  }, [editor, node, tableRef, getPos]);
  return {
    colWidths,
    rowHeights,
    tableRect,
    isResizing,
    isEditing,
    isSelected,
    selectedColumns,
    selectedRows,
    predelete
  };
};

// src/extensions/table/components/TableControl.tsx
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
function TableControl(props) {
  const { editor, tableRef, node } = props;
  const columnControlRef = useRef4(null);
  const rowControlRef = useRef4(null);
  const expandTableControlRef = useRef4(null);
  const handleKeyboardShortcut = useCallback6((e) => {
    if (e.altKey && e.code === "KeyC") columnControlRef.current?.focus();
    else if (e.altKey && e.code === "KeyR") rowControlRef.current?.focus();
    else if (e.altKey && e.code === "KeyA")
      expandTableControlRef.current?.focus();
  }, []);
  const {
    colWidths,
    rowHeights,
    tableRect,
    isEditing,
    isResizing,
    isSelected,
    predelete,
    selectedColumns,
    selectedRows
  } = useTableControl(props);
  useEffect5(() => {
    if (isEditing && editor.isFocused) {
      document.addEventListener("keydown", handleKeyboardShortcut);
      return () => document.removeEventListener("keydown", handleKeyboardShortcut);
    }
  }, [isEditing, editor.isFocused, handleKeyboardShortcut]);
  if (!tableRef.current || !tableRect || isResizing || !isEditing) return null;
  const { marginTop } = getComputedStyle(tableRef.current);
  return /* @__PURE__ */ jsxs12(
    TableControlWrapper,
    {
      contentEditable: false,
      style: {
        "--tableTop": `${tableRect.top}px`,
        "--tableWidth": `${tableRect.width}px`,
        "--tableHeight": `${tableRect.height}px`,
        "--tableMarginTop": `${marginTop}`
      },
      onKeyDown: (e) => {
        if (e.key === "Escape") editor.commands.focus();
        if (e.code === "KeyM" && e.altKey) {
          const tableMenu = document.getElementById("editor-table-menu");
          tableMenu?.focus();
        }
      },
      children: [
        /* @__PURE__ */ jsx13(
          TableSelectAll,
          {
            "aria-keyshortcuts": "Alt+T",
            "aria-label": "Select the whole table",
            selected: isSelected,
            predelete,
            onClick: (e) => {
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              editor.commands.selectTable();
            }
          }
        ),
        /* @__PURE__ */ jsx13(
          TableColumnControlGroup,
          {
            ref: columnControlRef,
            selectedColumns,
            predelete,
            colWidths,
            editor
          }
        ),
        /* @__PURE__ */ jsx13(
          TableRowControlGroup,
          {
            ref: rowControlRef,
            selectedRows,
            predelete,
            rowHeights,
            editor
          }
        ),
        /* @__PURE__ */ jsxs12(
          Toolbar8.Root,
          {
            ref: expandTableControlRef,
            "aria-label": "Expand table",
            "aria-keyshortcuts": "Alt+A",
            orientation: "vertical",
            children: [
              /* @__PURE__ */ jsx13(Toolbar8.Button, { asChild: true, children: /* @__PURE__ */ jsx13(
                TableInsertRowAfter,
                {
                  "aria-label": "Add new row",
                  onClick: (e) => {
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    const map = TableMap11.get(node);
                    editor.commands.addRowAt(map.height);
                  },
                  onFocus: () => {
                    const map = TableMap11.get(node);
                    editor.commands.preinsertRow(map.height);
                  },
                  onBlur: () => {
                    editor.commands.preinsertRow();
                  },
                  onMouseEnter: () => {
                    const map = TableMap11.get(node);
                    editor.commands.preinsertRow(map.height);
                  },
                  onMouseLeave: () => {
                    editor.commands.preinsertRow();
                  }
                }
              ) }),
              /* @__PURE__ */ jsx13(Toolbar8.Button, { asChild: true, children: /* @__PURE__ */ jsx13(
                TableInsertColumnAfter,
                {
                  "aria-label": "Add new column",
                  onClick: (e) => {
                    e.preventDefault();
                    e.nativeEvent.stopImmediatePropagation();
                    const map = TableMap11.get(node);
                    editor.commands.addColumnAt(map.width);
                  },
                  onFocus: () => {
                    const map = TableMap11.get(node);
                    editor.commands.preinsertCol(map.width);
                  },
                  onBlur: () => {
                    editor.commands.preinsertCol();
                  },
                  onMouseEnter: () => {
                    const map = TableMap11.get(node);
                    editor.commands.preinsertCol(map.width);
                  },
                  onMouseLeave: () => {
                    editor.commands.preinsertCol();
                  }
                }
              ) })
            ]
          }
        )
      ]
    }
  );
}
var controlFocus = css12`
  &:focus {
    &::after {
      ${(p) => p.theme.focus}
      z-index: 1;
    }
  }
`;
var TableControlWrapper = styled9.div`
  position: absolute;
  width: calc(var(--tableWidth) + 36px);
  height: calc(var(--tableHeight) + 36px);
  top: calc(var(--tableMarginTop) - 18px);
  left: -18px;
`;
var TableSelectAll = styled9.button`
  position: absolute;
  padding: 0;
  border: none;
  left: 21px;
  top: 21px;
  &::after {
    content: "";
    cursor: pointer;
    width: 10px;
    height: 10px;
    border: 1px solid #c3cddf;
    box-sizing: border-box;
    border-radius: 2px;
    position: absolute;
    top: -13px;
    left: -13px;
    background: ${(p) => getHandleColor(p)};
  }

  ${controlFocus}

  &:hover::after {
    background: ${colors9.grey200};
  }
`;
var TableInsertColumnAfter = styled9.button`
  position: absolute;
  top: 8px;
  width: 16px;
  font-size: 12px;
  left: calc(var(--tableWidth) + 20px);
  height: calc(var(--tableHeight) + 28px);
  padding: 0;
  border: none;

  &:focus {
    outline: none;
  }

  &::after {
    content: "+";
    cursor: pointer;
    position: relative;
    color: ${colors9.grey500};
    font-weight: 900;
    background: #f4f5f8;
    border: 1px solid #f4f5f8;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 0px 2px 2px 0px;
    width: 100%;
    height: 100%;
  }

  ${controlFocus}

  &:hover::after {
    background: ${colors9.indigo500};
    border: 1px solid ${colors9.indigo500};
    color: white;
  }
`;
var TableInsertRowAfter = styled9.button`
  position: absolute;
  height: 16px;
  font-size: 12px;
  left: 8px;
  padding: 0;
  border: none;
  top: calc(var(--tableHeight) + 20px);
  width: calc(var(--tableWidth) + 10px);
  &::after {
    content: "+";
    cursor: pointer;
    position: relative;
    color: ${colors9.grey500};
    font-weight: 900;
    background: #f4f5f8;
    border: 1px solid #f4f5f8;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 0px 0px 0px 2px;
    width: 100%;
    height: 100%;
  }

  &:focus {
    outline: none;
  }

  ${controlFocus}

  &:hover::after {
    background: ${colors9.indigo500};
    border: 1px solid ${colors9.indigo500};
    color: white;
  }
`;
function getHandleColor(props) {
  const { predelete, selected } = props;
  if (predelete && selected) return colors9.red500;
  if (selected) return colors9.indigo500;
  return "#f4f5f8";
}
var TableColumnControlGroup = forwardRef(function(props, ref) {
  const { editor, colWidths, selectedColumns, predelete, ...rest } = props;
  if (!colWidths) return null;
  return /* @__PURE__ */ jsx13(
    TableColumnControlGroupWrapper,
    {
      ref,
      orientation: "horizontal",
      "aria-label": "Column menu",
      "aria-keyshortcuts": "Alt+C",
      ...rest,
      children: colWidths.map((width, index) => /* @__PURE__ */ jsxs12(TableColumnControl, { style: { width }, children: [
        /* @__PURE__ */ jsx13(Toolbar8.Button, { asChild: true, children: /* @__PURE__ */ jsx13(
          TableColumnInsertBeforeControl,
          {
            "aria-label": `Insert new column as column ${index + 1}`,
            onClick: (e) => {
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              editor.commands.addColumnAt(index);
            },
            onFocus: () => {
              editor.commands.preinsertCol(index);
            },
            onBlur: () => {
              editor.commands.preinsertCol();
            },
            onMouseEnter: () => {
              editor.commands.preinsertCol(index);
            },
            onMouseLeave: () => {
              editor.commands.preinsertCol();
            }
          }
        ) }),
        /* @__PURE__ */ jsx13(Toolbar8.Button, { asChild: true, children: /* @__PURE__ */ jsx13(
          TableColumnGripControl,
          {
            "aria-label": `Select column ${index + 1}`,
            onClick: (e) => {
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              editor.commands.selectColumn(index);
            },
            predelete,
            selected: selectedColumns.includes(index),
            first: index === 0,
            last: index === colWidths.length - 1
          }
        ) })
      ] }, index))
    }
  );
});
var TableColumnControlGroupWrapper = styled9(Toolbar8.Root)`
  display: flex;
  flex-direction: row;
  height: 18px;
  position: absolute;
  border-collapse: collapse;
  left: 18px;
`;
var TableColumnControl = styled9.div`
  position: relative;
`;
var TableColumnGripControl = styled9.button`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  flex-grow: 1;
  padding: 0;
  border: none;

  &::after {
    content: "${(p) => p.selected || p.predelete ? "\u22EF" : " "}";
    color: ${(p) => p.selected || p.predelete ? "white" : "inherit"};
    cursor: pointer;
    width: calc(100% - ${(p) => p.first ? "3" : "2"}px);
    height: 8px;
    background: ${(p) => getHandleColor(p)};
    border-top: 1px solid ${(p) => getHandleColor(p)};
    border-bottom: 1px solid ${(p) => getHandleColor(p)};
    border-left: ${(p) => p.first ? "2px" : "1px"} solid
      ${(p) => getHandleColor(p)};
    border-right: ${(p) => p.last ? "2px" : "1px"} solid
      ${(p) => getHandleColor(p)};
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: center;
    font-size: 18px;
    line-height: 8px;
    position: absolute;
    top: -10px;
    border-radius: 2px 2px 0px 0px;
  }

  ${controlFocus}

  ${(p) => !p.selected && css12`
      &:hover::after,
      &:focus::after {
        content: "⋯";
        background: ${colors9.grey200};
        border-color: transparent;
        color: ${colors9.grey500};
      }
    `}
`;
var TableColumnInsertBeforeControl = styled9.button`
  padding: 0;
  border: none;

  &::after {
    content: " ";
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    width: 4px;
    height: 4px;
    background: ${colors9.grey500};
    font-weight: 900;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    line-height: 16px;
    color: white;
    position: absolute;
    left: -2px;
    bottom: 15px;
  }

  ${controlFocus}

  &:hover::after {
    content: "+";
    background: ${colors9.indigo500};
    width: 16px;
    height: 16px;
    left: -8px;
    top: -9px;
  }
`;
var TableRowControlGroup = forwardRef(function(props, ref) {
  const { editor, rowHeights, selectedRows, predelete, ...rest } = props;
  if (!rowHeights) return null;
  return /* @__PURE__ */ jsx13(
    TableRowControlGroupWrapper,
    {
      ref,
      orientation: "vertical",
      "aria-label": "Row menu",
      "aria-keyshortcuts": "Alt+R",
      ...rest,
      children: rowHeights.map((height, index) => /* @__PURE__ */ jsxs12(TableRowControl, { style: { height }, children: [
        /* @__PURE__ */ jsx13(Toolbar8.Button, { asChild: true, children: /* @__PURE__ */ jsx13(
          TableRowInsertBeforeControl,
          {
            "aria-label": `Insert new row as row ${index + 1}`,
            onClick: (e) => {
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              editor.commands.addRowAt(index);
            },
            onFocus: () => {
              editor.commands.preinsertRow(index);
            },
            onBlur: () => {
              editor.commands.preinsertRow();
            },
            onMouseEnter: () => {
              editor.commands.preinsertRow(index);
            },
            onMouseLeave: () => {
              editor.commands.preinsertRow();
            }
          }
        ) }),
        /* @__PURE__ */ jsx13(StyledToolbarButton, { asChild: true, children: /* @__PURE__ */ jsx13(
          TableRowGripControl,
          {
            "aria-label": `Select row ${index + 1}`,
            onClick: (e) => {
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              editor.commands.selectRow(index);
            },
            predelete,
            selected: selectedRows.includes(index),
            first: index === 0,
            last: index === rowHeights.length - 1
          }
        ) })
      ] }, index))
    }
  );
});
var TableRowControlGroupWrapper = styled9(Toolbar8.Root)`
  display: flex;
  flex-direction: column;
  width: 18px;
  position: absolute;
  top: 18px;
  border-collapse: collapse;
`;
var StyledToolbarButton = styled9(Toolbar8.Button)`
  outline: none;
`;
var TableRowControl = styled9.div`
  position: relative;
`;
var TableRowGripControl = styled9.button`
  position: absolute;
  right: 0;
  height: 100%;
  display: flex;
  flex-grow: 1;
  padding: 0;
  top: 0;
  border: none;

  &::after {
    right: 0;
    position: absolute;
    content: "${(p) => p.selected || p.predelete ? "\u22EF" : " "}";
    color: ${(p) => p.selected || p.predelete ? "white" : "inherit"};
    cursor: pointer;
    width: 8px;
    background: ${(p) => getHandleColor(p)};
    border-top: ${(p) => p.first ? "2px" : "1px"} solid
      ${(p) => getHandleColor(p)};
    border-bottom: ${(p) => p.last ? "2px" : "1px"} solid
      ${(p) => getHandleColor(p)};
    border-left: 1px solid ${(p) => getHandleColor(p)};
    border-right: 1px solid ${(p) => getHandleColor(p)};
    writing-mode: vertical-rl;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    align-self: center;
    font-size: 18px;
    height: calc(100% - ${(p) => p.first || p.last ? "3" : "2"}px);
    border-radius: 2px 0px 0px 2px;
  }

  ${controlFocus}

  ${(p) => !p.selected && css12`
      &:hover::after,
      &:focus::after {
        content: "⋯";
        background: ${colors9.grey200};
        border-color: transparent;
        color: ${colors9.grey500};
        writing-mode: vertical-rl;
      }
    `}
`;
var TableRowInsertBeforeControl = styled9.button`
  padding: 0;
  border: none;

  &::after {
    content: " ";
    border-radius: 50%;
    cursor: pointer;
    position: absolute;
    width: 4px;
    height: 4px;
    background: ${colors9.grey500};
    font-weight: 900;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    top: -2px;
    line-height: 16px;
    color: white;
  }

  ${controlFocus}

  &:hover::after {
    content: "+";
    background: ${colors9.indigo500};
    width: 16px;
    height: 16px;
    left: -9px;
    top: -6px;
  }
`;

// src/extensions/table/components/TableComponent.tsx
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
function TableComponent(props) {
  const { node, editor, getPos } = props;
  const [colWidths, setColWidths] = useState5([]);
  const [totalWidth, setTotalWidth] = useState5(0);
  const tableRef = useRef5(null);
  useEffect6(() => {
    const row = node.firstChild;
    if (row) {
      const newColWidths = [];
      let newTotalWidths = 0;
      for (let i = 0, col = 0; i < row.childCount; i += 1) {
        const { colspan, colwidth } = row.child(i).attrs;
        for (let j = 0; j < colspan; j += 1, col += 1) {
          const colWidth = (colwidth && colwidth[j]) ?? cellDefaultWidth;
          newTotalWidths += colWidth;
          newColWidths.push(colWidth);
        }
      }
      setColWidths(newColWidths);
      if (newTotalWidths < maxWidth) {
        setTotalWidth(newTotalWidths);
      } else {
        setTotalWidth(maxWidth);
      }
    }
  }, [node]);
  return /* @__PURE__ */ jsx14(NodeViewWrapper, { children: /* @__PURE__ */ jsx14(Root, { children: /* @__PURE__ */ jsx14(ScollableWrapper, { children: /* @__PURE__ */ jsxs13(Component, { children: [
    editor.isEditable && /* @__PURE__ */ jsx14(
      TableControl,
      {
        editor,
        tableRef,
        node,
        colNum: colWidths.length,
        getPos
      }
    ),
    /* @__PURE__ */ jsxs13(
      "table",
      {
        ref: tableRef,
        "data-table-alternate-background": props.node.attrs.alternateBackground,
        "data-cadmus-background-color": props.node.attrs.backgroundColor,
        style: {
          display: "inline-block",
          width: totalWidth,
          maxWidth: maxWidth - paddingForTableControl,
          "--background-color": props.node.attrs.backgroundColor
        },
        children: [
          /* @__PURE__ */ jsx14("colgroup", { children: colWidths.map((colWidth, index) => /* @__PURE__ */ jsx14("col", { style: { width: colWidth } }, index)) }),
          /* @__PURE__ */ jsx14(NodeViewContent, { as: "tbody" })
        ]
      }
    )
  ] }) }) }) });
}
var Root = styled10.div`
  display: flex;
  justify-content: center;
`;
var ScollableWrapper = styled10.div`
  max-width: 100%;
  overflow-x: auto;
  padding: 0px ${paddingForTableControl / 2}px;
`;
var Component = styled10.div`
  position: relative;
  display: inline-block;
  margin-top: 20px;
`;

// src/extensions/table/extensions/nodes/table.ts
var Table = TiptapTable.extend({
  selectable: false,
  group: "tableBlock",
  renderHTML({ node, HTMLAttributes }) {
    const firstRow = node.firstChild;
    let tableWidth = this.options.cellMinWidth;
    const colWidths = [];
    if (firstRow) {
      for (let i = 0, col = 0; i < firstRow.childCount; i += 1) {
        const { colspan = 0, colwidth } = firstRow.child(i).attrs;
        for (let j = 0; j < colspan; j += 1, col += 1) {
          const width = (colwidth && colwidth[j]) ?? this.options.cellMinWidth;
          tableWidth += width;
          colWidths.push(width);
        }
      }
      if (tableWidth > maxWidth) {
        tableWidth = maxWidth;
      }
    }
    const colgroups = colWidths.map((width) => [
      "col",
      { style: `width: ${width}px;` }
    ]);
    const { style = "" } = HTMLAttributes;
    return [
      "table",
      mergeAttributes16(this.options.HTMLAttributes, {
        ...HTMLAttributes,
        style: `${style};width: ${tableWidth}px;`
      }),
      ["colgroup", ...colgroups || []],
      ["tbody", ["div", 0]]
    ];
  },
  addProseMirrorPlugins() {
    return [
      columnResizing({
        editor: this.editor,
        handleWidth: this.options.handleWidth,
        cellMinWidth: this.options.cellMinWidth,
        lastColumnResizable: this.options.lastColumnResizable
      }),
      tableEditing({
        allowTableNodeSelection: this.options.allowTableNodeSelection
      }),
      TableControlPlugin({ editor: this.editor })
    ];
  },
  addKeyboardShortcuts() {
    return {
      // Not using 'Alt-m' like other editor menus here, becuase other menus can
      // be nested inside table
      "Alt-ArrowDown": () => {
        const tableMenu = document.getElementById("editor-table-menu");
        tableMenu?.focus();
        return true;
      },
      "Alt-t": () => {
        this.editor.commands.selectTable();
        return true;
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(TableComponent);
  }
});

// src/extensions/caption/caption.ts
import { mergeAttributes as mergeAttributes17, Node as Node20 } from "@tiptap/core";
import { Decoration as Decoration12 } from "@tiptap/pm/view";
var Caption = Node20.create({
  name: "caption",
  addOptions() {
    return {
      HTMLAttributes: {},
      placeholderText: "Please enter your caption here..."
    };
  },
  content: "paragraph+",
  isolating: true,
  onCreate() {
    const { type, options, editor } = this;
    const { storage } = editor;
    const { placeholderText } = options;
    const placeholderStorage = storage.placeholder;
    placeholderStorage?.placeholders?.set(this.name, {
      /**
       * Display the placeholder for caption if it has empty text content
       * and not in focus
       */
      check: (selection, node, pos) => {
        if (node.type.name !== type.name) return false;
        const nodeStart = pos;
        const nodeEnd = pos + node.nodeSize;
        const { from, to } = selection;
        if (from >= nodeStart && from <= nodeEnd || to <= nodeEnd && to >= nodeStart)
          return false;
        if (node.textContent === "") return true;
        return false;
      },
      decoration: (node, pos) => {
        const child = node.firstChild;
        if (!child) return null;
        return Decoration12.node(pos + 1, pos + 1 + child.nodeSize, {
          class: "empty-node",
          "data-placeholder-text": placeholderText
        });
      }
    });
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "figcaption",
      mergeAttributes17(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  parseHTML() {
    return [
      {
        tag: "figcaption"
      }
    ];
  }
});

// src/extensions/table/extensions/nodes/table-caption.ts
var TableCaption = Caption.extend({
  name: "tableCaption",
  tableRole: "table_caption",
  group: "table_block",
  isolating: true,
  addOptions() {
    return {
      ...this?.parent?.(),
      placeholderText: "Add table title..."
    };
  },
  addCommands() {
    return {
      toggleTableCaption: () => ({ commands, state }) => {
        const tableGroup = findTableGroup(state.selection);
        if (tableGroup) {
          return commands.toggleShowText(tableGroup.pos, this.type.name);
        }
        return false;
      },
      hideTableCaption: () => ({ commands, state }) => {
        const tableGroup = findTableGroup(state.selection);
        if (tableGroup) {
          return commands.hideText(tableGroup.pos, this.type.name);
        }
        return false;
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "figcaption",
        context: "tableGroup/",
        getAttrs(node) {
          if (typeof node === "string") return {};
          const element = node;
          return {
            hideText: element.style.display === "none" || element.getAttribute("data-cadmus-hidden") === "true"
          };
        }
      }
    ];
  }
});

// src/extensions/table/extensions/nodes/table-cell.ts
import {
  TableCell as TiptapTableCell
} from "@tiptap/extension-table-cell";
var TableCell = TiptapTableCell.extend({
  addOptions() {
    return {
      ...this.parent?.() ?? {},
      defaultColWidth: cellDefaultWidth
    };
  },
  addAttributes() {
    return {
      ...this.parent?.() ?? {},
      colwidth: {
        default: [this.options.defaultColWidth],
        parseHTML: (element) => {
          const colwidth = element.getAttribute("colwidth");
          const value = colwidth ? [parseInt(colwidth, 10)] : [this.options.defaultColWidth];
          return value;
        }
      }
    };
  }
});

// src/extensions/table/extensions/nodes/table-footnote.ts
import { mergeAttributes as mergeAttributes18, Node as Node21 } from "@tiptap/core";
import { Decoration as Decoration13 } from "@tiptap/pm/view";
var TableFootnote = Node21.create({
  name: "tableFootnote",
  addOptions() {
    return {
      HTMLAttributes: {
        "data-table-footer": "true"
      },
      placeholderText: "Add table notes and footer..."
    };
  },
  tableRole: "table_footnote",
  content: "paragraph+",
  isolating: true,
  onCreate() {
    const { type, options, editor } = this;
    const { storage } = editor;
    const { placeholderText } = options;
    const placeholderStorage = storage.placeholder;
    placeholderStorage?.placeholders.set(this.name, {
      /**
       * Display the placeholder for table footnote if it has empty text content
       * and not in focus
       */
      check: (selection, node, pos) => {
        if (node.type.name !== type.name) return false;
        const nodeStart = pos;
        const nodeEnd = pos + node.nodeSize;
        const { from, to } = selection;
        if (from >= nodeStart && from <= nodeEnd || to <= nodeEnd && to >= nodeStart)
          return false;
        if (node.textContent === "") return true;
        return false;
      },
      decoration: (node, pos) => {
        const child = node.firstChild;
        if (!child) return null;
        return Decoration13.node(pos + 1, pos + 1 + child.nodeSize, {
          class: "empty-node",
          "data-placeholder-text": placeholderText
        });
      }
    });
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes18(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  parseHTML() {
    return [
      {
        tag: "div",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const dom = node;
          if (dom.getAttribute("data-table-footer"))
            return {
              hideText: dom.style.display === "none" || dom.getAttribute("data-cadmus-hidden") === "true"
            };
          return false;
        },
        context: "tableGroup"
      }
    ];
  },
  addCommands() {
    return {
      toggleTableFootnote: () => ({ commands, state }) => {
        const tableGroup = findTableGroup(state.selection);
        if (tableGroup) {
          return commands.toggleShowText(tableGroup.pos, this.type.name);
        }
        return false;
      },
      hideTableFootnote: () => ({ commands, state }) => {
        const tableGroup = findTableGroup(state.selection);
        if (tableGroup) {
          return commands.hideText(tableGroup.pos, this.type.name);
        }
        return false;
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const sel = editor.state.selection;
        const tableGroup = findTableGroup(sel);
        if (!tableGroup) return false;
        const endPos = tableGroup.pos + tableGroup.node.nodeSize;
        editor.chain().insertContentAt(endPos, { type: "paragraph" }).run();
        return true;
      }
    };
  }
});

// src/extensions/table/extensions/nodes/table-group.ts
import { mergeAttributes as mergeAttributes19, Node as Node22 } from "@tiptap/core";
var TableGroup = Node22.create({
  name: "tableGroup",
  addOptions() {
    return {
      HTMLAttributes: {
        "data-table-group": true
      }
    };
  },
  group: "tableBlock",
  tableRole: "table_group",
  content: "tableCaption table tableFootnote",
  defining: true,
  renderHTML({ HTMLAttributes }) {
    return [
      "figure",
      mergeAttributes19(this.options.HTMLAttributes, HTMLAttributes),
      0
    ];
  },
  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs(node) {
          if (typeof node === "string") return false;
          const dom = node;
          if (dom.getAttribute("data-table-group") === "true" || dom.querySelector(":scope > table"))
            return {};
          return false;
        }
      }
    ];
  }
});

// src/extensions/table/extensions/nodes/table-header.ts
import {
  TableHeader as TiptapTableHeader
} from "@tiptap/extension-table-header";
var TableHeader = TiptapTableHeader.extend({
  addOptions() {
    return {
      ...this.parent?.() ?? {},
      defaultColWidth: cellDefaultWidth
    };
  },
  addAttributes() {
    return {
      ...this.parent?.() ?? {},
      colwidth: {
        default: [this.options.defaultColWidth],
        parseHTML: (element) => {
          const colwidth = element.getAttribute("colwidth");
          const value = colwidth ? [parseInt(colwidth, 10)] : [this.options.defaultColWidth];
          return value;
        }
      }
    };
  }
});

// src/extensions/table/extensions/table-background-alternate.ts
import { Extension as Extension17 } from "@tiptap/core";
import { CellSelection as CellSelection2, selectedRect as selectedRect3, TableMap as TableMap12 } from "@tiptap/pm/tables";
var TableBackgroundAlternate = Extension17.create({
  name: "TableBackgroundAlternate",
  addGlobalAttributes() {
    return [
      {
        types: ["table"],
        attributes: {
          alternateBackground: {
            default: false,
            parseHTML: (element) => element.getAttribute("data-table-alternate-background") === "true",
            renderHTML: (attributes) => {
              return {
                "data-table-alternate-background": attributes.alternateBackground ? "true" : "false"
              };
            }
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      toggleTableAlternateColor: (color = defaultTableColor) => ({ state, tr, dispatch }) => {
        const selection = state.selection;
        const table = findTable(selection);
        if (!table) return false;
        const { pos, node } = table;
        const { attrs } = node;
        const currAlternate = attrs.alternateBackground;
        const newAttrs = {
          alternateBackground: !currAlternate,
          backgroundColor: currAlternate ? void 0 : color
        };
        const rect = selectedRect3(state);
        const map = TableMap12.get(node);
        tr.setNodeMarkup(pos, void 0, {
          ...attrs,
          ...newAttrs
        }).setSelection(
          new CellSelection2(
            tr.doc.resolve(
              map.positionAt(rect.top, rect.left, node) + table.start
            ),
            tr.doc.resolve(
              map.positionAt(rect.bottom - 1, rect.right - 1, node) + table.start
            )
          )
        );
        dispatch?.(tr);
        return true;
      },
      setTableColor: (color) => ({ state, tr, dispatch }) => {
        const selection = state.selection;
        const table = findTable(selection);
        if (!table) return false;
        const { pos, node } = table;
        const { attrs } = node;
        const currAlternate = attrs.alternateBackground;
        if (!currAlternate) return false;
        const newColor = tableColors.find(
          ({ actual }) => actual === color
        )?.actual;
        if (!newColor) return false;
        const newAttrs = {
          backgroundColor: newColor
        };
        const rect = selectedRect3(state);
        const map = TableMap12.get(node);
        tr.setNodeMarkup(pos, void 0, {
          ...attrs,
          ...newAttrs
        }).setSelection(
          new CellSelection2(
            tr.doc.resolve(
              map.positionAt(rect.top, rect.left, node) + table.start
            ),
            tr.doc.resolve(
              map.positionAt(rect.bottom - 1, rect.right - 1, node) + table.start
            )
          )
        );
        dispatch?.(tr);
        return true;
      }
    };
  }
});

// src/extensions/table/extensions/table-cell-border.ts
import { Extension as Extension18 } from "@tiptap/core";
var TableCellBorder = Extension18.create({
  name: "tableCellBorder",
  addOptions() {
    return {
      sides: ["all", "bottom", "insideHorizontal", "top"]
    };
  },
  addGlobalAttributes() {
    function parseBorder(element) {
      return {
        left: element.hasAttribute("data-left-border") ? element.getAttribute("data-left-border") === "true" : true,
        right: element.hasAttribute("data-right-border") ? element.getAttribute("data-right-border") === "true" : true,
        bottom: element.hasAttribute("data-bottom-border") ? element.getAttribute("data-bottom-border") === "true" : true,
        top: element.hasAttribute("data-top-border") ? element.getAttribute("data-top-border") === "true" : true
      };
    }
    function renderBorder(attributes) {
      if (!attributes.border) return {};
      const border = attributes.border;
      return {
        "data-left-border": border.left ? "true" : "false",
        "data-right-border": border.right ? "true" : "false",
        "data-bottom-border": border.bottom ? "true" : "false",
        "data-top-border": border.top ? "true" : "false"
      };
    }
    return [
      {
        types: ["tableCell", "tableHeader"],
        attributes: {
          border: {
            default: {
              left: true,
              right: true,
              top: true,
              bottom: true
            },
            parseHTML: (element) => parseBorder(element),
            renderHTML: (attributes) => renderBorder(attributes)
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      setTableCellBorder: (side, border) => ({ state, dispatch }) => {
        if (!this.options.sides.includes(side)) return false;
        const table = findTable(state.selection);
        if (!table) return false;
        let { tr } = state;
        const cellsMap = getCellsMap(state.schema, state.selection);
        if (!cellsMap) return false;
        const { cells, height } = cellsMap;
        if (side === "insideHorizontal" && height < 2) return false;
        cells.map((_cell, index) => {
          tr = setCellBorder(cellsMap, index, side, border)(tr);
        });
        dispatch?.(tr);
        return true;
      }
    };
  }
});

// src/extensions/table/table.ts
var Table2 = Extension19.create({
  name: "cadmusTable",
  addExtensions() {
    return [
      TableCaption,
      TableCell.configure({
        defaultColWidth: cellDefaultWidth
      }),
      TableFootnote,
      TableGroup,
      TableHeader.configure({
        defaultColWidth: cellDefaultWidth
      }),
      Table.configure({
        resizable: true,
        lastColumnResizable: true,
        handleWidth: 5,
        cellMinWidth,
        allowTableNodeSelection: true
      }),
      TableRow,
      TableBackgroundAlternate,
      TableCellBorder
    ];
  },
  addProseMirrorPlugins() {
    return [TableDeleteIndicatorPlugin(), TableEnsureGroupPlugin()];
  },
  addCommands() {
    return {
      insertTableWithCaption: ({ rows = 2, cols = 2, withHeaderRow = false } = {}) => ({ chain, dispatch, state }) => {
        if (findCodeBlockSelection(state.selection) || findTableGroup(state.selection))
          return false;
        if (dispatch) {
          return chain().command(({ tr, dispatch: dispatch2, state: state2 }) => {
            const node = createTable(
              state2.schema,
              rows,
              cols,
              withHeaderRow
            );
            const offset2 = tr.selection.anchor + 1;
            tr.replaceSelectionWith(node).scrollIntoView().setSelection(TextSelection6.near(tr.doc.resolve(offset2)));
            dispatch2?.(tr);
            return true;
          }).command(({ tr }) => {
            const tableGroup = findTableGroup(tr.selection);
            if (tableGroup?.node) {
              const predicate = (node) => node.type.spec.tableRole && /cell/i.test(node.type.spec.tableRole);
              const firstCell = findChildren9(tableGroup.node, predicate)[0];
              if (firstCell?.node) {
                tr.setSelection(
                  TextSelection6.near(
                    tr.doc.resolve(tableGroup.start + firstCell.pos)
                  )
                );
                dispatch?.(tr);
              }
            }
            return true;
          }).run();
        }
        return true;
      },
      selectRow: (rowIndex) => ({ tr, dispatch }) => {
        dispatch?.(selectRow(rowIndex)(tr));
        return true;
      },
      selectColumn: (columnIndex) => ({ tr, dispatch }) => {
        dispatch?.(selectColumn(columnIndex)(tr));
        return true;
      },
      selectTable: () => ({ tr, dispatch }) => {
        dispatch?.(selectTable(tr));
        return true;
      },
      selectCellContent: () => ({ tr, dispatch }) => {
        const table = findTable(tr.selection);
        if (!table) return false;
        if (tr.selection instanceof CellSelection3) return false;
        const cell = findCellClosestToPos(tr.selection.$from);
        if (!cell) return false;
        if (dispatch) {
          tr.setSelection(
            TextSelection6.between(
              tr.doc.resolve(cell.start),
              tr.doc.resolve(cell.start + cell.node.nodeSize)
            )
          );
        }
        return true;
      },
      selectTableCaption: () => ({ tr, commands }) => {
        const tableCaption = findTableCaption(tr.selection);
        if (!tableCaption) return false;
        commands.setTextSelection({
          from: tableCaption.pos,
          to: tableCaption.pos + tableCaption.node.nodeSize
        });
        return true;
      },
      selectTableFootnote: () => ({ tr, commands }) => {
        const tableFootnote = findTableFootnote(tr.selection);
        if (!tableFootnote) return false;
        commands.setTextSelection({
          from: tableFootnote.pos,
          to: tableFootnote.pos + tableFootnote.node.nodeSize
        });
        return true;
      },
      setCellsBackground: (color) => ({ state, dispatch }) => {
        return setCellsBackground(color)(state, dispatch);
      },
      addColumnAt: (columnIndex) => ({ state, tr, dispatch }) => {
        const table = findTable(state.selection);
        if (!table) return false;
        const map = TableMap13.get(table.node);
        if (!map) return false;
        dispatch?.(
          addColumn(
            tr,
            {
              tableStart: table.start,
              map,
              table: table.node,
              left: 0,
              right: map.width - 1,
              top: 0,
              bottom: map.height - 1
            },
            columnIndex
          )
        );
        return true;
      },
      addRowAt: (rowIndex) => ({ state, tr, dispatch }) => {
        const table = findTable(state.selection);
        if (!table) return false;
        const map = TableMap13.get(table.node);
        if (!map) return false;
        dispatch?.(
          addRow(
            tr,
            {
              tableStart: table.start,
              map,
              table: table.node,
              left: 0,
              right: map.width - 1,
              top: 0,
              bottom: map.height - 1
            },
            rowIndex
          )
        );
        return true;
      },
      deleteSelectedTable: () => ({ chain }) => {
        return chain().first(({ commands }) => [
          commands.deleteColumn,
          commands.deleteRow
        ]).command(({ tr, dispatch }) => {
          const table = findTable(tr.selection);
          if (!table) return false;
          if (isTableSelected(tr.selection)) {
            const tableGroup = findTableGroup(tr.selection);
            if (!tableGroup) return false;
            dispatch?.(
              tr.delete(
                tableGroup.pos,
                tableGroup.pos + tableGroup.node.nodeSize
              )
            );
          }
          return true;
        }).command(({ commands }) => commands.predeleteHighlightTable(false)).run();
      },
      predeleteHighlightTable: (show) => ({ tr, dispatch }) => {
        dispatch?.(
          tr.setMeta(TableDeleteIndicatorPluginKey, {
            highlight: show
          })
        );
        return true;
      },
      preinsertCol: (colIndex) => ({ tr, dispatch }) => {
        dispatch?.(
          tr.setMeta(TableControlPluginKey, {
            col: colIndex !== void 0 ? colIndex : null,
            row: null
          })
        );
        return true;
      },
      preinsertRow: (rowIndex) => ({ tr, dispatch }) => {
        dispatch?.(
          tr.setMeta(TableControlPluginKey, {
            row: rowIndex !== void 0 ? rowIndex : null,
            col: null
          })
        );
        return true;
      },
      addTablePasteChip: () => ({ commands }) => commands.addPasteChipAfterPredicate(findTableGroup),
      handleTablePaste: (slice) => ({ state, dispatch }) => {
        const inTable = isInTable2(state);
        if (!inTable) return false;
        let cells = __pastedCells(slice);
        const sel = state.selection;
        if (sel instanceof CellSelection3) {
          if (!cells)
            cells = {
              width: 1,
              height: 1,
              rows: [
                Fragment19.from(
                  fitSlice(getTableNodeTypes(state.schema).cell, slice)
                )
              ]
            };
          const table = sel.$anchorCell.node(-1), start = sel.$anchorCell.start(-1);
          const rect = TableMap13.get(table).rectBetween(
            sel.$anchorCell.pos - start,
            sel.$headCell.pos - start
          );
          cells = __clipCells(
            cells,
            rect.right - rect.left,
            rect.bottom - rect.top
          );
          __insertCells(state, dispatch, start, rect, cells);
        } else if (cells) {
          const $cell = selectionCell(state);
          if (!$cell) return false;
          const start = $cell.start(-1);
          __insertCells(
            state,
            dispatch,
            start,
            TableMap13.get($cell.node(-1)).findCell($cell.pos - start),
            cells
          );
          return true;
        } else {
          return false;
        }
        return true;
      }
    };
  },
  addKeyboardShortcuts() {
    const handleTableDelete = () => this.editor.commands.command(({ state, dispatch, tr }) => {
      const tableGroup = findTableGroup(state.selection);
      if (tableGroup?.node && (isSelectionIncludeTable(state) || isTableSelected(state.selection))) {
        dispatch?.(
          tr.delete(tableGroup.pos, tableGroup.pos + tableGroup.node.nodeSize)
        );
        return true;
      }
      return false;
    });
    return {
      "Mod-a": () => this.editor.commands.first(({ commands }) => [
        () => commands.selectCellContent(),
        () => commands.selectTableCaption(),
        () => commands.selectTableFootnote()
      ]),
      Backspace: handleTableDelete,
      "Mod-Backspace": handleTableDelete,
      "Shift-Backspace": handleTableDelete,
      Delete: handleTableDelete,
      "Mod-Delete": handleTableDelete
    };
  }
});
function fitSlice(nodeType, slice) {
  const node = nodeType.createAndFill();
  const tr = new Transform(node).replace(0, node.content.size, slice);
  return tr.doc;
}

// src/extensions/task-item/task-item.ts
import {
  findParentNode as findParentNode11,
  getNodeType as getNodeType7,
  InputRule as InputRule5,
  inputRulesPlugin as inputRulesPlugin3,
  mergeAttributes as mergeAttributes20,
  Node as Node23,
  wrappingInputRule as wrappingInputRule3
} from "@tiptap/core";
var inputRegex5 = /^\s*(\[( ?|x|X)]\s)$/;
var TaskItem = Node23.create({
  name: "taskItem",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  content: "paragraph block*",
  defining: true,
  group: "listItemGroup",
  parseHTML() {
    return [
      {
        tag: 'li[data-type="taskItem"]',
        priority: 51
      },
      // parse checklist item from dropbox paper
      // this needs to be higher in priority compared to list-item parser
      // as they're using the same `<li>` tag
      // task-item is marked using `data-task-created` and the checked
      // status is marksd using `data-task-completed`
      {
        tag: "li",
        priority: 51,
        getAttrs: (node) => {
          if (typeof node === "string") return false;
          const dom = node;
          if (dom.getAttribute("data-task-created")) {
            return {
              checked: dom.getAttribute("data-task-completed")
            };
          }
          return false;
        }
      }
    ];
  },
  addAttributes() {
    return {
      checked: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-checked") === "true" || !!element.getAttribute("data-task-completed"),
        renderHTML: (attributes) => ({
          "data-checked": attributes.checked ? "true" : "false"
        }),
        keepOnSplit: false
      }
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      "li",
      mergeAttributes20(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "taskItem"
      }),
      [
        "label",
        [
          "input",
          {
            ...node.attrs.checked ? { checked: "true" } : {},
            disabled: "true",
            tabIndex: "-1",
            type: "checkbox"
          }
        ]
      ],
      ["div", 0]
    ];
  },
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.cadmusSplitListItem("taskItem")
    };
  },
  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      const listItem = document.createElement("li");
      const checkboxWrapper = document.createElement("label");
      const checkboxStyler = document.createElement("span");
      const checkbox = document.createElement("input");
      const content = document.createElement("div");
      content.id = Math.random().toString(32).slice(2);
      checkboxWrapper.contentEditable = "false";
      checkbox.type = "checkbox";
      checkbox.setAttribute("aria-labelledby", content.id);
      checkbox.setAttribute("tabindex", "-1");
      if (!editor.isEditable) {
        checkbox.readOnly = true;
      }
      checkbox.addEventListener("change", (event) => {
        if (!editor.isEditable) {
          checkbox.checked = !checkbox.checked;
          return;
        }
        const { checked } = event.target;
        if (typeof getPos === "function" && editor.isEditable) {
          editor.chain().focus(void 0, { scrollIntoView: false }).command(({ tr }) => {
            tr.setNodeMarkup(getPos(), void 0, {
              checked
            });
            return true;
          }).run();
        }
      });
      checkbox.addEventListener("keyup", (event) => {
        if (event.key === "Escape") {
          checkbox.blur();
        }
      });
      Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value);
      });
      listItem.dataset.checked = node.attrs.checked;
      if (node.attrs.checked) {
        checkbox.setAttribute("checked", "checked");
        listItem.setAttribute("data-checked", "true");
      }
      checkboxWrapper.append(checkbox, checkboxStyler);
      listItem.append(checkboxWrapper, content);
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        listItem.setAttribute(key, value);
      });
      return {
        dom: listItem,
        contentDOM: content,
        update: (updatedNode) => {
          if (updatedNode.type !== this.type) {
            return false;
          }
          listItem.dataset.checked = updatedNode.attrs.checked;
          if (updatedNode.attrs.checked) {
            checkbox.setAttribute("checked", "checked");
            listItem.setAttribute("data-checked", "true");
          } else {
            checkbox.removeAttribute("checked");
            listItem.setAttribute("data-checked", "false");
          }
          return true;
        }
      };
    };
  },
  addProseMirrorPlugins() {
    return [
      inputRulesPlugin3({
        editor: this.editor,
        rules: [
          wrappingInputRule3({
            find: inputRegex5,
            type: this.type,
            getAttributes: (match) => ({
              checked: ["x", "X"].includes(match[match.length - 1])
            })
          }),
          wrappingInputRule3({
            find: /^\s*(\[\])\s$/,
            type: this.type,
            getAttributes: (_match) => ({
              checked: false
            })
          }),
          new InputRule5({
            find: inputRegex5,
            handler: ({ state, range, match }) => {
              const { tr } = state;
              tr.deleteRange(range.from, range.to);
              const listType = getNodeType7("taskList", this.editor.schema);
              const canUpdate = wrapSelectedItems({
                itemType: this.type,
                listType,
                tr,
                editor: this.editor
              });
              if (!canUpdate) {
                const $cursor = tr.doc.resolve(range.from);
                if ($cursor.depth > 2) {
                  const maybeListItem = $cursor.node($cursor.depth - 1);
                  if (!isListItem(
                    maybeListItem.type.name,
                    this.editor.extensionManager.extensions
                  )) {
                    return null;
                  }
                } else {
                  return null;
                }
              }
              const checked = ["x", "X"].includes(match[match.length - 1]);
              const found = findParentNode11((node) => node.type === this.type)(
                tr.selection
              );
              if (found) {
                tr.setNodeMarkup(found.pos, void 0, { checked });
              }
            }
          })
        ]
      })
    ];
  }
});

// src/extensions/text-color/text-color.ts
import { DEFAULT_TEXT_COLOR, TEXT_COLORS } from "@vericus/cadmus-ui";
import { Extension as Extension20 } from "@tiptap/core";
import "@tiptap/extension-text-style";
import tinycolor2 from "tinycolor2";
var TextColor = Extension20.create({
  name: "textColor",
  addOptions() {
    return {
      types: ["textStyle"],
      defaultTextColor: DEFAULT_TEXT_COLOR,
      colors: TEXT_COLORS
    };
  },
  addGlobalAttributes() {
    const colors10 = this.options.colors.map((c) => tinycolor2(c).toHexString());
    const defaultTextColor = tinycolor2(
      this.options.defaultTextColor
    ).toHexString();
    colors10.push(defaultTextColor);
    const getColor = (color) => {
      return color && colors10.includes(tinycolor2(color).toHexString()) ? color : defaultTextColor;
    };
    return [
      {
        types: this.options.types,
        attributes: {
          color: {
            default: null,
            renderHTML: (attributes) => {
              if (!attributes.color) {
                return {};
              }
              return {
                style: `color: ${getColor(attributes.color)}`
              };
            },
            parseHTML: (element) => getColor(element.style.color)
          }
        }
      }
    ];
  },
  addCommands() {
    const colors10 = this.options.colors.map((c) => tinycolor2(c).toHexString());
    const defaultTextColor = tinycolor2(
      this.options.defaultTextColor
    ).toHexString();
    return {
      setTextColor: (color) => ({ commands, chain }) => {
        const hexColor = tinycolor2(color).toHexString();
        if (colors10.includes(hexColor)) {
          return commands.setMark("textStyle", { color: hexColor });
        } else if (hexColor === defaultTextColor) {
          return chain().setMark("textStyle", { color: null }).removeEmptyTextStyle().run();
        }
        return false;
      },
      unsetTextColor: () => ({ chain }) => {
        return chain().setMark("textStyle", { color: null }).removeEmptyTextStyle().run();
      }
    };
  }
});

// src/extensions/trailing-node/trailing-node.ts
import { Extension as Extension21 } from "@tiptap/core";
import { Plugin as Plugin29, PluginKey as PluginKey23 } from "@tiptap/pm/state";
function nodeEqualsType({ types, node }) {
  return Array.isArray(types) && types.includes(node.type);
}
var TrailingNode = Extension21.create({
  name: "trailingNode",
  addOptions() {
    return {
      node: "paragraph",
      minBlocks: 1
    };
  },
  addProseMirrorPlugins() {
    const plugin = new PluginKey23(this.name);
    const disabledNodes = Object.entries(this.editor.schema.nodes).map(([, value]) => value).filter(
      (node) => !(!!node.spec.allowGapCursor || node.spec.code || node.isAtom || !node.isTextblock)
    );
    return [
      new Plugin29({
        key: plugin,
        appendTransaction: (_, __, state) => {
          const { doc, tr, schema } = state;
          const shouldInsertNodeAtEnd = plugin.getState(state);
          const endPosition = doc.content.size;
          const type = schema.nodes[this.options.node];
          if (!shouldInsertNodeAtEnd) {
            return;
          }
          return tr.insert(endPosition, type.create());
        },
        state: {
          init: (_, state) => {
            const lastNode = state.tr.doc.lastChild;
            return lastNode && !nodeEqualsType({ node: lastNode, types: disabledNodes }) || state.tr.doc.childCount < this.options.minBlocks;
          },
          apply: (tr, value) => {
            if (!tr.docChanged) {
              return value;
            }
            const lastNode = tr.doc.lastChild;
            return lastNode && !nodeEqualsType({ node: lastNode, types: disabledNodes }) || tr.doc.childCount < this.options.minBlocks;
          }
        }
      })
    ];
  }
});

// src/extensions/video/video.ts
import { findParentNode as findParentNode12, Node as Node24 } from "@tiptap/core";
import { Plugin as Plugin31 } from "@tiptap/pm/state";

// src/extensions/video/video-node-view.ts
var VideoNodeView = class {
  dom;
  contentDOM;
  node;
  editor;
  video;
  menu;
  constructor(node, getPos, HTMLAttributes, editor) {
    this.node = node;
    this.editor = editor;
    const figcaption = document.createElement("figcaption");
    figcaption.style.textAlign = "center";
    const video = document.createElement("video");
    this.video = video;
    const baseStyle = "position: relative; width: 100%;";
    const attrStyle = typeof HTMLAttributes.style === "string" ? HTMLAttributes.style : "";
    video.setAttribute(
      "style",
      [baseStyle, attrStyle].filter(Boolean).join(" ")
    );
    Object.entries(HTMLAttributes).forEach(([key, value]) => {
      if (value == null || key === "style") return;
      video.setAttribute(key, String(value));
    });
    video.setAttribute("contenteditable", "false");
    video.setAttribute("controls", "true");
    video.setAttribute("controlsList", "nodownload noplaybackrate");
    this.menu = createMediaFloatingMenu("video", editor, getPos);
    const figure = document.createElement("figure");
    figure.setAttribute("data-video", "true");
    figure.append(video, this.menu.dom, figcaption);
    this.dom = figure;
    this.contentDOM = figcaption;
    this.update(node);
  }
  update(node) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    if (node.attrs.src !== this.video.getAttribute("src")) {
      if (node.attrs.src) {
        this.video.setAttribute("src", String(node.attrs.src));
      } else {
        this.video.removeAttribute("src");
      }
    }
    if (this.contentDOM) {
      this.contentDOM.style.display = node.attrs.hasCaption ? "" : "none";
    }
    if (this.dom) {
      this.dom.contentEditable = this.editor.isEditable ? "true" : "false";
    }
    this.menu.setCaptionState(!!node.attrs.hasCaption);
    return true;
  }
  ignoreMutation(mutation) {
    if (this.menu.dom.contains(mutation.target)) return true;
    return false;
  }
  stopEvent(event) {
    if (this.menu.dom.contains(event.target)) {
      if (event.type === "focus" || event.type === "blur") return false;
      return true;
    }
    return false;
  }
};

// src/extensions/video/video-plugin.ts
import { NodeSelection as NodeSelection5, Plugin as Plugin30 } from "@tiptap/pm/state";
var VideoPlugin = (editor) => new Plugin30({
  appendTransaction: (_transactions, _oldState, newState) => {
    const sel = findSelectedNodeByType(newState, "video");
    if (!sel) return null;
    const { node, from } = sel;
    if (node.attrs.hasCaption === false) {
      const nodeSelection = NodeSelection5.create(newState.doc, from);
      return newState.tr.setSelection(nodeSelection);
    }
    return null;
  },
  props: {
    handleDOMEvents: {
      paste(view, event) {
        if (!event.clipboardData || !isPastedFile(event)) return false;
        if (event.clipboardData?.getData("text/html")) return false;
        const files = Array.from(event.clipboardData.items).map((dt) => dt.getAsFile()).filter((file) => file !== null);
        if (files.length === 0) return false;
        const { tr } = view.state;
        if (!tr.selection.empty) {
          tr.deleteSelection();
        }
        const pos = tr.selection.from;
        const videos = files.filter((file) => /video/i.test(file.type)).map((file) => [file, ""]);
        if (videos.length === 0) return false;
        insertMedia(editor, videos, "video", pos);
        return true;
      },
      drop(view, event) {
        if (!isDroppedFile(event)) return false;
        if (event.dataTransfer?.getData("text/html")) return false;
        const videos = getDataTransferFiles(event).filter((file) => /video/i.test(file.type)).map((file) => [file, ""]);
        if (videos.length === 0) {
          return false;
        }
        const result = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        });
        if (result) {
          event.preventDefault();
          insertMedia(editor, videos, "video", result.pos);
          return true;
        }
        return false;
      }
    }
  }
});

// src/extensions/video/video.ts
var Video = Node24.create({
  name: "video",
  addOptions() {
    return {
      HTMLAttributes: {}
    };
  },
  group: "block",
  content: "inline*",
  selectable: true,
  draggable: true,
  defining: true,
  allowGapCursor: true,
  addNodeView() {
    return ({ node, getPos, editor, HTMLAttributes }) => new VideoNodeView(node, getPos, HTMLAttributes, editor);
  },
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-src")
      },
      hasCaption: {
        default: false,
        parseHTML: (element) => {
          const captionNode = element.querySelector(".media-caption");
          return !!captionNode;
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "figure[data-video]"
      }
    ];
  },
  renderHTML({ node }) {
    const { src, hasCaption } = node.attrs;
    return renderMediaHTML("video", src, hasCaption);
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const sel = editor.state.selection;
        const parentVideoNode = findParentNode12((node) => {
          return node.type == editor.schema.nodes.video;
        })(sel);
        if (!parentVideoNode) return false;
        const endPos = parentVideoNode.pos + parentVideoNode.node.nodeSize;
        editor.chain().insertContentAt(endPos, { type: "paragraph" }).run();
        return true;
      }
    };
  },
  addCommands() {
    return {
      setVideo: (options) => ({ tr, dispatch, state }) => {
        const { caption } = options;
        let node;
        if (caption?.length) {
          node = this.type.create(options, state.schema.text(caption));
        } else {
          node = this.type.create(options);
        }
        if (options.position !== void 0) {
          if (dispatch) tr.insert(options.position, node);
        } else {
          const { selection } = tr;
          if (dispatch)
            tr.replaceRangeWith(selection.from, selection.to, node);
        }
        return true;
      },
      setVideoSrc: (options) => ({ tr, dispatch }) => {
        const nodeType = this.type;
        if (tr.doc.nodeAt(options.position)?.type === nodeType) {
          if (dispatch)
            tr.setNodeAttribute(options.position, "src", options.src);
          return true;
        }
        return false;
      },
      toggleVideoCaption: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "video");
        if (!sel) return false;
        const { node, from } = sel;
        const { hasCaption } = node.attrs;
        if (hasCaption === true) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: false }).deleteRange({ from: from + 1, to: from + node.nodeSize }).run();
        }
        if (hasCaption === false) {
          return chain().setNodeSelection(from).updateAttributes(this.name, { hasCaption: true }).run();
        }
        return true;
      },
      deleteVideo: () => ({ chain, state }) => {
        const sel = findSelectedNodeByType(state, "video");
        if (sel) {
          const { node, from } = sel;
          return chain().deleteRange({ from, to: from + node.nodeSize }).run();
        }
        return false;
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin31({
        appendTransaction: (_, __, state) => {
          const { doc, tr, schema } = state;
          if (doc.nodeAt(0)?.type.name === "video") {
            const type = schema.nodes["paragraph"];
            return tr.insert(0, type.create());
          }
          return null;
        }
      }),
      VideoPlugin(this.editor)
    ];
  }
});

// src/extensions/code-block/language-imports.ts
import "codemirror-mode-elixir";
import "codemirror/mode/clike/clike.js";
import "codemirror/mode/css/css.js";
import "codemirror/mode/go/go.js";
import "codemirror/mode/haskell/haskell.js";
import "codemirror/mode/htmlmixed/htmlmixed.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/mode/markdown/markdown.js";
import "codemirror/mode/octave/octave.js";
import "codemirror/mode/python/python.js";
import "codemirror/mode/r/r.js";
import "codemirror/mode/shell/shell.js";
import "codemirror/mode/sql/sql.js";
import "codemirror/mode/xml/xml.js";
import "codemirror/mode/yaml/yaml.js";

export {
  MediaUploader,
  ALLOWED_MIME_TYPES,
  uploadMediaToServer,
  insertMedia,
  Audio,
  buildRegex,
  BASE_URL_REGEX,
  MARKDOWN_CODEBLOCK_REGEX,
  CODEBLOCK_MARKDOWN_INPUT_REGEX,
  isValidURL,
  textWordCount,
  getText,
  getLanguageDisplay,
  getLanguageMode,
  findCodeBlockSelection,
  parseMarkdownLanguage,
  CodeBlockDeleteIndicatorPluginKey,
  CodeBlockDeleteIndicatorPlugin,
  CodeBlockPastePluginKey,
  CodeBlockPastePlugin,
  PastePluginKey,
  copyEventHandler,
  Paste,
  Blank,
  listExistingBlanks,
  BlockBackground,
  BlockTextVisibility,
  Blockquote,
  List,
  BulletList,
  Code,
  fontFamilySans,
  editorTypography,
  findTableGroup,
  FloatingMenuComponent,
  FloatingMenuExtension,
  Collab,
  HistoryPluginKey,
  History,
  sendableWordCountChange,
  Document,
  SlimDocument,
  SingleBlockDocument,
  NodePasteRule,
  Emoji,
  EmptyMediaNode,
  TaskList,
  listFootnotes,
  currentActiveFootnoteId,
  FootnoteActiveStatusPluginKey,
  FootnoteListPluginKey,
  Footnote,
  htmlToDOMOutputSpec,
  FootnoteStatic,
  useFootnoteList,
  Highlight,
  openLink,
  getValidURL,
  Hyperlink,
  HyperlinkMenuComponent,
  Image2 as Image,
  ImageMenuComponent,
  getEnabledIndentStyles,
  Indentation,
  ListItem,
  BlockMathBase,
  InlineMathBase,
  MathFigure,
  MathServerExtension,
  OrderedList,
  PasteChip,
  Subscript,
  disableSpellCheck,
  enableCadmusSpellCheck,
  canSpellCheckWithCadmus,
  Suggestion,
  SuggestionMenuComponent,
  Superscript,
  TableMenuComponent,
  Table2 as Table,
  TaskItem,
  Telemetry,
  editorWordCount,
  TextColor,
  listThreads,
  currentActiveThreadId,
  ThreadActiveStatusPluginKey,
  ThreadListPluginKey,
  Thread,
  useThreadList,
  TrailingNode,
  Video,
  CadmusEditorWrapper
};
//# sourceMappingURL=chunk-WA3WIKHK.mjs.map